/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] }
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never }
import { DateString, DateTimeString } from '@dolpheen/apollo'
import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core'
export enum AccountNodeAccess {
  EDIT = 'EDIT',
  MANAGE = 'MANAGE',
  VIEW_PRIVATE = 'VIEW_PRIVATE',
  VIEW_PUBLIC = 'VIEW_PUBLIC',
}

export enum AccountScopeCode {
  APP_TOKEN_MANAGED_BY_PLAYBOOK = 'APP_TOKEN_MANAGED_BY_PLAYBOOK',
  APP_TOKEN_NAME_RESERVED = 'APP_TOKEN_NAME_RESERVED',
  BOOK_OUT_OF_SCOPE = 'BOOK_OUT_OF_SCOPE',
  BOOK_TARGET_IS_SOURCE = 'BOOK_TARGET_IS_SOURCE',
  BUSINESS_HOURS_INVALID = 'BUSINESS_HOURS_INVALID',
  OPERATOR_HAS_OPEN_BOOK = 'OPERATOR_HAS_OPEN_BOOK',
  OPERATOR_HAS_OPEN_ENGAGEMENTS = 'OPERATOR_HAS_OPEN_ENGAGEMENTS',
  UNIQUE = 'UNIQUE',
  USER_STILL_REFERENCED = 'USER_STILL_REFERENCED',
}

export enum AcquisitionFunnelStage {
  CONTACTED = 'CONTACTED',
  ELIGIBLE = 'ELIGIBLE',
  ORDER_CREATED = 'ORDER_CREATED',
  SUBMITTED = 'SUBMITTED',
}

/** The door a lead came through, read off Case.externalIdentifier’s prefix convention. Says which PRODUCER made the case, independently of what that producer wrote into CaseSource. */
export enum AcquisitionIntakePath {
  /** The legacy chatbot’s lead export (`chatbot-lead:`). */
  CHATBOT_LEAD = 'CHATBOT_LEAD',
  /** A Meta instant-form submission via the webhook gateway (`fb-form:`). */
  FB_FORM = 'FB_FORM',
  /** An escalation raised from a WhatsApp thread (`messaging-escalation:`). */
  MESSAGING_ESCALATION = 'MESSAGING_ESCALATION',
  /** The brain’s lead sync from a WhatsApp thread (`messaging-lead:`). */
  MESSAGING_LEAD = 'MESSAGING_LEAD',
  /** No external identifier: a case created inside the CRM — typed in by an operator, or filed from a conversation. */
  NONE = 'NONE',
  /** An identifier whose prefix this report does not know. */
  OTHER = 'OTHER',
  /** A website form submission (`web-form:`). */
  WEB_FORM = 'WEB_FORM',
}

export enum AcquisitionNeverReachedCategory {
  CONFIGURATION = 'CONFIGURATION',
  LEAD_UNREACHABLE = 'LEAD_UNREACHABLE',
  NO_IMPEDIMENT = 'NO_IMPEDIMENT',
  OUTREACH_SUPPRESSED = 'OUTREACH_SUPPRESSED',
  UNKNOWN = 'UNKNOWN',
}

export enum AcquisitionNeverReachedReason {
  BEHAVIOUR_RECORD_MISSING = 'BEHAVIOUR_RECORD_MISSING',
  BORN_INTO_THREAD = 'BORN_INTO_THREAD',
  CHANNEL_REFUSED = 'CHANNEL_REFUSED',
  ESCALATION_CASE = 'ESCALATION_CASE',
  EXISTING_CONVERSATION = 'EXISTING_CONVERSATION',
  NOTHING_RECORDED = 'NOTHING_RECORDED',
  NO_CONSENT = 'NO_CONSENT',
  NO_OPENER_LADDER = 'NO_OPENER_LADDER',
  NO_SCREENING_PLAYBOOK = 'NO_SCREENING_PLAYBOOK',
  NO_SEND_CHANNEL = 'NO_SEND_CHANNEL',
  NO_USABLE_PHONE = 'NO_USABLE_PHONE',
  NUMBER_NOT_REACHABLE = 'NUMBER_NOT_REACHABLE',
  PREPROCESS_NEVER_RAN = 'PREPROCESS_NEVER_RAN',
  THREAD_ALREADY_ENGAGED = 'THREAD_ALREADY_ENGAGED',
  THREAD_HELD_BY_OTHER_CUSTOMER = 'THREAD_HELD_BY_OTHER_CUSTOMER',
  UNRECOGNISED = 'UNRECOGNISED',
}

export type AgeFilter = {
  max?: number | null | undefined
  min?: number | null | undefined
  unit: AgePeriod
}

export enum AgePeriod {
  BUSINESS_DAY = 'BUSINESS_DAY',
  DAY = 'DAY',
  HOUR = 'HOUR',
}

export type AppCreateInput = {
  isActive?: boolean | null | undefined
  name: string
  permissions?: Array<Permission> | null | undefined
}

export type AppTokenCreateInput = {
  name: string
}

export type AppUpdateInput = {
  isActive: boolean
  name: string
  permissions?: Array<Permission> | null | undefined
}

export type AppsFilterInput = {
  isActive?: boolean | null | undefined
  search?: string | null | undefined
}

export enum AppsSortField {
  ACTIVE = 'ACTIVE',
  CREATED_AT = 'CREATED_AT',
  NAME = 'NAME',
}

export type AppsSortInput = {
  direction: SortDirection
  field: AppsSortField
}

export enum AuthScopeCode {
  APP_TOKEN_INVALID = 'APP_TOKEN_INVALID',
  INACTIVE = 'INACTIVE',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_PAYLOAD = 'INVALID_PAYLOAD',
  INVALID_TOKEN_TYPE = 'INVALID_TOKEN_TYPE',
  JWT_EXPIRED = 'JWT_EXPIRED',
  JWT_INVALID = 'JWT_INVALID',
  NO_USER_WITH_EMAIL = 'NO_USER_WITH_EMAIL',
  RATE_LIMITED = 'RATE_LIMITED',
  USER_ONLY = 'USER_ONLY',
}

export type BusinessEntitiesFilterInput = {
  directorUser?: Array<string | number> | null | undefined
  ids?: Array<string | number> | null | undefined
  search?: string | null | undefined
}

export enum BusinessEntitiesSortField {
  ALIAS_NAME = 'ALIAS_NAME',
  NAME = 'NAME',
}

export type BusinessEntitiesSortInput = {
  direction: SortDirection
  field: BusinessEntitiesSortField
}

export type BusinessEntityCreateInput = {
  aliasName: string
  bankAccount: string
  bankName: string
  companyFullAddress: string
  companyName: string
  companyShortAddress: string
  directorUser: string | number
  invoiceIdentifier: string
  name: string
  vatRate: number
  websiteUrl: string
}

export type BusinessEntityUpdateInput = {
  aliasName: string
  bankAccount: string
  bankName: string
  companyFullAddress: string
  companyName: string
  companyShortAddress: string
  directorUser: string | number
  invoiceIdentifier: string
  name: string
  vatRate: number
  websiteUrl: string
}

export type BusinessHoursSetInput = {
  /** Treat public holidays as closed days. Omitted ⇒ true. */
  observeHolidays?: boolean | null | undefined
  /** The OPEN hours. Validated server-side: the zone must be one IANA knows (use Asia/Jakarta) and every span must end after it starts. */
  sendWindow: MessagingSendWindowInput
}

export type CampaignCreateInput = {
  /** Optional CaseTag stamped onto a case when it is ASSIGNED to an operator. Applies on every flow, at whatever moment that flow assigns. */
  assignTagId?: string | number | null | undefined
  caseTypeId: string | number
  /** Concierge playbook (MessagingPlaybook global id) for an in-progress case (CaseStage ONBOARDING+). Null means no bot once in progress — the screening playbook is not inherited. */
  conciergePlaybookId?: string | number | null | undefined
  /** CaseActionType for the automatic “customer replied” contact signal on this campaign. Must be an ACTIVE type. Null ⇒ fall through to the event channel's default and then to the seeded `customer_replied` type; it does NOT switch the signal off. */
  customerRepliedActionTypeId?: string | number | null | undefined
  /** Behaviour fallbacks — REPLACES the campaign existing fallbacks in full on save; at most one entry per trigger. An absent trigger means LEAVE_OPEN, except UNDELIVERABLE whose absent default is ASSIGN. */
  fallbacks: Array<CampaignFallbackInput>
  flow: CaseFlow
  name: string
  /** The ordered opener ladder — REPLACES the campaign existing ladder in full on save. Sequence 0 is the immediate opener; 1..N are timed retries. Empty ⇒ no opener (the campaign just routes cases). */
  openers: Array<CampaignOpenerInput>
  /** Operator-QA OversightScenario (MessagingOversightScenario global id) for cases on this campaign. The effective campaign is used as-is; null ⇒ oversight disabled for the case. */
  oversightScenarioId?: string | number | null | undefined
  /** CaseActionType for the automatic “screening completed” contact signal (the screening playbook’s hand-off). Must be an ACTIVE type. Null ⇒ the event channel's default, then the seeded `screening_completed` type. */
  screeningCompletedActionTypeId?: string | number | null | undefined
  /** Screening playbook (MessagingPlaybook global id); it travels in the opener conversationInit. REQUIRED on the ON_SCREENED_ASSIGN flow (with an opener at sequence 0) and must be null on every other flow. */
  screeningPlaybookId?: string | number | null | undefined
  /** Soft ref to a messaging-core Channel; pickers must offer CONVERSATIONAL-purpose channels only. The effective campaign is used as-is; null ⇒ no opener channel (send skipped). */
  sendChannelId?: string | number | null | undefined
  status: CampaignStatus
}

/** Terminal disposition a campaign fallback runs on a still-PENDING case: LEAVE_OPEN does nothing (the case stays PENDING); CLOSE closes it with the fallback closeReason; ASSIGN routes it normally (round-robin) and attaches the optional tag. */
export enum CampaignFallbackAction {
  ASSIGN = 'ASSIGN',
  CLOSE = 'CLOSE',
  LEAVE_OPEN = 'LEAVE_OPEN',
}

export type CampaignFallbackInput = {
  action: CampaignFallbackAction
  /** Close reason for the CLOSE action (required when CLOSE). */
  closeReasonId?: string | number | null | undefined
  /** Optional case tag attached by the ASSIGN action (e.g. cold-lead). */
  tagId?: string | number | null | undefined
  trigger: CampaignFallbackTrigger
  /** Minutes the brain waits before the trigger fires. Required (> 0) for NO_RESPONSE / SCREENING_TIMEOUT with a real action; must be null for UNDELIVERABLE. */
  waitMinutes?: number | null | undefined
}

/** The stuck-case condition a campaign fallback guards against: NO_RESPONSE = the opener ladder was exhausted and the customer never replied; UNDELIVERABLE = the opener could not be delivered at all (e.g. the number is not on WhatsApp); SCREENING_TIMEOUT = the customer replied but the screening bot never handed off in time. */
export enum CampaignFallbackTrigger {
  NO_RESPONSE = 'NO_RESPONSE',
  SCREENING_TIMEOUT = 'SCREENING_TIMEOUT',
  UNDELIVERABLE = 'UNDELIVERABLE',
}

export type CampaignOpenerInput = {
  /** Minutes to wait after the previous opener before this rung sends (0 for sequence 0; must be > 0 for retries). */
  delayMinutes: number
  /** The template's {{n}} placeholder fills, in placeholder order — one per placeholder, so the count must match the template body. Null or absent (an older client) = a template without placeholders. */
  parameters?: Array<CampaignOpenerParameterInput> | null | undefined
  /** 0 = immediate opener; 1..N = retries in order. */
  sequence: number
  templateLanguage: string
  templateName: string
}

export type CampaignOpenerParameterInput = {
  fallback?: string | null | undefined
  source: CampaignOpenerParameterSource
  text?: string | null | undefined
}

/** Where a campaign opener template placeholder takes its value: CUSTOMER_NAME is the case's customer name, resolved per case when the opener is sent (with the fallback when the customer has no name); TEXT is a fixed string. */
export enum CampaignOpenerParameterSource {
  CUSTOMER_NAME = 'CUSTOMER_NAME',
  TEXT = 'TEXT',
}

export type CampaignResolverCreateInput = {
  campaignId: string | number
  /** JSON object of CaseSource.data equality checks; null/omitted means no data constraint. */
  dataFilter?: any
  isActive: boolean
  matchCampaign?: string | null | undefined
  matchKind?: string | null | undefined
  matchMedium?: string | null | undefined
  /** Exact match on CaseSource.tool (the acquisition form/widget/tool); null matches any. */
  matchTool?: string | null | undefined
  matchType?: string | null | undefined
  name: string
  /** Evaluation order — lowest first, first match wins. */
  priority: number
  /** When the rule may capture a new case, read at the case's arrival time. null = always; omitted = leave the saved schedule unchanged. */
  schedule?: CampaignResolverScheduleInput | null | undefined
}

export type CampaignResolverScheduleInput = {
  mode: CampaignResolverScheduleMode
  /** Public holidays count as closed days (outside the hours). */
  observeHolidays: boolean
  /** The weekly hours, in one IANA zone. */
  window: MessagingSendWindowInput
}

/** How a campaign rule reads its weekly hours: DURING applies the rule only while the hours are open; OUTSIDE only while they are closed (after hours, and on public holidays when those are observed). */
export enum CampaignResolverScheduleMode {
  DURING = 'DURING',
  OUTSIDE = 'OUTSIDE',
}

export type CampaignResolverSimulateInput = {
  /** JSON object of CaseSource.data equality checks; null/omitted means no data constraint. */
  dataFilter?: any
  matchCampaign?: string | null | undefined
  matchKind?: string | null | undefined
  matchMedium?: string | null | undefined
  matchTool?: string | null | undefined
  matchType?: string | null | undefined
  /** Evaluation order the rule would have — lowest first. */
  priority: number
  /** When simulating an EDIT of an existing rule, its id: the saved rule is replaced by this hypothetical (keeping its tiebreak position) instead of competing against it. */
  resolverId?: string | number | null | undefined
  /** The rule's schedule; each case is tested at its arrival time. null/omitted = always. */
  schedule?: CampaignResolverScheduleInput | null | undefined
}

export type CampaignResolverUpdateInput = {
  campaignId: string | number
  /** JSON object of CaseSource.data equality checks; null/omitted means no data constraint. */
  dataFilter?: any
  isActive: boolean
  matchCampaign?: string | null | undefined
  matchKind?: string | null | undefined
  matchMedium?: string | null | undefined
  /** Exact match on CaseSource.tool (the acquisition form/widget/tool); null matches any. */
  matchTool?: string | null | undefined
  matchType?: string | null | undefined
  name: string
  /** Evaluation order — lowest first, first match wins. */
  priority: number
  /** When the rule may capture a new case, read at the case's arrival time. null = always; omitted = leave the saved schedule unchanged. */
  schedule?: CampaignResolverScheduleInput | null | undefined
}

/** Only ACTIVE campaigns participate in case routing; resolver rules of PAUSED/ARCHIVED campaigns are skipped. Cases already stamped keep their snapshot. */
export enum CampaignStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  PAUSED = 'PAUSED',
}

export type CampaignUpdateInput = {
  /** Optional CaseTag stamped onto a case when it is ASSIGNED to an operator. Applies on every flow, at whatever moment that flow assigns. */
  assignTagId?: string | number | null | undefined
  caseTypeId: string | number
  /** Concierge playbook (MessagingPlaybook global id) for an in-progress case (CaseStage ONBOARDING+). Null means no bot once in progress — the screening playbook is not inherited. */
  conciergePlaybookId?: string | number | null | undefined
  /** CaseActionType for the automatic “customer replied” contact signal on this campaign. Must be an ACTIVE type. Null ⇒ fall through to the event channel's default and then to the seeded `customer_replied` type; it does NOT switch the signal off. */
  customerRepliedActionTypeId?: string | number | null | undefined
  /** Behaviour fallbacks — REPLACES the campaign existing fallbacks in full on save; at most one entry per trigger. An absent trigger means LEAVE_OPEN, except UNDELIVERABLE whose absent default is ASSIGN. */
  fallbacks: Array<CampaignFallbackInput>
  flow: CaseFlow
  name: string
  /** The ordered opener ladder — REPLACES the campaign existing ladder in full on save. Sequence 0 is the immediate opener; 1..N are timed retries. Empty ⇒ no opener (the campaign just routes cases). */
  openers: Array<CampaignOpenerInput>
  /** Operator-QA OversightScenario (MessagingOversightScenario global id) for cases on this campaign. The effective campaign is used as-is; null ⇒ oversight disabled for the case. */
  oversightScenarioId?: string | number | null | undefined
  /** CaseActionType for the automatic “screening completed” contact signal (the screening playbook’s hand-off). Must be an ACTIVE type. Null ⇒ the event channel's default, then the seeded `screening_completed` type. */
  screeningCompletedActionTypeId?: string | number | null | undefined
  /** Screening playbook (MessagingPlaybook global id); it travels in the opener conversationInit. REQUIRED on the ON_SCREENED_ASSIGN flow (with an opener at sequence 0) and must be null on every other flow. */
  screeningPlaybookId?: string | number | null | undefined
  /** Soft ref to a messaging-core Channel; pickers must offer CONVERSATIONAL-purpose channels only. The effective campaign is used as-is; null ⇒ no opener channel (send skipped). */
  sendChannelId?: string | number | null | undefined
  status: CampaignStatus
}

export enum CampaignsSortField {
  CREATED_AT = 'CREATED_AT',
  NAME = 'NAME',
}

export type CampaignsSortInput = {
  direction: SortDirection
  field: CampaignsSortField
}

/** How a lead ended up, as the acquisition report's drop-off table counts it. The five are mutually exclusive and cover every case; a list ORs them together. Every unpaid arm excludes paid cases, so they reconcile with the report cell that links here. */
export enum CaseAcquisitionOutcome {
  /** Unpaid and closed for any reason that is neither of the two above — INCLUDING closed with no reason recorded at all. */
  CLOSED_OTHER = 'CLOSED_OTHER',
  /** Unpaid and closed with a duplicate reason (stable value starting with `duplic`): the `duplicate_lead` the dedup import path closes DUPLICATE-classified leads at birth with, or an operator-picked one. */
  DUPLICATE = 'DUPLICATE',
  /** A payment in the deployment default currency landed on a non-cancelled order of the case. Terminal success; excluded from the other four whatever the case status now says. */
  PAID = 'PAID',
  /** Neither paid nor closed — the residual the report calls "Still open". */
  STILL_OPEN = 'STILL_OPEN',
  /** Unpaid and closed with a reason that reports the CASE_UNCONTACTABLE Meta event — we never got hold of the lead. */
  UNCONTACTABLE = 'UNCONTACTABLE',
}

export type CaseActionCreateInput = {
  comment: string
  date: DateTimeString
  resultId: string | number
  typeId: string | number
}

export type CaseActionResultCreateInput = {
  isActive: boolean
  name: string
  success?: boolean | null | undefined
  value?: string | null | undefined
}

export type CaseActionResultUpdateInput = {
  isActive: boolean
  name: string
  success?: boolean | null | undefined
  value?: string | null | undefined
}

export enum CaseActionResultsSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type CaseActionResultsSortInput = {
  direction: SortDirection
  field: CaseActionResultsSortField
}

export type CaseActionTypeCreateInput = {
  isActive: boolean
  name: string
  value?: string | null | undefined
}

export type CaseActionTypeUpdateInput = {
  isActive: boolean
  name: string
  value?: string | null | undefined
}

export enum CaseActionTypesSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type CaseActionTypesSortInput = {
  direction: SortDirection
  field: CaseActionTypesSortField
}

export type CaseActionUpdateInput = {
  comment: string
  date: DateTimeString
  resultId: string | number
  typeId: string | number
}

export type CaseAddressAssignInput = {
  customerAddressId: string | number
}

export type CaseAddressAssignNewInput = {
  city: string
  cityArea?: string | null | undefined
  countryAreaId?: string | number | null | undefined
  countryCode?: string | null | undefined
  isDefault?: boolean | null | undefined
  isPermanentResidency: boolean
  note?: string | null | undefined
  postalCode: string
  streetAddress1: string
  streetAddress2?: string | null | undefined
}

export type CaseAssignInput = {
  /** Client-generated assignment idempotency key. Omit only from clients that predate it: the server then mints one, and a retried request assigns twice. */
  operationId?: string | null | undefined
  userId: string | number
}

/** Which assignment-ladder rung picked the current assignee: the customer-relationship ENGAGEMENT holder, the thread's CONVERSATION_PICKER, a SIMILARITY match (same customer or same contact phone/email across duplicate customer rows), the round-robin POOL, or a MANUAL choice by a person. RESERVED (the pre-opener earmark) is no longer written since 2026-09-10 and remains only on cases assigned before that. */
export enum CaseAssignedVia {
  CONVERSATION_PICKER = 'CONVERSATION_PICKER',
  ENGAGEMENT = 'ENGAGEMENT',
  MANUAL = 'MANUAL',
  POOL = 'POOL',
  RESERVED = 'RESERVED',
  SIMILARITY = 'SIMILARITY',
}

export type CaseBookReassignInput = {
  fromUserId: string | number
  /** Client-generated idempotency key for this bounded book-drain invocation. Reuse the same key when retrying a drain; a new key drains again. */
  operationId: string
  toUserId?: string | number | null | undefined
}

export type CaseBulkAssignInput = {
  caseIds: Array<string | number>
  /** Client-generated idempotency key for this bulk invocation. Omit only from clients that predate it: the server then mints one, and a retried request assigns twice. */
  operationId?: string | null | undefined
  userId: string | number
}

export type CaseBulkReopenInput = {
  caseIds: Array<string | number>
}

export type CaseCloseInput = {
  caseCloseReasonId?: string | number | null | undefined
}

export type CaseCloseReasonCreateInput = {
  isActive: boolean
  name: string
  reportedMetaEvent?: MetaEvent | null | undefined
  value?: string | null | undefined
}

export type CaseCloseReasonUpdateInput = {
  isActive: boolean
  name: string
  reportedMetaEvent?: MetaEvent | null | undefined
  value?: string | null | undefined
}

export enum CaseCloseReasonsSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type CaseCloseReasonsSortInput = {
  direction: SortDirection
  field: CaseCloseReasonsSortField
}

/** Filter cases by the contact actions an OPERATOR has logged. Actions the platform writes by itself — the customer’s first reply, the screening hand-off — are never counted by any value here: this is the supervision question "has a human worked this case". The acquisition funnel counts every action, automatic ones included. */
export enum CaseContactActionFilter {
  /** Cases where an operator has logged at least one action but none of theirs succeeded. */
  ALL_UNSUCCESSFUL = 'ALL_UNSUCCESSFUL',
  /** Cases with at least one action logged by an operator. */
  ANY = 'ANY',
  /** Cases where no operator has logged an action — the uncontacted queue. A case the platform has written an automatic action on still counts as uncontacted here, because nobody has worked it. */
  NONE = 'NONE',
  /** Cases with at least one successful action logged by an operator — judged on the success snapshot taken when the action was written, not on the current setting of its result. */
  SUCCESSFUL = 'SUCCESSFUL',
}

export type CaseCreateFromConversationInput = {
  /** The triage conversation this case is being created out of. */
  conversationId: string | number
  /** The existing customer the thread belongs to. Refused unless the conversation is one of this customer’s linkable threads. Exactly one of `customerId` and `newCustomerName` is required. */
  customerId?: string | number | null | undefined
  /** Create a NEW customer with this name and the thread’s WhatsApp number, and file the case for them. Refused when any customer already holds that number — pick that customer instead. Exactly one of `customerId` and `newCustomerName` is required. */
  newCustomerName?: string | null | undefined
  /** Optional note recorded on the new case’s timeline. */
  note?: string | null | undefined
}

export type CaseCreateInput = {
  /** Which of the customer’s phones this case is about. Omit for the customer’s default. Pass it when the case is created beside a conversation, so the case records the handset the customer is actually writing from. Refused unless the phone belongs to this case’s customer. */
  contactPhoneId?: string | number | null | undefined
  estimatedValue?: MoneyInput | null | undefined
  existingCustomerId?: string | number | null | undefined
  newCustomer?: CustomerCreateInput | null | undefined
  typeId: string | number
}

/** Filter cases by recent customer inbound on their engagement (window: 7 days). */
export enum CaseCustomerInboundFilter {
  /** Cases whose customer has not written in the last 7 days (or never, or whose case has no engagement). */
  NOT_RECENT = 'NOT_RECENT',
  /** Cases whose customer wrote in the last 7 days. */
  RECENT = 'RECENT',
}

export type CaseDocumentCreateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  /** Storage reference from uploadUrlRequest mutation */
  storageFileRef: string
  type: CaseDocumentType
}

export enum CaseDocumentType {
  CLOSING_MEDIATION_LETTER = 'CLOSING_MEDIATION_LETTER',
  OTHER = 'OTHER',
  SCREENSHOTS = 'SCREENSHOTS',
  SCREENSHOTS_FOLLOW_UP = 'SCREENSHOTS_FOLLOW_UP',
  SCREENSHOTS_LEGAL_STATEMENT = 'SCREENSHOTS_LEGAL_STATEMENT',
}

export type CaseDocumentUpdateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  type: CaseDocumentType
}

/** Deprecated with the merge-review queue. Kept so older clients still validate. */
export enum CaseDuplicateReviewState {
  /** Reviewed — the two customers are distinct people. */
  DISMISSED = 'DISMISSED',
  /** Reviewed — the two customers were merged. */
  MERGED = 'MERGED',
  /** Still pending, but a reviewer holding both sides asked a manager to merge. */
  MERGE_REQUESTED = 'MERGE_REQUESTED',
  /** A manager or the operator holding both sides still has to decide. */
  PENDING = 'PENDING',
}

export type CaseEmailAssignInput = {
  contactEmailId: string | number
}

export type CaseEmailAssignNewInput = {
  emailAddress: string
  isDefault?: boolean | null | undefined
  note?: string | null | undefined
}

export enum CaseEventType {
  ACTION_ADDED = 'ACTION_ADDED',
  ACTION_DELETED = 'ACTION_DELETED',
  ACTION_EDITED = 'ACTION_EDITED',
  ADDRESS_ASSIGNED = 'ADDRESS_ASSIGNED',
  ASSIGNED = 'ASSIGNED',
  CLOSED = 'CLOSED',
  CREATED = 'CREATED',
  CUSTOMER_MERGED = 'CUSTOMER_MERGED',
  DOCUMENT_DELETED = 'DOCUMENT_DELETED',
  DOCUMENT_EDITED = 'DOCUMENT_EDITED',
  DOCUMENT_UPLOADED = 'DOCUMENT_UPLOADED',
  EMAIL_ASSIGNED = 'EMAIL_ASSIGNED',
  FINANCIAL_ACCOUNT_ADDED = 'FINANCIAL_ACCOUNT_ADDED',
  FINANCIAL_ACCOUNT_DELETED = 'FINANCIAL_ACCOUNT_DELETED',
  FINANCIAL_ACCOUNT_EDITED = 'FINANCIAL_ACCOUNT_EDITED',
  IDENTITY_CONFIRMED = 'IDENTITY_CONFIRMED',
  MOVED_TO_CUSTOMER = 'MOVED_TO_CUSTOMER',
  NOTE_ADDED = 'NOTE_ADDED',
  PHONE_ASSIGNED = 'PHONE_ASSIGNED',
  REMINDER_ADDED = 'REMINDER_ADDED',
  REMINDER_CANCELLED = 'REMINDER_CANCELLED',
  REMINDER_DONE = 'REMINDER_DONE',
  REMINDER_RESCHEDULED = 'REMINDER_RESCHEDULED',
  REOPENED = 'REOPENED',
  TAG_ATTACHED = 'TAG_ATTACHED',
  TAG_DETACHED = 'TAG_DETACHED',
}

/** Assignment flow: DIRECT_ASSIGN assigns at preprocess (the original behaviour); ON_RESPONSE_ASSIGN waits for the first customer reply; ON_SCREENED_ASSIGN waits for the screening playbook to hand off. */
export enum CaseFlow {
  DIRECT_ASSIGN = 'DIRECT_ASSIGN',
  ON_RESPONSE_ASSIGN = 'ON_RESPONSE_ASSIGN',
  ON_SCREENED_ASSIGN = 'ON_SCREENED_ASSIGN',
}

/** Why the effective flow differs from what campaign resolution produced, or why the conversation bind/outreach could not run even when the flow stood (THREAD_HELD_BY_OTHER_CUSTOMER marks a case whose WhatsApp number is captive to a different customer record). Null means the resolved flow ran unmodified. */
export enum CaseFlowChangeReason {
  BORN_INTO_THREAD = 'BORN_INTO_THREAD',
  CHANNEL_REFUSED = 'CHANNEL_REFUSED',
  ESCALATION_CASE = 'ESCALATION_CASE',
  EXISTING_CONVERSATION = 'EXISTING_CONVERSATION',
  NO_CONSENT = 'NO_CONSENT',
  NO_OPENER_LADDER = 'NO_OPENER_LADDER',
  NO_SCREENING_PLAYBOOK = 'NO_SCREENING_PLAYBOOK',
  NO_SEND_CHANNEL = 'NO_SEND_CHANNEL',
  NO_USABLE_PHONE = 'NO_USABLE_PHONE',
  NUMBER_NOT_REACHABLE = 'NUMBER_NOT_REACHABLE',
  THREAD_ALREADY_ENGAGED = 'THREAD_ALREADY_ENGAGED',
  THREAD_HELD_BY_OTHER_CUSTOMER = 'THREAD_HELD_BY_OTHER_CUSTOMER',
}

export type CaseIdentityConfirmInput = {
  /** Optional note for the case timeline, e.g. "chatting: wife". */
  note?: string | null | undefined
}

/** Identity/case-policy provenance written once by the import path: NEW (no identity match), RETURNING (same person, no qualifying case), DUPLICATE (same person with a qualifying open/recent case), or SUSPECTED_DUPLICATE (historical, no longer written). Read-only. */
export enum CaseImportClassification {
  DUPLICATE = 'DUPLICATE',
  NEW = 'NEW',
  RETURNING = 'RETURNING',
  /** Historical, no longer written: a phone match now always links. */
  SUSPECTED_DUPLICATE = 'SUSPECTED_DUPLICATE',
}

export type CaseImportQuarantineResolveInput = {
  /** The corrected lead, in the same shape the quarantine row stores — the repair form edits what it displays. Deliberately untyped rather than a mirror of the import input tree: the payload is quarantined precisely because it does not fit that shape, and validation happens against the canonical import schema on submit, which is what produces the per-field errors the form shows. */
  content: any
}

/** How far a rejected lead got before it failed. DATA (the Pub/Sub body could not be decoded) and ENVELOPE (not a valid message envelope) mean nothing about the lead is readable and the payload is whatever survived. CONTENT means the lead itself did not satisfy the import schema — the common case, and what a producer/consumer version skew looks like. HANDLER means it was schema-valid but the import rejected it (unknown case type, unusable contact details). */
export enum CaseImportQuarantineStage {
  CONTENT = 'CONTENT',
  DATA = 'DATA',
  ENVELOPE = 'ENVELOPE',
  HANDLER = 'HANDLER',
}

export type CaseImportQuarantinesFilterInput = {
  /** Case-insensitive match across the extracted name, email, phone and external identifier. */
  search?: string | null | undefined
  stage?: Array<CaseImportQuarantineStage> | null | undefined
}

export enum CaseImportQuarantinesSortField {
  CREATED_AT = 'CREATED_AT',
  STAGE = 'STAGE',
}

export type CaseImportQuarantinesSortInput = {
  direction: SortDirection
  field: CaseImportQuarantinesSortField
}

export type CaseMovePhoneInput = {
  mode: CaseMovePhoneMode
  /** OWN_NUMBER only: the new customer's number. KEEP and MOVE refuse it. */
  phoneNumber?: string | null | undefined
}

/** What happens to the WhatsApp number when a case moves to a new customer. */
export enum CaseMovePhoneMode {
  /** The number stays with the current customer; the new customer gets no phone and the case links to the thread as a shared number. */
  KEEP = 'KEEP',
  /** The number is the new customer's: the case's phone and the threads on it move. */
  MOVE = 'MOVE',
  /** The new customer has a number of their own that no customer holds yet. */
  OWN_NUMBER = 'OWN_NUMBER',
}

/** Why a case moves to a new customer. */
export enum CaseMoveReason {
  /** Settles the identity check: a different debtor. */
  IDENTITY_CHECK = 'IDENTITY_CHECK',
  /** A scanned KTP's NIK differs from the customer's. */
  NIK_MISMATCH = 'NIK_MISMATCH',
}

export type CaseMoveToNewCustomerInput = {
  /** A KTP of the current customer that is really the new customer's; it moves. */
  customerDocumentId?: string | number | null | undefined
  /** The customer the operator saw on the case. Refused with MOVE_STALE when the case belongs to someone else by now, so a repeated submit creates nothing. */
  expectedCustomerId: string | number
  /** The new customer's NIK. Required, and different, for NIK_MISMATCH. */
  governmentId?: string | null | undefined
  /** The new customer's name. */
  name: string
  /** Optional note for the case timeline. */
  note?: string | null | undefined
  phone: CaseMovePhoneInput
  reason: CaseMoveReason
}

export type CaseNoteAddInput = {
  message: string
}

export type CasePhoneAssignInput = {
  contactPhoneId: string | number
}

export type CasePhoneAssignNewInput = {
  isDefault?: boolean | null | undefined
  note?: string | null | undefined
  phoneNumber: string
}

export type CaseReminderCreateInput = {
  /** The promised instant. From now to a year ahead. */
  dueAt: DateTimeString
  /** At most 500 characters. */
  note?: string | null | undefined
  /** How to follow up (Call, WhatsApp). Must be an active type. */
  typeId?: string | number | null | undefined
}

/** Filter cases by an OPEN reminder that is not yet due. */
export enum CaseReminderFilter {
  /** Cases with no OPEN reminder due in the future (overdue ones do not count). */
  NO_OPEN_FUTURE = 'NO_OPEN_FUTURE',
  /** Cases with at least one OPEN reminder due in the future. */
  OPEN_FUTURE = 'OPEN_FUTURE',
}

/** Whose reminders to read. */
export enum CaseReminderScope {
  /** Every reminder, unassigned cases included. Requires MANAGE_CASES. */
  ALL = 'ALL',
  /** Reminders on cases assigned to the caller. */
  MINE = 'MINE',
  /** Reminders on cases assigned to anyone in the caller's team. Requires the TEAMLEADER role. */
  TEAM = 'TEAM',
}

/** Who booked a reminder: an operator, or the lead import (the customer booked a call). */
export enum CaseReminderSource {
  IMPORT = 'IMPORT',
  OPERATOR = 'OPERATOR',
}

export enum CaseReminderStatus {
  CANCELLED = 'CANCELLED',
  DONE = 'DONE',
  OPEN = 'OPEN',
}

export type CaseReminderUpdateInput = {
  /** New due time; omit to keep it. A change re-arms the due-time alert. */
  dueAt?: DateTimeString | null | undefined
  /** Omit to keep; null clears. */
  note?: string | null | undefined
  /** Omit to keep; null clears. */
  typeId?: string | number | null | undefined
}

export type CaseRemindersFilterInput = {
  /** Only reminders of this case. */
  caseId?: string | number | null | undefined
  /** Due at or after this instant. */
  dueFrom?: DateTimeString | null | undefined
  /** Due before this instant (exclusive). */
  dueTo?: DateTimeString | null | undefined
  /** true = OPEN and already past due; false = everything else. Judged against the server's clock, so a row is never in both lists. */
  overdue?: boolean | null | undefined
  /** Only these statuses. Omitted = every status. */
  status?: Array<CaseReminderStatus> | null | undefined
}

export enum CaseScopeCode {
  ASSIGNEE_INACTIVE = 'ASSIGNEE_INACTIVE',
  ASSIGNEE_MESSAGING_PERMISSION_REQUIRED = 'ASSIGNEE_MESSAGING_PERMISSION_REQUIRED',
  CASE_CLOSED = 'CASE_CLOSED',
  CASE_LOCKED = 'CASE_LOCKED',
  CLOSE_REASON_NOT_REOPENABLE = 'CLOSE_REASON_NOT_REOPENABLE',
  IDENTITY_CHECK_NOT_PENDING = 'IDENTITY_CHECK_NOT_PENDING',
  IMPORT_QUARANTINE_ALREADY_RESOLVED = 'IMPORT_QUARANTINE_ALREADY_RESOLVED',
  IMPORT_QUARANTINE_INVALID = 'IMPORT_QUARANTINE_INVALID',
  IMPORT_QUARANTINE_TOO_MANY = 'IMPORT_QUARANTINE_TOO_MANY',
  INVALID_CASE_TYPE = 'INVALID_CASE_TYPE',
  INVALID_DOCUMENT_FINANCIAL_ACCOUNT = 'INVALID_DOCUMENT_FINANCIAL_ACCOUNT',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PHONE = 'INVALID_PHONE',
  MISSING_ADDRESS = 'MISSING_ADDRESS',
  MISSING_BIRTH_INFO = 'MISSING_BIRTH_INFO',
  MISSING_CASE_TYPE_LOCALIZATION = 'MISSING_CASE_TYPE_LOCALIZATION',
  MISSING_CONTACT_PHONE = 'MISSING_CONTACT_PHONE',
  MISSING_FINANCIAL_ACCOUNT_DEBT = 'MISSING_FINANCIAL_ACCOUNT_DEBT',
  MISSING_FINANCIAL_ACCOUNT_PROVIDER = 'MISSING_FINANCIAL_ACCOUNT_PROVIDER',
  MISSING_FINANCIAL_ACCOUNT_REGISTERED_CONTACT = 'MISSING_FINANCIAL_ACCOUNT_REGISTERED_CONTACT',
  MISSING_FINANCIAL_ACCOUNT_TYPE_LOCALIZATION = 'MISSING_FINANCIAL_ACCOUNT_TYPE_LOCALIZATION',
  MISSING_GOVERNMENT_ID = 'MISSING_GOVERNMENT_ID',
  MISSING_OCCUPATION = 'MISSING_OCCUPATION',
  MISSING_ORDER = 'MISSING_ORDER',
  MISSING_PAID_AMOUNT = 'MISSING_PAID_AMOUNT',
  MOVE_CASE_PENDING = 'MOVE_CASE_PENDING',
  MOVE_DOCUMENT_INVALID = 'MOVE_DOCUMENT_INVALID',
  MOVE_GOVERNMENT_ID_HELD = 'MOVE_GOVERNMENT_ID_HELD',
  MOVE_NIK_NOT_DIFFERENT = 'MOVE_NIK_NOT_DIFFERENT',
  MOVE_NUMBER_SHARED = 'MOVE_NUMBER_SHARED',
  MOVE_PHONE_HELD = 'MOVE_PHONE_HELD',
  MOVE_PHONE_REQUIRED = 'MOVE_PHONE_REQUIRED',
  MOVE_PHONE_VERIFIED = 'MOVE_PHONE_VERIFIED',
  MOVE_SIGNED_DOCUMENTS = 'MOVE_SIGNED_DOCUMENTS',
  MOVE_STALE = 'MOVE_STALE',
  MOVE_TAG_ABSENT = 'MOVE_TAG_ABSENT',
  NO_FINANCIAL_ACCOUNTS = 'NO_FINANCIAL_ACCOUNTS',
  REMINDER_ACTION_ALREADY_LINKED = 'REMINDER_ACTION_ALREADY_LINKED',
  REMINDER_ACTION_MISMATCH = 'REMINDER_ACTION_MISMATCH',
  REMINDER_CANCEL_FORBIDDEN = 'REMINDER_CANCEL_FORBIDDEN',
  REMINDER_DUE_IN_PAST = 'REMINDER_DUE_IN_PAST',
  REMINDER_DUE_TOO_FAR = 'REMINDER_DUE_TOO_FAR',
  REMINDER_NOTE_TOO_LONG = 'REMINDER_NOTE_TOO_LONG',
  REMINDER_NOT_OPEN = 'REMINDER_NOT_OPEN',
  REMINDER_NO_ENGAGEMENT = 'REMINDER_NO_ENGAGEMENT',
  REMINDER_RANGE_INVALID = 'REMINDER_RANGE_INVALID',
  REMINDER_SCOPE_FORBIDDEN = 'REMINDER_SCOPE_FORBIDDEN',
  REMINDER_TYPE_INACTIVE = 'REMINDER_TYPE_INACTIVE',
  REQUIRED = 'REQUIRED',
  SYSTEM_TAG_MANAGED = 'SYSTEM_TAG_MANAGED',
  UNIQUE = 'UNIQUE',
  UNIQUE_CASE_TYPE_REQUIRED = 'UNIQUE_CASE_TYPE_REQUIRED',
}

/** Grouping dimension for CaseSource-attributed reports. Placeholder values (empty string, or the legacy "-" the webhook gateway wrote for unknown campaign/type/medium) join the unattributed group (key null) instead of forming a group of their own. */
export enum CaseSourceDimension {
  /** Meta ad (ad_name, falling back to ad_id) read from CaseSource.data — populated for instant-form leads only; every other source is unattributed. */
  AD = 'AD',
  /** Meta ad set (adset_name, falling back to adset_id) read from CaseSource.data — populated for instant-form leads only; every other source is unattributed. */
  AD_SET = 'AD_SET',
  /** CaseSource.campaign — the campaign the traffic came from. */
  CAMPAIGN = 'CAMPAIGN',
  /** The Meta campaign id from CaseSource.data, and nothing else — stable across renames, which is what lets ad spend join the funnel. A lead carrying no campaign id is unattributed here even when it carries a campaign NAME, exactly as a non-instant-form lead is on AD and AD_SET: there is no id fallback to a name, because one campaign keyed under both would be two rows sharing one budget. Group keys are opaque ids, so each group carries the campaign name as its `label`. Its unattributed group legitimately differs from CAMPAIGN’s in both directions. */
  CAMPAIGN_ID = 'CAMPAIGN_ID',
  /** CaseSource.kind — the acquisition surface class (website-form, meta-instant-form, ...). */
  KIND = 'KIND',
  /** CaseSource.medium — e.g. paid vs organic. */
  MEDIUM = 'MEDIUM',
  /** CaseSource.tool — the instrument within the surface (website form slug, Meta lead-form id, ...). */
  TOOL = 'TOOL',
  /** CaseSource.type — e.g. the Meta platform (fb / ig) or the web source type. */
  TYPE = 'TYPE',
}

/** One CaseSource dimension of the acquisition report. The three arms OR together, and blankOrPlaceholder + noSourceRow together are exactly the report's Unattributed group. */
export type CaseSourceValueFilter = {
  /** The case has a CaseSource row AND a usable value on this dimension — the complement of blankOrPlaceholder + noSourceRow. On kind this is the acquisition report's "attributed leads only" narrowing: the leads that arrived through ads or the website. */
  anyValue?: boolean | null | undefined
  /** The case HAS a CaseSource row, but this dimension is empty on it: NULL, the empty string, or the legacy "-" placeholder — the three values the report folds into its Unattributed key. On the ad and ad-set dimensions this is also every lead that did not arrive through a Meta instant form, because that is the only place those keys exist. */
  blankOrPlaceholder?: boolean | null | undefined
  /** The case has NO CaseSource row at all — never attributed, rather than attributed blank. The report counts these in Unattributed too (it LEFT JOINs the row), so an Unattributed link needs this arm AND blankOrPlaceholder; alone it is the "no source" slice of the funnel's Unattributed decomposition. */
  noSourceRow?: boolean | null | undefined
  /** Exact group keys, as the report renders them — case-sensitive, and never the empty string or "-" (those are blankOrPlaceholder). On the ad and ad-set dimensions the key is the Meta *_name when the lead carries one and the *_id only otherwise, the same fallback the report groups on: a key that named an ad never matches a row whose id happens to read the same. */
  values?: Array<string> | null | undefined
}

/** The computed stage of a case based on its progress. */
export enum CaseStage {
  /** All products have ended and servicing is complete. */
  COMPLETE = 'COMPLETE',
  /** Case has an active order that has not yet been fulfilled. */
  ONBOARDING = 'ONBOARDING',
  /** Case has active products being serviced. */
  SERVICING = 'SERVICING',
  /** No successful contact has been made with the customer. */
  UNCONTACTED = 'UNCONTACTED',
}

export type CaseStartConversationInput = {
  /** Conversational channel to start the conversation on. */
  channelId: string | number
  /** Client-generated UUID — outbound idempotency key */
  clientMsgId: string
  /** Per-conversation monotonic sequence stamped at send */
  clientSeq: number
  /** Operator attests the customer consented to WhatsApp contact. Must be true — the server rejects an unticked send (typed error). */
  consent: boolean
  /** The operator was shown the safety-word confirmation dialog for this template's fill and chose to send it anyway. Same contract as MessagingMessageSendInput.safetyAcknowledged — it skips the pre-send warning only. Present here because this mutation has its own send and therefore its own dialog; without it the dialog could be opened but never confirmed. */
  safetyAcknowledged?: boolean | null | undefined
  template: MessagingMessageSendTemplateInput
}

export enum CaseStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  PENDING = 'PENDING',
}

export type CaseTagAttachInput = {
  caseTagId: string | number
}

export type CaseTagCreateInput = {
  color?: string | null | undefined
  isActive: boolean
  name: string
  value?: string | null | undefined
}

export type CaseTagDetachInput = {
  caseTagId: string | number
}

export type CaseTagHasFilter = {
  has?: Array<string | number> | null | undefined
  hasNot?: Array<string | number> | null | undefined
}

export type CaseTagUpdateInput = {
  color?: string | null | undefined
  isActive: boolean
  name: string
  value?: string | null | undefined
}

export enum CaseTagsSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type CaseTagsSortInput = {
  direction: SortDirection
  field: CaseTagsSortField
}

export type CaseTypeCreateInput = {
  /** Code prefix for generating case codes (2 uppercase letters) */
  codePrefix: string
  financialAccountTypes?: Array<string | number> | null | undefined
  /** The campaign used as the default behaviour bundle when CampaignResolver matches no campaign. Must belong to this case type. Edit-only: a case type has no campaigns at create time, so this can only be set on update. */
  implicitCampaignId?: string | number | null | undefined
  isActive: boolean
  name: string
  nameUsedInDocuments?: string | null | undefined
  value?: string | null | undefined
}

export type CaseTypeUpdateInput = {
  /** Code prefix for generating case codes (2 uppercase letters) */
  codePrefix: string
  financialAccountTypes?: Array<string | number> | null | undefined
  /** The campaign used as the default behaviour bundle when CampaignResolver matches no campaign. Must belong to this case type. Edit-only: a case type has no campaigns at create time, so this can only be set on update. */
  implicitCampaignId?: string | number | null | undefined
  isActive: boolean
  name: string
  nameUsedInDocuments?: string | null | undefined
  value?: string | null | undefined
}

export enum CaseTypesSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type CaseTypesSortInput = {
  direction: SortDirection
  field: CaseTypesSortField
}

export type CasesAllFilterInput = {
  age?: AgeFilter | null | undefined
  assignedToUser?: Array<string | number> | null | undefined
  assignedToUserTeam?: Array<string | number> | null | undefined
  closeReasons?: Array<string | number> | null | undefined
  closed?: DateRange | null | undefined
  /** Whether an OPERATOR has logged a contact action on the case, and how it went. Actions the platform writes by itself (the customer’s first reply, the screening hand-off) are not counted here — this is the supervision question "has a human worked this case", which is what the Home tiles this filter backs are asking. */
  contactAction?: CaseContactActionFilter | null | undefined
  created?: DateRange | null | undefined
  customer?: Array<string | number> | null | undefined
  /** Whether the customer has written to us lately on the case's engagement (CustomerEngagement.lastInboundAt). NOT_RECENT is what the nightly close sweeps add so a talking customer is never swept closed. */
  customerInbound?: CaseCustomerInboundFilter | null | undefined
  /**
   * Deprecated. Accepted and ignored.
   * @deprecated The merge-review queue is gone. Accepted and ignored; removed with the CustomerMergeCandidate table.
   */
  duplicateReview?: Array<CaseDuplicateReviewState> | null | undefined
  emailAddress?: string | null | undefined
  orderAge?: AgeFilter | null | undefined
  orderPaymentStatus?: Array<OrderPaymentStatus> | null | undefined
  /** How the lead ended up, as the acquisition report's drop-off table counts it — the list ORs the arms. Not derivable from `status` + `closeReasons`: every arm also excludes paid cases, and CLOSED_OTHER is a NOT-IN the opaque-id `closeReasons` filter cannot express. */
  outcome?: Array<CaseAcquisitionOutcome> | null | undefined
  phoneNumber?: string | null | undefined
  /** Whether the case holds an OPEN reminder that is not yet due (a promised call). NO_OPEN_FUTURE is what the nightly close sweeps add so a booked callback is never swept closed. */
  reminder?: CaseReminderFilter | null | undefined
  reservedForUser?: Array<string | number> | null | undefined
  search?: string | null | undefined
  /** The report's AD dimension, which is not a column: it reads CaseSource.data->>'ad_name' and falls back to 'ad_id'. Meta instant-form leads only; unindexed JSON. */
  sourceAd?: CaseSourceValueFilter | null | undefined
  /** The report's AD_SET dimension, which is not a column: it reads CaseSource.data->>'adset_name' and falls back to 'adset_id'. Meta instant-form leads only; unindexed JSON. */
  sourceAdSet?: CaseSourceValueFilter | null | undefined
  /** CaseSource.campaign — the report's CAMPAIGN dimension, one third of the ad-provenance trio. Indexed. */
  sourceCampaign?: CaseSourceValueFilter | null | undefined
  /** The report's CAMPAIGN_ID dimension, which is not a column: it reads CaseSource.data->>'campaign_id' and NOTHING else, so a group key survives a Meta rename. A lead carrying no campaign id is unattributed here even when it carries a campaign NAME — narrow on `sourceCampaign` for that. Unindexed JSON. */
  sourceCampaignId?: CaseSourceValueFilter | null | undefined
  /** CaseSource.kind — the report's KIND dimension: which acquisition surface class the lead arrived on. Indexed. */
  sourceKind?: CaseSourceValueFilter | null | undefined
  /** CaseSource.medium — the report's MEDIUM dimension, one third of the ad-provenance trio. NOT indexed. */
  sourceMedium?: CaseSourceValueFilter | null | undefined
  /** CaseSource.tool — the report's TOOL dimension: which form or widget captured the lead, within the surface `kind` names. Indexed. */
  sourceTool?: CaseSourceValueFilter | null | undefined
  /** CaseSource.type — the report's TYPE dimension, one third of the ad-provenance trio (where the traffic came from). NOT indexed. */
  sourceType?: CaseSourceValueFilter | null | undefined
  stage?: Array<CaseStage> | null | undefined
  status?: Array<CaseStatus> | null | undefined
  tag?: CaseTagHasFilter | null | undefined
  type?: Array<string | number> | null | undefined
}

export enum CasesAllSortField {
  ASSIGNEE = 'ASSIGNEE',
  CUSTOMER = 'CUSTOMER',
  DATE = 'DATE',
}

export type CasesAllSortInput = {
  direction: SortDirection
  field: CasesAllSortField
}

export type CasesTeamAssignedFilterInput = {
  age?: AgeFilter | null | undefined
  assignedToUser?: Array<string | number> | null | undefined
  closeReasons?: Array<string | number> | null | undefined
  closed?: DateRange | null | undefined
  contactAction?: CaseContactActionFilter | null | undefined
  created?: DateRange | null | undefined
  customer?: Array<string | number> | null | undefined
  /**
   * Deprecated. Accepted and ignored.
   * @deprecated The merge-review queue is gone. Accepted and ignored; removed with the CustomerMergeCandidate table.
   */
  duplicateReview?: Array<CaseDuplicateReviewState> | null | undefined
  emailAddress?: string | null | undefined
  orderPaymentStatus?: Array<OrderPaymentStatus> | null | undefined
  phoneNumber?: string | null | undefined
  search?: string | null | undefined
  stage?: Array<CaseStage> | null | undefined
  status?: Array<CaseStatus> | null | undefined
  tag?: CaseTagHasFilter | null | undefined
}

export enum CasesTeamAssignedSortField {
  CUSTOMER = 'CUSTOMER',
  DATE = 'DATE',
}

export type CasesTeamAssignedSortInput = {
  direction: SortDirection
  field: CasesTeamAssignedSortField
}

export type CasesUserAssignedFilterInput = {
  age?: AgeFilter | null | undefined
  contactAction?: CaseContactActionFilter | null | undefined
  created?: DateRange | null | undefined
  customer?: Array<string | number> | null | undefined
  /**
   * Deprecated. Accepted and ignored.
   * @deprecated The merge-review queue is gone. Accepted and ignored; removed with the CustomerMergeCandidate table.
   */
  duplicateReview?: Array<CaseDuplicateReviewState> | null | undefined
  emailAddress?: string | null | undefined
  orderPaymentStatus?: Array<OrderPaymentStatus> | null | undefined
  phoneNumber?: string | null | undefined
  search?: string | null | undefined
  stage?: Array<CaseStage> | null | undefined
  status?: Array<CaseStatus> | null | undefined
  tag?: CaseTagHasFilter | null | undefined
}

export enum CasesUserAssignedSortField {
  CUSTOMER = 'CUSTOMER',
  DATE = 'DATE',
}

export type CasesUserAssignedSortInput = {
  direction: SortDirection
  field: CasesUserAssignedSortField
}

export enum CommonScopeCode {
  INVALID = 'INVALID',
  INVALID_MODEL = 'INVALID_MODEL',
  NOT_FOUND = 'NOT_FOUND',
  NOT_FRONTEND_URL = 'NOT_FRONTEND_URL',
  PAGINATION_INVALID = 'PAGINATION_INVALID',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  RECORD_EXISTS = 'RECORD_EXISTS',
  REQUIRED = 'REQUIRED',
  SINGLE = 'SINGLE',
  STORE_ERROR = 'STORE_ERROR',
  UNSUPPORTED_NODE = 'UNSUPPORTED_NODE',
  UPLOAD_ERROR = 'UPLOAD_ERROR',
}

export type CustomerAddressCreateInput = {
  city: string
  cityArea?: string | null | undefined
  countryAreaId?: string | number | null | undefined
  countryCode?: string | null | undefined
  isDefault?: boolean | null | undefined
  isPermanentResidency: boolean
  note?: string | null | undefined
  postalCode: string
  streetAddress1: string
  streetAddress2?: string | null | undefined
}

export enum CustomerCompanyType {
  AGRICULTURE_OR_MINING = 'AGRICULTURE_OR_MINING',
  CONSTRUCTION = 'CONSTRUCTION',
  EDUCATION = 'EDUCATION',
  FINANCE = 'FINANCE',
  GOVERNMENT_OR_BUMN = 'GOVERNMENT_OR_BUMN',
  HEALTHCARE = 'HEALTHCARE',
  MANUFACTURING = 'MANUFACTURING',
  OTHER = 'OTHER',
  SERVICES = 'SERVICES',
  TECHNOLOGY = 'TECHNOLOGY',
  TRADE_OR_RETAIL = 'TRADE_OR_RETAIL',
  TRANSPORTATION = 'TRANSPORTATION',
}

export type CustomerContactEmailCreateInput = {
  emailAddress: string
  isDefault?: boolean | null | undefined
  note?: string | null | undefined
}

export type CustomerContactPhoneCreateInput = {
  isDefault?: boolean | null | undefined
  note?: string | null | undefined
  phoneNumber: string
}

export type CustomerCreateInput = {
  address?: CustomerAddressCreateInput | null | undefined
  birthDate?: DateString | null | undefined
  birthPlace?: string | null | undefined
  companyType?: CustomerCompanyType | null | undefined
  contactEmail?: CustomerContactEmailCreateInput | null | undefined
  contactPhone?: CustomerContactPhoneCreateInput | null | undefined
  /** Free-text domicile. Ignored (derived) when `domicileRegencyId` or `domicileCountryAreaId` is supplied; otherwise stored as-is and best-effort matched to a province/regency. */
  domicile?: string | null | undefined
  /** Province of the current domicile. Omit to leave unchanged; null clears the domicile. Filled from the regency when only `domicileRegencyId` is given. */
  domicileCountryAreaId?: string | number | null | undefined
  /** City/regency of the current domicile. Must belong to `domicileCountryAreaId` when both are given. */
  domicileRegencyId?: string | number | null | undefined
  educationLevel?: CustomerEducationLevel | null | undefined
  employmentStatus?: CustomerEmploymentStatus | null | undefined
  gender?: Gender | null | undefined
  governmentId?: string | null | undefined
  jobTitle?: string | null | undefined
  /** The customer's preferred portal language (e.g. `id` / `en`). */
  language?: string | null | undefined
  marriageStatus?: CustomerMaritalStatus | null | undefined
  name: string
  occupation?: string | null | undefined
  /** What `OTHER` in `otherDebtTypes` stands for. Required when OTHER is selected; cleared automatically when it is not. */
  otherDebtTypeOther?: string | null | undefined
  /** Omitting or passing null leaves the stored value unchanged. */
  otherDebtTypes?: Array<CustomerOtherDebtType> | null | undefined
  referralSource?: CustomerReferralSource | null | undefined
  residentialStatus?: CustomerResidentialStatus | null | undefined
  salaryRange?: CustomerSalaryRange | null | undefined
}

export type CustomerDocumentCreateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  /** Storage reference from uploadUrlRequest mutation */
  storageFileRef: string
  type: CustomerDocumentType
}

export enum CustomerDocumentType {
  ID_CARD = 'ID_CARD',
  NPWP_CARD = 'NPWP_CARD',
  OTHER = 'OTHER',
}

export type CustomerDocumentUpdateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  type: CustomerDocumentType
}

export enum CustomerEducationLevel {
  DIPLOMA = 'DIPLOMA',
  JUNIOR_HIGH_SCHOOL = 'JUNIOR_HIGH_SCHOOL',
  NONE = 'NONE',
  POSTGRADUATE = 'POSTGRADUATE',
  PRIMARY_SCHOOL = 'PRIMARY_SCHOOL',
  SENIOR_HIGH_SCHOOL = 'SENIOR_HIGH_SCHOOL',
  UNIVERSITY = 'UNIVERSITY',
}

export enum CustomerEmploymentStatus {
  CIVIL_SERVANT = 'CIVIL_SERVANT',
  CONTRACT_EMPLOYEE = 'CONTRACT_EMPLOYEE',
  FREELANCE = 'FREELANCE',
  HOMEMAKER = 'HOMEMAKER',
  OTHER = 'OTHER',
  PERMANENT_EMPLOYEE = 'PERMANENT_EMPLOYEE',
  RETIRED = 'RETIRED',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  STUDENT = 'STUDENT',
  UNEMPLOYED = 'UNEMPLOYED',
}

export enum CustomerEventType {
  ADDRESS_ADDED = 'ADDRESS_ADDED',
  ADDRESS_DEFAULT = 'ADDRESS_DEFAULT',
  ADDRESS_REMOVED = 'ADDRESS_REMOVED',
  CONTACT_EMAIL_ADDED = 'CONTACT_EMAIL_ADDED',
  CONTACT_EMAIL_DEFAULT = 'CONTACT_EMAIL_DEFAULT',
  CONTACT_EMAIL_REMOVED = 'CONTACT_EMAIL_REMOVED',
  CONTACT_EMAIL_VERIFIED = 'CONTACT_EMAIL_VERIFIED',
  CONTACT_PHONE_ADDED = 'CONTACT_PHONE_ADDED',
  CONTACT_PHONE_DEFAULT = 'CONTACT_PHONE_DEFAULT',
  CONTACT_PHONE_REMOVED = 'CONTACT_PHONE_REMOVED',
  CONTACT_PHONE_VERIFIED = 'CONTACT_PHONE_VERIFIED',
  CONTACT_VERIFICATION_CONFLICT = 'CONTACT_VERIFICATION_CONFLICT',
  CREATED = 'CREATED',
  CUSTOMER_ANONYMIZED = 'CUSTOMER_ANONYMIZED',
  CUSTOMER_MERGED = 'CUSTOMER_MERGED',
  DOCUMENT_DELETED = 'DOCUMENT_DELETED',
  DOCUMENT_EDITED = 'DOCUMENT_EDITED',
  DOCUMENT_UPLOADED = 'DOCUMENT_UPLOADED',
  UPDATED = 'UPDATED',
}

export enum CustomerMaritalStatus {
  DIVORCED = 'DIVORCED',
  MARRIED = 'MARRIED',
  SINGLE = 'SINGLE',
  WIDOWED = 'WIDOWED',
}

export enum CustomerOtherDebtType {
  CREDIT_CARD = 'CREDIT_CARD',
  FRIENDS_FAMILY = 'FRIENDS_FAMILY',
  INFORMAL_LENDER = 'INFORMAL_LENDER',
  KOPERASI = 'KOPERASI',
  LEASING = 'LEASING',
  OTHER = 'OTHER',
  PAYLATER = 'PAYLATER',
  PEGADAIAN = 'PEGADAIAN',
}

/** Delivery the operator chose for "Send portal access". */
export enum CustomerPortalInviteDeliveryMethod {
  EMAIL = 'EMAIL',
  READ_ALOUD = 'READ_ALOUD',
  WHATSAPP = 'WHATSAPP',
}

/** "Send portal access" enabled the customer but has no invite to show. The customer stays enabled. */
export enum CustomerPortalInviteFailure {
  /** READ_ALOUD: no token came back, and the previous invite may be gone. Resend. */
  NOT_ISSUED = 'NOT_ISSUED',
  /** The invite was not sent: it never reached the portal, or the portal refused it. */
  NOT_SENT = 'NOT_SENT',
  /** EMAIL/WHATSAPP: the invite may not have arrived or may not work. Resend it. */
  UNKNOWN = 'UNKNOWN',
}

/** Status of a portal-invite target contact. Severity, most severe first: VERIFIED_ELSEWHERE, SHARED_NUMBER, HELD_BY_OTHER_DEBTOR, MOVED_DEBTOR_SHARES_IT, IDENTITY_CHECK_OPEN, HELD_BY_SAME_DEBTOR, HELD_ELSEWHERE, NONE. Only HELD_ELSEWHERE and NONE are sendable; HELD_ELSEWHERE needs the acknowledgement. */
export enum CustomerPortalInviteTargetStatus {
  /** Another holder has a different NIK. Refused. */
  HELD_BY_OTHER_DEBTOR = 'HELD_BY_OTHER_DEBTOR',
  /** Another holder has the same NIK. Refused: merge these customers first. */
  HELD_BY_SAME_DEBTOR = 'HELD_BY_SAME_DEBTOR',
  /** Another holder, one of the two NIKs missing. Allowed once the operator acknowledges the warning. */
  HELD_ELSEWHERE = 'HELD_ELSEWHERE',
  /** An open case of this customer carries identity-check. Refused. */
  IDENTITY_CHECK_OPEN = 'IDENTITY_CHECK_OPEN',
  /** A "Different debtor" move kept this number shared with the moved case. Refused. Phones only. */
  MOVED_DEBTOR_SHARES_IT = 'MOVED_DEBTOR_SHARES_IT',
  /** No evidence anyone else holds the contact. Allowed. */
  NONE = 'NONE',
  /** More customers hold the contact than LEAD_MATCH_MAX_CLUSTER. Refused. */
  SHARED_NUMBER = 'SHARED_NUMBER',
  /** Another customer has the contact verified. Refused. */
  VERIFIED_ELSEWHERE = 'VERIFIED_ELSEWHERE',
}

export enum CustomerReferralSource {
  ADVERTISEMENT = 'ADVERTISEMENT',
  FRIEND_OR_FAMILY = 'FRIEND_OR_FAMILY',
  NEWS_MEDIA = 'NEWS_MEDIA',
  OTHER = 'OTHER',
  SEARCH_ENGINE = 'SEARCH_ENGINE',
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  YOUTUBE = 'YOUTUBE',
}

export enum CustomerResidentialStatus {
  COMPANY_PROVIDED = 'COMPANY_PROVIDED',
  FAMILY_OWNED = 'FAMILY_OWNED',
  OTHER = 'OTHER',
  OWNED = 'OWNED',
  RENTED = 'RENTED',
}

export enum CustomerSalaryRange {
  ABOVE_20M = 'ABOVE_20M',
  BELOW_5M = 'BELOW_5M',
  FROM_5M_TO_10M = 'FROM_5M_TO_10M',
  FROM_10M_TO_20M = 'FROM_10M_TO_20M',
}

export enum CustomerScopeCode {
  DELETE_BLOCKED_CASE_SUBSTANCE = 'DELETE_BLOCKED_CASE_SUBSTANCE',
  DELETE_BLOCKED_CONVERSATIONS = 'DELETE_BLOCKED_CONVERSATIONS',
  DELETE_BLOCKED_DOCUMENTS = 'DELETE_BLOCKED_DOCUMENTS',
  DELETE_BLOCKED_OPEN_CASE = 'DELETE_BLOCKED_OPEN_CASE',
  DELETE_BLOCKED_PORTAL = 'DELETE_BLOCKED_PORTAL',
  IDENTITY_CHECK_PENDING = 'IDENTITY_CHECK_PENDING',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PHONE = 'INVALID_PHONE',
  IN_USE = 'IN_USE',
  MERGE_CANDIDATE_ALREADY_RESOLVED = 'MERGE_CANDIDATE_ALREADY_RESOLVED',
  MERGE_CANDIDATE_STALE = 'MERGE_CANDIDATE_STALE',
  MERGE_GOVERNMENT_ID_CONFLICT = 'MERGE_GOVERNMENT_ID_CONFLICT',
  MERGE_PORTAL_LOGIN_LOST = 'MERGE_PORTAL_LOGIN_LOST',
  PORTAL_INVITE_MESSAGING_UNAVAILABLE = 'PORTAL_INVITE_MESSAGING_UNAVAILABLE',
  PORTAL_INVITE_NO_TRANSACTIONAL_CHANNEL = 'PORTAL_INVITE_NO_TRANSACTIONAL_CHANNEL',
  PORTAL_INVITE_PORTAL_NOT_CONFIGURED = 'PORTAL_INVITE_PORTAL_NOT_CONFIGURED',
  PORTAL_INVITE_TARGET_HELD_BY_OTHER_DEBTOR = 'PORTAL_INVITE_TARGET_HELD_BY_OTHER_DEBTOR',
  PORTAL_INVITE_TARGET_HELD_BY_SAME_DEBTOR = 'PORTAL_INVITE_TARGET_HELD_BY_SAME_DEBTOR',
  PORTAL_INVITE_TARGET_IDENTITY_CHECK_OPEN = 'PORTAL_INVITE_TARGET_IDENTITY_CHECK_OPEN',
  PORTAL_INVITE_TARGET_MOVED_DEBTOR_SHARES_IT = 'PORTAL_INVITE_TARGET_MOVED_DEBTOR_SHARES_IT',
  PORTAL_INVITE_TARGET_NOT_ACKNOWLEDGED = 'PORTAL_INVITE_TARGET_NOT_ACKNOWLEDGED',
  PORTAL_INVITE_TARGET_PHONE_INVALID = 'PORTAL_INVITE_TARGET_PHONE_INVALID',
  PORTAL_INVITE_TARGET_SHARED_NUMBER = 'PORTAL_INVITE_TARGET_SHARED_NUMBER',
  PORTAL_INVITE_TARGET_VERIFIED_ELSEWHERE = 'PORTAL_INVITE_TARGET_VERIFIED_ELSEWHERE',
  PORTAL_INVITE_TEMPLATE_NOT_APPROVED = 'PORTAL_INVITE_TEMPLATE_NOT_APPROVED',
  PORTAL_INVITE_WHATSAPP_DISABLED = 'PORTAL_INVITE_WHATSAPP_DISABLED',
  REQUIRED = 'REQUIRED',
  REQUIRED_MIN_LENGTH = 'REQUIRED_MIN_LENGTH',
  UNIQUE = 'UNIQUE',
}

export type CustomerUpdateInput = {
  birthDate?: DateString | null | undefined
  birthPlace?: string | null | undefined
  companyType?: CustomerCompanyType | null | undefined
  /** Free-text domicile. Ignored (derived) when `domicileRegencyId` or `domicileCountryAreaId` is supplied; otherwise stored as-is and best-effort matched to a province/regency. */
  domicile?: string | null | undefined
  /** Province of the current domicile. Omit to leave unchanged; null clears the domicile. Filled from the regency when only `domicileRegencyId` is given. */
  domicileCountryAreaId?: string | number | null | undefined
  /** City/regency of the current domicile. Must belong to `domicileCountryAreaId` when both are given. */
  domicileRegencyId?: string | number | null | undefined
  educationLevel?: CustomerEducationLevel | null | undefined
  employmentStatus?: CustomerEmploymentStatus | null | undefined
  gender?: Gender | null | undefined
  governmentId?: string | null | undefined
  jobTitle?: string | null | undefined
  /** The customer's preferred portal language (e.g. `id` / `en`). */
  language?: string | null | undefined
  marriageStatus?: CustomerMaritalStatus | null | undefined
  /** Omit or pass null to leave the name unchanged. A customer name can never be cleared. */
  name?: string | null | undefined
  occupation?: string | null | undefined
  /** What `OTHER` in `otherDebtTypes` stands for. Required when OTHER is selected; cleared automatically when it is not. */
  otherDebtTypeOther?: string | null | undefined
  /** Omitting or passing null leaves the stored value unchanged. */
  otherDebtTypes?: Array<CustomerOtherDebtType> | null | undefined
  referralSource?: CustomerReferralSource | null | undefined
  residentialStatus?: CustomerResidentialStatus | null | undefined
  salaryRange?: CustomerSalaryRange | null | undefined
}

export type CustomersFilterInput = {
  emailAddress?: string | null | undefined
  ids?: Array<string | number> | null | undefined
  name?: string | null | undefined
  phoneNumber?: string | null | undefined
  /** Searches based on name, phone number and email */
  search?: string | null | undefined
}

export enum CustomersSortField {
  CREATED = 'CREATED',
  NAME = 'NAME',
}

export type CustomersSortInput = {
  direction: SortDirection
  field: CustomersSortField
}

export type DateRange = {
  gte?: DateTimeString | null | undefined
  lte?: DateTimeString | null | undefined
}

export enum EsignAutosignStatus {
  APPROVED = 'APPROVED',
  NOT_REGISTERED = 'NOT_REGISTERED',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
}

export enum EsignRequestStatus {
  COMPLETED = 'COMPLETED',
  DECLINED = 'DECLINED',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING = 'PENDING',
  VOIDED = 'VOIDED',
}

export type FileUploadRequestInput = {
  /** Original file name */
  fileName: string
  /** MIME type of the file (e.g., application/pdf, image/png) */
  mimeType: string
}

export type FinancialAccountCreateInput = {
  financialProviderId?: string | number | null | undefined
  /** The date when the payment was originally due. */
  lastDueDate?: DateTimeString | null | undefined
  name?: string | null | undefined
  outstandingDebt?: MoneyInput | null | undefined
  principalAmount?: MoneyInput | null | undefined
  providerReference?: string | null | undefined
  /** Promise-to-pay date (optional). */
  ptpDate?: DateTimeString | null | undefined
  registeredContactPhoneId?: string | number | null | undefined
  status: FinancialAccountStatus
  typeId: string | number
}

export type FinancialAccountDocumentCreateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  /** Storage reference from uploadUrlRequest mutation */
  storageFileRef: string
  type: FinancialAccountDocumentType
}

export enum FinancialAccountDocumentType {
  DEBT_PAYMENT_PROOF = 'DEBT_PAYMENT_PROOF',
  OTHER = 'OTHER',
  PROOF_OF_DEBT = 'PROOF_OF_DEBT',
}

export type FinancialAccountDocumentUpdateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  type: FinancialAccountDocumentType
}

export enum FinancialAccountScopeCode {
  CASE_LOCKED = 'CASE_LOCKED',
  HAS_DOCUMENTS = 'HAS_DOCUMENTS',
  PTP_DATE_IN_PAST = 'PTP_DATE_IN_PAST',
  REQUIRED = 'REQUIRED',
}

export type FinancialAccountSetProviderContactInput = {
  createFinancialProviderContact?: FinancialProviderContactCreateInput | null | undefined
  updateFinancialProviderContact?: FinancialAccountSetProviderContactUpdateInput | null | undefined
}

export type FinancialAccountSetProviderContactUpdateInput = {
  financialProviderContact?: FinancialProviderContactUpdateInput | null | undefined
  financialProviderContactId: string | number
}

export enum FinancialAccountStatus {
  ACTIVE = 'ACTIVE',
  PROPOSED = 'PROPOSED',
  TERMINATED = 'TERMINATED',
}

export type FinancialAccountTypeCreateInput = {
  /** Code prefix for generating account codes (2 uppercase letters) */
  codePrefix: string
  isActive: boolean
  name: string
  nameUsedInDocuments?: string | null | undefined
  value?: string | null | undefined
}

export type FinancialAccountTypeUpdateInput = {
  /** Code prefix for generating account codes (2 uppercase letters) */
  codePrefix: string
  isActive: boolean
  name: string
  nameUsedInDocuments?: string | null | undefined
  value?: string | null | undefined
}

export enum FinancialAccountTypesSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type FinancialAccountTypesSortInput = {
  direction: SortDirection
  field: FinancialAccountTypesSortField
}

export type FinancialAccountUpdateInput = {
  financialProviderId?: string | number | null | undefined
  /** The date when the payment was originally due. */
  lastDueDate?: DateTimeString | null | undefined
  name: string
  outstandingDebt?: MoneyInput | null | undefined
  principalAmount?: MoneyInput | null | undefined
  providerReference?: string | null | undefined
  /** Promise-to-pay date (optional). */
  ptpDate?: DateTimeString | null | undefined
  registeredContactPhoneId?: string | number | null | undefined
  status: FinancialAccountStatus
  typeId: string | number
}

export type FinancialProviderContactCreateInput = {
  contactEmail?: FinancialProviderContactEmailInput | null | undefined
  contactPhone?: FinancialProviderContactPhoneInput | null | undefined
  name: string
  role: string
}

export type FinancialProviderContactEmailInput = {
  emailAddress: string
  note?: string | null | undefined
}

export type FinancialProviderContactPhoneInput = {
  note?: string | null | undefined
  phoneNumber: string
}

export enum FinancialProviderContactSortField {
  CREATED = 'CREATED',
  NAME = 'NAME',
  ROLE = 'ROLE',
}

export type FinancialProviderContactSortInput = {
  direction: SortDirection
  field: FinancialProviderContactSortField
}

export type FinancialProviderContactUpdateInput = {
  contactEmail?: FinancialProviderContactEmailInput | null | undefined
  contactPhone?: FinancialProviderContactPhoneInput | null | undefined
  name: string
  role: string
}

export type FinancialProviderCreateInput = {
  accountTypes?: Array<string | number> | null | undefined
  businessName: string
  name: string
}

export enum FinancialProviderScopeCode {
  REQUIRED = 'REQUIRED',
}

export type FinancialProviderUpdateInput = {
  accountTypes?: Array<string | number> | null | undefined
  businessName: string
  name: string
}

export type FinancialProvidersFilterInput = {
  contactEmail?: string | null | undefined
  contactName?: string | null | undefined
  contactPhone?: string | null | undefined
  name?: string | null | undefined
  /** Searches based on name, contact name, contact email, contact phone */
  search?: string | null | undefined
}

export enum FinancialProvidersSortField {
  CREATED = 'CREATED',
  NAME = 'NAME',
}

export type FinancialProvidersSortInput = {
  direction: SortDirection
  field: FinancialProvidersSortField
}

export enum Gender {
  FEMALE = 'FEMALE',
  MALE = 'MALE',
}

export type MediationTicketCreateInput = {
  contactId: string | number
  /** Storage reference for issue proof screenshot (required) */
  issueProofRef: string
}

/** Which rung of the assignment ladder chose the case owner: the preprocess RESERVATION, the customer ENGAGEMENT (Flow B — overrules the thread’s picker), the CONVERSATION_PICKER holding the thread (Flow A), a SIMILARITY match (this customer’s last case, or a suspected duplicate’s), the round-robin POOL, or UNASSIGNED when no operator was eligible. */
export enum MessagingCaseAssignmentReason {
  CONVERSATION_PICKER = 'CONVERSATION_PICKER',
  ENGAGEMENT = 'ENGAGEMENT',
  POOL = 'POOL',
  RESERVED = 'RESERVED',
  SIMILARITY = 'SIMILARITY',
  UNASSIGNED = 'UNASSIGNED',
}

export type MessagingChannelAutoReplyInput = {
  /** Also answer on BOT-owned threads. Omitted ⇒ false. Read the warning on MessagingChannelAutoReply.applyWhenBotOwned before enabling it. */
  applyWhenBotOwned?: boolean | null | undefined
  /** Whether the number answers out of hours at all. */
  enabled: boolean
  /** Treat public holidays as closed days. Omitted ⇒ true. */
  observeHolidays?: boolean | null | undefined
  /** The OFFICE HOURS — when the number IS staffed; the notice fires outside them. Validated server-side: the zone must be one IANA knows and every rule must end after it starts. */
  sendWindow: MessagingSendWindowInput
  /** The reply copy (Indonesian). `{nextOpening}` is replaced at send time with the next opening in the window's zone. */
  text: string
}

export type MessagingChannelCaseActionDefaultInput = {
  /** CaseActionType the automatic “customer replied” signal is filed under on this number, unless the case’s campaign overrides it. Must be an ACTIVE type. Null ⇒ the seeded `customer_replied` type; it does not switch the signal off. */
  customerRepliedActionTypeId?: string | number | null | undefined
  /** CaseActionType the automatic “screening completed” signal is filed under on this number, unless the case’s campaign overrides it. Must be an ACTIVE type. Null ⇒ the seeded `screening_completed` type. */
  screeningCompletedActionTypeId?: string | number | null | undefined
}

export type MessagingChannelCreateInput = {
  /** Meta Graph long-lived token — write-only, never returned */
  accessToken: string
  /** Default oversight scenario (MessagingOversightScenario global id) copied onto inbound-first (caseless) conversations — the QA-sampling source absent case behaviour. Rejected on TRANSACTIONAL channels. */
  defaultOversightScenarioId?: string | number | null | undefined
  /** Whether inbound-first conversations start BOT- or HUMAN-owned. Omitted ⇒ messaging-core default (HUMAN). */
  defaultOwner?: MessagingConversationOwnership | null | undefined
  /** Default brain playbook (MessagingPlaybook global id) copied onto inbound-first (caseless) conversations on this channel. Requires defaultOwner BOT to actually run. */
  defaultPlaybookId?: string | number | null | undefined
  displayNumber: string
  /** The Meta APP id whose system user issued this channel's access token. Numeric and public — it appears in webhook configuration and Business Manager URLs — so unlike the token it is shown freely. It lives on the channel rather than in deployment config because it describes the TOKEN: several Meta apps can be subscribed to one WhatsApp account, so two channels can hold tokens from different apps. Needed only to author an image template header; null means that one action is unavailable on this channel. */
  metaAppId?: string | null | undefined
  phoneNumberId: string
  /** Omitted = CONVERSATIONAL. Immutable post-create (a number changing purpose is a different channel) — deliberately absent from the update input. */
  purpose?: MessagingChannelPurpose | null | undefined
  status?: MessagingChannelStatus | null | undefined
  /** Channel-level SUPPORT-posture fallback playbook (MessagingPlaybook global id; case-aware playbooks) — the bot for a recognised-but-caseless conversation, configured here and nowhere else. Rejected on TRANSACTIONAL channels. */
  supportPlaybookId?: string | number | null | undefined
  wabaId?: string | null | undefined
}

export enum MessagingChannelPurpose {
  CONVERSATIONAL = 'CONVERSATIONAL',
  TRANSACTIONAL = 'TRANSACTIONAL',
}

export enum MessagingChannelStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  RESTRICTED = 'RESTRICTED',
}

export type MessagingChannelTemplateCreateInput = {
  /** `UTILITY` or `MARKETING`. `AUTHENTICATION` is refused: Meta owns those bodies outright and the live ones are referenced by name from infrastructure config this product cannot see, so the whole category is managed in Business Manager. Note Meta may APPROVE a template under a different category than the one requested — recategorisation is automatic and changes billing. */
  category: string
  /** Meta's components array, exactly as Meta expects it. */
  components: any
  /** The image for an `IMAGE` header, from `channelTemplateHeaderImageUpload`. Required when the components carry an image header, and refused when they do not — an attached image that no header uses would otherwise vanish with nothing saying why. */
  headerImageAssetId?: string | null | undefined
  /** Meta locale code, e.g. `id`, `en_US`. Also immutable. */
  language: string
  /** How long Meta keeps trying to deliver, in seconds (30–43200). Omit to leave Meta's default — sending the field is what SETS it. Utility range only: the marketing range is settable exclusively through the Marketing Messages API, which this platform does not use. */
  messageSendTtlSeconds?: number | null | undefined
  /** Lowercase letters, digits and underscores only. Immutable for the life of the template — Meta has no rename, and a name freed by deleting an APPROVED template stays locked for 30 days. */
  name: string
}

export type MessagingChannelTemplateHeaderImageUploadInput = {
  /** `image/jpeg` or `image/png` — the only image types Meta accepts for a template header. Advisory: the server sniffs the bytes and stores what it found, which is the value that reaches Meta. */
  contentType: string
  /** Standard base64 of the file bytes. No `data:` URL prefix. */
  data: string
  /** The operator's filename. Cosmetic — Meta neither stores it nor shows it to the customer — but it is a required parameter on the upload. */
  filename?: string | null | undefined
}

export type MessagingChannelTemplateUpdateInput = {
  /** Forwarded only while the template is REJECTED. Meta refuses a category change once a template has been approved, and PAUSED is only reachable after an approval — so in every other state it is dropped rather than sent into a guaranteed rejection. */
  category?: string | null | undefined
  /** Meta's complete components array. Anything left out is REMOVED from the template — Meta has no partial edit. */
  components: any
  /** A REPLACEMENT image for the header, or omit to keep the current one. Omitting it does NOT drop the image: Meta needs a live upload handle on every edit and refuses the one it already holds, so messaging-core re-uploads the stored file and mints a new handle. An edit that only fixes a typo therefore keeps the image without the operator re-attaching it. */
  headerImageAssetId?: string | null | undefined
  /** See the create input. Omit to leave the current value alone. */
  messageSendTtlSeconds?: number | null | undefined
}

export type MessagingChannelTransactionalAutoReplyInput = {
  /** Meta locale code of that template (e.g. `id`, `en_US`). Omitted ⇒ `id`. Must match the approved template EXACTLY: Meta resolves (name, language) as a PAIR, so a mismatch fails the send rather than falling back to another language. */
  templateLanguage?: string | null | undefined
  /** The APPROVED Meta template to send. Checked against this channel's template mirror at save time — a name Meta does not know is a send that can never happen, and finding that out per customer instead of here is the failure this validation exists to prevent. */
  templateName: string
}

export type MessagingChannelUpdateInput = {
  /** Rotate the Meta Graph token — write-only, never returned */
  accessToken?: string | null | undefined
  /** Default oversight scenario (MessagingOversightScenario global id) for inbound-first conversations. Omitted = unchanged; explicit null clears the default. Rejected on TRANSACTIONAL channels. */
  defaultOversightScenarioId?: string | number | null | undefined
  /** Whether inbound-first conversations start BOT- or HUMAN-owned. Omitted = unchanged. */
  defaultOwner?: MessagingConversationOwnership | null | undefined
  /** Default brain playbook (MessagingPlaybook global id) for inbound-first conversations. Omitted = unchanged; explicit null clears the default. */
  defaultPlaybookId?: string | number | null | undefined
  displayNumber?: string | null | undefined
  /** true makes this the default transactional channel (flips the flag off every other channel); false unsets it. Rejected on non-TRANSACTIONAL channels. */
  isDefaultTransactional?: boolean | null | undefined
  /** The Meta APP id whose system user issued this channel's access token. Patch semantics: omitted leaves it unchanged, explicit null clears it (image template headers stop being authorable here). Mutable despite sitting beside the immutable identity fields — rotating `accessToken` to one issued by a different app has to be able to move this with it, or the next image-header save fails at Meta naming neither id. */
  metaAppId?: string | null | undefined
  status?: MessagingChannelStatus | null | undefined
  /** Channel-level SUPPORT-posture fallback playbook (MessagingPlaybook global id; case-aware playbooks). Omitted = unchanged; explicit null clears the fallback. Rejected on TRANSACTIONAL channels. */
  supportPlaybookId?: string | number | null | undefined
}

export enum MessagingChannelVerifyCheckStatus {
  FAILED = 'FAILED',
  OK = 'OK',
  SKIPPED = 'SKIPPED',
}

/** What a conversation timeline entry IS. Closed vocabulary: handoff (ownership moved) or insight (an oversight observation). */
export enum MessagingConversationEventKind {
  HANDOFF = 'HANDOFF',
  INSIGHT = 'INSIGHT',
}

export enum MessagingConversationLifecycle {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum MessagingConversationOwnership {
  BOT = 'BOT',
  HUMAN = 'HUMAN',
}

export enum MessagingConversationScope {
  ASSIGNED = 'ASSIGNED',
  HISTORY = 'HISTORY',
  SUPERVISED = 'SUPERVISED',
  UNASSIGNED = 'UNASSIGNED',
}

export enum MessagingMessageDirection {
  IN = 'IN',
  OUT = 'OUT',
}

export enum MessagingMessageHistoryDirection {
  NEWER = 'NEWER',
  OLDER = 'OLDER',
}

export enum MessagingMessageInteractiveKind {
  BUTTONS = 'BUTTONS',
  LIST = 'LIST',
}

export enum MessagingMessageInteractiveReplyKind {
  BUTTON = 'BUTTON',
  LIST = 'LIST',
}

export type MessagingMessageSendContextInput = {
  /** wamid of the message being quoted (quoted reply) */
  quotedWamid: string
}

export type MessagingMessageSendInput = {
  /** Client-generated UUID — outbound idempotency key */
  clientMsgId: string
  /** Per-conversation monotonic sequence stamped at send */
  clientSeq: number
  context?: MessagingMessageSendContextInput | null | undefined
  /** messaging-core conversation id (drives send-authz scoping) */
  conversationId: string | number
  media?: MessagingMessageSendMediaInput | null | undefined
  /** The operator was shown the safety-word confirmation dialog for THIS wording and chose to send it anyway. Set it only after a MESSAGE_FLAGGED_UNSAFE refusal on the identical text — it skips the pre-send warning and nothing else. It is PROVENANCE, not a bypass: messaging-core re-scans the outbound and applies the sticky safety-flagged tag whether this is set or not. */
  safetyAcknowledged?: boolean | null | undefined
  /** Required iff type=TEMPLATE */
  template?: MessagingMessageSendTemplateInput | null | undefined
  text?: string | null | undefined
  type: MessagingMessageSendType
}

export type MessagingMessageSendMediaInput = {
  caption?: string | null | undefined
  filename?: string | null | undefined
  mimeType: string
  /** storageFileRef from fileUploadRequest. crm-core resolves it to a signed read URL server-side and forwards that as the cmd media url — the operator never supplies a raw URL (no SSRF surface). */
  storageFileRef: string
}

export type MessagingMessageSendTemplateInput = {
  /** The picker's variable fill (Meta send-API components shape). Opaque to crm-core — shape-validated by the cmd schema only, like the admin mirror's components; Meta is the authority on the fill. */
  components?: any
  /** Meta locale code, e.g. `id`, `en_US`. */
  language: string
  name: string
}

export enum MessagingMessageSendType {
  AUDIO = 'AUDIO',
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
  TEMPLATE = 'TEMPLATE',
  TEXT = 'TEXT',
  VIDEO = 'VIDEO',
}

export enum MessagingMessageSource {
  BRAIN = 'BRAIN',
  EXTERNAL = 'EXTERNAL',
  OPERATOR = 'OPERATOR',
  SYSTEM = 'SYSTEM',
}

export enum MessagingMessageState {
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
  FAILED_PERMANENT = 'FAILED_PERMANENT',
  FAILED_RETRYABLE = 'FAILED_RETRYABLE',
  QUEUED = 'QUEUED',
  READ = 'READ',
  RECEIVED = 'RECEIVED',
  SENDING = 'SENDING',
  SENT = 'SENT',
}

export enum MessagingMessageType {
  AUDIO = 'AUDIO',
  DELETED = 'DELETED',
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
  INTERACTIVE = 'INTERACTIVE',
  REACTION = 'REACTION',
  STICKER = 'STICKER',
  TEMPLATE = 'TEMPLATE',
  TEXT = 'TEXT',
  UNKNOWN = 'UNKNOWN',
  VIDEO = 'VIDEO',
}

export enum MessagingModelMaturity {
  EXPERIMENTAL = 'EXPERIMENTAL',
  GA = 'GA',
  PREVIEW = 'PREVIEW',
}

/** Per-number WhatsApp capability learned from message outcomes: WA_CAPABLE after a received message or a successful send, NOT_WA / INVALID after a permanent recipient failure, UNKNOWN before any definitive signal. */
export enum MessagingNumberCapability {
  INVALID = 'INVALID',
  NOT_WA = 'NOT_WA',
  UNKNOWN = 'UNKNOWN',
  WA_CAPABLE = 'WA_CAPABLE',
}

export type MessagingOversightScenarioCreateInput = {
  /** Analyzer kinds to run; omitted ⇒ none (fail-closed). */
  analyzers?: Array<OversightAnalyzerKind> | null | undefined
  /** Master on/off; defaults to enabled when omitted. */
  enabled?: boolean | null | undefined
  /** conversation-health every-N-inbound cadence (default 5). */
  healthEveryN?: number | null | undefined
  /** conversation-health burst debounce ms (default 120000). */
  healthMinIntervalMs?: number | null | undefined
  /** Per-conversation per-UTC-day model-call budget (default 20). */
  maxCallsPerConvDay?: number | null | undefined
  /** Exact Vertex model id the analyzers call. */
  model: string
  /** Human-readable display label. */
  name: string
  /** Base percent of conversations analysed (0–100). */
  sampleRate: number
}

export type MessagingOversightScenarioUpdateInput = {
  /** Analyzer kinds to run; empty ⇒ none (fail-closed). */
  analyzers?: Array<OversightAnalyzerKind> | null | undefined
  /** Master on/off. */
  enabled?: boolean | null | undefined
  /** conversation-health every-N-inbound cadence. */
  healthEveryN?: number | null | undefined
  /** conversation-health burst debounce ms. */
  healthMinIntervalMs?: number | null | undefined
  /** Per-conversation per-UTC-day model-call budget. */
  maxCallsPerConvDay?: number | null | undefined
  /** Exact Vertex model id the analyzers call. */
  model?: string | null | undefined
  /** Rename the scenario. */
  name?: string | null | undefined
  /** Base percent of conversations analysed (0–100). */
  sampleRate?: number | null | undefined
}

export type MessagingPlaybookCreateInput = {
  aggregationDelayMs: number
  /** Link a CRM App: crm-core mints an AppToken on it and forwards the secret to the brain — leads this playbook captures write to the CRM as that app. The token never passes through the operator. */
  appId?: string | number | null | undefined
  description?: string | null | undefined
  /** Read-only CRM fetch tools this version exposes to the responder (names from the messagingTools catalog). Empty = tools off. */
  enabledTools?: Array<string>
  /** Quiet window (ms) before a follow-up nudge is due. */
  followUpDelayMs?: number
  /** Opt-in: proactively re-engage a quiet, unsettled conversation. */
  followUpEnabled?: boolean
  /** Hard cap on follow-up nudges per conversation. */
  followUpMaxCount?: number
  /** Post-nudge quiet (ms): how long a no_result lead this playbook captured waits after the nudge went out before it syncs to the CRM (the customer's time to answer it). 0 = no wait; max 24h. */
  followUpPostNudgeQuietMs?: number
  /** Nudge generation instruction; non-empty makes the brain generate a contextual nudge from the prompt + history. */
  followUpPrompt?: string
  /** Send window for the follow-up nudge — REQUIRED when followUpEnabled (the brain rejects/disarms an enabled follow-up without one, fail closed). Gates only the nudge; a nudge due outside it is deferred to the next opening. */
  followUpSendWindow?: MessagingSendWindowInput | null | undefined
  /** Deterministic fallback nudge sent verbatim when generation is off or fails. */
  followUpTemplate?: string
  handoffTurnLimit: number
  historyLimit: number
  leadCaptureEnabled: boolean
  /** Opt-in: feed inbound media bytes to the model (multimodal vision). */
  mediaVisionEnabled?: boolean
  /** Responder model for this version (a bare model id from the model picker) — required; every version pins its own model. */
  modelId: string
  /** Free-text display label. Identity is a server-generated uuid; the operator never chooses it. */
  name: string
  outcomes?: Array<string>
  summaryEnabled?: boolean
  systemPrompt: string
  /** Responder sampling temperature (0–2) for this version. Leave empty to use the provider default — the setting the shipped prompts were tuned at. */
  temperature?: number | null | undefined
  turnLimit: number
  /** The playbook's role, and which config slots will accept it: 'screening' (campaign first contact, or a channel default that treats unknown inbound as a lead), 'concierge' (a case already in progress), 'support' (a recognised client with no active case). PERMANENT — a playbook is referenced by id from campaigns and channels, so retyping one would invalidate those references silently. The remedy for a mistake is a new playbook, which starts its version history over. */
  type: MessagingPlaybookType
}

/** The playbook's role. Fixed at creation in the brain and never changed, because campaigns and channels reference playbooks by id and a retype would invalidate those references silently. */
export enum MessagingPlaybookType {
  CONCIERGE = 'CONCIERGE',
  INTAKE = 'INTAKE',
  SCREENING = 'SCREENING',
  SUPPORT = 'SUPPORT',
}

export type MessagingPlaybookUpdateInput = {
  /** Re-link to a CRM App (a fresh AppToken is minted and forwarded; the replaced one is revoked) or null to unlink, which also clears the brain's stored token. */
  appId?: string | number | null | undefined
  description?: string | null | undefined
  /** Rename the playbook (its free-text display label). */
  name?: string | null | undefined
}

export type MessagingPlaybookVersionInput = {
  aggregationDelayMs: number
  /** Read-only CRM fetch tools this version exposes to the responder (names from the messagingTools catalog). Empty = tools off. */
  enabledTools?: Array<string>
  /** Quiet window (ms) before a follow-up nudge is due. */
  followUpDelayMs?: number
  /** Opt-in: proactively re-engage a quiet, unsettled conversation. */
  followUpEnabled?: boolean
  /** Hard cap on follow-up nudges per conversation. */
  followUpMaxCount?: number
  /** Post-nudge quiet (ms): how long a no_result lead this playbook captured waits after the nudge went out before it syncs to the CRM (the customer's time to answer it). 0 = no wait; max 24h. */
  followUpPostNudgeQuietMs?: number
  /** Nudge generation instruction; non-empty makes the brain generate a contextual nudge from the prompt + history. */
  followUpPrompt?: string
  /** Send window for the follow-up nudge — REQUIRED when followUpEnabled (the brain rejects/disarms an enabled follow-up without one, fail closed). Gates only the nudge; a nudge due outside it is deferred to the next opening. */
  followUpSendWindow?: MessagingSendWindowInput | null | undefined
  /** Deterministic fallback nudge sent verbatim when generation is off or fails. */
  followUpTemplate?: string
  handoffTurnLimit: number
  historyLimit: number
  leadCaptureEnabled: boolean
  /** Opt-in: feed inbound media bytes to the model (multimodal vision). */
  mediaVisionEnabled?: boolean
  /** Responder model for this version (a bare model id from the model picker) — required; every version pins its own model. */
  modelId: string
  outcomes?: Array<string>
  summaryEnabled?: boolean
  systemPrompt: string
  /** Responder sampling temperature (0–2) for this version. Leave empty to use the provider default — the setting the shipped prompts were tuned at. */
  temperature?: number | null | undefined
  turnLimit: number
}

export type MessagingQuickReplyCreateInput = {
  /** The text inserted into the composer. */
  body: string
  /** Order in the picker. Omitted ⇒ 0, and ties break on `shortcut`. */
  sequence?: number | null | undefined
  /** The token typed after `/`. A leading slash is accepted and stripped; case is not significant. */
  shortcut: string
}

export type MessagingQuickReplyUpdateInput = {
  /** New body text. */
  body?: string | null | undefined
  /** New order in the picker. */
  sequence?: number | null | undefined
  /** New shortcut; normalised the same way as on create. */
  shortcut?: string | null | undefined
}

/** How a safety term is matched: as a whole word, or anywhere inside a word. */
export enum MessagingSafetyMatchMode {
  ANYWHERE = 'ANYWHERE',
  WHOLE_WORD = 'WHOLE_WORD',
}

export type MessagingSafetyTermCreateInput = {
  /** Omit for enabled: adding the term was the opinion. */
  enabled?: boolean | null | undefined
  /** Omit for `WHOLE_WORD`, the conservative default — it cannot over-match. Preview an `ANYWHERE` candidate before saving it. */
  matchMode?: MessagingSafetyMatchMode | null | undefined
  /** Optional note, up to 500 characters — why this term is on the list. */
  note?: string | null | undefined
  /** The word or phrase, 1–120 characters. Stored as typed; matching is case-insensitive, so adding `Anjing` beside `anjing` is refused as a duplicate. */
  term: string
}

export type MessagingSafetyTermPreviewInput = {
  /** Required here, unlike on create: the mode is most of what the preview is testing. */
  matchMode: MessagingSafetyMatchMode
  /** How many recent outbound messages to sample, 1–5000. Omit for messaging-core's default (1000). Larger samples cost a wider read, and the answer reports the size actually reached. */
  sampleSize?: number | null | undefined
  /** The candidate word or phrase, 1–120 characters. */
  term: string
}

export type MessagingSafetyTermUpdateInput = {
  /** The reversible off switch. Prefer it to deleting when a term over-matches: the row, its note and its history survive. */
  enabled: boolean
  matchMode: MessagingSafetyMatchMode
  /** Optional note, up to 500 characters. OMITTING IT CLEARS THE EXISTING NOTE. */
  note?: string | null | undefined
  /** The word or phrase, 1–120 characters. ⚠ Renaming resets the visible `hitCount` — the history stays attached to the old spelling (see the field on `MessagingSafetyTerm`). */
  term: string
}

/** ISO weekday key a send-window rule applies to. */
export enum MessagingSendWindowDay {
  FRI = 'FRI',
  MON = 'MON',
  SAT = 'SAT',
  SUN = 'SUN',
  THU = 'THU',
  TUE = 'TUE',
  WED = 'WED',
}

/** When the follow-up nudge may be sent: local wall-clock rules read in one IANA zone. A nudge due outside the window is deferred to the next opening, never dropped. */
export type MessagingSendWindowInput = {
  /** Local-time spans (1–14). Every day 00:00–24:00 is the explicit "any time" choice. */
  rules: Array<MessagingSendWindowRuleInput>
  /** IANA zone the rules are read in (e.g. `Asia/Jakarta`). */
  tz: string
}

/** One local-time span of a send window: `start` (HH:mm, inclusive) to `end` (HH:mm or 24:00, exclusive) on each listed day. */
export type MessagingSendWindowRuleInput = {
  /** Weekdays this span applies to (at least one). */
  days: Array<MessagingSendWindowDay>
  /** Local `HH:mm`, exclusive; `24:00` = end of day. Must be after `start`. */
  end: string
  /** Local `HH:mm`, inclusive. */
  start: string
}

export enum MetaEvent {
  CASE_ACCEPTED = 'CASE_ACCEPTED',
  CASE_CONTACTED = 'CASE_CONTACTED',
  CASE_ELIGIBLE = 'CASE_ELIGIBLE',
  CASE_UNCONTACTABLE = 'CASE_UNCONTACTABLE',
  CASE_WON = 'CASE_WON',
}

export type MoneyInput = {
  amount: number
  currency: string
}

export type NegotiationTicketCreateInput = {
  /** Contact ID (optional for negotiation tickets) */
  contactId?: string | number | null | undefined
  /** Current outstanding debt (amount and currency) from the app lender (required) */
  outstandingDebt: MoneyInput
  /** Storage reference for screenshot of app lender showing current outstanding debt amount (optional) */
  outstandingDebtProofRef?: string | null | undefined
  /** Storage reference for screenshot proof that client has availability of money for debt payment (required) */
  paymentAvailabilityProofRef: string
}

export type NegotiationTicketOutstandingDebtUpdateInput = {
  /** The outstanding debt amount. */
  outstandingDebt: MoneyInput
}

export type NegotiationTicketSettlementUpdateInput = {
  /** The adjusted debt amount after negotiation. */
  adjustedDebt: MoneyInput
}

export type OrderAddonCreateInput = {
  businessEntityId?: string | number | null | undefined
  durationInMonths?: number | null | undefined
  feeRate?: number | null | undefined
  financialAccountIds: Array<string | number>
  productPackageId: string | number
  startAt: DateTimeString
}

export type OrderAmountUpdateInput = {
  /** The new amount for the order. */
  amount: MoneyInput
}

export type OrderDocumentCreateInput = {
  documentTypeId: string | number
  note?: string | null | undefined
  /** Mark the uploaded document as already signed. */
  signed?: boolean
  /** Storage reference from uploadUrlRequest mutation */
  storageFileRef: string
}

export type OrderDocumentGenerateInput = {
  /** ID of the OrderDocumentType to generate. */
  documentTypeId: string | number
}

export type OrderDocumentTypeCreateInput = {
  /** When true, an e-meterai (Indonesian e-stamp) is applied to documents of this type during the e-signature flow, consuming the doc-maker e-meterai balance. Independent of requiresSignature placement. */
  appliesMeterai: boolean
  /** Optional filename template. Must contain {{hash}} and end with .pdf when provided. */
  documentFilenameTemplate?: string | null | undefined
  /** When true, the type is template-driven and a document will be generated; when false the document must be uploaded manually. */
  isGeneratable: boolean
  /** Human-readable name, unique within the product package. */
  name: string
  /** When true, documents of this type need an e-signature before they unblock state progression. User-controlled; the FE typically pre-fills it from the verify mutation result (signatures.length > 0) and lets the user override. */
  requiresSignature: boolean
}

export type OrderDocumentTypeTemplateTestRenderInput = {
  /** Storage file reference from a pending upload. If null, use the stored template. */
  storageFileRef?: string | null | undefined
}

export type OrderDocumentTypeTemplateUploadInput = {
  /** Storage file reference from the template upload request. */
  storageFileRef: string
}

export type OrderDocumentTypeTemplateVerifyInput = {
  /** Storage file reference of the DOCX template to verify. Must be a pending upload owned by the current account. */
  storageFileRef: string
}

export type OrderDocumentTypeUpdateInput = {
  /** When true, an e-meterai (Indonesian e-stamp) is applied to documents of this type during the e-signature flow, consuming the doc-maker e-meterai balance. */
  appliesMeterai?: boolean | null | undefined
  /** Filename template. Must contain {{hash}} and end with .pdf when set. */
  documentFilenameTemplate?: string | null | undefined
  /** When true, the type is template-driven and a document will be generated; when false the document must be uploaded manually. */
  isGeneratable?: boolean | null | undefined
  name?: string | null | undefined
  /** When true, documents of this type need an e-signature before they unblock state progression. User-controlled. */
  requiresSignature?: boolean | null | undefined
}

export type OrderDocumentUpdateInput = {
  documentTypeId: string | number
  note?: string | null | undefined
}

export enum OrderEventType {
  AMOUNT_ADJUSTED = 'AMOUNT_ADJUSTED',
  CANCELLED = 'CANCELLED',
  CONFIRMED = 'CONFIRMED',
  CREATED = 'CREATED',
  DOCUMENT_CREATED = 'DOCUMENT_CREATED',
  DOCUMENT_DELETED = 'DOCUMENT_DELETED',
  DOCUMENT_EDITED = 'DOCUMENT_EDITED',
  DOCUMENT_SIGNED = 'DOCUMENT_SIGNED',
  FULFILLED = 'FULFILLED',
  LOCKED = 'LOCKED',
  PAYMENT_CREATED = 'PAYMENT_CREATED',
  PAYMENT_DELETED = 'PAYMENT_DELETED',
  PAYMENT_UPDATED = 'PAYMENT_UPDATED',
  STATE_PROGRESSED = 'STATE_PROGRESSED',
  UPDATED = 'UPDATED',
}

export type OrderInitialCreateInput = {
  businessEntityId?: string | number | null | undefined
  durationInMonths?: number | null | undefined
  feeRate?: number | null | undefined
  productPackageId: string | number
}

export type OrderPaymentCreateInput = {
  adjustOrderAmount: boolean
  paidAmount: MoneyInput
  paidAt: DateTimeString
}

export enum OrderPaymentStatus {
  FULLY_PAID = 'FULLY_PAID',
  OVERPAID = 'OVERPAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  UNPAID = 'UNPAID',
}

export type OrderPaymentUpdateInput = {
  adjustOrderAmount: boolean
  paidAmount: MoneyInput
  paidAt: DateTimeString
}

export type OrderStateProgressInput = {
  state: OrderStatus
}

export enum OrderStatus {
  CANCELLED = 'CANCELLED',
  CONFIRMED = 'CONFIRMED',
  DRAFT = 'DRAFT',
  FULFILLED = 'FULFILLED',
  LOCKED = 'LOCKED',
}

export type OrderUpdateInput = {
  businessEntityId?: string | number | null | undefined
  durationInMonths?: number | null | undefined
  feeRate?: number | null | undefined
  productPackageId: string | number
  startAt?: DateTimeString | null | undefined
}

export enum OrderValidationEntity {
  CASE_ADDRESS = 'CASE_ADDRESS',
  CASE_EMAIL = 'CASE_EMAIL',
  CASE_PHONE = 'CASE_PHONE',
  CUSTOMER = 'CUSTOMER',
  CUSTOMER_BIRTH_INFO = 'CUSTOMER_BIRTH_INFO',
  CUSTOMER_GOVERNMENT_ID = 'CUSTOMER_GOVERNMENT_ID',
  CUSTOMER_OCCUPATION = 'CUSTOMER_OCCUPATION',
  FINANCIAL_ACCOUNTS = 'FINANCIAL_ACCOUNTS',
  FINANCIAL_ACCOUNTS_PROVIDER_REFERENCE = 'FINANCIAL_ACCOUNTS_PROVIDER_REFERENCE',
  FINANCIAL_ACCOUNTS_PTP_DATE = 'FINANCIAL_ACCOUNTS_PTP_DATE',
  FINANCIAL_ACCOUNTS_REGISTERED_PHONE = 'FINANCIAL_ACCOUNTS_REGISTERED_PHONE',
}

/** An oversight analyzer kind the brain ships; a scenario runs the set it lists (empty ⇒ none, fail-closed). */
export enum OversightAnalyzerKind {
  CONVERSATION_HEALTH = 'CONVERSATION_HEALTH',
  DOCUMENT_CLASSIFIER = 'DOCUMENT_CLASSIFIER',
  NOOP = 'NOOP',
}

export enum Period {
  DAY = 'DAY',
  HOUR = 'HOUR',
}

export enum Permission {
  MANAGE_APPS = 'MANAGE_APPS',
  MANAGE_CASES = 'MANAGE_CASES',
  MANAGE_CHANNELS = 'MANAGE_CHANNELS',
  MANAGE_CONVERSATIONS = 'MANAGE_CONVERSATIONS',
  MANAGE_CUSTOMERS = 'MANAGE_CUSTOMERS',
  MANAGE_FINANCIAL_PROVIDERS = 'MANAGE_FINANCIAL_PROVIDERS',
  MANAGE_PLAYBOOKS = 'MANAGE_PLAYBOOKS',
  MANAGE_SAFETY_POLICY = 'MANAGE_SAFETY_POLICY',
  MANAGE_SETTINGS = 'MANAGE_SETTINGS',
  MANAGE_TEMPLATES = 'MANAGE_TEMPLATES',
  MANAGE_TICKETS = 'MANAGE_TICKETS',
  MANAGE_USERS = 'MANAGE_USERS',
  READ_ACQUISITION = 'READ_ACQUISITION',
  READ_CASES = 'READ_CASES',
  READ_CUSTOMERS = 'READ_CUSTOMERS',
  READ_FINANCE = 'READ_FINANCE',
  READ_MESSAGING = 'READ_MESSAGING',
  USE_MESSAGING = 'USE_MESSAGING',
}

export type ProductCreateInput = {
  caseTypeId: string | number
  name: string
  productType: ProductType
}

export enum ProductPackageAvailability {
  ADDON = 'ADDON',
  INITIAL = 'INITIAL',
}

export type ProductPackageCreateInput = {
  availability: Array<ProductPackageAvailability>
  caseTypeId: string | number
  defaultDurationMonths: number
  defaultFeeRate: number
  minimumFee: MoneyInput
  minimumFeeRate: number
  name: string
  /** Additional fixed-amount order lines charged on top of the service fee. */
  orderLines?: Array<ProductPackageOrderLineInput> | null | undefined
  productIds?: Array<string | number> | null | undefined
  publicName: string
  /** Label of the service fee line as it appears on order documents. */
  serviceLineLabel: string
  stateDependencies?: Array<ProductPackageStateDependenciesInput> | null | undefined
  stateEffects?: Array<ProductPackageStateEffectsInput> | null | undefined
}

export type ProductPackageOrderLineInput = {
  amount: MoneyInput
  label: string
}

export type ProductPackageStateDependenciesInput = {
  caseDocumentTypes: Array<CaseDocumentType>
  customerDocumentTypes: Array<CustomerDocumentType>
  financialAccountDocumentTypes: Array<FinancialAccountDocumentType>
  orderDocumentTypeIds: Array<string | number>
  orderPaymentStatus: Array<OrderPaymentStatus>
  state: OrderStatus
  supportAutomaticStateChange: boolean
  validEntities: Array<OrderValidationEntity>
}

export type ProductPackageStateEffectsInput = {
  generateDocumentTypeIds: Array<string | number>
  state: OrderStatus
}

export type ProductPackageUpdateInput = {
  availability: Array<ProductPackageAvailability>
  defaultDurationMonths: number
  defaultFeeRate: number
  isActive: boolean
  minimumFee: MoneyInput
  minimumFeeRate: number
  name: string
  /** Additional fixed-amount order lines charged on top of the service fee. */
  orderLines?: Array<ProductPackageOrderLineInput> | null | undefined
  productIds?: Array<string | number> | null | undefined
  publicName: string
  /** Label of the service fee line as it appears on order documents. */
  serviceLineLabel: string
  stateDependencies?: Array<ProductPackageStateDependenciesInput> | null | undefined
  stateEffects?: Array<ProductPackageStateEffectsInput> | null | undefined
}

export enum ProductPackagesSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type ProductPackagesSortInput = {
  direction: SortDirection
  field: ProductPackagesSortField
}

export enum ProductType {
  MEDIATION = 'MEDIATION',
  NEGOTIATION = 'NEGOTIATION',
}

export type ProductUpdateInput = {
  isActive: boolean
  name: string
}

export enum ProductsSortField {
  ACTIVE = 'ACTIVE',
  CASE_TYPE = 'CASE_TYPE',
  NAME = 'NAME',
  PRODUCT_TYPE = 'PRODUCT_TYPE',
}

export type ProductsSortInput = {
  direction: SortDirection
  field: ProductsSortField
}

/** Indonesian second-level area type: kabupaten (regency) or kota (city). */
export enum RegencyKind {
  KABUPATEN = 'KABUPATEN',
  KOTA = 'KOTA',
}

export enum ReportCalendarPeriod {
  DAY = 'DAY',
  MONTH = 'MONTH',
  WEEK = 'WEEK',
}

export enum ReportMessagingDimension {
  KIND = 'KIND',
  SOURCE = 'SOURCE',
  TEMPLATE = 'TEMPLATE',
}

export enum ReportMessagingOutboundKind {
  SESSION = 'SESSION',
  TEMPLATE = 'TEMPLATE',
}

export enum ReportMessagingResponder {
  BRAIN = 'BRAIN',
  EXTERNAL = 'EXTERNAL',
  OPERATOR = 'OPERATOR',
  SYSTEM = 'SYSTEM',
  UNANSWERED = 'UNANSWERED',
}

export enum Scope {
  ACCOUNT = 'ACCOUNT',
  AUTH = 'AUTH',
  CASE = 'CASE',
  COMMON = 'COMMON',
  CONTACT = 'CONTACT',
  CUSTOMER = 'CUSTOMER',
  ESIGN = 'ESIGN',
  FINANCIAL_ACCOUNT = 'FINANCIAL_ACCOUNT',
  FINANCIAL_PROVIDER = 'FINANCIAL_PROVIDER',
  MESSAGING = 'MESSAGING',
  OCR = 'OCR',
  ORDER = 'ORDER',
  PRODUCT = 'PRODUCT',
  TICKET = 'TICKET',
  UPLOAD = 'UPLOAD',
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type TicketActionCreateInput = {
  comment?: string | null | undefined
  date: DateTimeString
  resultId: string | number
  typeId: string | number
}

export type TicketActionResultCreateInput = {
  isActive: boolean
  name: string
  success?: boolean | null | undefined
  value?: string | null | undefined
}

export type TicketActionResultUpdateInput = {
  isActive: boolean
  name: string
  success?: boolean | null | undefined
  value?: string | null | undefined
}

export enum TicketActionResultsSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type TicketActionResultsSortInput = {
  direction: SortDirection
  field: TicketActionResultsSortField
}

export type TicketActionTypeCreateInput = {
  /** Customer-facing label shown on the portal ticket timeline; `null` hides this action type from customers. */
  customerFacingName?: string | null | undefined
  isActive: boolean
  name: string
  value?: string | null | undefined
}

export type TicketActionTypeUpdateInput = {
  /** Customer-facing label shown on the portal ticket timeline; `null` hides this action type from customers. */
  customerFacingName?: string | null | undefined
  isActive: boolean
  name: string
  value?: string | null | undefined
}

export enum TicketActionTypesSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type TicketActionTypesSortInput = {
  direction: SortDirection
  field: TicketActionTypesSortField
}

export type TicketActionUpdateInput = {
  comment?: string | null | undefined
  date: DateTimeString
  resultId: string | number
  typeId: string | number
}

export type TicketBulkAssignInput = {
  ticketIds: Array<string | number>
  userId: string | number
}

export type TicketBulkCloseInput = {
  ticketCloseReasonId: string | number
  ticketIds: Array<string | number>
}

export type TicketCloseInput = {
  ticketCloseReasonId: string | number
}

export type TicketCloseReasonCreateInput = {
  isActive: boolean
  name: string
  success?: boolean | null | undefined
  ticketTypes?: Array<TicketType> | null | undefined
  value?: string | null | undefined
}

export type TicketCloseReasonUpdateInput = {
  isActive: boolean
  name: string
  success?: boolean | null | undefined
  ticketTypes?: Array<TicketType> | null | undefined
  value?: string | null | undefined
}

export enum TicketCloseReasonsSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type TicketCloseReasonsSortInput = {
  direction: SortDirection
  field: TicketCloseReasonsSortField
}

/** Filter tickets by their contact action status. */
export enum TicketContactActionFilter {
  /** Tickets with at least one action recorded. */
  ANY = 'ANY',
  /** Tickets with no actions recorded. */
  NONE = 'NONE',
  /** Tickets with at least one successful action (result.success = true). */
  SUCCESSFUL = 'SUCCESSFUL',
}

export type TicketDocumentCreateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  /** Storage reference from uploadUrlRequest mutation */
  storageFileRef: string
  type: TicketDocumentType
}

export enum TicketDocumentType {
  ISSUE_PROOF = 'ISSUE_PROOF',
  OTHER = 'OTHER',
  OUTSTANDING_DEBT_PROOF = 'OUTSTANDING_DEBT_PROOF',
  PAYMENT_AVAILABILITY_PROOF = 'PAYMENT_AVAILABILITY_PROOF',
}

export type TicketDocumentUpdateInput = {
  note?: string | null | undefined
  otherType?: string | null | undefined
  type: TicketDocumentType
}

export enum TicketEventType {
  ACTION_ADDED = 'ACTION_ADDED',
  ACTION_DELETED = 'ACTION_DELETED',
  ACTION_EDITED = 'ACTION_EDITED',
  ASSIGNED = 'ASSIGNED',
  CLOSED = 'CLOSED',
  CREATED = 'CREATED',
  DOCUMENT_DELETED = 'DOCUMENT_DELETED',
  DOCUMENT_EDITED = 'DOCUMENT_EDITED',
  DOCUMENT_UPLOADED = 'DOCUMENT_UPLOADED',
  NOTE_ADDED = 'NOTE_ADDED',
  TAG_ATTACHED = 'TAG_ATTACHED',
  TAG_DETACHED = 'TAG_DETACHED',
}

export type TicketNoteAddInput = {
  message: string
}

export enum TicketScopeCode {
  CASE_PRODUCT_NOT_ACTIVE = 'CASE_PRODUCT_NOT_ACTIVE',
  FINANCIAL_ACCOUNT_NOT_IN_PRODUCT = 'FINANCIAL_ACCOUNT_NOT_IN_PRODUCT',
  NOT_FOUND = 'NOT_FOUND',
  REQUIRED = 'REQUIRED',
  TAG_NOT_ACTIVE = 'TAG_NOT_ACTIVE',
  TAG_NOT_ENABLED_FOR_TYPE = 'TAG_NOT_ENABLED_FOR_TYPE',
  TICKET_CLOSED = 'TICKET_CLOSED',
  UNIQUE = 'UNIQUE',
}

export enum TicketStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  PREPROCESSING = 'PREPROCESSING',
}

export type TicketTagAttachInput = {
  ticketTagId: string | number
}

export type TicketTagCreateInput = {
  color?: string | null | undefined
  isActive: boolean
  name: string
  ticketTypes?: Array<TicketType> | null | undefined
  value?: string | null | undefined
}

export type TicketTagDetachInput = {
  ticketTagId: string | number
}

export type TicketTagUpdateInput = {
  color?: string | null | undefined
  isActive: boolean
  name: string
  ticketTypes?: Array<TicketType> | null | undefined
  value?: string | null | undefined
}

export enum TicketTagsSortField {
  ACTIVE = 'ACTIVE',
  NAME = 'NAME',
}

export type TicketTagsSortInput = {
  direction: SortDirection
  field: TicketTagsSortField
}

export enum TicketType {
  MEDIATION = 'MEDIATION',
  NEGOTIATION = 'NEGOTIATION',
}

export type TicketsAllFilterInput = {
  assignedToUser?: Array<string | number> | null | undefined
  assignedToUserTeam?: Array<string | number> | null | undefined
  closeReasons?: Array<string | number> | null | undefined
  closed?: DateRange | null | undefined
  contactAction?: TicketContactActionFilter | null | undefined
  created?: DateRange | null | undefined
  phoneNumber?: string | null | undefined
  productId?: Array<string | number> | null | undefined
  search?: string | null | undefined
  status?: Array<TicketStatus> | null | undefined
  tag?: Array<string | number> | null | undefined
}

export enum TicketsAllSortField {
  ASSIGNEE = 'ASSIGNEE',
  DATE = 'DATE',
}

export type TicketsAllSortInput = {
  direction: SortDirection
  field: TicketsAllSortField
}

export type TicketsTeamAssignedFilterInput = {
  assignedToUser?: Array<string | number> | null | undefined
  closeReasons?: Array<string | number> | null | undefined
  closed?: DateRange | null | undefined
  contactAction?: TicketContactActionFilter | null | undefined
  created?: DateRange | null | undefined
  phoneNumber?: string | null | undefined
  productId?: Array<string | number> | null | undefined
  search?: string | null | undefined
  status?: Array<TicketStatus> | null | undefined
  tag?: Array<string | number> | null | undefined
}

export enum TicketsTeamAssignedSortField {
  ASSIGNEE = 'ASSIGNEE',
  DATE = 'DATE',
}

export type TicketsTeamAssignedSortInput = {
  direction: SortDirection
  field: TicketsTeamAssignedSortField
}

export type TicketsUserAssignedFilterInput = {
  contactAction?: TicketContactActionFilter | null | undefined
  created?: DateRange | null | undefined
  phoneNumber?: string | null | undefined
  productId?: Array<string | number> | null | undefined
  search?: string | null | undefined
  status?: Array<TicketStatus> | null | undefined
  tag?: Array<string | number> | null | undefined
}

export enum TicketsUserAssignedSortField {
  DATE = 'DATE',
}

export type TicketsUserAssignedSortInput = {
  direction: SortDirection
  field: TicketsUserAssignedSortField
}

export type UserCreateInput = {
  email: string
  firstName?: string | null | undefined
  isActive?: boolean | null | undefined
  lastName?: string | null | undefined
  permissions?: Array<Permission> | null | undefined
  roleDescription?: string | null | undefined
  roles: Array<UserRole>
  /** Operator-QA oversight sample-rate multiplier: scales the base oversight sample rate for cases assigned to this user (>= 0). Null leaves it unchanged. 1.0 = no change. */
  sampleRateMultiplier?: number | null | undefined
  team?: string | number | null | undefined
}

export enum UserNotificationType {
  ERROR = 'ERROR',
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
}

export enum UserRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MEDITATOR = 'MEDITATOR',
  NEGOTIATOR = 'NEGOTIATOR',
  OPERATOR = 'OPERATOR',
  OPERATOR_INACTIVE = 'OPERATOR_INACTIVE',
  OTHER = 'OTHER',
  TEAMLEADER = 'TEAMLEADER',
}

export type UserTeamCreateInput = {
  businessEntities: Array<string | number>
  caseTypes: Array<string | number>
  name: string
  types: Array<UserTeamType>
}

export enum UserTeamType {
  MEDIATIONS = 'MEDIATIONS',
  NEGOTIATIONS = 'NEGOTIATIONS',
  OPERATIONS = 'OPERATIONS',
  SUPPORT = 'SUPPORT',
}

export type UserTeamUpdateInput = {
  businessEntities: Array<string | number>
  caseTypes: Array<string | number>
  name: string
  types: Array<UserTeamType>
}

export type UserTeamsFilterInput = {
  ids?: Array<string | number> | null | undefined
  search?: string | null | undefined
  type?: Array<UserTeamType> | null | undefined
}

export enum UserTeamsSortField {
  NAME = 'NAME',
}

export type UserTeamsSortInput = {
  direction: SortDirection
  field: UserTeamsSortField
}

export type UserUpdateInput = {
  firstName?: string | null | undefined
  isActive?: boolean | null | undefined
  lastName?: string | null | undefined
  permissions?: Array<Permission> | null | undefined
  roleDescription?: string | null | undefined
  roles: Array<UserRole>
  /** Operator-QA oversight sample-rate multiplier: scales the base oversight sample rate for cases assigned to this user (>= 0). Null leaves it unchanged. 1.0 = no change. */
  sampleRateMultiplier?: number | null | undefined
  team?: string | number | null | undefined
}

export type UsersFilterInput = {
  caseType?: Array<string | number> | null | undefined
  ids?: Array<string | number> | null | undefined
  isActive?: boolean | null | undefined
  role?: Array<UserRole> | null | undefined
  search?: string | null | undefined
  team?: Array<string | number> | null | undefined
}

export enum UsersSortField {
  ASSIGNED_CASES_COUNT = 'ASSIGNED_CASES_COUNT',
  EMAIL = 'EMAIL',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  TEAM_NAME = 'TEAM_NAME',
}

export type UsersSortInput = {
  direction: SortDirection
  field: UsersSortField
}

export type BaseFinancialAccountFragment = {
  __typename: 'FinancialAccount'
  id: string
  code: string
  createdAt: DateTimeString
  lastDueDate: DateTimeString | null
  dpd: number | null
  index: number | null
  status: FinancialAccountStatus
  providerReference: string | null
  ptpDate: DateTimeString | null
  type: { __typename: 'FinancialAccountType'; id: string; name: string }
  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
  principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
  provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    role: string
    phoneNumberNote: string | null
    emailAddress: string | null
    emailAddressNote: string | null
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    } | null
  } | null
  registeredContactPhone: {
    __typename: 'CustomerContactPhone'
    id: string
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    }
  } | null
}

type CaseCard_Case_Fragment = {
  __typename: 'Case'
  id: string
  code: string
  stage: CaseStage
  status: CaseStatus
  flow: CaseFlow | null
  updatedAt: DateTimeString
  type: { __typename: 'CaseType'; id: string; name: string }
  assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
}

type CaseCard_CaseBrief_Fragment = {
  __typename: 'CaseBrief'
  id: string
  code: string
  stage: CaseStage
  status: CaseStatus
  flow: CaseFlow | null
  updatedAt: DateTimeString
  type: { __typename: 'CaseType'; id: string; name: string }
  assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
}

export type CaseCardFragment = CaseCard_Case_Fragment | CaseCard_CaseBrief_Fragment

export type CustomerDomicileFragment = {
  __typename: 'Customer'
  domicile: string | null
  domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
  domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
}

export type PageInfoFragment = {
  __typename: 'PageInfo'
  endCursor: string | null
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor: string | null
}

export type PhoneNumberCopyableFragment = {
  __typename: 'PhoneNumber'
  countryCode: string | null
  displayNumber: string
  international: string
  national: string | null
}

export type RemoteFileInfoFragment = {
  __typename: 'RemoteFile'
  url: string | null
  mimeType: string
  fileName: string
}

export type SelectCountryFragment = {
  __typename: 'Country'
  code: string
  name: string
  isDefault: boolean
}

export type SelectCountryAreaFragment = { __typename: 'CountryArea'; id: string; name: string }

export type SelectCountryWithAreasFragment = {
  __typename: 'Country'
  code: string
  name: string
  isDefault: boolean
  areas: Array<{ __typename: 'CountryArea'; id: string; name: string }>
}

export type SelectRegencyFragment = {
  __typename: 'Regency'
  id: string
  name: string
  kind: RegencyKind
}

type TicketCard_MediationTicket_Fragment = {
  __typename: 'MediationTicket'
  id: string
  code: string
  createdAt: DateTimeString
  status: TicketStatus
  type: TicketType
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
    } | null
  }
  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  lastEventConnection: {
    __typename: 'TicketEventConnection'
    edges: Array<{
      __typename: 'TicketEventEdge'
      node: { __typename: 'TicketEvent'; date: DateTimeString }
    }>
  }
}

type TicketCard_NegotiationTicket_Fragment = {
  __typename: 'NegotiationTicket'
  id: string
  code: string
  createdAt: DateTimeString
  status: TicketStatus
  type: TicketType
  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  lastEventConnection: {
    __typename: 'TicketEventConnection'
    edges: Array<{
      __typename: 'TicketEventEdge'
      node: { __typename: 'TicketEvent'; date: DateTimeString }
    }>
  }
}

export type TicketCardFragment =
  TicketCard_MediationTicket_Fragment | TicketCard_NegotiationTicket_Fragment

export type FileUploadManyRequestMutationVariables = Exact<{
  input: Array<FileUploadRequestInput> | FileUploadRequestInput
}>

export type FileUploadManyRequestMutation = {
  __typename: 'Mutation'
  fileUploadManyRequest: Array<{
    __typename: 'FileUploadRequest'
    uploadUrl: string
    storageFileRef: string
    expiresAt: DateTimeString
    headers: Array<{ __typename: 'FileUploadHeader'; name: string; value: string }>
  }>
}

export type CountriesForSelectWithAreasQueryVariables = Exact<{ [key: string]: never }>

export type CountriesForSelectWithAreasQuery = {
  __typename: 'Query'
  countries: Array<{
    __typename: 'Country'
    code: string
    name: string
    isDefault: boolean
    areas: Array<{ __typename: 'CountryArea'; id: string; name: string }>
  }>
}

export type DomicileOptionsQueryVariables = Exact<{ [key: string]: never }>

export type DomicileOptionsQuery = {
  __typename: 'Query'
  countries: Array<{
    __typename: 'Country'
    code: string
    name: string
    isDefault: boolean
    areas: Array<{
      __typename: 'CountryArea'
      id: string
      name: string
      regencies: Array<{ __typename: 'Regency'; id: string; name: string; kind: RegencyKind }>
    }>
  }>
}

export type NotReferencedQueryVariables = Exact<{ [key: string]: never }>

export type NotReferencedQuery = {
  __typename: 'Query'
  errorCodesAccount: Array<AccountScopeCode>
  errorCodesAuth: Array<AuthScopeCode>
  errorCodesCommon: Array<CommonScopeCode>
  errorCodesCase: Array<CaseScopeCode>
  errorCodesCustomer: Array<CustomerScopeCode>
  errorCodesFinancialAccount: Array<FinancialAccountScopeCode>
  errorCodesFinancialProvider: Array<FinancialProviderScopeCode>
  errorCodesTickets: Array<TicketScopeCode>
  scopes: Array<Scope>
}

export type CustomerInfoFragment = {
  __typename: 'Customer'
  id: string
  name: string
  birthDate: DateString | null
  birthPlace: string | null
  gender: Gender | null
  governmentId: string | null
  educationLevel: CustomerEducationLevel | null
  marriageStatus: CustomerMaritalStatus | null
  cases: { __typename: 'CaseConnection'; totalCount: number }
}

export type SearchCustomersQueryVariables = Exact<{
  after?: string | null | undefined
  first: number
  query: string
}>

export type SearchCustomersQuery = {
  __typename: 'Query'
  search: {
    __typename: 'CustomerConnection'
    edges: Array<{
      __typename: 'CustomerEdge'
      node: {
        __typename: 'Customer'
        id: string
        name: string
        defaultEmail: { __typename: 'CustomerContactEmail'; emailAddress: string } | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string }
        } | null
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type SearchFinancialAccountTypeProvidersQueryVariables = Exact<{
  financialAccountTypeId: string | number
  after?: string | null | undefined
  first: number
  query: string
}>

export type SearchFinancialAccountTypeProvidersQuery = {
  __typename: 'Query'
  search: {
    __typename: 'FinancialProviderConnection'
    edges: Array<{
      __typename: 'FinancialProviderEdge'
      node: { __typename: 'FinancialProvider'; id: string; name: string }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type SearchFinancialProviderContactsQueryVariables = Exact<{
  financialProviderId: string | number
  after?: string | null | undefined
  first: number
  query: string
}>

export type SearchFinancialProviderContactsQuery = {
  __typename: 'Query'
  search: {
    __typename: 'FinancialProviderContactConnection'
    edges: Array<{
      __typename: 'FinancialProviderContactEdge'
      node: {
        __typename: 'FinancialProviderContact'
        id: string
        name: string
        role: string
        phoneNumberNote: string | null
        emailAddress: string | null
        emailAddressNote: string | null
        phoneNumber: {
          __typename: 'PhoneNumber'
          displayNumber: string
          international: string
        } | null
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type SearchUsersQueryVariables = Exact<{
  after?: string | null | undefined
  first: number
  query: string
}>

export type SearchUsersQuery = {
  __typename: 'Query'
  search: {
    __typename: 'UserConnection'
    edges: Array<{
      __typename: 'UserEdge'
      node: { __typename: 'User'; id: string; email: string; displayName: string }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type SearchUsersByFilterQueryVariables = Exact<{
  after?: string | null | undefined
  first: number
  filter: UsersFilterInput
}>

export type SearchUsersByFilterQuery = {
  __typename: 'Query'
  search: {
    __typename: 'UserConnection'
    edges: Array<{
      __typename: 'UserEdge'
      node: { __typename: 'User'; id: string; email: string; displayName: string }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type AppFragment = {
  __typename: 'App'
  id: string
  name: string
  isActive: boolean
  tokens: Array<{
    __typename: 'AppToken'
    id: string
    name: string
    token: string
    playbook: { __typename: 'AppTokenPlaybook'; id: string; name: string | null } | null
  }>
  accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
}

export type AppCreateMutationVariables = Exact<{
  input: AppCreateInput
}>

export type AppCreateMutation = {
  __typename: 'Mutation'
  appCreate: {
    __typename: 'AppCreate'
    app: {
      __typename: 'App'
      id: string
      name: string
      isActive: boolean
      tokens: Array<{
        __typename: 'AppToken'
        id: string
        name: string
        token: string
        playbook: { __typename: 'AppTokenPlaybook'; id: string; name: string | null } | null
      }>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
    }
  }
}

export type AppDeleteMutationVariables = Exact<{
  id: string | number
}>

export type AppDeleteMutation = {
  __typename: 'Mutation'
  appDelete: {
    __typename: 'AppDelete'
    app: {
      __typename: 'App'
      id: string
      name: string
      isActive: boolean
      tokens: Array<{
        __typename: 'AppToken'
        id: string
        name: string
        token: string
        playbook: { __typename: 'AppTokenPlaybook'; id: string; name: string | null } | null
      }>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
    }
  }
}

export type AppTokenCreateMutationVariables = Exact<{
  id: string | number
  input: AppTokenCreateInput
}>

export type AppTokenCreateMutation = {
  __typename: 'Mutation'
  appTokenCreate: {
    __typename: 'AppTokenCreate'
    token: string
    appToken: { __typename: 'AppToken'; name: string; id: string }
  }
}

export type AppTokenDeleteMutationVariables = Exact<{
  id: string | number
}>

export type AppTokenDeleteMutation = {
  __typename: 'Mutation'
  appTokenDelete: {
    __typename: 'AppTokenDelete'
    appToken: { __typename: 'AppToken'; name: string; id: string }
  }
}

export type AppUpdateMutationVariables = Exact<{
  id: string | number
  input: AppUpdateInput
}>

export type AppUpdateMutation = {
  __typename: 'Mutation'
  appUpdate: {
    __typename: 'AppUpdate'
    app: {
      __typename: 'App'
      id: string
      name: string
      isActive: boolean
      tokens: Array<{
        __typename: 'AppToken'
        id: string
        name: string
        token: string
        playbook: { __typename: 'AppTokenPlaybook'; id: string; name: string | null } | null
      }>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
    }
  }
}

export type AppCreatePermissionsQueryVariables = Exact<{ [key: string]: never }>

export type AppCreatePermissionsQuery = {
  __typename: 'Query'
  permissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
}

export type AppDetailQueryVariables = Exact<{
  id: string | number
}>

export type AppDetailQuery = {
  __typename: 'Query'
  app: {
    __typename: 'App'
    id: string
    name: string
    isActive: boolean
    tokens: Array<{
      __typename: 'AppToken'
      id: string
      name: string
      token: string
      playbook: { __typename: 'AppTokenPlaybook'; id: string; name: string | null } | null
    }>
    accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
  }
  permissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
}

export type AppListQueryVariables = Exact<{
  before?: string | null | undefined
  after?: string | null | undefined
  first?: number | null | undefined
  last?: number | null | undefined
  sort?: AppsSortInput | null | undefined
  filter?: AppsFilterInput | null | undefined
}>

export type AppListQuery = {
  __typename: 'Query'
  apps: {
    __typename: 'AppConnection'
    totalCount: number
    edges: Array<{
      __typename: 'AppEdge'
      node: { __typename: 'App'; id: string; name: string; isActive: boolean }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type AccountPermissionFragment = {
  __typename: 'AccountPermission'
  code: Permission
  name: string
}

type Author_App_Fragment = { __typename: 'App'; displayName: string }

type Author_User_Fragment = { __typename: 'User'; displayName: string }

export type AuthorFragment = Author_App_Fragment | Author_User_Fragment

export type UserFragment = {
  __typename: 'User'
  id: string
  email: string
  displayName: string
  firstName: string
  lastName: string
  roles: Array<UserRole>
  accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
  avatar: { __typename: 'Image'; url: string } | null
  userTeam: { __typename: 'UserTeam'; id: string; name: string; types: Array<UserTeamType> } | null
  canHandleCaseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }> | null
}

export type ConfirmAccountMutationVariables = Exact<{
  email: string
  password: string
  token: string
}>

export type ConfirmAccountMutation = {
  __typename: 'Mutation'
  confirmAccount: {
    __typename: 'ConfirmAccount'
    refreshToken: string
    accessToken: string
    user: {
      __typename: 'User'
      id: string
      email: string
      displayName: string
      firstName: string
      lastName: string
      roles: Array<UserRole>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
      avatar: { __typename: 'Image'; url: string } | null
      userTeam: {
        __typename: 'UserTeam'
        id: string
        name: string
        types: Array<UserTeamType>
      } | null
      canHandleCaseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }> | null
    }
  }
}

export type RefreshTokenMutationVariables = Exact<{
  token: string
}>

export type RefreshTokenMutation = {
  __typename: 'Mutation'
  tokenRefresh: { __typename: 'TokenRefresh'; token: string }
}

export type RequestPasswordResetMutationVariables = Exact<{
  email: string
  redirectUrl: string
}>

export type RequestPasswordResetMutation = { __typename: 'Mutation'; requestPasswordReset: boolean }

export type RequestedPasswordSetMutationVariables = Exact<{
  email: string
  password: string
  token: string
}>

export type RequestedPasswordSetMutation = {
  __typename: 'Mutation'
  requestedPasswordSet: {
    __typename: 'RequestedPasswordSet'
    refreshToken: string
    accessToken: string
    user: {
      __typename: 'User'
      id: string
      email: string
      displayName: string
      firstName: string
      lastName: string
      roles: Array<UserRole>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
      avatar: { __typename: 'Image'; url: string } | null
      userTeam: {
        __typename: 'UserTeam'
        id: string
        name: string
        types: Array<UserTeamType>
      } | null
      canHandleCaseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }> | null
    }
  }
}

export type TokenAuthMutationVariables = Exact<{
  email: string
  password: string
}>

export type TokenAuthMutation = {
  __typename: 'Mutation'
  tokenCreate: {
    __typename: 'TokenCreate'
    accessToken: string
    refreshToken: string
    user: {
      __typename: 'User'
      id: string
      email: string
      displayName: string
      firstName: string
      lastName: string
      roles: Array<UserRole>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
      avatar: { __typename: 'Image'; url: string } | null
      userTeam: {
        __typename: 'UserTeam'
        id: string
        name: string
        types: Array<UserTeamType>
      } | null
      canHandleCaseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }> | null
    }
  }
}

export type VerifyTokenMutationVariables = Exact<{
  token: string
}>

export type VerifyTokenMutation = {
  __typename: 'Mutation'
  tokenVerifyUser: {
    __typename: 'TokenVerifyUser'
    user: {
      __typename: 'User'
      id: string
      email: string
      displayName: string
      firstName: string
      lastName: string
      roles: Array<UserRole>
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
      avatar: { __typename: 'Image'; url: string } | null
      userTeam: {
        __typename: 'UserTeam'
        id: string
        name: string
        types: Array<UserTeamType>
      } | null
      canHandleCaseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }> | null
    } | null
  }
}

export type UserNotifiedSubscriptionVariables = Exact<{
  ignoreSameSession?: boolean | null | undefined
}>

export type UserNotifiedSubscription = {
  __typename: 'Subscription'
  userNotified: {
    __typename: 'UserNotification'
    text: string
    type: UserNotificationType
    author:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
  }
}

export type BusinessEntityDetailFragment = {
  __typename: 'BusinessEntity'
  aliasName: string
  companyName: string
  companyFullAddress: string
  companyShortAddress: string
  invoiceIdentifier: string
  bankName: string
  bankAccount: string
  vatRate: number
  websiteUrl: string
  id: string
  name: string
  directorUser: { __typename: 'User'; id: string; displayName: string; email: string } | null
  teams: Array<{ __typename: 'UserTeam'; id: string; name: string }>
}

export type BusinessEntityListFragment = {
  __typename: 'BusinessEntity'
  id: string
  name: string
  directorUser: { __typename: 'User'; id: string; displayName: string } | null
  teams: Array<{ __typename: 'UserTeam'; id: string; name: string }>
}

export type BusinessHoursFragment = {
  __typename: 'BusinessHours'
  observeHolidays: boolean
  updatedAt: DateTimeString
  sendWindow: {
    __typename: 'MessagingSendWindow'
    tz: string
    rules: Array<{
      __typename: 'MessagingSendWindowRule'
      days: Array<MessagingSendWindowDay>
      start: string
      end: string
    }>
  }
}

export type BusinessEntityAddMutationVariables = Exact<{
  input: BusinessEntityCreateInput
}>

export type BusinessEntityAddMutation = {
  __typename: 'Mutation'
  businessEntityCreate: {
    __typename: 'BusinessEntityCreate'
    businessEntity: {
      __typename: 'BusinessEntity'
      aliasName: string
      companyName: string
      companyFullAddress: string
      companyShortAddress: string
      invoiceIdentifier: string
      bankName: string
      bankAccount: string
      vatRate: number
      websiteUrl: string
      id: string
      name: string
      directorUser: { __typename: 'User'; id: string; displayName: string; email: string } | null
      teams: Array<{ __typename: 'UserTeam'; id: string; name: string }>
    }
  }
}

export type BusinessEntityClearBusinessHoursMutationVariables = Exact<{
  id: string | number
}>

export type BusinessEntityClearBusinessHoursMutation = {
  __typename: 'Mutation'
  businessEntityClearBusinessHours: {
    __typename: 'BusinessEntityClearBusinessHours'
    businessEntity: {
      __typename: 'BusinessEntity'
      id: string
      businessHours: {
        __typename: 'BusinessHours'
        observeHolidays: boolean
        updatedAt: DateTimeString
        sendWindow: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        }
      } | null
    }
  }
}

export type BusinessEntityDeleteMutationVariables = Exact<{
  id: string | number
}>

export type BusinessEntityDeleteMutation = {
  __typename: 'Mutation'
  businessEntityDelete: { __typename: 'BusinessEntityDelete' }
}

export type BusinessEntitySetBusinessHoursMutationVariables = Exact<{
  id: string | number
  input: BusinessHoursSetInput
}>

export type BusinessEntitySetBusinessHoursMutation = {
  __typename: 'Mutation'
  businessEntitySetBusinessHours: {
    __typename: 'BusinessEntitySetBusinessHours'
    businessEntity: {
      __typename: 'BusinessEntity'
      id: string
      businessHours: {
        __typename: 'BusinessHours'
        observeHolidays: boolean
        updatedAt: DateTimeString
        sendWindow: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        }
      } | null
    }
  }
}

export type BusinessEntityUpdateMutationVariables = Exact<{
  id: string | number
  input: BusinessEntityUpdateInput
}>

export type BusinessEntityUpdateMutation = {
  __typename: 'Mutation'
  businessEntityUpdate: {
    __typename: 'BusinessEntityUpdate'
    businessEntity: {
      __typename: 'BusinessEntity'
      aliasName: string
      companyName: string
      companyFullAddress: string
      companyShortAddress: string
      invoiceIdentifier: string
      bankName: string
      bankAccount: string
      vatRate: number
      websiteUrl: string
      id: string
      name: string
      directorUser: { __typename: 'User'; id: string; displayName: string; email: string } | null
      teams: Array<{ __typename: 'UserTeam'; id: string; name: string }>
    }
  }
}

export type BusinessEntityBusinessHoursQueryVariables = Exact<{
  id: string | number
}>

export type BusinessEntityBusinessHoursQuery = {
  __typename: 'Query'
  businessEntity: {
    __typename: 'BusinessEntity'
    id: string
    businessHours: {
      __typename: 'BusinessHours'
      observeHolidays: boolean
      updatedAt: DateTimeString
      sendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          start: string
          end: string
        }>
      }
    } | null
  }
}

export type BusinessEntityDetailQueryVariables = Exact<{
  id: string | number
}>

export type BusinessEntityDetailQuery = {
  __typename: 'Query'
  businessEntity: {
    __typename: 'BusinessEntity'
    aliasName: string
    companyName: string
    companyFullAddress: string
    companyShortAddress: string
    invoiceIdentifier: string
    bankName: string
    bankAccount: string
    vatRate: number
    websiteUrl: string
    id: string
    name: string
    directorUser: { __typename: 'User'; id: string; displayName: string; email: string } | null
    teams: Array<{ __typename: 'UserTeam'; id: string; name: string }>
  }
}

export type BusinessEntityListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: BusinessEntitiesFilterInput | null | undefined
  sort?: BusinessEntitiesSortInput | null | undefined
}>

export type BusinessEntityListQuery = {
  __typename: 'Query'
  businessEntities: {
    __typename: 'BusinessEntityConnection'
    totalCount: number
    edges: Array<{
      __typename: 'BusinessEntityEdge'
      cursor: string
      node: {
        __typename: 'BusinessEntity'
        id: string
        name: string
        directorUser: { __typename: 'User'; id: string; displayName: string } | null
        teams: Array<{ __typename: 'UserTeam'; id: string; name: string }>
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseActionFragment = {
  __typename: 'CaseAction'
  id: string
  comment: string
  date: DateTimeString
  createdAt: DateTimeString
  type: { __typename: 'CaseActionType'; id: string; name: string }
  result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
}

export type CaseActionResultOptionFragment = {
  __typename: 'CaseActionResult'
  id: string
  name: string
}

export type CaseActionTypeOptionFragment = {
  __typename: 'CaseActionType'
  id: string
  name: string
}

export type CaseAssignmentReasonFragment = {
  __typename: 'CaseAssignmentReason'
  rung: CaseAssignedVia
  at: DateTimeString
  user: { __typename: 'User'; id: string; displayName: string } | null
  actor: { __typename: 'User'; id: string; displayName: string } | null
  similarCase: {
    __typename: 'CaseAssignmentSimilarCase'
    id: string
    code: string
    customerId: string
    holderKind: string
  } | null
  excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
}

export type CaseCloseReasonOptionFragment = {
  __typename: 'CaseCloseReason'
  id: string
  name: string
}

export type CaseDetailFragment = {
  __typename: 'Case'
  id: string
  code: string
  createdAt: DateTimeString
  closedAt: DateTimeString | null
  accountNodeAccess: AccountNodeAccess | null
  updatedAt: DateTimeString
  moveToNewCustomerBlocker: CaseScopeCode | null
  status: CaseStatus
  stage: CaseStage
  flow: CaseFlow | null
  assignedVia: CaseAssignedVia | null
  importClassification: CaseImportClassification | null
  importSubmittedName: string | null
  identityCheckMergedName: string | null
  locked: boolean
  conversations: Array<{
    __typename: 'MessagingConversation'
    id: string
    customerWaId: string
    customerName: string | null
    lifecycle: MessagingConversationLifecycle
    lastActivityAt: DateTimeString
    lastInboundSeq: number | null
    needsAttention: boolean
    owesReply: boolean
    dismissed: boolean
    safetyFlagged: boolean
    ownership: MessagingConversationOwnership
    windowExpiresAt: DateTimeString | null
    customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
    assignee: { __typename: 'User'; id: string; displayName: string } | null
    linkedCases: Array<{
      __typename: 'CaseBrief'
      id: string
      code: string
      stage: CaseStage
      type: { __typename: 'CaseType'; id: string; name: string }
    }>
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    } | null
    lastMessage: {
      __typename: 'MessagingMessagePreview'
      createdAt: DateTimeString
      direction: MessagingMessageDirection
      seq: number
      source: MessagingMessageSource | null
      state: MessagingMessageState
      textPreview: string | null
      type: MessagingMessageType
      sender: { __typename: 'User'; id: string; displayName: string } | null
    } | null
  }>
  tags: Array<{
    __typename: 'CaseTag'
    id: string
    name: string
    color: string | null
    value: string
  }>
  sharedNumberThread: {
    __typename: 'CaseSharedNumberThread'
    conversationId: string | null
    case: {
      __typename: 'Case'
      id: string
      code: string
      stage: CaseStage
      status: CaseStatus
      flow: CaseFlow | null
      updatedAt: DateTimeString
      customer: { __typename: 'Customer'; id: string; name: string }
      type: { __typename: 'CaseType'; id: string; name: string }
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
    } | null
  } | null
  type: {
    __typename: 'CaseType'
    id: string
    name: string
    productPackages: Array<{
      __typename: 'ProductPackage'
      name: string
      minimumFeeRate: number
      minimumFee: { __typename: 'Money'; amount: number; currency: string }
    }>
  }
  estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
  importClassificationReason?: {
    __typename: 'CaseImportClassificationReason'
    matcherVersion: number
    clusterSize: number
    guardrailMaxCluster: number | null
    cell: string | null
    emailRelation: string | null
    nameRelation: string | null
    evidenceScore: number | null
    nameCheck: string | null
    sharedNumber: boolean | null
    originConversationCustomer: boolean | null
    submittedName: string | null
  } | null
  assignmentReason?: {
    __typename: 'CaseAssignmentReason'
    rung: CaseAssignedVia
    at: DateTimeString
    reservation: {
      __typename: 'CaseAssignmentReason'
      rung: CaseAssignedVia
      at: DateTimeString
      user: { __typename: 'User'; id: string; displayName: string } | null
      actor: { __typename: 'User'; id: string; displayName: string } | null
      similarCase: {
        __typename: 'CaseAssignmentSimilarCase'
        id: string
        code: string
        customerId: string
        holderKind: string
      } | null
      excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
    } | null
    user: { __typename: 'User'; id: string; displayName: string } | null
    actor: { __typename: 'User'; id: string; displayName: string } | null
    similarCase: {
      __typename: 'CaseAssignmentSimilarCase'
      id: string
      code: string
      customerId: string
      holderKind: string
    } | null
    excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
  } | null
  source: {
    __typename: 'CaseSource'
    kind: string | null
    type: string | null
    medium: string | null
    campaign: string | null
    tool: string | null
    data: any
    optInSource: string | null
    optInAt: DateTimeString | null
  } | null
  behaviour: {
    __typename: 'CaseBehaviour'
    campaignResolverId: string | null
    flowChangeReason: CaseFlowChangeReason | null
    campaign: { __typename: 'Campaign'; id: string; name: string } | null
    campaignResolver: {
      __typename: 'CampaignResolver'
      id: string
      name: string
      priority: number
    } | null
  } | null
  closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
  actions: Array<{
    __typename: 'CaseAction'
    id: string
    comment: string
    date: DateTimeString
    createdAt: DateTimeString
    type: { __typename: 'CaseActionType'; id: string; name: string }
    result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
  }>
  financialAccounts: Array<{
    __typename: 'FinancialAccount'
    fullName: string | null
    id: string
    code: string
    createdAt: DateTimeString
    lastDueDate: DateTimeString | null
    dpd: number | null
    index: number | null
    status: FinancialAccountStatus
    providerReference: string | null
    ptpDate: DateTimeString | null
    documents: {
      __typename: 'FinancialAccountDocumentConnection'
      totalCount: number
      edges: Array<{
        __typename: 'FinancialAccountDocumentEdge'
        node: {
          __typename: 'FinancialAccountDocument'
          id: string
          type: FinancialAccountDocumentType
          otherType: string | null
          note: string | null
          createdAt: DateTimeString
          financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
          file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    tickets: {
      __typename: 'FinancialAccountTicketConnection'
      totalCount: number
      edges: Array<{
        __typename: 'FinancialAccountTicketEdge'
        node:
          | {
              __typename: 'MediationTicket'
              id: string
              code: string
              createdAt: DateTimeString
              status: TicketStatus
              type: TicketType
              providerContact: {
                __typename: 'FinancialProviderContact'
                id: string
                name: string
                phoneNumber: {
                  __typename: 'PhoneNumber'
                  countryCode: string | null
                  displayNumber: string
                  international: string
                } | null
              }
              assignedTo: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: {
                __typename: 'TicketCloseReason'
                name: string
                success: boolean
              } | null
              lastEventConnection: {
                __typename: 'TicketEventConnection'
                edges: Array<{
                  __typename: 'TicketEventEdge'
                  node: { __typename: 'TicketEvent'; date: DateTimeString }
                }>
              }
            }
          | {
              __typename: 'NegotiationTicket'
              id: string
              code: string
              createdAt: DateTimeString
              status: TicketStatus
              type: TicketType
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
              assignedTo: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: {
                __typename: 'TicketCloseReason'
                name: string
                success: boolean
              } | null
              lastEventConnection: {
                __typename: 'TicketEventConnection'
                edges: Array<{
                  __typename: 'TicketEventEdge'
                  node: { __typename: 'TicketEvent'; date: DateTimeString }
                }>
              }
            }
      }>
    }
    type: { __typename: 'FinancialAccountType'; id: string; name: string }
    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
    principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
    provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
    providerContact: {
      __typename: 'FinancialProviderContact'
      id: string
      name: string
      role: string
      phoneNumberNote: string | null
      emailAddress: string | null
      emailAddressNote: string | null
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      } | null
    } | null
    registeredContactPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    } | null
  }>
  documents: {
    __typename: 'CaseDocumentConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseDocumentEdge'
      node: {
        __typename: 'CaseDocument'
        id: string
        type: CaseDocumentType
        otherType: string | null
        note: string | null
        createdAt: DateTimeString
        file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  events: {
    __typename: 'CaseEventConnection'
    edges: Array<{
      __typename: 'CaseEventEdge'
      node: {
        __typename: 'CaseEvent'
        id: string
        type: CaseEventType
        date: DateTimeString
        parameters: any
        actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
        actionType: { __typename: 'CaseActionType'; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
        user: { __typename: 'User'; id: string; displayName: string; email: string } | null
        financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
        fromCustomer: {
          __typename: 'CaseEventCustomer'
          id: string
          code: string
          name: string
        } | null
        toCustomer: {
          __typename: 'CaseEventCustomer'
          id: string
          code: string
          name: string
        } | null
      }
    }>
  }
  customer: {
    __typename: 'Customer'
    id: string
    code: string
    name: string
    accountNodeAccess: AccountNodeAccess | null
    portalEnabledAt: DateTimeString | null
    birthDate: DateString | null
    birthPlace: string | null
    gender: Gender | null
    governmentId: string | null
    educationLevel: CustomerEducationLevel | null
    marriageStatus: CustomerMaritalStatus | null
    occupation: string | null
    residentialStatus: CustomerResidentialStatus | null
    jobTitle: string | null
    employmentStatus: CustomerEmploymentStatus | null
    companyType: CustomerCompanyType | null
    salaryRange: CustomerSalaryRange | null
    referralSource: CustomerReferralSource | null
    otherDebtTypes: Array<CustomerOtherDebtType>
    otherDebtTypeOther: string | null
    note: string | null
    domicile: string | null
    defaultPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
    } | null
    defaultEmail: { __typename: 'CustomerContactEmail'; id: string; emailAddress: string } | null
    addresses: Array<{
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }>
    contactPhones: Array<{
      __typename: 'CustomerContactPhone'
      id: string
      note: string | null
      isDefault: boolean
      whatsappCapability: MessagingNumberCapability
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    }>
    contactEmails: Array<{
      __typename: 'CustomerContactEmail'
      id: string
      emailAddress: string
      note: string | null
      isDefault: boolean
    }>
    cases: {
      __typename: 'CaseConnection'
      totalCount: number
      edges: Array<{
        __typename: 'CaseEdge'
        node: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        }
      }>
    }
    documents: {
      __typename: 'CustomerDocumentConnection'
      totalCount: number
      edges: Array<{
        __typename: 'CustomerDocumentEdge'
        node: {
          __typename: 'CustomerDocument'
          id: string
          type: CustomerDocumentType
          otherType: string | null
          note: string | null
          createdAt: DateTimeString
          file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    events: {
      __typename: 'CustomerEventConnection'
      edges: Array<{
        __typename: 'CustomerEventEdge'
        node: {
          __typename: 'CustomerEvent'
          id: string
          type: CustomerEventType
          date: DateTimeString
          parameters: any
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
  }
  address: {
    __typename: 'CustomerAddress'
    id: string
    city: string
    cityArea: string | null
    note: string | null
    postalCode: string
    streetAddress1: string
    streetAddress2: string | null
    isPermanentResidency: boolean
    isDefault: boolean
    country: { __typename: 'Country'; code: string; name: string } | null
    countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
  } | null
  contactEmail: {
    __typename: 'CustomerContactEmail'
    id: string
    emailAddress: string
    note: string | null
    isDefault: boolean
  } | null
  contactPhone: {
    __typename: 'CustomerContactPhone'
    verifiedAt: DateTimeString | null
    id: string
    note: string | null
    isDefault: boolean
    whatsappCapability: MessagingNumberCapability
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    }
  } | null
  assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
  assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
  totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
  orderCreateOptions: {
    __typename: 'OrderCreateOption'
    availability: ProductPackageAvailability
  } | null
  pendingOrder: {
    __typename: 'Order'
    id: string
    code: string
    status: OrderStatus
    paymentStatus: OrderPaymentStatus
    feeRate: number
    adjustedFeeRate: number | null
    durationInMonths: number
    documentBookCode: string | null
    startAt: DateTimeString | null
    createdAt: DateTimeString
    businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
    productPackage: {
      __typename: 'ProductPackage'
      id: string
      name: string
      documentTypes: Array<{
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
        appliesMeterai: boolean
        documentFilenameTemplate: string | null
        hasTemplate: boolean
        updatedAt: DateTimeString
      }>
    }
    fee: { __typename: 'Money'; amount: number; currency: string } | null
    estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
    paidAmount: { __typename: 'Money'; amount: number; currency: string }
    remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
    additionalPaymentOrderAdjustmentRange: {
      __typename: 'MoneyRange'
      min: { __typename: 'Money'; amount: number; currency: string }
      max: { __typename: 'Money'; amount: number; currency: string }
    } | null
    currentProgress: {
      __typename: 'OrderCurrentProgress'
      nextState: OrderStatus
      isValid: boolean
      blockers: Array<
        | {
            __typename: 'OrderStateProgressBlockerCaseDocumentType'
            caseDocumentType: CaseDocumentType
          }
        | {
            __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
            case: { __typename: 'Case'; id: string }
          }
        | {
            __typename: 'OrderStateProgressBlockerCustomerDocumentType'
            customerDocumentType: CustomerDocumentType
          }
        | {
            __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
            financialAccountDocumentType: FinancialAccountDocumentType
          }
        | {
            __typename: 'OrderStateProgressBlockerGenerateDocumentType'
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
          }
        | {
            __typename: 'OrderStateProgressBlockerOrderDocumentType'
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
          }
        | {
            __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
            orderPaymentStatus: Array<OrderPaymentStatus>
          }
        | {
            __typename: 'OrderStateProgressBlockerValidationEntity'
            orderValidationEntity: OrderValidationEntity
            entityReferenceCodes: Array<string>
          }
      >
    } | null
    payments: Array<{
      __typename: 'OrderPayment'
      id: string
      paidAt: DateTimeString
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
    }>
    documents: {
      __typename: 'OrderDocumentConnection'
      totalCount: number
      edges: Array<{
        __typename: 'OrderDocumentEdge'
        node: {
          __typename: 'OrderDocument'
          id: string
          generated: boolean
          rendered: boolean
          signed: boolean
          canBeSigned: boolean
          note: string | null
          createdAt: DateTimeString
          documentType: {
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
          }
          signers: Array<{
            __typename: 'OrderDocumentSignerModel'
            role: string
            name: string | null
            email: string | null
            isCustomer: boolean
          }>
          activeEsignRequest: {
            __typename: 'OrderDocumentEsignRequest'
            id: string
            status: EsignRequestStatus
            signerName: string
            signerEmail: string
            createdAt: DateTimeString
            signedOrderDocument: {
              __typename: 'OrderDocument'
              id: string
              file: { __typename: 'RemoteFile'; url: string | null }
            } | null
          } | null
          file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    events: {
      __typename: 'OrderEventConnection'
      edges: Array<{
        __typename: 'OrderEventEdge'
        node: {
          __typename: 'OrderEvent'
          id: string
          type: OrderEventType
          date: DateTimeString
          parameters: any
          documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    financialAccounts: Array<{
      __typename: 'OrderFinancialAccount'
      id: string
      financialAccount: { __typename: 'FinancialAccount'; id: string }
      outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
    }>
    caseProducts: Array<{
      __typename: 'CaseProduct'
      id: string
      isActive: boolean
      product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
    }>
  } | null
  orders: Array<{
    __typename: 'Order'
    id: string
    code: string
    status: OrderStatus
    feeRate: number
    createdAt: DateTimeString
    documentBookCode: string | null
    invoiceBookCode: string | null
    startAt: DateTimeString | null
    durationInMonths: number
    paymentStatus: OrderPaymentStatus
    businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
    productPackage: {
      __typename: 'ProductPackage'
      id: string
      name: string
      documentTypes: Array<{
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
        appliesMeterai: boolean
        documentFilenameTemplate: string | null
        hasTemplate: boolean
        updatedAt: DateTimeString
      }>
    }
    fee: { __typename: 'Money'; amount: number; currency: string } | null
    estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
    paidAmount: { __typename: 'Money'; amount: number; currency: string }
    payments: Array<{
      __typename: 'OrderPayment'
      id: string
      paidAt: DateTimeString
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
    }>
    documents: {
      __typename: 'OrderDocumentConnection'
      totalCount: number
      edges: Array<{
        __typename: 'OrderDocumentEdge'
        node: {
          __typename: 'OrderDocument'
          id: string
          generated: boolean
          rendered: boolean
          signed: boolean
          canBeSigned: boolean
          note: string | null
          createdAt: DateTimeString
          documentType: {
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
          }
          signers: Array<{
            __typename: 'OrderDocumentSignerModel'
            role: string
            name: string | null
            email: string | null
            isCustomer: boolean
          }>
          activeEsignRequest: {
            __typename: 'OrderDocumentEsignRequest'
            id: string
            status: EsignRequestStatus
            signerName: string
            signerEmail: string
            createdAt: DateTimeString
            signedOrderDocument: {
              __typename: 'OrderDocument'
              id: string
              file: { __typename: 'RemoteFile'; url: string | null }
            } | null
          } | null
          file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    events: {
      __typename: 'OrderEventConnection'
      edges: Array<{
        __typename: 'OrderEventEdge'
        node: {
          __typename: 'OrderEvent'
          id: string
          type: OrderEventType
          date: DateTimeString
          parameters: any
          documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    financialAccounts: Array<{
      __typename: 'OrderFinancialAccount'
      id: string
      financialAccount: { __typename: 'FinancialAccount'; id: string }
      outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
    }>
    caseProducts: Array<{
      __typename: 'CaseProduct'
      id: string
      isActive: boolean
      product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
    }>
  }>
  products: Array<{
    __typename: 'CaseProduct'
    id: string
    createdAt: DateTimeString
    startAt: DateTimeString
    endAt: DateTimeString
    isActive: boolean
    product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
    financialAccounts: Array<{
      __typename: 'CaseProductFinancialAccount'
      id: string
      financialAccount: { __typename: 'FinancialAccount'; id: string }
    }>
  }>
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
}

export type CaseDetailCaseDocumentFragment = {
  __typename: 'CaseDocument'
  id: string
  type: CaseDocumentType
  otherType: string | null
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type CaseDetailCustomerDocumentFragment = {
  __typename: 'CustomerDocument'
  id: string
  type: CustomerDocumentType
  otherType: string | null
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

type CaseDetailDocument_CaseDocument_Fragment = {
  __typename: 'CaseDocument'
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

type CaseDetailDocument_CustomerDocument_Fragment = {
  __typename: 'CustomerDocument'
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

type CaseDetailDocument_FinancialAccountDocument_Fragment = {
  __typename: 'FinancialAccountDocument'
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

type CaseDetailDocument_OrderDocument_Fragment = {
  __typename: 'OrderDocument'
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

type CaseDetailDocument_TicketDocument_Fragment = {
  __typename: 'TicketDocument'
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type CaseDetailDocumentFragment =
  | CaseDetailDocument_CaseDocument_Fragment
  | CaseDetailDocument_CustomerDocument_Fragment
  | CaseDetailDocument_FinancialAccountDocument_Fragment
  | CaseDetailDocument_OrderDocument_Fragment
  | CaseDetailDocument_TicketDocument_Fragment

export type CaseDetailFinancialAccountDocumentFragment = {
  __typename: 'FinancialAccountDocument'
  id: string
  type: FinancialAccountDocumentType
  otherType: string | null
  note: string | null
  createdAt: DateTimeString
  financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type CaseDetailOrderFragment = {
  __typename: 'Order'
  id: string
  code: string
  status: OrderStatus
  paymentStatus: OrderPaymentStatus
  feeRate: number
  adjustedFeeRate: number | null
  durationInMonths: number
  documentBookCode: string | null
  startAt: DateTimeString | null
  createdAt: DateTimeString
  businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
  productPackage: {
    __typename: 'ProductPackage'
    id: string
    name: string
    documentTypes: Array<{
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }>
  }
  fee: { __typename: 'Money'; amount: number; currency: string } | null
  estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
  paidAmount: { __typename: 'Money'; amount: number; currency: string }
  remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
  additionalPaymentOrderAdjustmentRange: {
    __typename: 'MoneyRange'
    min: { __typename: 'Money'; amount: number; currency: string }
    max: { __typename: 'Money'; amount: number; currency: string }
  } | null
  currentProgress: {
    __typename: 'OrderCurrentProgress'
    nextState: OrderStatus
    isValid: boolean
    blockers: Array<
      | {
          __typename: 'OrderStateProgressBlockerCaseDocumentType'
          caseDocumentType: CaseDocumentType
        }
      | {
          __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
          case: { __typename: 'Case'; id: string }
        }
      | {
          __typename: 'OrderStateProgressBlockerCustomerDocumentType'
          customerDocumentType: CustomerDocumentType
        }
      | {
          __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
          financialAccountDocumentType: FinancialAccountDocumentType
        }
      | {
          __typename: 'OrderStateProgressBlockerGenerateDocumentType'
          documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
        }
      | {
          __typename: 'OrderStateProgressBlockerOrderDocumentType'
          documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
        }
      | {
          __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
          orderPaymentStatus: Array<OrderPaymentStatus>
        }
      | {
          __typename: 'OrderStateProgressBlockerValidationEntity'
          orderValidationEntity: OrderValidationEntity
          entityReferenceCodes: Array<string>
        }
    >
  } | null
  payments: Array<{
    __typename: 'OrderPayment'
    id: string
    paidAt: DateTimeString
    paidAmount: { __typename: 'Money'; amount: number; currency: string }
  }>
  documents: {
    __typename: 'OrderDocumentConnection'
    totalCount: number
    edges: Array<{
      __typename: 'OrderDocumentEdge'
      node: {
        __typename: 'OrderDocument'
        id: string
        generated: boolean
        rendered: boolean
        signed: boolean
        canBeSigned: boolean
        note: string | null
        createdAt: DateTimeString
        documentType: {
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
        }
        signers: Array<{
          __typename: 'OrderDocumentSignerModel'
          role: string
          name: string | null
          email: string | null
          isCustomer: boolean
        }>
        activeEsignRequest: {
          __typename: 'OrderDocumentEsignRequest'
          id: string
          status: EsignRequestStatus
          signerName: string
          signerEmail: string
          createdAt: DateTimeString
          signedOrderDocument: {
            __typename: 'OrderDocument'
            id: string
            file: { __typename: 'RemoteFile'; url: string | null }
          } | null
        } | null
        file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  events: {
    __typename: 'OrderEventConnection'
    edges: Array<{
      __typename: 'OrderEventEdge'
      node: {
        __typename: 'OrderEvent'
        id: string
        type: OrderEventType
        date: DateTimeString
        parameters: any
        documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  financialAccounts: Array<{
    __typename: 'OrderFinancialAccount'
    id: string
    financialAccount: { __typename: 'FinancialAccount'; id: string }
    outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
  }>
  caseProducts: Array<{
    __typename: 'CaseProduct'
    id: string
    isActive: boolean
    product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
  }>
}

export type CaseDetailOrderDocumentFragment = {
  __typename: 'OrderDocument'
  id: string
  generated: boolean
  rendered: boolean
  signed: boolean
  canBeSigned: boolean
  note: string | null
  createdAt: DateTimeString
  documentType: {
    __typename: 'OrderDocumentType'
    id: string
    name: string
    isGeneratable: boolean
    requiresSignature: boolean
  }
  signers: Array<{
    __typename: 'OrderDocumentSignerModel'
    role: string
    name: string | null
    email: string | null
    isCustomer: boolean
  }>
  activeEsignRequest: {
    __typename: 'OrderDocumentEsignRequest'
    id: string
    status: EsignRequestStatus
    signerName: string
    signerEmail: string
    createdAt: DateTimeString
    signedOrderDocument: {
      __typename: 'OrderDocument'
      id: string
      file: { __typename: 'RemoteFile'; url: string | null }
    } | null
  } | null
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type CaseDetailOrderDocumentEsignRequestFragment = {
  __typename: 'OrderDocumentEsignRequest'
  id: string
  status: EsignRequestStatus
  signerName: string
  signerEmail: string
  createdAt: DateTimeString
  signedOrderDocument: {
    __typename: 'OrderDocument'
    id: string
    file: { __typename: 'RemoteFile'; url: string | null }
  } | null
}

export type CaseDetailOrderHistoryFragment = {
  __typename: 'Order'
  id: string
  code: string
  status: OrderStatus
  feeRate: number
  createdAt: DateTimeString
  documentBookCode: string | null
  invoiceBookCode: string | null
  startAt: DateTimeString | null
  durationInMonths: number
  paymentStatus: OrderPaymentStatus
  businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
  productPackage: {
    __typename: 'ProductPackage'
    id: string
    name: string
    documentTypes: Array<{
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }>
  }
  fee: { __typename: 'Money'; amount: number; currency: string } | null
  estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
  paidAmount: { __typename: 'Money'; amount: number; currency: string }
  payments: Array<{
    __typename: 'OrderPayment'
    id: string
    paidAt: DateTimeString
    paidAmount: { __typename: 'Money'; amount: number; currency: string }
  }>
  documents: {
    __typename: 'OrderDocumentConnection'
    totalCount: number
    edges: Array<{
      __typename: 'OrderDocumentEdge'
      node: {
        __typename: 'OrderDocument'
        id: string
        generated: boolean
        rendered: boolean
        signed: boolean
        canBeSigned: boolean
        note: string | null
        createdAt: DateTimeString
        documentType: {
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
        }
        signers: Array<{
          __typename: 'OrderDocumentSignerModel'
          role: string
          name: string | null
          email: string | null
          isCustomer: boolean
        }>
        activeEsignRequest: {
          __typename: 'OrderDocumentEsignRequest'
          id: string
          status: EsignRequestStatus
          signerName: string
          signerEmail: string
          createdAt: DateTimeString
          signedOrderDocument: {
            __typename: 'OrderDocument'
            id: string
            file: { __typename: 'RemoteFile'; url: string | null }
          } | null
        } | null
        file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  events: {
    __typename: 'OrderEventConnection'
    edges: Array<{
      __typename: 'OrderEventEdge'
      node: {
        __typename: 'OrderEvent'
        id: string
        type: OrderEventType
        date: DateTimeString
        parameters: any
        documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  financialAccounts: Array<{
    __typename: 'OrderFinancialAccount'
    id: string
    financialAccount: { __typename: 'FinancialAccount'; id: string }
    outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
  }>
  caseProducts: Array<{
    __typename: 'CaseProduct'
    id: string
    isActive: boolean
    product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
  }>
}

export type CaseDetailProductFragment = {
  __typename: 'CaseProduct'
  id: string
  createdAt: DateTimeString
  startAt: DateTimeString
  endAt: DateTimeString
  isActive: boolean
  product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
  financialAccounts: Array<{
    __typename: 'CaseProductFinancialAccount'
    id: string
    financialAccount: { __typename: 'FinancialAccount'; id: string }
  }>
}

export type CaseDetailProductPackageFragment = {
  __typename: 'ProductPackage'
  id: string
  name: string
  minimumFeeRate: number
  defaultFeeRate: number
  defaultDurationMonths: number
  minimumFee: { __typename: 'Money'; amount: number; currency: string }
}

export type CaseEventFragment = {
  __typename: 'CaseEvent'
  id: string
  type: CaseEventType
  date: DateTimeString
  parameters: any
  actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
  actionType: { __typename: 'CaseActionType'; name: string } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
  user: { __typename: 'User'; id: string; displayName: string; email: string } | null
  financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
  fromCustomer: { __typename: 'CaseEventCustomer'; id: string; code: string; name: string } | null
  toCustomer: { __typename: 'CaseEventCustomer'; id: string; code: string; name: string } | null
}

export type CaseFinancialAccountFragment = {
  __typename: 'FinancialAccount'
  fullName: string | null
  id: string
  code: string
  createdAt: DateTimeString
  lastDueDate: DateTimeString | null
  dpd: number | null
  index: number | null
  status: FinancialAccountStatus
  providerReference: string | null
  ptpDate: DateTimeString | null
  documents: {
    __typename: 'FinancialAccountDocumentConnection'
    totalCount: number
    edges: Array<{
      __typename: 'FinancialAccountDocumentEdge'
      node: {
        __typename: 'FinancialAccountDocument'
        id: string
        type: FinancialAccountDocumentType
        otherType: string | null
        note: string | null
        createdAt: DateTimeString
        financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
        file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  tickets: {
    __typename: 'FinancialAccountTicketConnection'
    totalCount: number
    edges: Array<{
      __typename: 'FinancialAccountTicketEdge'
      node:
        | {
            __typename: 'MediationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
              } | null
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            lastEventConnection: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: { __typename: 'TicketEvent'; date: DateTimeString }
              }>
            }
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            lastEventConnection: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: { __typename: 'TicketEvent'; date: DateTimeString }
              }>
            }
          }
    }>
  }
  type: { __typename: 'FinancialAccountType'; id: string; name: string }
  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
  principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
  provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    role: string
    phoneNumberNote: string | null
    emailAddress: string | null
    emailAddressNote: string | null
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    } | null
  } | null
  registeredContactPhone: {
    __typename: 'CustomerContactPhone'
    id: string
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    }
  } | null
}

export type CaseListFragment = {
  __typename: 'Case'
  id: string
  createdAt: DateTimeString
  code: string
  stage: CaseStage
  status: CaseStatus
  flow: CaseFlow | null
  closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
  pendingOrder: {
    __typename: 'Order'
    id: string
    status: OrderStatus
    paymentStatus: OrderPaymentStatus
    fee: { __typename: 'Money'; amount: number; currency: string } | null
  } | null
  products: Array<{
    __typename: 'CaseProduct'
    id: string
    isActive: boolean
    product: { __typename: 'Product'; name: string; productType: ProductType }
  }>
  customer: { __typename: 'Customer'; id: string; code: string; name: string }
  assignedToUser: { __typename: 'User'; displayName: string } | null
  tags: Array<{ __typename: 'CaseTag'; id: string; name: string; color: string | null }>
  type: { __typename: 'CaseType'; id: string; name: string }
}

export type CaseReminderBriefFragment = {
  __typename: 'CaseReminder'
  id: string
  dueAt: DateTimeString
  note: string | null
  status: CaseReminderStatus
  source: CaseReminderSource
  isOverdue: boolean
  outsideBusinessHours: boolean | null
  type: { __typename: 'CaseActionType'; id: string; name: string } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type CaseReminderRowFragment = {
  __typename: 'CaseReminder'
  id: string
  dueAt: DateTimeString
  note: string | null
  status: CaseReminderStatus
  source: CaseReminderSource
  isOverdue: boolean
  outsideBusinessHours: boolean | null
  case: {
    __typename: 'Case'
    id: string
    code: string
    customer: { __typename: 'Customer'; id: string; name: string }
    assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
  } | null
  type: { __typename: 'CaseActionType'; id: string; name: string } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type FinancialAccountTypeOptionFragment = {
  __typename: 'FinancialAccountType'
  id: string
  name: string
}

export type OrderEventFragment = {
  __typename: 'OrderEvent'
  id: string
  type: OrderEventType
  date: DateTimeString
  parameters: any
  documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

type OrderStateBlocker_OrderStateProgressBlockerCaseDocumentType_Fragment = {
  __typename: 'OrderStateProgressBlockerCaseDocumentType'
  caseDocumentType: CaseDocumentType
}

type OrderStateBlocker_OrderStateProgressBlockerCaseIdentityCheck_Fragment = {
  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
  case: { __typename: 'Case'; id: string }
}

type OrderStateBlocker_OrderStateProgressBlockerCustomerDocumentType_Fragment = {
  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
  customerDocumentType: CustomerDocumentType
}

type OrderStateBlocker_OrderStateProgressBlockerFinancialAccountDocumentType_Fragment = {
  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
  financialAccountDocumentType: FinancialAccountDocumentType
}

type OrderStateBlocker_OrderStateProgressBlockerGenerateDocumentType_Fragment = {
  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
}

type OrderStateBlocker_OrderStateProgressBlockerOrderDocumentType_Fragment = {
  __typename: 'OrderStateProgressBlockerOrderDocumentType'
  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
}

type OrderStateBlocker_OrderStateProgressBlockerOrderPaymentStatus_Fragment = {
  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
  orderPaymentStatus: Array<OrderPaymentStatus>
}

type OrderStateBlocker_OrderStateProgressBlockerValidationEntity_Fragment = {
  __typename: 'OrderStateProgressBlockerValidationEntity'
  orderValidationEntity: OrderValidationEntity
  entityReferenceCodes: Array<string>
}

export type OrderStateBlockerFragment =
  | OrderStateBlocker_OrderStateProgressBlockerCaseDocumentType_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerCaseIdentityCheck_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerCustomerDocumentType_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerFinancialAccountDocumentType_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerGenerateDocumentType_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerOrderDocumentType_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerOrderPaymentStatus_Fragment
  | OrderStateBlocker_OrderStateProgressBlockerValidationEntity_Fragment

export type CaseActionCreateMutationVariables = Exact<{
  caseId: string | number
  input: CaseActionCreateInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseActionCreateMutation = {
  __typename: 'Mutation'
  caseActionCreate: {
    __typename: 'CaseActionCreate'
    caseAction: {
      __typename: 'CaseAction'
      id: string
      result: { __typename: 'CaseActionResult'; id: string; name: string }
      type: { __typename: 'CaseActionType'; id: string; name: string }
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type CaseActionDeleteMutationVariables = Exact<{
  id: string | number
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseActionDeleteMutation = {
  __typename: 'Mutation'
  caseActionDelete: {
    __typename: 'CaseActionDelete'
    caseAction: {
      __typename: 'CaseAction'
      result: { __typename: 'CaseActionResult'; id: string; name: string }
      type: { __typename: 'CaseActionType'; id: string; name: string }
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type CaseActionUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseActionUpdateInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseActionUpdateMutation = {
  __typename: 'Mutation'
  caseActionUpdate: {
    __typename: 'CaseActionUpdate'
    caseAction: {
      __typename: 'CaseAction'
      result: { __typename: 'CaseActionResult'; id: string; name: string }
      type: { __typename: 'CaseActionType'; id: string; name: string }
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type CaseAddressAssignMutationVariables = Exact<{
  id: string | number
  input: CaseAddressAssignInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseAddressAssignMutation = {
  __typename: 'Mutation'
  caseAddressAssign: {
    __typename: 'CaseAddressAssign'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseAddressAssignNewMutationVariables = Exact<{
  id: string | number
  input: CaseAddressAssignNewInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseAddressAssignNewMutation = {
  __typename: 'Mutation'
  caseAddressAssignNew: {
    __typename: 'CaseAddressAssignNew'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseAssignMutationVariables = Exact<{
  id: string | number
  input: CaseAssignInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseAssignMutation = {
  __typename: 'Mutation'
  caseAssign: {
    __typename: 'CaseAssign'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseBulkAssignMutationVariables = Exact<{
  input: CaseBulkAssignInput
}>

export type CaseBulkAssignMutation = {
  __typename: 'Mutation'
  caseBulkAssign: {
    __typename: 'CaseBulkAssign'
    cases: Array<{
      __typename: 'Case'
      id: string
      status: CaseStatus
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
    }>
  }
}

export type CaseBulkReopenMutationVariables = Exact<{
  input: CaseBulkReopenInput
}>

export type CaseBulkReopenMutation = {
  __typename: 'Mutation'
  caseBulkReopen: {
    __typename: 'CaseBulkReopen'
    cases: Array<{ __typename: 'Case'; id: string; status: CaseStatus }>
  }
}

export type CaseCloseMutationVariables = Exact<{
  id: string | number
  input: CaseCloseInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseCloseMutation = {
  __typename: 'Mutation'
  caseClose: {
    __typename: 'CaseClose'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseCreateMutationVariables = Exact<{
  input: CaseCreateInput
}>

export type CaseCreateMutation = {
  __typename: 'Mutation'
  caseCreate: { __typename: 'CaseCreate'; case: { __typename: 'Case'; id: string; code: string } }
}

export type CaseDocumentCreateMutationVariables = Exact<{
  caseId: string | number
  input: CaseDocumentCreateInput
}>

export type CaseDocumentCreateMutation = {
  __typename: 'Mutation'
  caseDocumentCreate: {
    __typename: 'CaseDocumentCreate'
    caseDocument: {
      __typename: 'CaseDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      case: {
        __typename: 'Case'
        id: string
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
      }
    }
  }
}

export type CaseDocumentDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CaseDocumentDeleteMutation = {
  __typename: 'Mutation'
  caseDocumentDelete: {
    __typename: 'CaseDocumentDelete'
    caseDocument: {
      __typename: 'CaseDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      case: {
        __typename: 'Case'
        id: string
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
      }
    }
  }
}

export type CaseDocumentUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseDocumentUpdateInput
}>

export type CaseDocumentUpdateMutation = {
  __typename: 'Mutation'
  caseDocumentUpdate: {
    __typename: 'CaseDocumentUpdate'
    caseDocument: {
      __typename: 'CaseDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      case: {
        __typename: 'Case'
        id: string
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
      }
    }
  }
}

export type CaseEmailAssignMutationVariables = Exact<{
  id: string | number
  input: CaseEmailAssignInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseEmailAssignMutation = {
  __typename: 'Mutation'
  caseEmailAssign: {
    __typename: 'CaseEmailAssign'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseEmailAssignNewMutationVariables = Exact<{
  id: string | number
  input: CaseEmailAssignNewInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseEmailAssignNewMutation = {
  __typename: 'Mutation'
  caseEmailAssignNew: {
    __typename: 'CaseEmailAssignNew'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseIdentityConfirmMutationVariables = Exact<{
  id: string | number
  input: CaseIdentityConfirmInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseIdentityConfirmMutation = {
  __typename: 'Mutation'
  caseIdentityConfirm: {
    __typename: 'CaseIdentityConfirm'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseMoveToNewCustomerMutationVariables = Exact<{
  id: string | number
  input: CaseMoveToNewCustomerInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseMoveToNewCustomerMutation = {
  __typename: 'Mutation'
  caseMoveToNewCustomer: {
    __typename: 'CaseMoveToNewCustomer'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
    customer: { __typename: 'Customer'; id: string; name: string }
    previousCustomer: { __typename: 'Customer'; id: string; name: string }
  }
}

export type CaseNoteAddMutationVariables = Exact<{
  id: string | number
  input: CaseNoteAddInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseNoteAddMutation = {
  __typename: 'Mutation'
  caseNoteAdd: {
    __typename: 'CaseNoteAdd'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CasePhoneAssignMutationVariables = Exact<{
  id: string | number
  input: CasePhoneAssignInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CasePhoneAssignMutation = {
  __typename: 'Mutation'
  casePhoneAssign: {
    __typename: 'CasePhoneAssign'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CasePhoneAssignNewMutationVariables = Exact<{
  id: string | number
  input: CasePhoneAssignNewInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CasePhoneAssignNewMutation = {
  __typename: 'Mutation'
  casePhoneAssignNew: {
    __typename: 'CasePhoneAssignNew'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseReminderCancelMutationVariables = Exact<{
  id: string | number
}>

export type CaseReminderCancelMutation = {
  __typename: 'Mutation'
  caseReminderCancel: {
    __typename: 'CaseReminderCancel'
    caseReminder: {
      __typename: 'CaseReminder'
      id: string
      dueAt: DateTimeString
      note: string | null
      status: CaseReminderStatus
      source: CaseReminderSource
      isOverdue: boolean
      outsideBusinessHours: boolean | null
      type: { __typename: 'CaseActionType'; id: string; name: string } | null
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
  }
}

export type CaseReminderCompleteMutationVariables = Exact<{
  id: string | number
  actionId?: string | number | null | undefined
}>

export type CaseReminderCompleteMutation = {
  __typename: 'Mutation'
  caseReminderComplete: {
    __typename: 'CaseReminderComplete'
    caseReminder: {
      __typename: 'CaseReminder'
      id: string
      dueAt: DateTimeString
      note: string | null
      status: CaseReminderStatus
      source: CaseReminderSource
      isOverdue: boolean
      outsideBusinessHours: boolean | null
      type: { __typename: 'CaseActionType'; id: string; name: string } | null
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
  }
}

export type CaseReminderCreateMutationVariables = Exact<{
  caseId: string | number
  input: CaseReminderCreateInput
}>

export type CaseReminderCreateMutation = {
  __typename: 'Mutation'
  caseReminderCreate: {
    __typename: 'CaseReminderCreate'
    caseReminder: {
      __typename: 'CaseReminder'
      id: string
      dueAt: DateTimeString
      note: string | null
      status: CaseReminderStatus
      source: CaseReminderSource
      isOverdue: boolean
      outsideBusinessHours: boolean | null
      type: { __typename: 'CaseActionType'; id: string; name: string } | null
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
  }
}

export type CaseReminderUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseReminderUpdateInput
}>

export type CaseReminderUpdateMutation = {
  __typename: 'Mutation'
  caseReminderUpdate: {
    __typename: 'CaseReminderUpdate'
    caseReminder: {
      __typename: 'CaseReminder'
      id: string
      dueAt: DateTimeString
      note: string | null
      status: CaseReminderStatus
      source: CaseReminderSource
      isOverdue: boolean
      outsideBusinessHours: boolean | null
      type: { __typename: 'CaseActionType'; id: string; name: string } | null
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
  }
}

export type CaseReopenMutationVariables = Exact<{
  id: string | number
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseReopenMutation = {
  __typename: 'Mutation'
  caseReopen: {
    __typename: 'CaseReopen'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseTagAttachMutationVariables = Exact<{
  id: string | number
  input: CaseTagAttachInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseTagAttachMutation = {
  __typename: 'Mutation'
  caseTagAttach: {
    __typename: 'CaseTagAttach'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type CaseTagDetachMutationVariables = Exact<{
  id: string | number
  input: CaseTagDetachInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseTagDetachMutation = {
  __typename: 'Mutation'
  caseTagDetach: {
    __typename: 'CaseTagDetach'
    case: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type FinancialAccountCreateMutationVariables = Exact<{
  caseId: string | number
  input: FinancialAccountCreateInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type FinancialAccountCreateMutation = {
  __typename: 'Mutation'
  financialAccountCreate: {
    __typename: 'FinancialAccountCreate'
    financialAccount: {
      __typename: 'FinancialAccount'
      id: string
      code: string
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type FinancialAccountDeleteMutationVariables = Exact<{
  id: string | number
  PERMISSION_MANAGE_CASES?: boolean
}>

export type FinancialAccountDeleteMutation = {
  __typename: 'Mutation'
  financialAccountDelete: {
    __typename: 'FinancialAccountDelete'
    financialAccount: {
      __typename: 'FinancialAccount'
      id: string
      code: string
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type FinancialAccountDocumentCreateMutationVariables = Exact<{
  financialAccountId: string | number
  input: FinancialAccountDocumentCreateInput
}>

export type FinancialAccountDocumentCreateMutation = {
  __typename: 'Mutation'
  financialAccountDocumentCreate: {
    __typename: 'FinancialAccountDocumentCreate'
    financialAccountDocument: {
      __typename: 'FinancialAccountDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      financialAccount: {
        __typename: 'FinancialAccount'
        id: string
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        case: {
          __typename: 'Case'
          id: string
          events: {
            __typename: 'CaseEventConnection'
            edges: Array<{
              __typename: 'CaseEventEdge'
              node: {
                __typename: 'CaseEvent'
                id: string
                type: CaseEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'CaseActionResult'
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'CaseActionType'; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string; email: string } | null
                financialAccount: {
                  __typename: 'FinancialAccount'
                  id: string
                  name: string
                } | null
                fromCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
                toCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
              }
            }>
          }
        }
      }
    }
  }
}

export type FinancialAccountDocumentDeleteMutationVariables = Exact<{
  id: string | number
}>

export type FinancialAccountDocumentDeleteMutation = {
  __typename: 'Mutation'
  financialAccountDocumentDelete: {
    __typename: 'FinancialAccountDocumentDelete'
    financialAccountDocument: {
      __typename: 'FinancialAccountDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      financialAccount: {
        __typename: 'FinancialAccount'
        id: string
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        case: {
          __typename: 'Case'
          id: string
          events: {
            __typename: 'CaseEventConnection'
            edges: Array<{
              __typename: 'CaseEventEdge'
              node: {
                __typename: 'CaseEvent'
                id: string
                type: CaseEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'CaseActionResult'
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'CaseActionType'; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string; email: string } | null
                financialAccount: {
                  __typename: 'FinancialAccount'
                  id: string
                  name: string
                } | null
                fromCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
                toCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
              }
            }>
          }
        }
      }
    }
  }
}

export type FinancialAccountDocumentUpdateMutationVariables = Exact<{
  id: string | number
  input: FinancialAccountDocumentUpdateInput
}>

export type FinancialAccountDocumentUpdateMutation = {
  __typename: 'Mutation'
  financialAccountDocumentUpdate: {
    __typename: 'FinancialAccountDocumentUpdate'
    financialAccountDocument: {
      __typename: 'FinancialAccountDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      financialAccount: {
        __typename: 'FinancialAccount'
        id: string
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        case: {
          __typename: 'Case'
          id: string
          events: {
            __typename: 'CaseEventConnection'
            edges: Array<{
              __typename: 'CaseEventEdge'
              node: {
                __typename: 'CaseEvent'
                id: string
                type: CaseEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'CaseActionResult'
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'CaseActionType'; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string; email: string } | null
                financialAccount: {
                  __typename: 'FinancialAccount'
                  id: string
                  name: string
                } | null
                fromCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
                toCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
              }
            }>
          }
        }
      }
    }
  }
}

export type FinancialAccountSetProviderContactMutationVariables = Exact<{
  id: string | number
  input: FinancialAccountSetProviderContactInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type FinancialAccountSetProviderContactMutation = {
  __typename: 'Mutation'
  financialAccountSetProviderContact: {
    __typename: 'FinancialAccountSetProviderContact'
    financialAccount: {
      __typename: 'FinancialAccount'
      name: string
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type FinancialAccountUpdateMutationVariables = Exact<{
  id: string | number
  input: FinancialAccountUpdateInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type FinancialAccountUpdateMutation = {
  __typename: 'Mutation'
  financialAccountUpdate: {
    __typename: 'FinancialAccountUpdate'
    financialAccount: {
      __typename: 'FinancialAccount'
      id: string
      code: string
      case: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        closedAt: DateTimeString | null
        accountNodeAccess: AccountNodeAccess | null
        updatedAt: DateTimeString
        moveToNewCustomerBlocker: CaseScopeCode | null
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        assignedVia: CaseAssignedVia | null
        importClassification: CaseImportClassification | null
        importSubmittedName: string | null
        identityCheckMergedName: string | null
        locked: boolean
        conversations: Array<{
          __typename: 'MessagingConversation'
          id: string
          customerWaId: string
          customerName: string | null
          lifecycle: MessagingConversationLifecycle
          lastActivityAt: DateTimeString
          lastInboundSeq: number | null
          needsAttention: boolean
          owesReply: boolean
          dismissed: boolean
          safetyFlagged: boolean
          ownership: MessagingConversationOwnership
          windowExpiresAt: DateTimeString | null
          customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
          assignee: { __typename: 'User'; id: string; displayName: string } | null
          linkedCases: Array<{
            __typename: 'CaseBrief'
            id: string
            code: string
            stage: CaseStage
            type: { __typename: 'CaseType'; id: string; name: string }
          }>
          channel: {
            __typename: 'MessagingChannel'
            id: string
            displayNumber: string
            defaultPlaybookId: string | null
            defaultOwner: MessagingConversationOwnership | null
            defaultOversightScenarioId: string | null
            supportPlaybookId: string | null
            isDefaultTransactional: boolean
            phoneNumberId: string
            provider: string
            purpose: MessagingChannelPurpose
            status: MessagingChannelStatus
            wabaId: string | null
            metaAppId: string | null
            caseType: { __typename: 'CaseType'; id: string; name: string } | null
          } | null
          lastMessage: {
            __typename: 'MessagingMessagePreview'
            createdAt: DateTimeString
            direction: MessagingMessageDirection
            seq: number
            source: MessagingMessageSource | null
            state: MessagingMessageState
            textPreview: string | null
            type: MessagingMessageType
            sender: { __typename: 'User'; id: string; displayName: string } | null
          } | null
        }>
        tags: Array<{
          __typename: 'CaseTag'
          id: string
          name: string
          color: string | null
          value: string
        }>
        sharedNumberThread: {
          __typename: 'CaseSharedNumberThread'
          conversationId: string | null
          case: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            customer: { __typename: 'Customer'; id: string; name: string }
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          } | null
        } | null
        type: {
          __typename: 'CaseType'
          id: string
          name: string
          productPackages: Array<{
            __typename: 'ProductPackage'
            name: string
            minimumFeeRate: number
            minimumFee: { __typename: 'Money'; amount: number; currency: string }
          }>
        }
        estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
        importClassificationReason?: {
          __typename: 'CaseImportClassificationReason'
          matcherVersion: number
          clusterSize: number
          guardrailMaxCluster: number | null
          cell: string | null
          emailRelation: string | null
          nameRelation: string | null
          evidenceScore: number | null
          nameCheck: string | null
          sharedNumber: boolean | null
          originConversationCustomer: boolean | null
          submittedName: string | null
        } | null
        assignmentReason?: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          reservation: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        source: {
          __typename: 'CaseSource'
          kind: string | null
          type: string | null
          medium: string | null
          campaign: string | null
          tool: string | null
          data: any
          optInSource: string | null
          optInAt: DateTimeString | null
        } | null
        behaviour: {
          __typename: 'CaseBehaviour'
          campaignResolverId: string | null
          flowChangeReason: CaseFlowChangeReason | null
          campaign: { __typename: 'Campaign'; id: string; name: string } | null
          campaignResolver: {
            __typename: 'CampaignResolver'
            id: string
            name: string
            priority: number
          } | null
        } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        actions: Array<{
          __typename: 'CaseAction'
          id: string
          comment: string
          date: DateTimeString
          createdAt: DateTimeString
          type: { __typename: 'CaseActionType'; id: string; name: string }
          result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }>
        financialAccounts: Array<{
          __typename: 'FinancialAccount'
          fullName: string | null
          id: string
          code: string
          createdAt: DateTimeString
          lastDueDate: DateTimeString | null
          dpd: number | null
          index: number | null
          status: FinancialAccountStatus
          providerReference: string | null
          ptpDate: DateTimeString | null
          documents: {
            __typename: 'FinancialAccountDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountDocumentEdge'
              node: {
                __typename: 'FinancialAccountDocument'
                id: string
                type: FinancialAccountDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          tickets: {
            __typename: 'FinancialAccountTicketConnection'
            totalCount: number
            edges: Array<{
              __typename: 'FinancialAccountTicketEdge'
              node:
                | {
                    __typename: 'MediationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    providerContact: {
                      __typename: 'FinancialProviderContact'
                      id: string
                      name: string
                      phoneNumber: {
                        __typename: 'PhoneNumber'
                        countryCode: string | null
                        displayNumber: string
                        international: string
                      } | null
                    }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
                | {
                    __typename: 'NegotiationTicket'
                    id: string
                    code: string
                    createdAt: DateTimeString
                    status: TicketStatus
                    type: TicketType
                    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                    closeReason: {
                      __typename: 'TicketCloseReason'
                      name: string
                      success: boolean
                    } | null
                    lastEventConnection: {
                      __typename: 'TicketEventConnection'
                      edges: Array<{
                        __typename: 'TicketEventEdge'
                        node: { __typename: 'TicketEvent'; date: DateTimeString }
                      }>
                    }
                  }
            }>
          }
          type: { __typename: 'FinancialAccountType'; id: string; name: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          providerContact: {
            __typename: 'FinancialProviderContact'
            id: string
            name: string
            role: string
            phoneNumberNote: string | null
            emailAddress: string | null
            emailAddressNote: string | null
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            } | null
          } | null
          registeredContactPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
        }>
        documents: {
          __typename: 'CaseDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseDocumentEdge'
            node: {
              __typename: 'CaseDocument'
              id: string
              type: CaseDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CaseEventConnection'
          edges: Array<{
            __typename: 'CaseEventEdge'
            node: {
              __typename: 'CaseEvent'
              id: string
              type: CaseEventType
              date: DateTimeString
              parameters: any
              actionResult: {
                __typename: 'CaseActionResult'
                name: string
                success: boolean
              } | null
              actionType: { __typename: 'CaseActionType'; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
              user: { __typename: 'User'; id: string; displayName: string; email: string } | null
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
              fromCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
              toCustomer: {
                __typename: 'CaseEventCustomer'
                id: string
                code: string
                name: string
              } | null
            }
          }>
        }
        customer: {
          __typename: 'Customer'
          id: string
          code: string
          name: string
          accountNodeAccess: AccountNodeAccess | null
          portalEnabledAt: DateTimeString | null
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          occupation: string | null
          residentialStatus: CustomerResidentialStatus | null
          jobTitle: string | null
          employmentStatus: CustomerEmploymentStatus | null
          companyType: CustomerCompanyType | null
          salaryRange: CustomerSalaryRange | null
          referralSource: CustomerReferralSource | null
          otherDebtTypes: Array<CustomerOtherDebtType>
          otherDebtTypeOther: string | null
          note: string | null
          domicile: string | null
          defaultPhone: {
            __typename: 'CustomerContactPhone'
            id: string
            phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
          } | null
          defaultEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
          } | null
          addresses: Array<{
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          }>
          contactPhones: Array<{
            __typename: 'CustomerContactPhone'
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          }>
          contactEmails: Array<{
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          }>
          cases: {
            __typename: 'CaseConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseEdge'
              node: {
                __typename: 'Case'
                id: string
                code: string
                stage: CaseStage
                status: CaseStatus
                flow: CaseFlow | null
                updatedAt: DateTimeString
                type: { __typename: 'CaseType'; id: string; name: string }
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              }
            }>
          }
          documents: {
            __typename: 'CustomerDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CustomerDocumentEdge'
              node: {
                __typename: 'CustomerDocument'
                id: string
                type: CustomerDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CustomerEventConnection'
            edges: Array<{
              __typename: 'CustomerEventEdge'
              node: {
                __typename: 'CustomerEvent'
                id: string
                type: CustomerEventType
                date: DateTimeString
                parameters: any
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          domicileRegency: {
            __typename: 'Regency'
            id: string
            name: string
            kind: RegencyKind
          } | null
        }
        address: {
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        } | null
        contactEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        } | null
        contactPhone: {
          __typename: 'CustomerContactPhone'
          verifiedAt: DateTimeString | null
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
        assignedToUser: {
          __typename: 'User'
          id: string
          email: string
          displayName: string
        } | null
        assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
        totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
        orderCreateOptions: {
          __typename: 'OrderCreateOption'
          availability: ProductPackageAvailability
        } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }
    }
  }
}

export type MediationTicketCreateMutationVariables = Exact<{
  financialAccountId: string | number
  input: MediationTicketCreateInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type MediationTicketCreateMutation = {
  __typename: 'Mutation'
  mediationTicketCreate: {
    __typename: 'MediationTicketCreate'
    mediationTicket: {
      __typename: 'MediationTicket'
      id: string
      status: TicketStatus
      financialAccount: {
        __typename: 'FinancialAccount'
        id: string
        case: {
          __typename: 'Case'
          id: string
          code: string
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          accountNodeAccess: AccountNodeAccess | null
          updatedAt: DateTimeString
          moveToNewCustomerBlocker: CaseScopeCode | null
          status: CaseStatus
          stage: CaseStage
          flow: CaseFlow | null
          assignedVia: CaseAssignedVia | null
          importClassification: CaseImportClassification | null
          importSubmittedName: string | null
          identityCheckMergedName: string | null
          locked: boolean
          conversations: Array<{
            __typename: 'MessagingConversation'
            id: string
            customerWaId: string
            customerName: string | null
            lifecycle: MessagingConversationLifecycle
            lastActivityAt: DateTimeString
            lastInboundSeq: number | null
            needsAttention: boolean
            owesReply: boolean
            dismissed: boolean
            safetyFlagged: boolean
            ownership: MessagingConversationOwnership
            windowExpiresAt: DateTimeString | null
            customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
            assignee: { __typename: 'User'; id: string; displayName: string } | null
            linkedCases: Array<{
              __typename: 'CaseBrief'
              id: string
              code: string
              stage: CaseStage
              type: { __typename: 'CaseType'; id: string; name: string }
            }>
            channel: {
              __typename: 'MessagingChannel'
              id: string
              displayNumber: string
              defaultPlaybookId: string | null
              defaultOwner: MessagingConversationOwnership | null
              defaultOversightScenarioId: string | null
              supportPlaybookId: string | null
              isDefaultTransactional: boolean
              phoneNumberId: string
              provider: string
              purpose: MessagingChannelPurpose
              status: MessagingChannelStatus
              wabaId: string | null
              metaAppId: string | null
              caseType: { __typename: 'CaseType'; id: string; name: string } | null
            } | null
            lastMessage: {
              __typename: 'MessagingMessagePreview'
              createdAt: DateTimeString
              direction: MessagingMessageDirection
              seq: number
              source: MessagingMessageSource | null
              state: MessagingMessageState
              textPreview: string | null
              type: MessagingMessageType
              sender: { __typename: 'User'; id: string; displayName: string } | null
            } | null
          }>
          tags: Array<{
            __typename: 'CaseTag'
            id: string
            name: string
            color: string | null
            value: string
          }>
          sharedNumberThread: {
            __typename: 'CaseSharedNumberThread'
            conversationId: string | null
            case: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              customer: { __typename: 'Customer'; id: string; name: string }
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            } | null
          } | null
          type: {
            __typename: 'CaseType'
            id: string
            name: string
            productPackages: Array<{
              __typename: 'ProductPackage'
              name: string
              minimumFeeRate: number
              minimumFee: { __typename: 'Money'; amount: number; currency: string }
            }>
          }
          estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
          importClassificationReason?: {
            __typename: 'CaseImportClassificationReason'
            matcherVersion: number
            clusterSize: number
            guardrailMaxCluster: number | null
            cell: string | null
            emailRelation: string | null
            nameRelation: string | null
            evidenceScore: number | null
            nameCheck: string | null
            sharedNumber: boolean | null
            originConversationCustomer: boolean | null
            submittedName: string | null
          } | null
          assignmentReason?: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            reservation: {
              __typename: 'CaseAssignmentReason'
              rung: CaseAssignedVia
              at: DateTimeString
              user: { __typename: 'User'; id: string; displayName: string } | null
              actor: { __typename: 'User'; id: string; displayName: string } | null
              similarCase: {
                __typename: 'CaseAssignmentSimilarCase'
                id: string
                code: string
                customerId: string
                holderKind: string
              } | null
              excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
            } | null
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          source: {
            __typename: 'CaseSource'
            kind: string | null
            type: string | null
            medium: string | null
            campaign: string | null
            tool: string | null
            data: any
            optInSource: string | null
            optInAt: DateTimeString | null
          } | null
          behaviour: {
            __typename: 'CaseBehaviour'
            campaignResolverId: string | null
            flowChangeReason: CaseFlowChangeReason | null
            campaign: { __typename: 'Campaign'; id: string; name: string } | null
            campaignResolver: {
              __typename: 'CampaignResolver'
              id: string
              name: string
              priority: number
            } | null
          } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          actions: Array<{
            __typename: 'CaseAction'
            id: string
            comment: string
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'CaseActionType'; id: string; name: string }
            result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          financialAccounts: Array<{
            __typename: 'FinancialAccount'
            fullName: string | null
            id: string
            code: string
            createdAt: DateTimeString
            lastDueDate: DateTimeString | null
            dpd: number | null
            index: number | null
            status: FinancialAccountStatus
            providerReference: string | null
            ptpDate: DateTimeString | null
            documents: {
              __typename: 'FinancialAccountDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'FinancialAccountDocumentEdge'
                node: {
                  __typename: 'FinancialAccountDocument'
                  id: string
                  type: FinancialAccountDocumentType
                  otherType: string | null
                  note: string | null
                  createdAt: DateTimeString
                  financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            tickets: {
              __typename: 'FinancialAccountTicketConnection'
              totalCount: number
              edges: Array<{
                __typename: 'FinancialAccountTicketEdge'
                node:
                  | {
                      __typename: 'MediationTicket'
                      id: string
                      code: string
                      createdAt: DateTimeString
                      status: TicketStatus
                      type: TicketType
                      providerContact: {
                        __typename: 'FinancialProviderContact'
                        id: string
                        name: string
                        phoneNumber: {
                          __typename: 'PhoneNumber'
                          countryCode: string | null
                          displayNumber: string
                          international: string
                        } | null
                      }
                      assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                      closeReason: {
                        __typename: 'TicketCloseReason'
                        name: string
                        success: boolean
                      } | null
                      lastEventConnection: {
                        __typename: 'TicketEventConnection'
                        edges: Array<{
                          __typename: 'TicketEventEdge'
                          node: { __typename: 'TicketEvent'; date: DateTimeString }
                        }>
                      }
                    }
                  | {
                      __typename: 'NegotiationTicket'
                      id: string
                      code: string
                      createdAt: DateTimeString
                      status: TicketStatus
                      type: TicketType
                      outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                      assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                      closeReason: {
                        __typename: 'TicketCloseReason'
                        name: string
                        success: boolean
                      } | null
                      lastEventConnection: {
                        __typename: 'TicketEventConnection'
                        edges: Array<{
                          __typename: 'TicketEventEdge'
                          node: { __typename: 'TicketEvent'; date: DateTimeString }
                        }>
                      }
                    }
              }>
            }
            type: { __typename: 'FinancialAccountType'; id: string; name: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
            principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
            provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              role: string
              phoneNumberNote: string | null
              emailAddress: string | null
              emailAddressNote: string | null
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
                national: string | null
              } | null
            } | null
            registeredContactPhone: {
              __typename: 'CustomerContactPhone'
              id: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
                national: string | null
              }
            } | null
          }>
          documents: {
            __typename: 'CaseDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseDocumentEdge'
              node: {
                __typename: 'CaseDocument'
                id: string
                type: CaseDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CaseEventConnection'
            edges: Array<{
              __typename: 'CaseEventEdge'
              node: {
                __typename: 'CaseEvent'
                id: string
                type: CaseEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'CaseActionResult'
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'CaseActionType'; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string; email: string } | null
                financialAccount: {
                  __typename: 'FinancialAccount'
                  id: string
                  name: string
                } | null
                fromCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
                toCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
              }
            }>
          }
          customer: {
            __typename: 'Customer'
            id: string
            code: string
            name: string
            accountNodeAccess: AccountNodeAccess | null
            portalEnabledAt: DateTimeString | null
            birthDate: DateString | null
            birthPlace: string | null
            gender: Gender | null
            governmentId: string | null
            educationLevel: CustomerEducationLevel | null
            marriageStatus: CustomerMaritalStatus | null
            occupation: string | null
            residentialStatus: CustomerResidentialStatus | null
            jobTitle: string | null
            employmentStatus: CustomerEmploymentStatus | null
            companyType: CustomerCompanyType | null
            salaryRange: CustomerSalaryRange | null
            referralSource: CustomerReferralSource | null
            otherDebtTypes: Array<CustomerOtherDebtType>
            otherDebtTypeOther: string | null
            note: string | null
            domicile: string | null
            defaultPhone: {
              __typename: 'CustomerContactPhone'
              id: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              }
            } | null
            defaultEmail: {
              __typename: 'CustomerContactEmail'
              id: string
              emailAddress: string
            } | null
            addresses: Array<{
              __typename: 'CustomerAddress'
              id: string
              city: string
              cityArea: string | null
              note: string | null
              postalCode: string
              streetAddress1: string
              streetAddress2: string | null
              isPermanentResidency: boolean
              isDefault: boolean
              country: { __typename: 'Country'; code: string; name: string } | null
              countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
            }>
            contactPhones: Array<{
              __typename: 'CustomerContactPhone'
              id: string
              note: string | null
              isDefault: boolean
              whatsappCapability: MessagingNumberCapability
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
                national: string | null
              }
            }>
            contactEmails: Array<{
              __typename: 'CustomerContactEmail'
              id: string
              emailAddress: string
              note: string | null
              isDefault: boolean
            }>
            cases: {
              __typename: 'CaseConnection'
              totalCount: number
              edges: Array<{
                __typename: 'CaseEdge'
                node: {
                  __typename: 'Case'
                  id: string
                  code: string
                  stage: CaseStage
                  status: CaseStatus
                  flow: CaseFlow | null
                  updatedAt: DateTimeString
                  type: { __typename: 'CaseType'; id: string; name: string }
                  assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                }
              }>
            }
            documents: {
              __typename: 'CustomerDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'CustomerDocumentEdge'
                node: {
                  __typename: 'CustomerDocument'
                  id: string
                  type: CustomerDocumentType
                  otherType: string | null
                  note: string | null
                  createdAt: DateTimeString
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            events: {
              __typename: 'CustomerEventConnection'
              edges: Array<{
                __typename: 'CustomerEventEdge'
                node: {
                  __typename: 'CustomerEvent'
                  id: string
                  type: CustomerEventType
                  date: DateTimeString
                  parameters: any
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
            domicileRegency: {
              __typename: 'Regency'
              id: string
              name: string
              kind: RegencyKind
            } | null
          }
          address: {
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          } | null
          contactEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          } | null
          contactPhone: {
            __typename: 'CustomerContactPhone'
            verifiedAt: DateTimeString | null
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
          assignedToUser: {
            __typename: 'User'
            id: string
            email: string
            displayName: string
          } | null
          assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
          totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
          orderCreateOptions: {
            __typename: 'OrderCreateOption'
            availability: ProductPackageAvailability
          } | null
          pendingOrder: {
            __typename: 'Order'
            id: string
            code: string
            status: OrderStatus
            paymentStatus: OrderPaymentStatus
            feeRate: number
            adjustedFeeRate: number | null
            durationInMonths: number
            documentBookCode: string | null
            startAt: DateTimeString | null
            createdAt: DateTimeString
            businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
            productPackage: {
              __typename: 'ProductPackage'
              id: string
              name: string
              documentTypes: Array<{
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
                appliesMeterai: boolean
                documentFilenameTemplate: string | null
                hasTemplate: boolean
                updatedAt: DateTimeString
              }>
            }
            fee: { __typename: 'Money'; amount: number; currency: string } | null
            estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
            remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
            additionalPaymentOrderAdjustmentRange: {
              __typename: 'MoneyRange'
              min: { __typename: 'Money'; amount: number; currency: string }
              max: { __typename: 'Money'; amount: number; currency: string }
            } | null
            currentProgress: {
              __typename: 'OrderCurrentProgress'
              nextState: OrderStatus
              isValid: boolean
              blockers: Array<
                | {
                    __typename: 'OrderStateProgressBlockerCaseDocumentType'
                    caseDocumentType: CaseDocumentType
                  }
                | {
                    __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                    case: { __typename: 'Case'; id: string }
                  }
                | {
                    __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                    customerDocumentType: CustomerDocumentType
                  }
                | {
                    __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                    financialAccountDocumentType: FinancialAccountDocumentType
                  }
                | {
                    __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                    documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                  }
                | {
                    __typename: 'OrderStateProgressBlockerOrderDocumentType'
                    documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                  }
                | {
                    __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                    orderPaymentStatus: Array<OrderPaymentStatus>
                  }
                | {
                    __typename: 'OrderStateProgressBlockerValidationEntity'
                    orderValidationEntity: OrderValidationEntity
                    entityReferenceCodes: Array<string>
                  }
              >
            } | null
            payments: Array<{
              __typename: 'OrderPayment'
              id: string
              paidAt: DateTimeString
              paidAmount: { __typename: 'Money'; amount: number; currency: string }
            }>
            documents: {
              __typename: 'OrderDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'OrderDocumentEdge'
                node: {
                  __typename: 'OrderDocument'
                  id: string
                  generated: boolean
                  rendered: boolean
                  signed: boolean
                  canBeSigned: boolean
                  note: string | null
                  createdAt: DateTimeString
                  documentType: {
                    __typename: 'OrderDocumentType'
                    id: string
                    name: string
                    isGeneratable: boolean
                    requiresSignature: boolean
                  }
                  signers: Array<{
                    __typename: 'OrderDocumentSignerModel'
                    role: string
                    name: string | null
                    email: string | null
                    isCustomer: boolean
                  }>
                  activeEsignRequest: {
                    __typename: 'OrderDocumentEsignRequest'
                    id: string
                    status: EsignRequestStatus
                    signerName: string
                    signerEmail: string
                    createdAt: DateTimeString
                    signedOrderDocument: {
                      __typename: 'OrderDocument'
                      id: string
                      file: { __typename: 'RemoteFile'; url: string | null }
                    } | null
                  } | null
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            events: {
              __typename: 'OrderEventConnection'
              edges: Array<{
                __typename: 'OrderEventEdge'
                node: {
                  __typename: 'OrderEvent'
                  id: string
                  type: OrderEventType
                  date: DateTimeString
                  parameters: any
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            financialAccounts: Array<{
              __typename: 'OrderFinancialAccount'
              id: string
              financialAccount: { __typename: 'FinancialAccount'; id: string }
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
            }>
            caseProducts: Array<{
              __typename: 'CaseProduct'
              id: string
              isActive: boolean
              product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
            }>
          } | null
          orders: Array<{
            __typename: 'Order'
            id: string
            code: string
            status: OrderStatus
            feeRate: number
            createdAt: DateTimeString
            documentBookCode: string | null
            invoiceBookCode: string | null
            startAt: DateTimeString | null
            durationInMonths: number
            paymentStatus: OrderPaymentStatus
            businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
            productPackage: {
              __typename: 'ProductPackage'
              id: string
              name: string
              documentTypes: Array<{
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
                appliesMeterai: boolean
                documentFilenameTemplate: string | null
                hasTemplate: boolean
                updatedAt: DateTimeString
              }>
            }
            fee: { __typename: 'Money'; amount: number; currency: string } | null
            estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
            payments: Array<{
              __typename: 'OrderPayment'
              id: string
              paidAt: DateTimeString
              paidAmount: { __typename: 'Money'; amount: number; currency: string }
            }>
            documents: {
              __typename: 'OrderDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'OrderDocumentEdge'
                node: {
                  __typename: 'OrderDocument'
                  id: string
                  generated: boolean
                  rendered: boolean
                  signed: boolean
                  canBeSigned: boolean
                  note: string | null
                  createdAt: DateTimeString
                  documentType: {
                    __typename: 'OrderDocumentType'
                    id: string
                    name: string
                    isGeneratable: boolean
                    requiresSignature: boolean
                  }
                  signers: Array<{
                    __typename: 'OrderDocumentSignerModel'
                    role: string
                    name: string | null
                    email: string | null
                    isCustomer: boolean
                  }>
                  activeEsignRequest: {
                    __typename: 'OrderDocumentEsignRequest'
                    id: string
                    status: EsignRequestStatus
                    signerName: string
                    signerEmail: string
                    createdAt: DateTimeString
                    signedOrderDocument: {
                      __typename: 'OrderDocument'
                      id: string
                      file: { __typename: 'RemoteFile'; url: string | null }
                    } | null
                  } | null
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            events: {
              __typename: 'OrderEventConnection'
              edges: Array<{
                __typename: 'OrderEventEdge'
                node: {
                  __typename: 'OrderEvent'
                  id: string
                  type: OrderEventType
                  date: DateTimeString
                  parameters: any
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            financialAccounts: Array<{
              __typename: 'OrderFinancialAccount'
              id: string
              financialAccount: { __typename: 'FinancialAccount'; id: string }
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
            }>
            caseProducts: Array<{
              __typename: 'CaseProduct'
              id: string
              isActive: boolean
              product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
            }>
          }>
          products: Array<{
            __typename: 'CaseProduct'
            id: string
            createdAt: DateTimeString
            startAt: DateTimeString
            endAt: DateTimeString
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
            financialAccounts: Array<{
              __typename: 'CaseProductFinancialAccount'
              id: string
              financialAccount: { __typename: 'FinancialAccount'; id: string }
            }>
          }>
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }
      }
    }
  }
}

export type NegotiationTicketCreateMutationVariables = Exact<{
  financialAccountId: string | number
  input: NegotiationTicketCreateInput
  PERMISSION_MANAGE_CASES?: boolean
}>

export type NegotiationTicketCreateMutation = {
  __typename: 'Mutation'
  negotiationTicketCreate: {
    __typename: 'NegotiationTicketCreate'
    negotiationTicket: {
      __typename: 'NegotiationTicket'
      id: string
      status: TicketStatus
      financialAccount: {
        __typename: 'FinancialAccount'
        id: string
        case: {
          __typename: 'Case'
          id: string
          code: string
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          accountNodeAccess: AccountNodeAccess | null
          updatedAt: DateTimeString
          moveToNewCustomerBlocker: CaseScopeCode | null
          status: CaseStatus
          stage: CaseStage
          flow: CaseFlow | null
          assignedVia: CaseAssignedVia | null
          importClassification: CaseImportClassification | null
          importSubmittedName: string | null
          identityCheckMergedName: string | null
          locked: boolean
          conversations: Array<{
            __typename: 'MessagingConversation'
            id: string
            customerWaId: string
            customerName: string | null
            lifecycle: MessagingConversationLifecycle
            lastActivityAt: DateTimeString
            lastInboundSeq: number | null
            needsAttention: boolean
            owesReply: boolean
            dismissed: boolean
            safetyFlagged: boolean
            ownership: MessagingConversationOwnership
            windowExpiresAt: DateTimeString | null
            customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
            assignee: { __typename: 'User'; id: string; displayName: string } | null
            linkedCases: Array<{
              __typename: 'CaseBrief'
              id: string
              code: string
              stage: CaseStage
              type: { __typename: 'CaseType'; id: string; name: string }
            }>
            channel: {
              __typename: 'MessagingChannel'
              id: string
              displayNumber: string
              defaultPlaybookId: string | null
              defaultOwner: MessagingConversationOwnership | null
              defaultOversightScenarioId: string | null
              supportPlaybookId: string | null
              isDefaultTransactional: boolean
              phoneNumberId: string
              provider: string
              purpose: MessagingChannelPurpose
              status: MessagingChannelStatus
              wabaId: string | null
              metaAppId: string | null
              caseType: { __typename: 'CaseType'; id: string; name: string } | null
            } | null
            lastMessage: {
              __typename: 'MessagingMessagePreview'
              createdAt: DateTimeString
              direction: MessagingMessageDirection
              seq: number
              source: MessagingMessageSource | null
              state: MessagingMessageState
              textPreview: string | null
              type: MessagingMessageType
              sender: { __typename: 'User'; id: string; displayName: string } | null
            } | null
          }>
          tags: Array<{
            __typename: 'CaseTag'
            id: string
            name: string
            color: string | null
            value: string
          }>
          sharedNumberThread: {
            __typename: 'CaseSharedNumberThread'
            conversationId: string | null
            case: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              customer: { __typename: 'Customer'; id: string; name: string }
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            } | null
          } | null
          type: {
            __typename: 'CaseType'
            id: string
            name: string
            productPackages: Array<{
              __typename: 'ProductPackage'
              name: string
              minimumFeeRate: number
              minimumFee: { __typename: 'Money'; amount: number; currency: string }
            }>
          }
          estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
          importClassificationReason?: {
            __typename: 'CaseImportClassificationReason'
            matcherVersion: number
            clusterSize: number
            guardrailMaxCluster: number | null
            cell: string | null
            emailRelation: string | null
            nameRelation: string | null
            evidenceScore: number | null
            nameCheck: string | null
            sharedNumber: boolean | null
            originConversationCustomer: boolean | null
            submittedName: string | null
          } | null
          assignmentReason?: {
            __typename: 'CaseAssignmentReason'
            rung: CaseAssignedVia
            at: DateTimeString
            reservation: {
              __typename: 'CaseAssignmentReason'
              rung: CaseAssignedVia
              at: DateTimeString
              user: { __typename: 'User'; id: string; displayName: string } | null
              actor: { __typename: 'User'; id: string; displayName: string } | null
              similarCase: {
                __typename: 'CaseAssignmentSimilarCase'
                id: string
                code: string
                customerId: string
                holderKind: string
              } | null
              excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
            } | null
            user: { __typename: 'User'; id: string; displayName: string } | null
            actor: { __typename: 'User'; id: string; displayName: string } | null
            similarCase: {
              __typename: 'CaseAssignmentSimilarCase'
              id: string
              code: string
              customerId: string
              holderKind: string
            } | null
            excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
          } | null
          source: {
            __typename: 'CaseSource'
            kind: string | null
            type: string | null
            medium: string | null
            campaign: string | null
            tool: string | null
            data: any
            optInSource: string | null
            optInAt: DateTimeString | null
          } | null
          behaviour: {
            __typename: 'CaseBehaviour'
            campaignResolverId: string | null
            flowChangeReason: CaseFlowChangeReason | null
            campaign: { __typename: 'Campaign'; id: string; name: string } | null
            campaignResolver: {
              __typename: 'CampaignResolver'
              id: string
              name: string
              priority: number
            } | null
          } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          actions: Array<{
            __typename: 'CaseAction'
            id: string
            comment: string
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'CaseActionType'; id: string; name: string }
            result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          financialAccounts: Array<{
            __typename: 'FinancialAccount'
            fullName: string | null
            id: string
            code: string
            createdAt: DateTimeString
            lastDueDate: DateTimeString | null
            dpd: number | null
            index: number | null
            status: FinancialAccountStatus
            providerReference: string | null
            ptpDate: DateTimeString | null
            documents: {
              __typename: 'FinancialAccountDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'FinancialAccountDocumentEdge'
                node: {
                  __typename: 'FinancialAccountDocument'
                  id: string
                  type: FinancialAccountDocumentType
                  otherType: string | null
                  note: string | null
                  createdAt: DateTimeString
                  financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            tickets: {
              __typename: 'FinancialAccountTicketConnection'
              totalCount: number
              edges: Array<{
                __typename: 'FinancialAccountTicketEdge'
                node:
                  | {
                      __typename: 'MediationTicket'
                      id: string
                      code: string
                      createdAt: DateTimeString
                      status: TicketStatus
                      type: TicketType
                      providerContact: {
                        __typename: 'FinancialProviderContact'
                        id: string
                        name: string
                        phoneNumber: {
                          __typename: 'PhoneNumber'
                          countryCode: string | null
                          displayNumber: string
                          international: string
                        } | null
                      }
                      assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                      closeReason: {
                        __typename: 'TicketCloseReason'
                        name: string
                        success: boolean
                      } | null
                      lastEventConnection: {
                        __typename: 'TicketEventConnection'
                        edges: Array<{
                          __typename: 'TicketEventEdge'
                          node: { __typename: 'TicketEvent'; date: DateTimeString }
                        }>
                      }
                    }
                  | {
                      __typename: 'NegotiationTicket'
                      id: string
                      code: string
                      createdAt: DateTimeString
                      status: TicketStatus
                      type: TicketType
                      outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                      assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                      closeReason: {
                        __typename: 'TicketCloseReason'
                        name: string
                        success: boolean
                      } | null
                      lastEventConnection: {
                        __typename: 'TicketEventConnection'
                        edges: Array<{
                          __typename: 'TicketEventEdge'
                          node: { __typename: 'TicketEvent'; date: DateTimeString }
                        }>
                      }
                    }
              }>
            }
            type: { __typename: 'FinancialAccountType'; id: string; name: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
            principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
            provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              role: string
              phoneNumberNote: string | null
              emailAddress: string | null
              emailAddressNote: string | null
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
                national: string | null
              } | null
            } | null
            registeredContactPhone: {
              __typename: 'CustomerContactPhone'
              id: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
                national: string | null
              }
            } | null
          }>
          documents: {
            __typename: 'CaseDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'CaseDocumentEdge'
              node: {
                __typename: 'CaseDocument'
                id: string
                type: CaseDocumentType
                otherType: string | null
                note: string | null
                createdAt: DateTimeString
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'CaseEventConnection'
            edges: Array<{
              __typename: 'CaseEventEdge'
              node: {
                __typename: 'CaseEvent'
                id: string
                type: CaseEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'CaseActionResult'
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'CaseActionType'; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string; email: string } | null
                financialAccount: {
                  __typename: 'FinancialAccount'
                  id: string
                  name: string
                } | null
                fromCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
                toCustomer: {
                  __typename: 'CaseEventCustomer'
                  id: string
                  code: string
                  name: string
                } | null
              }
            }>
          }
          customer: {
            __typename: 'Customer'
            id: string
            code: string
            name: string
            accountNodeAccess: AccountNodeAccess | null
            portalEnabledAt: DateTimeString | null
            birthDate: DateString | null
            birthPlace: string | null
            gender: Gender | null
            governmentId: string | null
            educationLevel: CustomerEducationLevel | null
            marriageStatus: CustomerMaritalStatus | null
            occupation: string | null
            residentialStatus: CustomerResidentialStatus | null
            jobTitle: string | null
            employmentStatus: CustomerEmploymentStatus | null
            companyType: CustomerCompanyType | null
            salaryRange: CustomerSalaryRange | null
            referralSource: CustomerReferralSource | null
            otherDebtTypes: Array<CustomerOtherDebtType>
            otherDebtTypeOther: string | null
            note: string | null
            domicile: string | null
            defaultPhone: {
              __typename: 'CustomerContactPhone'
              id: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              }
            } | null
            defaultEmail: {
              __typename: 'CustomerContactEmail'
              id: string
              emailAddress: string
            } | null
            addresses: Array<{
              __typename: 'CustomerAddress'
              id: string
              city: string
              cityArea: string | null
              note: string | null
              postalCode: string
              streetAddress1: string
              streetAddress2: string | null
              isPermanentResidency: boolean
              isDefault: boolean
              country: { __typename: 'Country'; code: string; name: string } | null
              countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
            }>
            contactPhones: Array<{
              __typename: 'CustomerContactPhone'
              id: string
              note: string | null
              isDefault: boolean
              whatsappCapability: MessagingNumberCapability
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
                national: string | null
              }
            }>
            contactEmails: Array<{
              __typename: 'CustomerContactEmail'
              id: string
              emailAddress: string
              note: string | null
              isDefault: boolean
            }>
            cases: {
              __typename: 'CaseConnection'
              totalCount: number
              edges: Array<{
                __typename: 'CaseEdge'
                node: {
                  __typename: 'Case'
                  id: string
                  code: string
                  stage: CaseStage
                  status: CaseStatus
                  flow: CaseFlow | null
                  updatedAt: DateTimeString
                  type: { __typename: 'CaseType'; id: string; name: string }
                  assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                }
              }>
            }
            documents: {
              __typename: 'CustomerDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'CustomerDocumentEdge'
                node: {
                  __typename: 'CustomerDocument'
                  id: string
                  type: CustomerDocumentType
                  otherType: string | null
                  note: string | null
                  createdAt: DateTimeString
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            events: {
              __typename: 'CustomerEventConnection'
              edges: Array<{
                __typename: 'CustomerEventEdge'
                node: {
                  __typename: 'CustomerEvent'
                  id: string
                  type: CustomerEventType
                  date: DateTimeString
                  parameters: any
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
            domicileRegency: {
              __typename: 'Regency'
              id: string
              name: string
              kind: RegencyKind
            } | null
          }
          address: {
            __typename: 'CustomerAddress'
            id: string
            city: string
            cityArea: string | null
            note: string | null
            postalCode: string
            streetAddress1: string
            streetAddress2: string | null
            isPermanentResidency: boolean
            isDefault: boolean
            country: { __typename: 'Country'; code: string; name: string } | null
            countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
          } | null
          contactEmail: {
            __typename: 'CustomerContactEmail'
            id: string
            emailAddress: string
            note: string | null
            isDefault: boolean
          } | null
          contactPhone: {
            __typename: 'CustomerContactPhone'
            verifiedAt: DateTimeString | null
            id: string
            note: string | null
            isDefault: boolean
            whatsappCapability: MessagingNumberCapability
            phoneNumber: {
              __typename: 'PhoneNumber'
              countryCode: string | null
              displayNumber: string
              international: string
              national: string | null
            }
          } | null
          assignedToUser: {
            __typename: 'User'
            id: string
            email: string
            displayName: string
          } | null
          assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
          totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
          orderCreateOptions: {
            __typename: 'OrderCreateOption'
            availability: ProductPackageAvailability
          } | null
          pendingOrder: {
            __typename: 'Order'
            id: string
            code: string
            status: OrderStatus
            paymentStatus: OrderPaymentStatus
            feeRate: number
            adjustedFeeRate: number | null
            durationInMonths: number
            documentBookCode: string | null
            startAt: DateTimeString | null
            createdAt: DateTimeString
            businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
            productPackage: {
              __typename: 'ProductPackage'
              id: string
              name: string
              documentTypes: Array<{
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
                appliesMeterai: boolean
                documentFilenameTemplate: string | null
                hasTemplate: boolean
                updatedAt: DateTimeString
              }>
            }
            fee: { __typename: 'Money'; amount: number; currency: string } | null
            estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
            remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
            additionalPaymentOrderAdjustmentRange: {
              __typename: 'MoneyRange'
              min: { __typename: 'Money'; amount: number; currency: string }
              max: { __typename: 'Money'; amount: number; currency: string }
            } | null
            currentProgress: {
              __typename: 'OrderCurrentProgress'
              nextState: OrderStatus
              isValid: boolean
              blockers: Array<
                | {
                    __typename: 'OrderStateProgressBlockerCaseDocumentType'
                    caseDocumentType: CaseDocumentType
                  }
                | {
                    __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                    case: { __typename: 'Case'; id: string }
                  }
                | {
                    __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                    customerDocumentType: CustomerDocumentType
                  }
                | {
                    __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                    financialAccountDocumentType: FinancialAccountDocumentType
                  }
                | {
                    __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                    documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                  }
                | {
                    __typename: 'OrderStateProgressBlockerOrderDocumentType'
                    documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                  }
                | {
                    __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                    orderPaymentStatus: Array<OrderPaymentStatus>
                  }
                | {
                    __typename: 'OrderStateProgressBlockerValidationEntity'
                    orderValidationEntity: OrderValidationEntity
                    entityReferenceCodes: Array<string>
                  }
              >
            } | null
            payments: Array<{
              __typename: 'OrderPayment'
              id: string
              paidAt: DateTimeString
              paidAmount: { __typename: 'Money'; amount: number; currency: string }
            }>
            documents: {
              __typename: 'OrderDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'OrderDocumentEdge'
                node: {
                  __typename: 'OrderDocument'
                  id: string
                  generated: boolean
                  rendered: boolean
                  signed: boolean
                  canBeSigned: boolean
                  note: string | null
                  createdAt: DateTimeString
                  documentType: {
                    __typename: 'OrderDocumentType'
                    id: string
                    name: string
                    isGeneratable: boolean
                    requiresSignature: boolean
                  }
                  signers: Array<{
                    __typename: 'OrderDocumentSignerModel'
                    role: string
                    name: string | null
                    email: string | null
                    isCustomer: boolean
                  }>
                  activeEsignRequest: {
                    __typename: 'OrderDocumentEsignRequest'
                    id: string
                    status: EsignRequestStatus
                    signerName: string
                    signerEmail: string
                    createdAt: DateTimeString
                    signedOrderDocument: {
                      __typename: 'OrderDocument'
                      id: string
                      file: { __typename: 'RemoteFile'; url: string | null }
                    } | null
                  } | null
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            events: {
              __typename: 'OrderEventConnection'
              edges: Array<{
                __typename: 'OrderEventEdge'
                node: {
                  __typename: 'OrderEvent'
                  id: string
                  type: OrderEventType
                  date: DateTimeString
                  parameters: any
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            financialAccounts: Array<{
              __typename: 'OrderFinancialAccount'
              id: string
              financialAccount: { __typename: 'FinancialAccount'; id: string }
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
            }>
            caseProducts: Array<{
              __typename: 'CaseProduct'
              id: string
              isActive: boolean
              product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
            }>
          } | null
          orders: Array<{
            __typename: 'Order'
            id: string
            code: string
            status: OrderStatus
            feeRate: number
            createdAt: DateTimeString
            documentBookCode: string | null
            invoiceBookCode: string | null
            startAt: DateTimeString | null
            durationInMonths: number
            paymentStatus: OrderPaymentStatus
            businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
            productPackage: {
              __typename: 'ProductPackage'
              id: string
              name: string
              documentTypes: Array<{
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
                appliesMeterai: boolean
                documentFilenameTemplate: string | null
                hasTemplate: boolean
                updatedAt: DateTimeString
              }>
            }
            fee: { __typename: 'Money'; amount: number; currency: string } | null
            estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
            payments: Array<{
              __typename: 'OrderPayment'
              id: string
              paidAt: DateTimeString
              paidAmount: { __typename: 'Money'; amount: number; currency: string }
            }>
            documents: {
              __typename: 'OrderDocumentConnection'
              totalCount: number
              edges: Array<{
                __typename: 'OrderDocumentEdge'
                node: {
                  __typename: 'OrderDocument'
                  id: string
                  generated: boolean
                  rendered: boolean
                  signed: boolean
                  canBeSigned: boolean
                  note: string | null
                  createdAt: DateTimeString
                  documentType: {
                    __typename: 'OrderDocumentType'
                    id: string
                    name: string
                    isGeneratable: boolean
                    requiresSignature: boolean
                  }
                  signers: Array<{
                    __typename: 'OrderDocumentSignerModel'
                    role: string
                    name: string | null
                    email: string | null
                    isCustomer: boolean
                  }>
                  activeEsignRequest: {
                    __typename: 'OrderDocumentEsignRequest'
                    id: string
                    status: EsignRequestStatus
                    signerName: string
                    signerEmail: string
                    createdAt: DateTimeString
                    signedOrderDocument: {
                      __typename: 'OrderDocument'
                      id: string
                      file: { __typename: 'RemoteFile'; url: string | null }
                    } | null
                  } | null
                  file: {
                    __typename: 'RemoteFile'
                    url: string | null
                    mimeType: string
                    fileName: string
                  }
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            events: {
              __typename: 'OrderEventConnection'
              edges: Array<{
                __typename: 'OrderEventEdge'
                node: {
                  __typename: 'OrderEvent'
                  id: string
                  type: OrderEventType
                  date: DateTimeString
                  parameters: any
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                }
              }>
            }
            financialAccounts: Array<{
              __typename: 'OrderFinancialAccount'
              id: string
              financialAccount: { __typename: 'FinancialAccount'; id: string }
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
            }>
            caseProducts: Array<{
              __typename: 'CaseProduct'
              id: string
              isActive: boolean
              product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
            }>
          }>
          products: Array<{
            __typename: 'CaseProduct'
            id: string
            createdAt: DateTimeString
            startAt: DateTimeString
            endAt: DateTimeString
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
            financialAccounts: Array<{
              __typename: 'CaseProductFinancialAccount'
              id: string
              financialAccount: { __typename: 'FinancialAccount'; id: string }
            }>
          }>
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
        }
      }
    }
  }
}

export type OrderAddonCreateMutationVariables = Exact<{
  caseId: string | number
  input: OrderAddonCreateInput
}>

export type OrderAddonCreateMutation = {
  __typename: 'Mutation'
  orderAddonCreate: {
    __typename: 'OrderCreate'
    order: {
      __typename: 'Order'
      case: {
        __typename: 'Case'
        id: string
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
      }
    }
  }
}

export type OrderAmountUpdateMutationVariables = Exact<{
  id: string | number
  input: OrderAmountUpdateInput
}>

export type OrderAmountUpdateMutation = {
  __typename: 'Mutation'
  orderAmountUpdate: {
    __typename: 'OrderAmountUpdate'
    order: {
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      paymentStatus: OrderPaymentStatus
      feeRate: number
      adjustedFeeRate: number | null
      durationInMonths: number
      documentBookCode: string | null
      startAt: DateTimeString | null
      createdAt: DateTimeString
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
      additionalPaymentOrderAdjustmentRange: {
        __typename: 'MoneyRange'
        min: { __typename: 'Money'; amount: number; currency: string }
        max: { __typename: 'Money'; amount: number; currency: string }
      } | null
      currentProgress: {
        __typename: 'OrderCurrentProgress'
        nextState: OrderStatus
        isValid: boolean
        blockers: Array<
          | {
              __typename: 'OrderStateProgressBlockerCaseDocumentType'
              caseDocumentType: CaseDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
              case: { __typename: 'Case'; id: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerCustomerDocumentType'
              customerDocumentType: CustomerDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
              financialAccountDocumentType: FinancialAccountDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerGenerateDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
              orderPaymentStatus: Array<OrderPaymentStatus>
            }
          | {
              __typename: 'OrderStateProgressBlockerValidationEntity'
              orderValidationEntity: OrderValidationEntity
              entityReferenceCodes: Array<string>
            }
        >
      } | null
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    }
  }
}

export type OrderCancelMutationVariables = Exact<{
  id: string | number
}>

export type OrderCancelMutation = {
  __typename: 'Mutation'
  orderCancel: {
    __typename: 'OrderCancel'
    order: {
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      paymentStatus: OrderPaymentStatus
      feeRate: number
      adjustedFeeRate: number | null
      durationInMonths: number
      documentBookCode: string | null
      startAt: DateTimeString | null
      createdAt: DateTimeString
      case: {
        __typename: 'Case'
        id: string
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
      }
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
      additionalPaymentOrderAdjustmentRange: {
        __typename: 'MoneyRange'
        min: { __typename: 'Money'; amount: number; currency: string }
        max: { __typename: 'Money'; amount: number; currency: string }
      } | null
      currentProgress: {
        __typename: 'OrderCurrentProgress'
        nextState: OrderStatus
        isValid: boolean
        blockers: Array<
          | {
              __typename: 'OrderStateProgressBlockerCaseDocumentType'
              caseDocumentType: CaseDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
              case: { __typename: 'Case'; id: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerCustomerDocumentType'
              customerDocumentType: CustomerDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
              financialAccountDocumentType: FinancialAccountDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerGenerateDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
              orderPaymentStatus: Array<OrderPaymentStatus>
            }
          | {
              __typename: 'OrderStateProgressBlockerValidationEntity'
              orderValidationEntity: OrderValidationEntity
              entityReferenceCodes: Array<string>
            }
        >
      } | null
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    }
  }
}

export type OrderDocumentCreateMutationVariables = Exact<{
  orderId: string | number
  input: OrderDocumentCreateInput
}>

export type OrderDocumentCreateMutation = {
  __typename: 'Mutation'
  orderDocumentCreate: {
    __typename: 'OrderDocumentCreate'
    orderDocument: {
      __typename: 'OrderDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      order: {
        __typename: 'Order'
        id: string
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type OrderDocumentDeleteMutationVariables = Exact<{
  id: string | number
}>

export type OrderDocumentDeleteMutation = {
  __typename: 'Mutation'
  orderDocumentDelete: {
    __typename: 'OrderDocumentDelete'
    orderDocument: {
      __typename: 'OrderDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      order: {
        __typename: 'Order'
        id: string
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type OrderDocumentGenerateMutationVariables = Exact<{
  id: string | number
  input: OrderDocumentGenerateInput
}>

export type OrderDocumentGenerateMutation = {
  __typename: 'Mutation'
  orderDocumentGenerate: {
    __typename: 'OrderDocumentGenerate'
    orderDocument: {
      __typename: 'OrderDocument'
      documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
      file: { __typename: 'RemoteFile'; url: string | null }
      order: {
        __typename: 'Order'
        id: string
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type OrderDocumentRequestSignatureMutationVariables = Exact<{
  id: string | number
}>

export type OrderDocumentRequestSignatureMutation = {
  __typename: 'Mutation'
  orderDocumentRequestSignature: {
    __typename: 'OrderDocumentRequestSignature'
    orderDocument: {
      __typename: 'OrderDocument'
      id: string
      generated: boolean
      rendered: boolean
      signed: boolean
      canBeSigned: boolean
      note: string | null
      createdAt: DateTimeString
      documentType: {
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
      }
      signers: Array<{
        __typename: 'OrderDocumentSignerModel'
        role: string
        name: string | null
        email: string | null
        isCustomer: boolean
      }>
      activeEsignRequest: {
        __typename: 'OrderDocumentEsignRequest'
        id: string
        status: EsignRequestStatus
        signerName: string
        signerEmail: string
        createdAt: DateTimeString
        signedOrderDocument: {
          __typename: 'OrderDocument'
          id: string
          file: { __typename: 'RemoteFile'; url: string | null }
        } | null
      } | null
      file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
  }
}

export type OrderDocumentSyncEsignStatusMutationVariables = Exact<{
  id: string | number
}>

export type OrderDocumentSyncEsignStatusMutation = {
  __typename: 'Mutation'
  orderDocumentSyncEsignStatus: {
    __typename: 'OrderDocumentSyncEsignStatus'
    orderDocument: {
      __typename: 'OrderDocument'
      id: string
      generated: boolean
      rendered: boolean
      signed: boolean
      canBeSigned: boolean
      note: string | null
      createdAt: DateTimeString
      documentType: {
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
      }
      signers: Array<{
        __typename: 'OrderDocumentSignerModel'
        role: string
        name: string | null
        email: string | null
        isCustomer: boolean
      }>
      activeEsignRequest: {
        __typename: 'OrderDocumentEsignRequest'
        id: string
        status: EsignRequestStatus
        signerName: string
        signerEmail: string
        createdAt: DateTimeString
        signedOrderDocument: {
          __typename: 'OrderDocument'
          id: string
          file: { __typename: 'RemoteFile'; url: string | null }
        } | null
      } | null
      file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
    esignRequest: {
      __typename: 'OrderDocumentEsignRequest'
      id: string
      status: EsignRequestStatus
      signerName: string
      signerEmail: string
      createdAt: DateTimeString
      signedOrderDocument: {
        __typename: 'OrderDocument'
        id: string
        file: { __typename: 'RemoteFile'; url: string | null }
      } | null
    }
  }
}

export type OrderDocumentUpdateMutationVariables = Exact<{
  id: string | number
  input: OrderDocumentUpdateInput
}>

export type OrderDocumentUpdateMutation = {
  __typename: 'Mutation'
  orderDocumentUpdate: {
    __typename: 'OrderDocumentUpdate'
    orderDocument: {
      __typename: 'OrderDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      order: {
        __typename: 'Order'
        id: string
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type OrderInitialCreateMutationVariables = Exact<{
  caseId: string | number
  input: OrderInitialCreateInput
}>

export type OrderInitialCreateMutation = {
  __typename: 'Mutation'
  orderInitialCreate: {
    __typename: 'OrderCreate'
    order: {
      __typename: 'Order'
      case: {
        __typename: 'Case'
        id: string
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
      }
    }
  }
}

export type OrderPaymentAddMutationVariables = Exact<{
  id: string | number
  input: OrderPaymentCreateInput
}>

export type OrderPaymentAddMutation = {
  __typename: 'Mutation'
  orderPaymentCreate: {
    __typename: 'OrderPaymentCreate'
    orderPayment: {
      __typename: 'OrderPayment'
      order: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }
    }
  }
}

export type OrderPaymentDeleteMutationVariables = Exact<{
  id: string | number
}>

export type OrderPaymentDeleteMutation = {
  __typename: 'Mutation'
  orderPaymentDelete: {
    __typename: 'OrderPaymentDelete'
    orderPayment: {
      __typename: 'OrderPayment'
      order: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }
    }
  }
}

export type OrderPaymentUpdateMutationVariables = Exact<{
  id: string | number
  input: OrderPaymentUpdateInput
}>

export type OrderPaymentUpdateMutation = {
  __typename: 'Mutation'
  orderPaymentUpdate: {
    __typename: 'OrderPaymentUpdate'
    orderPayment: {
      __typename: 'OrderPayment'
      order: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }
    }
  }
}

export type OrderStateProgressMutationVariables = Exact<{
  id: string | number
  input: OrderStateProgressInput
}>

export type OrderStateProgressMutation = {
  __typename: 'Mutation'
  orderStateProgress: {
    __typename: 'OrderStateProgress'
    order: {
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      paymentStatus: OrderPaymentStatus
      feeRate: number
      adjustedFeeRate: number | null
      durationInMonths: number
      documentBookCode: string | null
      startAt: DateTimeString | null
      createdAt: DateTimeString
      case: {
        __typename: 'Case'
        id: string
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          createdAt: DateTimeString
          startAt: DateTimeString
          endAt: DateTimeString
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          financialAccounts: Array<{
            __typename: 'CaseProductFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
          }>
        }>
        pendingOrder: {
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          feeRate: number
          adjustedFeeRate: number | null
          durationInMonths: number
          documentBookCode: string | null
          startAt: DateTimeString | null
          createdAt: DateTimeString
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
          additionalPaymentOrderAdjustmentRange: {
            __typename: 'MoneyRange'
            min: { __typename: 'Money'; amount: number; currency: string }
            max: { __typename: 'Money'; amount: number; currency: string }
          } | null
          currentProgress: {
            __typename: 'OrderCurrentProgress'
            nextState: OrderStatus
            isValid: boolean
            blockers: Array<
              | {
                  __typename: 'OrderStateProgressBlockerCaseDocumentType'
                  caseDocumentType: CaseDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                  case: { __typename: 'Case'; id: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                  customerDocumentType: CustomerDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                  financialAccountDocumentType: FinancialAccountDocumentType
                }
              | {
                  __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderDocumentType'
                  documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                }
              | {
                  __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                  orderPaymentStatus: Array<OrderPaymentStatus>
                }
              | {
                  __typename: 'OrderStateProgressBlockerValidationEntity'
                  orderValidationEntity: OrderValidationEntity
                  entityReferenceCodes: Array<string>
                }
            >
          } | null
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        } | null
        orders: Array<{
          __typename: 'Order'
          id: string
          code: string
          status: OrderStatus
          feeRate: number
          createdAt: DateTimeString
          documentBookCode: string | null
          invoiceBookCode: string | null
          startAt: DateTimeString | null
          durationInMonths: number
          paymentStatus: OrderPaymentStatus
          businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
          productPackage: {
            __typename: 'ProductPackage'
            id: string
            name: string
            documentTypes: Array<{
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
              appliesMeterai: boolean
              documentFilenameTemplate: string | null
              hasTemplate: boolean
              updatedAt: DateTimeString
            }>
          }
          fee: { __typename: 'Money'; amount: number; currency: string } | null
          estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
          payments: Array<{
            __typename: 'OrderPayment'
            id: string
            paidAt: DateTimeString
            paidAmount: { __typename: 'Money'; amount: number; currency: string }
          }>
          documents: {
            __typename: 'OrderDocumentConnection'
            totalCount: number
            edges: Array<{
              __typename: 'OrderDocumentEdge'
              node: {
                __typename: 'OrderDocument'
                id: string
                generated: boolean
                rendered: boolean
                signed: boolean
                canBeSigned: boolean
                note: string | null
                createdAt: DateTimeString
                documentType: {
                  __typename: 'OrderDocumentType'
                  id: string
                  name: string
                  isGeneratable: boolean
                  requiresSignature: boolean
                }
                signers: Array<{
                  __typename: 'OrderDocumentSignerModel'
                  role: string
                  name: string | null
                  email: string | null
                  isCustomer: boolean
                }>
                activeEsignRequest: {
                  __typename: 'OrderDocumentEsignRequest'
                  id: string
                  status: EsignRequestStatus
                  signerName: string
                  signerEmail: string
                  createdAt: DateTimeString
                  signedOrderDocument: {
                    __typename: 'OrderDocument'
                    id: string
                    file: { __typename: 'RemoteFile'; url: string | null }
                  } | null
                } | null
                file: {
                  __typename: 'RemoteFile'
                  url: string | null
                  mimeType: string
                  fileName: string
                }
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          events: {
            __typename: 'OrderEventConnection'
            edges: Array<{
              __typename: 'OrderEventEdge'
              node: {
                __typename: 'OrderEvent'
                id: string
                type: OrderEventType
                date: DateTimeString
                parameters: any
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
              }
            }>
          }
          financialAccounts: Array<{
            __typename: 'OrderFinancialAccount'
            id: string
            financialAccount: { __typename: 'FinancialAccount'; id: string }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }>
          caseProducts: Array<{
            __typename: 'CaseProduct'
            id: string
            isActive: boolean
            product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
          }>
        }>
      }
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
      additionalPaymentOrderAdjustmentRange: {
        __typename: 'MoneyRange'
        min: { __typename: 'Money'; amount: number; currency: string }
        max: { __typename: 'Money'; amount: number; currency: string }
      } | null
      currentProgress: {
        __typename: 'OrderCurrentProgress'
        nextState: OrderStatus
        isValid: boolean
        blockers: Array<
          | {
              __typename: 'OrderStateProgressBlockerCaseDocumentType'
              caseDocumentType: CaseDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
              case: { __typename: 'Case'; id: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerCustomerDocumentType'
              customerDocumentType: CustomerDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
              financialAccountDocumentType: FinancialAccountDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerGenerateDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
              orderPaymentStatus: Array<OrderPaymentStatus>
            }
          | {
              __typename: 'OrderStateProgressBlockerValidationEntity'
              orderValidationEntity: OrderValidationEntity
              entityReferenceCodes: Array<string>
            }
        >
      } | null
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    } | null
    blockers: Array<
      | {
          __typename: 'OrderStateProgressBlockerCaseDocumentType'
          caseDocumentType: CaseDocumentType
        }
      | {
          __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
          case: { __typename: 'Case'; id: string }
        }
      | {
          __typename: 'OrderStateProgressBlockerCustomerDocumentType'
          customerDocumentType: CustomerDocumentType
        }
      | {
          __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
          financialAccountDocumentType: FinancialAccountDocumentType
        }
      | {
          __typename: 'OrderStateProgressBlockerGenerateDocumentType'
          documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
        }
      | {
          __typename: 'OrderStateProgressBlockerOrderDocumentType'
          documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
        }
      | {
          __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
          orderPaymentStatus: Array<OrderPaymentStatus>
        }
      | {
          __typename: 'OrderStateProgressBlockerValidationEntity'
          orderValidationEntity: OrderValidationEntity
          entityReferenceCodes: Array<string>
        }
    > | null
  }
}

export type OrderUpdateMutationVariables = Exact<{
  id: string | number
  input: OrderUpdateInput
}>

export type OrderUpdateMutation = {
  __typename: 'Mutation'
  orderUpdate: {
    __typename: 'OrderUpdate'
    order: {
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      paymentStatus: OrderPaymentStatus
      feeRate: number
      adjustedFeeRate: number | null
      durationInMonths: number
      documentBookCode: string | null
      startAt: DateTimeString | null
      createdAt: DateTimeString
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
      additionalPaymentOrderAdjustmentRange: {
        __typename: 'MoneyRange'
        min: { __typename: 'Money'; amount: number; currency: string }
        max: { __typename: 'Money'; amount: number; currency: string }
      } | null
      currentProgress: {
        __typename: 'OrderCurrentProgress'
        nextState: OrderStatus
        isValid: boolean
        blockers: Array<
          | {
              __typename: 'OrderStateProgressBlockerCaseDocumentType'
              caseDocumentType: CaseDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
              case: { __typename: 'Case'; id: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerCustomerDocumentType'
              customerDocumentType: CustomerDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
              financialAccountDocumentType: FinancialAccountDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerGenerateDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
              orderPaymentStatus: Array<OrderPaymentStatus>
            }
          | {
              __typename: 'OrderStateProgressBlockerValidationEntity'
              orderValidationEntity: OrderValidationEntity
              entityReferenceCodes: Array<string>
            }
        >
      } | null
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    }
  }
}

export type CaseActionOptionsQueryVariables = Exact<{
  id: string | number
}>

export type CaseActionOptionsQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    availableActionResults: Array<{ __typename: 'CaseActionResult'; id: string; name: string }>
    availableActionTypes: Array<{ __typename: 'CaseActionType'; id: string; name: string }>
  }
}

export type CaseAvailableTagsQueryVariables = Exact<{
  id: string | number
}>

export type CaseAvailableTagsQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    availableTags: Array<{ __typename: 'CaseTag'; id: string; name: string; color: string | null }>
  }
}

export type CaseCloseOptionsQueryVariables = Exact<{
  id: string | number
}>

export type CaseCloseOptionsQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    availableCloseReasons: Array<{ __typename: 'CaseCloseReason'; id: string; name: string }>
  }
}

export type CaseCreateNewForCustomerOptionsQueryVariables = Exact<{ [key: string]: never }>

export type CaseCreateNewForCustomerOptionsQuery = {
  __typename: 'Query'
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
}

export type CaseCreateOptionsQueryVariables = Exact<{ [key: string]: never }>

export type CaseCreateOptionsQuery = {
  __typename: 'Query'
  me: {
    __typename: 'User'
    id: string
    canHandleCaseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }> | null
  } | null
}

export type CaseDetailQueryVariables = Exact<{
  id: string | number
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseDetailQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    code: string
    createdAt: DateTimeString
    closedAt: DateTimeString | null
    accountNodeAccess: AccountNodeAccess | null
    updatedAt: DateTimeString
    moveToNewCustomerBlocker: CaseScopeCode | null
    status: CaseStatus
    stage: CaseStage
    flow: CaseFlow | null
    assignedVia: CaseAssignedVia | null
    importClassification: CaseImportClassification | null
    importSubmittedName: string | null
    identityCheckMergedName: string | null
    locked: boolean
    conversations: Array<{
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }>
    tags: Array<{
      __typename: 'CaseTag'
      id: string
      name: string
      color: string | null
      value: string
    }>
    sharedNumberThread: {
      __typename: 'CaseSharedNumberThread'
      conversationId: string | null
      case: {
        __typename: 'Case'
        id: string
        code: string
        stage: CaseStage
        status: CaseStatus
        flow: CaseFlow | null
        updatedAt: DateTimeString
        customer: { __typename: 'Customer'; id: string; name: string }
        type: { __typename: 'CaseType'; id: string; name: string }
        assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      } | null
    } | null
    type: {
      __typename: 'CaseType'
      id: string
      name: string
      productPackages: Array<{
        __typename: 'ProductPackage'
        name: string
        minimumFeeRate: number
        minimumFee: { __typename: 'Money'; amount: number; currency: string }
      }>
    }
    estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
    importClassificationReason?: {
      __typename: 'CaseImportClassificationReason'
      matcherVersion: number
      clusterSize: number
      guardrailMaxCluster: number | null
      cell: string | null
      emailRelation: string | null
      nameRelation: string | null
      evidenceScore: number | null
      nameCheck: string | null
      sharedNumber: boolean | null
      originConversationCustomer: boolean | null
      submittedName: string | null
    } | null
    assignmentReason?: {
      __typename: 'CaseAssignmentReason'
      rung: CaseAssignedVia
      at: DateTimeString
      reservation: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      user: { __typename: 'User'; id: string; displayName: string } | null
      actor: { __typename: 'User'; id: string; displayName: string } | null
      similarCase: {
        __typename: 'CaseAssignmentSimilarCase'
        id: string
        code: string
        customerId: string
        holderKind: string
      } | null
      excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
    } | null
    source: {
      __typename: 'CaseSource'
      kind: string | null
      type: string | null
      medium: string | null
      campaign: string | null
      tool: string | null
      data: any
      optInSource: string | null
      optInAt: DateTimeString | null
    } | null
    behaviour: {
      __typename: 'CaseBehaviour'
      campaignResolverId: string | null
      flowChangeReason: CaseFlowChangeReason | null
      campaign: { __typename: 'Campaign'; id: string; name: string } | null
      campaignResolver: {
        __typename: 'CampaignResolver'
        id: string
        name: string
        priority: number
      } | null
    } | null
    closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
    actions: Array<{
      __typename: 'CaseAction'
      id: string
      comment: string
      date: DateTimeString
      createdAt: DateTimeString
      type: { __typename: 'CaseActionType'; id: string; name: string }
      result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }>
    financialAccounts: Array<{
      __typename: 'FinancialAccount'
      fullName: string | null
      id: string
      code: string
      createdAt: DateTimeString
      lastDueDate: DateTimeString | null
      dpd: number | null
      index: number | null
      status: FinancialAccountStatus
      providerReference: string | null
      ptpDate: DateTimeString | null
      documents: {
        __typename: 'FinancialAccountDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'FinancialAccountDocumentEdge'
          node: {
            __typename: 'FinancialAccountDocument'
            id: string
            type: FinancialAccountDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      tickets: {
        __typename: 'FinancialAccountTicketConnection'
        totalCount: number
        edges: Array<{
          __typename: 'FinancialAccountTicketEdge'
          node:
            | {
                __typename: 'MediationTicket'
                id: string
                code: string
                createdAt: DateTimeString
                status: TicketStatus
                type: TicketType
                providerContact: {
                  __typename: 'FinancialProviderContact'
                  id: string
                  name: string
                  phoneNumber: {
                    __typename: 'PhoneNumber'
                    countryCode: string | null
                    displayNumber: string
                    international: string
                  } | null
                }
                assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: {
                  __typename: 'TicketCloseReason'
                  name: string
                  success: boolean
                } | null
                lastEventConnection: {
                  __typename: 'TicketEventConnection'
                  edges: Array<{
                    __typename: 'TicketEventEdge'
                    node: { __typename: 'TicketEvent'; date: DateTimeString }
                  }>
                }
              }
            | {
                __typename: 'NegotiationTicket'
                id: string
                code: string
                createdAt: DateTimeString
                status: TicketStatus
                type: TicketType
                outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: {
                  __typename: 'TicketCloseReason'
                  name: string
                  success: boolean
                } | null
                lastEventConnection: {
                  __typename: 'TicketEventConnection'
                  edges: Array<{
                    __typename: 'TicketEventEdge'
                    node: { __typename: 'TicketEvent'; date: DateTimeString }
                  }>
                }
              }
        }>
      }
      type: { __typename: 'FinancialAccountType'; id: string; name: string }
      outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
      principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
      provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
      providerContact: {
        __typename: 'FinancialProviderContact'
        id: string
        name: string
        role: string
        phoneNumberNote: string | null
        emailAddress: string | null
        emailAddressNote: string | null
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        } | null
      } | null
      registeredContactPhone: {
        __typename: 'CustomerContactPhone'
        id: string
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
    }>
    documents: {
      __typename: 'CaseDocumentConnection'
      totalCount: number
      edges: Array<{
        __typename: 'CaseDocumentEdge'
        node: {
          __typename: 'CaseDocument'
          id: string
          type: CaseDocumentType
          otherType: string | null
          note: string | null
          createdAt: DateTimeString
          file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    events: {
      __typename: 'CaseEventConnection'
      edges: Array<{
        __typename: 'CaseEventEdge'
        node: {
          __typename: 'CaseEvent'
          id: string
          type: CaseEventType
          date: DateTimeString
          parameters: any
          actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
          actionType: { __typename: 'CaseActionType'; name: string } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
          user: { __typename: 'User'; id: string; displayName: string; email: string } | null
          financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
          fromCustomer: {
            __typename: 'CaseEventCustomer'
            id: string
            code: string
            name: string
          } | null
          toCustomer: {
            __typename: 'CaseEventCustomer'
            id: string
            code: string
            name: string
          } | null
        }
      }>
    }
    customer: {
      __typename: 'Customer'
      id: string
      code: string
      name: string
      accountNodeAccess: AccountNodeAccess | null
      portalEnabledAt: DateTimeString | null
      birthDate: DateString | null
      birthPlace: string | null
      gender: Gender | null
      governmentId: string | null
      educationLevel: CustomerEducationLevel | null
      marriageStatus: CustomerMaritalStatus | null
      occupation: string | null
      residentialStatus: CustomerResidentialStatus | null
      jobTitle: string | null
      employmentStatus: CustomerEmploymentStatus | null
      companyType: CustomerCompanyType | null
      salaryRange: CustomerSalaryRange | null
      referralSource: CustomerReferralSource | null
      otherDebtTypes: Array<CustomerOtherDebtType>
      otherDebtTypeOther: string | null
      note: string | null
      domicile: string | null
      defaultPhone: {
        __typename: 'CustomerContactPhone'
        id: string
        phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
      } | null
      defaultEmail: { __typename: 'CustomerContactEmail'; id: string; emailAddress: string } | null
      addresses: Array<{
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      }>
      contactPhones: Array<{
        __typename: 'CustomerContactPhone'
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      }>
      contactEmails: Array<{
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      }>
      cases: {
        __typename: 'CaseConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseEdge'
          node: {
            __typename: 'Case'
            id: string
            code: string
            stage: CaseStage
            status: CaseStatus
            flow: CaseFlow | null
            updatedAt: DateTimeString
            type: { __typename: 'CaseType'; id: string; name: string }
            assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
          }
        }>
      }
      documents: {
        __typename: 'CustomerDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CustomerDocumentEdge'
          node: {
            __typename: 'CustomerDocument'
            id: string
            type: CustomerDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CustomerEventConnection'
        edges: Array<{
          __typename: 'CustomerEventEdge'
          node: {
            __typename: 'CustomerEvent'
            id: string
            type: CustomerEventType
            date: DateTimeString
            parameters: any
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
    }
    address: {
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    } | null
    contactEmail: {
      __typename: 'CustomerContactEmail'
      id: string
      emailAddress: string
      note: string | null
      isDefault: boolean
    } | null
    contactPhone: {
      __typename: 'CustomerContactPhone'
      verifiedAt: DateTimeString | null
      id: string
      note: string | null
      isDefault: boolean
      whatsappCapability: MessagingNumberCapability
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    } | null
    assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
    assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
    totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
    orderCreateOptions: {
      __typename: 'OrderCreateOption'
      availability: ProductPackageAvailability
    } | null
    pendingOrder: {
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      paymentStatus: OrderPaymentStatus
      feeRate: number
      adjustedFeeRate: number | null
      durationInMonths: number
      documentBookCode: string | null
      startAt: DateTimeString | null
      createdAt: DateTimeString
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
      additionalPaymentOrderAdjustmentRange: {
        __typename: 'MoneyRange'
        min: { __typename: 'Money'; amount: number; currency: string }
        max: { __typename: 'Money'; amount: number; currency: string }
      } | null
      currentProgress: {
        __typename: 'OrderCurrentProgress'
        nextState: OrderStatus
        isValid: boolean
        blockers: Array<
          | {
              __typename: 'OrderStateProgressBlockerCaseDocumentType'
              caseDocumentType: CaseDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
              case: { __typename: 'Case'; id: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerCustomerDocumentType'
              customerDocumentType: CustomerDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
              financialAccountDocumentType: FinancialAccountDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerGenerateDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
              orderPaymentStatus: Array<OrderPaymentStatus>
            }
          | {
              __typename: 'OrderStateProgressBlockerValidationEntity'
              orderValidationEntity: OrderValidationEntity
              entityReferenceCodes: Array<string>
            }
        >
      } | null
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    } | null
    orders: Array<{
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      feeRate: number
      createdAt: DateTimeString
      documentBookCode: string | null
      invoiceBookCode: string | null
      startAt: DateTimeString | null
      durationInMonths: number
      paymentStatus: OrderPaymentStatus
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    }>
    products: Array<{
      __typename: 'CaseProduct'
      id: string
      createdAt: DateTimeString
      startAt: DateTimeString
      endAt: DateTimeString
      isActive: boolean
      product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      financialAccounts: Array<{
        __typename: 'CaseProductFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
      }>
    }>
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
  }
}

export type CaseFinancialAccountOptionsQueryVariables = Exact<{
  id: string | number
}>

export type CaseFinancialAccountOptionsQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    availableFinancialAccountTypes: Array<{
      __typename: 'FinancialAccountType'
      id: string
      name: string
    }>
  }
}

export type CaseListAllQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: CasesAllFilterInput | null | undefined
  sort?: CasesAllSortInput | null | undefined
}>

export type CaseListAllQuery = {
  __typename: 'Query'
  casesAll: {
    __typename: 'CaseConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseEdge'
      node: {
        __typename: 'Case'
        id: string
        createdAt: DateTimeString
        code: string
        stage: CaseStage
        status: CaseStatus
        flow: CaseFlow | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          fee: { __typename: 'Money'; amount: number; currency: string } | null
        } | null
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; name: string; productType: ProductType }
        }>
        customer: { __typename: 'Customer'; id: string; code: string; name: string }
        assignedToUser: { __typename: 'User'; displayName: string } | null
        tags: Array<{ __typename: 'CaseTag'; id: string; name: string; color: string | null }>
        type: { __typename: 'CaseType'; id: string; name: string }
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseListFilterOptionsQueryVariables = Exact<{ [key: string]: never }>

export type CaseListFilterOptionsQuery = {
  __typename: 'Query'
  caseCloseReasons: {
    __typename: 'CaseCloseReasonConnection'
    edges: Array<{
      __typename: 'CaseCloseReasonEdge'
      node: { __typename: 'CaseCloseReason'; id: string; name: string }
    }>
  }
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
  caseTags: {
    __typename: 'CaseTagConnection'
    edges: Array<{
      __typename: 'CaseTagEdge'
      node: { __typename: 'CaseTag'; id: string; name: string }
    }>
  }
}

export type CaseListTeamAssignedQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: CasesTeamAssignedFilterInput | null | undefined
  sort?: CasesTeamAssignedSortInput | null | undefined
}>

export type CaseListTeamAssignedQuery = {
  __typename: 'Query'
  casesTeamAssigned: {
    __typename: 'CaseConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseEdge'
      node: {
        __typename: 'Case'
        id: string
        createdAt: DateTimeString
        code: string
        stage: CaseStage
        status: CaseStatus
        flow: CaseFlow | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          fee: { __typename: 'Money'; amount: number; currency: string } | null
        } | null
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; name: string; productType: ProductType }
        }>
        customer: { __typename: 'Customer'; id: string; code: string; name: string }
        assignedToUser: { __typename: 'User'; displayName: string } | null
        tags: Array<{ __typename: 'CaseTag'; id: string; name: string; color: string | null }>
        type: { __typename: 'CaseType'; id: string; name: string }
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseListUserAssignedQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: CasesUserAssignedFilterInput | null | undefined
  sort?: CasesUserAssignedSortInput | null | undefined
}>

export type CaseListUserAssignedQuery = {
  __typename: 'Query'
  casesUserAssigned: {
    __typename: 'CaseConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseEdge'
      node: {
        __typename: 'Case'
        id: string
        createdAt: DateTimeString
        code: string
        stage: CaseStage
        status: CaseStatus
        flow: CaseFlow | null
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        pendingOrder: {
          __typename: 'Order'
          id: string
          status: OrderStatus
          paymentStatus: OrderPaymentStatus
          fee: { __typename: 'Money'; amount: number; currency: string } | null
        } | null
        products: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; name: string; productType: ProductType }
        }>
        customer: { __typename: 'Customer'; id: string; code: string; name: string }
        assignedToUser: { __typename: 'User'; displayName: string } | null
        tags: Array<{ __typename: 'CaseTag'; id: string; name: string; color: string | null }>
        type: { __typename: 'CaseType'; id: string; name: string }
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseOpenRemindersQueryVariables = Exact<{
  caseId: string | number
}>

export type CaseOpenRemindersQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    reminders: Array<{
      __typename: 'CaseReminder'
      id: string
      dueAt: DateTimeString
      note: string | null
      status: CaseReminderStatus
      source: CaseReminderSource
      isOverdue: boolean
      outsideBusinessHours: boolean | null
      type: { __typename: 'CaseActionType'; id: string; name: string } | null
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }>
  }
}

export type CaseOrderOptionsQueryVariables = Exact<{
  id: string | number
}>

export type CaseOrderOptionsQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    type: {
      __typename: 'CaseType'
      id: string
      businessEntities: Array<{ __typename: 'BusinessEntity'; id: string; name: string }>
    }
    orderCreateOptions: {
      __typename: 'OrderCreateOption'
      availability: ProductPackageAvailability
      productPackages: Array<{
        __typename: 'ProductPackage'
        id: string
        name: string
        minimumFeeRate: number
        defaultFeeRate: number
        defaultDurationMonths: number
        minimumFee: { __typename: 'Money'; amount: number; currency: string }
      }>
    } | null
  }
}

export type CaseReminderCountsByDayQueryVariables = Exact<{
  scope: CaseReminderScope
  from: DateString
  to: DateString
}>

export type CaseReminderCountsByDayQuery = {
  __typename: 'Query'
  caseReminderCountsByDay: Array<{
    __typename: 'CaseReminderDayCount'
    date: string
    open: number
    overdue: number
  }>
}

export type CaseReminderHoursCheckQueryVariables = Exact<{
  caseId: string | number
  dueAt: DateTimeString
}>

export type CaseReminderHoursCheckQuery = {
  __typename: 'Query'
  caseReminderHoursCheck: {
    __typename: 'CaseReminderHoursCheck'
    outsideBusinessHours: boolean | null
    nextOpening: DateTimeString | null
  }
}

export type CaseReminderSummaryQueryVariables = Exact<{
  scope: CaseReminderScope
}>

export type CaseReminderSummaryQuery = {
  __typename: 'Query'
  caseReminderSummary: { __typename: 'CaseReminderSummary'; overdue: number; dueToday: number }
}

export type CaseRemindersQueryVariables = Exact<{
  scope: CaseReminderScope
  filter?: CaseRemindersFilterInput | null | undefined
  first?: number | null | undefined
  after?: string | null | undefined
}>

export type CaseRemindersQuery = {
  __typename: 'Query'
  caseReminders: {
    __typename: 'CaseReminderConnection'
    totalCount: number
    pageInfo: { __typename: 'PageInfo'; hasNextPage: boolean; endCursor: string | null }
    edges: Array<{
      __typename: 'CaseReminderEdge'
      node: {
        __typename: 'CaseReminder'
        id: string
        dueAt: DateTimeString
        note: string | null
        status: CaseReminderStatus
        source: CaseReminderSource
        isOverdue: boolean
        outsideBusinessHours: boolean | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          customer: { __typename: 'Customer'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
        } | null
        type: { __typename: 'CaseActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
}

export type InitialCaseFilterCustomersQueryVariables = Exact<{
  customerIds?: Array<string | number> | string | number | null | undefined
}>

export type InitialCaseFilterCustomersQuery = {
  __typename: 'Query'
  customers: {
    __typename: 'CustomerConnection'
    edges: Array<{
      __typename: 'CustomerEdge'
      node: { __typename: 'Customer'; id: string; name: string }
    }>
  }
}

export type InitialCaseFilterUsersQueryVariables = Exact<{
  userIds?: Array<string | number> | string | number | null | undefined
}>

export type InitialCaseFilterUsersQuery = {
  __typename: 'Query'
  users: {
    __typename: 'UserConnection'
    edges: Array<{
      __typename: 'UserEdge'
      node: { __typename: 'User'; id: string; displayName: string }
    }>
  }
}

export type CaseReminderDueSubscriptionVariables = Exact<{ [key: string]: never }>

export type CaseReminderDueSubscription = {
  __typename: 'Subscription'
  caseReminderDue: {
    __typename: 'CaseReminderDueEvent'
    reminderId: string
    caseId: string
    caseCode: string
    dueAt: DateTimeString
  }
}

export type CaseUpdatedSubscriptionVariables = Exact<{
  id: string | number
  ignoreSameSession?: boolean | null | undefined
  PERMISSION_MANAGE_CASES?: boolean
}>

export type CaseUpdatedSubscription = {
  __typename: 'Subscription'
  caseUpdated: {
    __typename: 'CaseUpdatedEventModel'
    author:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
    node: {
      __typename: 'Case'
      id: string
      code: string
      createdAt: DateTimeString
      closedAt: DateTimeString | null
      accountNodeAccess: AccountNodeAccess | null
      updatedAt: DateTimeString
      moveToNewCustomerBlocker: CaseScopeCode | null
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      assignedVia: CaseAssignedVia | null
      importClassification: CaseImportClassification | null
      importSubmittedName: string | null
      identityCheckMergedName: string | null
      locked: boolean
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
      tags: Array<{
        __typename: 'CaseTag'
        id: string
        name: string
        color: string | null
        value: string
      }>
      sharedNumberThread: {
        __typename: 'CaseSharedNumberThread'
        conversationId: string | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          customer: { __typename: 'Customer'; id: string; name: string }
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        } | null
      } | null
      type: {
        __typename: 'CaseType'
        id: string
        name: string
        productPackages: Array<{
          __typename: 'ProductPackage'
          name: string
          minimumFeeRate: number
          minimumFee: { __typename: 'Money'; amount: number; currency: string }
        }>
      }
      estimatedValue: { __typename: 'Money'; amount: number; currency: string } | null
      importClassificationReason?: {
        __typename: 'CaseImportClassificationReason'
        matcherVersion: number
        clusterSize: number
        guardrailMaxCluster: number | null
        cell: string | null
        emailRelation: string | null
        nameRelation: string | null
        evidenceScore: number | null
        nameCheck: string | null
        sharedNumber: boolean | null
        originConversationCustomer: boolean | null
        submittedName: string | null
      } | null
      assignmentReason?: {
        __typename: 'CaseAssignmentReason'
        rung: CaseAssignedVia
        at: DateTimeString
        reservation: {
          __typename: 'CaseAssignmentReason'
          rung: CaseAssignedVia
          at: DateTimeString
          user: { __typename: 'User'; id: string; displayName: string } | null
          actor: { __typename: 'User'; id: string; displayName: string } | null
          similarCase: {
            __typename: 'CaseAssignmentSimilarCase'
            id: string
            code: string
            customerId: string
            holderKind: string
          } | null
          excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
        } | null
        user: { __typename: 'User'; id: string; displayName: string } | null
        actor: { __typename: 'User'; id: string; displayName: string } | null
        similarCase: {
          __typename: 'CaseAssignmentSimilarCase'
          id: string
          code: string
          customerId: string
          holderKind: string
        } | null
        excludedUsers: Array<{ __typename: 'User'; id: string; displayName: string }>
      } | null
      source: {
        __typename: 'CaseSource'
        kind: string | null
        type: string | null
        medium: string | null
        campaign: string | null
        tool: string | null
        data: any
        optInSource: string | null
        optInAt: DateTimeString | null
      } | null
      behaviour: {
        __typename: 'CaseBehaviour'
        campaignResolverId: string | null
        flowChangeReason: CaseFlowChangeReason | null
        campaign: { __typename: 'Campaign'; id: string; name: string } | null
        campaignResolver: {
          __typename: 'CampaignResolver'
          id: string
          name: string
          priority: number
        } | null
      } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      actions: Array<{
        __typename: 'CaseAction'
        id: string
        comment: string
        date: DateTimeString
        createdAt: DateTimeString
        type: { __typename: 'CaseActionType'; id: string; name: string }
        result: { __typename: 'CaseActionResult'; id: string; name: string; success: boolean }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
      }>
      financialAccounts: Array<{
        __typename: 'FinancialAccount'
        fullName: string | null
        id: string
        code: string
        createdAt: DateTimeString
        lastDueDate: DateTimeString | null
        dpd: number | null
        index: number | null
        status: FinancialAccountStatus
        providerReference: string | null
        ptpDate: DateTimeString | null
        documents: {
          __typename: 'FinancialAccountDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountDocumentEdge'
            node: {
              __typename: 'FinancialAccountDocument'
              id: string
              type: FinancialAccountDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              financialAccount: { __typename: 'FinancialAccount'; id: string; name: string }
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        tickets: {
          __typename: 'FinancialAccountTicketConnection'
          totalCount: number
          edges: Array<{
            __typename: 'FinancialAccountTicketEdge'
            node:
              | {
                  __typename: 'MediationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  providerContact: {
                    __typename: 'FinancialProviderContact'
                    id: string
                    name: string
                    phoneNumber: {
                      __typename: 'PhoneNumber'
                      countryCode: string | null
                      displayNumber: string
                      international: string
                    } | null
                  }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
              | {
                  __typename: 'NegotiationTicket'
                  id: string
                  code: string
                  createdAt: DateTimeString
                  status: TicketStatus
                  type: TicketType
                  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                  closeReason: {
                    __typename: 'TicketCloseReason'
                    name: string
                    success: boolean
                  } | null
                  lastEventConnection: {
                    __typename: 'TicketEventConnection'
                    edges: Array<{
                      __typename: 'TicketEventEdge'
                      node: { __typename: 'TicketEvent'; date: DateTimeString }
                    }>
                  }
                }
          }>
        }
        type: { __typename: 'FinancialAccountType'; id: string; name: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
        principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
        provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
        providerContact: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          phoneNumberNote: string | null
          emailAddress: string | null
          emailAddressNote: string | null
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          } | null
        } | null
        registeredContactPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        } | null
      }>
      documents: {
        __typename: 'CaseDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseDocumentEdge'
          node: {
            __typename: 'CaseDocument'
            id: string
            type: CaseDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CaseEventConnection'
        edges: Array<{
          __typename: 'CaseEventEdge'
          node: {
            __typename: 'CaseEvent'
            id: string
            type: CaseEventType
            date: DateTimeString
            parameters: any
            actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
            actionType: { __typename: 'CaseActionType'; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
            user: { __typename: 'User'; id: string; displayName: string; email: string } | null
            financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
            fromCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
            toCustomer: {
              __typename: 'CaseEventCustomer'
              id: string
              code: string
              name: string
            } | null
          }
        }>
      }
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        accountNodeAccess: AccountNodeAccess | null
        portalEnabledAt: DateTimeString | null
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        note: string | null
        domicile: string | null
        defaultPhone: {
          __typename: 'CustomerContactPhone'
          id: string
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
        } | null
        defaultEmail: {
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
        } | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
        }>
        cases: {
          __typename: 'CaseConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CaseEdge'
            node: {
              __typename: 'Case'
              id: string
              code: string
              stage: CaseStage
              status: CaseStatus
              flow: CaseFlow | null
              updatedAt: DateTimeString
              type: { __typename: 'CaseType'; id: string; name: string }
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
            }
          }>
        }
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      address: {
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      } | null
      contactEmail: {
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
      } | null
      contactPhone: {
        __typename: 'CustomerContactPhone'
        verifiedAt: DateTimeString | null
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
      assignedToUser: { __typename: 'User'; id: string; email: string; displayName: string } | null
      assignedToUserTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      totalDebt: { __typename: 'Money'; amount: number; currency: string } | null
      orderCreateOptions: {
        __typename: 'OrderCreateOption'
        availability: ProductPackageAvailability
      } | null
      pendingOrder: {
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        paymentStatus: OrderPaymentStatus
        feeRate: number
        adjustedFeeRate: number | null
        durationInMonths: number
        documentBookCode: string | null
        startAt: DateTimeString | null
        createdAt: DateTimeString
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
        additionalPaymentOrderAdjustmentRange: {
          __typename: 'MoneyRange'
          min: { __typename: 'Money'; amount: number; currency: string }
          max: { __typename: 'Money'; amount: number; currency: string }
        } | null
        currentProgress: {
          __typename: 'OrderCurrentProgress'
          nextState: OrderStatus
          isValid: boolean
          blockers: Array<
            | {
                __typename: 'OrderStateProgressBlockerCaseDocumentType'
                caseDocumentType: CaseDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                case: { __typename: 'Case'; id: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                customerDocumentType: CustomerDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                financialAccountDocumentType: FinancialAccountDocumentType
              }
            | {
                __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderDocumentType'
                documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
              }
            | {
                __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                orderPaymentStatus: Array<OrderPaymentStatus>
              }
            | {
                __typename: 'OrderStateProgressBlockerValidationEntity'
                orderValidationEntity: OrderValidationEntity
                entityReferenceCodes: Array<string>
              }
          >
        } | null
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      } | null
      orders: Array<{
        __typename: 'Order'
        id: string
        code: string
        status: OrderStatus
        feeRate: number
        createdAt: DateTimeString
        documentBookCode: string | null
        invoiceBookCode: string | null
        startAt: DateTimeString | null
        durationInMonths: number
        paymentStatus: OrderPaymentStatus
        businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
        productPackage: {
          __typename: 'ProductPackage'
          id: string
          name: string
          documentTypes: Array<{
            __typename: 'OrderDocumentType'
            id: string
            name: string
            isGeneratable: boolean
            requiresSignature: boolean
            appliesMeterai: boolean
            documentFilenameTemplate: string | null
            hasTemplate: boolean
            updatedAt: DateTimeString
          }>
        }
        fee: { __typename: 'Money'; amount: number; currency: string } | null
        estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
        payments: Array<{
          __typename: 'OrderPayment'
          id: string
          paidAt: DateTimeString
          paidAmount: { __typename: 'Money'; amount: number; currency: string }
        }>
        documents: {
          __typename: 'OrderDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'OrderDocumentEdge'
            node: {
              __typename: 'OrderDocument'
              id: string
              generated: boolean
              rendered: boolean
              signed: boolean
              canBeSigned: boolean
              note: string | null
              createdAt: DateTimeString
              documentType: {
                __typename: 'OrderDocumentType'
                id: string
                name: string
                isGeneratable: boolean
                requiresSignature: boolean
              }
              signers: Array<{
                __typename: 'OrderDocumentSignerModel'
                role: string
                name: string | null
                email: string | null
                isCustomer: boolean
              }>
              activeEsignRequest: {
                __typename: 'OrderDocumentEsignRequest'
                id: string
                status: EsignRequestStatus
                signerName: string
                signerEmail: string
                createdAt: DateTimeString
                signedOrderDocument: {
                  __typename: 'OrderDocument'
                  id: string
                  file: { __typename: 'RemoteFile'; url: string | null }
                } | null
              } | null
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'OrderEventConnection'
          edges: Array<{
            __typename: 'OrderEventEdge'
            node: {
              __typename: 'OrderEvent'
              id: string
              type: OrderEventType
              date: DateTimeString
              parameters: any
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        financialAccounts: Array<{
          __typename: 'OrderFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
        }>
        caseProducts: Array<{
          __typename: 'CaseProduct'
          id: string
          isActive: boolean
          product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        }>
      }>
      products: Array<{
        __typename: 'CaseProduct'
        id: string
        createdAt: DateTimeString
        startAt: DateTimeString
        endAt: DateTimeString
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
        financialAccounts: Array<{
          __typename: 'CaseProductFinancialAccount'
          id: string
          financialAccount: { __typename: 'FinancialAccount'; id: string }
        }>
      }>
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type OrderDocumentUpdatedSubscriptionVariables = Exact<{
  id: string | number
  ignoreSameSession?: boolean | null | undefined
  fetchInitial?: boolean | null | undefined
}>

export type OrderDocumentUpdatedSubscription = {
  __typename: 'Subscription'
  orderDocumentUpdated: {
    __typename: 'OrderDocumentUpdatedEventModel'
    author:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
    node: {
      __typename: 'OrderDocument'
      id: string
      generated: boolean
      rendered: boolean
      signed: boolean
      canBeSigned: boolean
      note: string | null
      createdAt: DateTimeString
      documentType: {
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
      }
      signers: Array<{
        __typename: 'OrderDocumentSignerModel'
        role: string
        name: string | null
        email: string | null
        isCustomer: boolean
      }>
      activeEsignRequest: {
        __typename: 'OrderDocumentEsignRequest'
        id: string
        status: EsignRequestStatus
        signerName: string
        signerEmail: string
        createdAt: DateTimeString
        signedOrderDocument: {
          __typename: 'OrderDocument'
          id: string
          file: { __typename: 'RemoteFile'; url: string | null }
        } | null
      } | null
      file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }
  }
}

export type OrderUpdatedSubscriptionVariables = Exact<{
  id: string | number
  ignoreSameSession?: boolean | null | undefined
  fetchInitial?: boolean | null | undefined
}>

export type OrderUpdatedSubscription = {
  __typename: 'Subscription'
  orderUpdated: {
    __typename: 'OrderUpdatedEventModel'
    author:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
    node: {
      __typename: 'Order'
      id: string
      code: string
      status: OrderStatus
      paymentStatus: OrderPaymentStatus
      feeRate: number
      adjustedFeeRate: number | null
      durationInMonths: number
      documentBookCode: string | null
      startAt: DateTimeString | null
      createdAt: DateTimeString
      businessEntity: { __typename: 'BusinessEntity'; id: string; name: string }
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        name: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
      fee: { __typename: 'Money'; amount: number; currency: string } | null
      estimatedFee: { __typename: 'Money'; amount: number; currency: string } | null
      paidAmount: { __typename: 'Money'; amount: number; currency: string }
      remainingAmount: { __typename: 'Money'; amount: number; currency: string } | null
      additionalPaymentOrderAdjustmentRange: {
        __typename: 'MoneyRange'
        min: { __typename: 'Money'; amount: number; currency: string }
        max: { __typename: 'Money'; amount: number; currency: string }
      } | null
      currentProgress: {
        __typename: 'OrderCurrentProgress'
        nextState: OrderStatus
        isValid: boolean
        blockers: Array<
          | {
              __typename: 'OrderStateProgressBlockerCaseDocumentType'
              caseDocumentType: CaseDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
              case: { __typename: 'Case'; id: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerCustomerDocumentType'
              customerDocumentType: CustomerDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
              financialAccountDocumentType: FinancialAccountDocumentType
            }
          | {
              __typename: 'OrderStateProgressBlockerGenerateDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderDocumentType'
              documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
            }
          | {
              __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
              orderPaymentStatus: Array<OrderPaymentStatus>
            }
          | {
              __typename: 'OrderStateProgressBlockerValidationEntity'
              orderValidationEntity: OrderValidationEntity
              entityReferenceCodes: Array<string>
            }
        >
      } | null
      payments: Array<{
        __typename: 'OrderPayment'
        id: string
        paidAt: DateTimeString
        paidAmount: { __typename: 'Money'; amount: number; currency: string }
      }>
      documents: {
        __typename: 'OrderDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'OrderDocumentEdge'
          node: {
            __typename: 'OrderDocument'
            id: string
            generated: boolean
            rendered: boolean
            signed: boolean
            canBeSigned: boolean
            note: string | null
            createdAt: DateTimeString
            documentType: {
              __typename: 'OrderDocumentType'
              id: string
              name: string
              isGeneratable: boolean
              requiresSignature: boolean
            }
            signers: Array<{
              __typename: 'OrderDocumentSignerModel'
              role: string
              name: string | null
              email: string | null
              isCustomer: boolean
            }>
            activeEsignRequest: {
              __typename: 'OrderDocumentEsignRequest'
              id: string
              status: EsignRequestStatus
              signerName: string
              signerEmail: string
              createdAt: DateTimeString
              signedOrderDocument: {
                __typename: 'OrderDocument'
                id: string
                file: { __typename: 'RemoteFile'; url: string | null }
              } | null
            } | null
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'OrderEventConnection'
        edges: Array<{
          __typename: 'OrderEventEdge'
          node: {
            __typename: 'OrderEvent'
            id: string
            type: OrderEventType
            date: DateTimeString
            parameters: any
            documentType: { __typename: 'OrderDocumentType'; id: string; name: string } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      financialAccounts: Array<{
        __typename: 'OrderFinancialAccount'
        id: string
        financialAccount: { __typename: 'FinancialAccount'; id: string }
        outstandingDebt: { __typename: 'Money'; amount: number; currency: string } | null
      }>
      caseProducts: Array<{
        __typename: 'CaseProduct'
        id: string
        isActive: boolean
        product: { __typename: 'Product'; id: string; productType: ProductType; name: string }
      }>
    }
  }
}

export type ChannelAdminInfoFragment = {
  __typename: 'MessagingChannel'
  id: string
  displayNumber: string
  defaultPlaybookId: string | null
  defaultOwner: MessagingConversationOwnership | null
  defaultOversightScenarioId: string | null
  supportPlaybookId: string | null
  isDefaultTransactional: boolean
  phoneNumberId: string
  provider: string
  purpose: MessagingChannelPurpose
  status: MessagingChannelStatus
  wabaId: string | null
  metaAppId: string | null
  health: {
    __typename: 'MessagingChannelHealth'
    qualityRating: string | null
    qualityEvent: string | null
    qualityEventAt: DateTimeString | null
    messagingLimit: string | null
    healthSyncedAt: DateTimeString | null
  } | null
  caseActionDefaults: {
    __typename: 'MessagingChannelCaseActionDefault'
    id: string
    customerRepliedActionTypeId: string | null
    screeningCompletedActionTypeId: string | null
    updatedAt: DateTimeString
  } | null
  transactionalAutoReply: {
    __typename: 'MessagingChannelTransactionalAutoReply'
    id: string
    templateName: string
    templateLanguage: string
    updatedAt: DateTimeString
  } | null
  autoReply: {
    __typename: 'MessagingChannelAutoReply'
    id: string
    enabled: boolean
    text: string
    applyWhenBotOwned: boolean
    observeHolidays: boolean
    updatedAt: DateTimeString
    sendWindow: {
      __typename: 'MessagingSendWindow'
      tz: string
      rules: Array<{
        __typename: 'MessagingSendWindowRule'
        days: Array<MessagingSendWindowDay>
        start: string
        end: string
      }>
    } | null
  } | null
  caseType: { __typename: 'CaseType'; id: string; name: string } | null
}

export type ChannelBindCaseTypeMutationVariables = Exact<{
  id: string | number
  caseTypeId?: string | number | null | undefined
}>

export type ChannelBindCaseTypeMutation = {
  __typename: 'Mutation'
  channelBindCaseType: {
    __typename: 'MessagingChannelBindCaseType'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelClearTransactionalAutoReplyMutationVariables = Exact<{
  id: string | number
}>

export type ChannelClearTransactionalAutoReplyMutation = {
  __typename: 'Mutation'
  channelClearTransactionalAutoReply: {
    __typename: 'MessagingChannelTransactionalAutoReplyMutation'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      health: {
        __typename: 'MessagingChannelHealth'
        qualityRating: string | null
        qualityEvent: string | null
        qualityEventAt: DateTimeString | null
        messagingLimit: string | null
        healthSyncedAt: DateTimeString | null
      } | null
      caseActionDefaults: {
        __typename: 'MessagingChannelCaseActionDefault'
        id: string
        customerRepliedActionTypeId: string | null
        screeningCompletedActionTypeId: string | null
        updatedAt: DateTimeString
      } | null
      transactionalAutoReply: {
        __typename: 'MessagingChannelTransactionalAutoReply'
        id: string
        templateName: string
        templateLanguage: string
        updatedAt: DateTimeString
      } | null
      autoReply: {
        __typename: 'MessagingChannelAutoReply'
        id: string
        enabled: boolean
        text: string
        applyWhenBotOwned: boolean
        observeHolidays: boolean
        updatedAt: DateTimeString
        sendWindow: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        } | null
      } | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelCreateMutationVariables = Exact<{
  input: MessagingChannelCreateInput
}>

export type ChannelCreateMutation = {
  __typename: 'Mutation'
  channelCreate: {
    __typename: 'MessagingChannelCreate'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelRegisterNumberMutationVariables = Exact<{
  id: string | number
  pin: string
}>

export type ChannelRegisterNumberMutation = {
  __typename: 'Mutation'
  channelRegisterNumber: {
    __typename: 'MessagingChannelRegister'
    success: boolean
    registration: {
      __typename: 'MessagingChannelVerifyCheck'
      status: MessagingChannelVerifyCheckStatus
      detail: string | null
    } | null
    phoneNumber: {
      __typename: 'MessagingChannelVerifyPhoneNumber'
      id: string
      displayPhoneNumber: string | null
      platformType: string | null
      status: string | null
    } | null
  }
}

export type ChannelSetAutoReplyMutationVariables = Exact<{
  id: string | number
  input: MessagingChannelAutoReplyInput
}>

export type ChannelSetAutoReplyMutation = {
  __typename: 'Mutation'
  channelSetAutoReply: {
    __typename: 'MessagingChannelSetAutoReply'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      health: {
        __typename: 'MessagingChannelHealth'
        qualityRating: string | null
        qualityEvent: string | null
        qualityEventAt: DateTimeString | null
        messagingLimit: string | null
        healthSyncedAt: DateTimeString | null
      } | null
      caseActionDefaults: {
        __typename: 'MessagingChannelCaseActionDefault'
        id: string
        customerRepliedActionTypeId: string | null
        screeningCompletedActionTypeId: string | null
        updatedAt: DateTimeString
      } | null
      transactionalAutoReply: {
        __typename: 'MessagingChannelTransactionalAutoReply'
        id: string
        templateName: string
        templateLanguage: string
        updatedAt: DateTimeString
      } | null
      autoReply: {
        __typename: 'MessagingChannelAutoReply'
        id: string
        enabled: boolean
        text: string
        applyWhenBotOwned: boolean
        observeHolidays: boolean
        updatedAt: DateTimeString
        sendWindow: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        } | null
      } | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelSetCaseActionDefaultsMutationVariables = Exact<{
  id: string | number
  input: MessagingChannelCaseActionDefaultInput
}>

export type ChannelSetCaseActionDefaultsMutation = {
  __typename: 'Mutation'
  channelSetCaseActionDefaults: {
    __typename: 'MessagingChannelSetCaseActionDefaults'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      health: {
        __typename: 'MessagingChannelHealth'
        qualityRating: string | null
        qualityEvent: string | null
        qualityEventAt: DateTimeString | null
        messagingLimit: string | null
        healthSyncedAt: DateTimeString | null
      } | null
      caseActionDefaults: {
        __typename: 'MessagingChannelCaseActionDefault'
        id: string
        customerRepliedActionTypeId: string | null
        screeningCompletedActionTypeId: string | null
        updatedAt: DateTimeString
      } | null
      transactionalAutoReply: {
        __typename: 'MessagingChannelTransactionalAutoReply'
        id: string
        templateName: string
        templateLanguage: string
        updatedAt: DateTimeString
      } | null
      autoReply: {
        __typename: 'MessagingChannelAutoReply'
        id: string
        enabled: boolean
        text: string
        applyWhenBotOwned: boolean
        observeHolidays: boolean
        updatedAt: DateTimeString
        sendWindow: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        } | null
      } | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelSetTransactionalAutoReplyMutationVariables = Exact<{
  id: string | number
  input: MessagingChannelTransactionalAutoReplyInput
}>

export type ChannelSetTransactionalAutoReplyMutation = {
  __typename: 'Mutation'
  channelSetTransactionalAutoReply: {
    __typename: 'MessagingChannelTransactionalAutoReplyMutation'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      health: {
        __typename: 'MessagingChannelHealth'
        qualityRating: string | null
        qualityEvent: string | null
        qualityEventAt: DateTimeString | null
        messagingLimit: string | null
        healthSyncedAt: DateTimeString | null
      } | null
      caseActionDefaults: {
        __typename: 'MessagingChannelCaseActionDefault'
        id: string
        customerRepliedActionTypeId: string | null
        screeningCompletedActionTypeId: string | null
        updatedAt: DateTimeString
      } | null
      transactionalAutoReply: {
        __typename: 'MessagingChannelTransactionalAutoReply'
        id: string
        templateName: string
        templateLanguage: string
        updatedAt: DateTimeString
      } | null
      autoReply: {
        __typename: 'MessagingChannelAutoReply'
        id: string
        enabled: boolean
        text: string
        applyWhenBotOwned: boolean
        observeHolidays: boolean
        updatedAt: DateTimeString
        sendWindow: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        } | null
      } | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelSubscribeAppMutationVariables = Exact<{
  id: string | number
}>

export type ChannelSubscribeAppMutation = {
  __typename: 'Mutation'
  channelSubscribeApp: {
    __typename: 'MessagingChannelSubscribeApp'
    success: boolean
    subscribedApps: Array<{
      __typename: 'MessagingChannelSubscribedApp'
      id: string
      name: string | null
      link: string | null
    }>
  }
}

export type ChannelUpdateMutationVariables = Exact<{
  id: string | number
  input: MessagingChannelUpdateInput
}>

export type ChannelUpdateMutation = {
  __typename: 'Mutation'
  channelUpdate: {
    __typename: 'MessagingChannelUpdate'
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    }
  }
}

export type ChannelVerifyMutationVariables = Exact<{
  id: string | number
}>

export type ChannelVerifyMutation = {
  __typename: 'Mutation'
  channelVerify: {
    __typename: 'MessagingChannelVerify'
    checks: {
      __typename: 'MessagingChannelVerifyChecks'
      phoneNumberAccess: {
        __typename: 'MessagingChannelVerifyCheck'
        status: MessagingChannelVerifyCheckStatus
        detail: string | null
      }
      registration: {
        __typename: 'MessagingChannelVerifyCheck'
        status: MessagingChannelVerifyCheckStatus
        detail: string | null
      }
      wabaAccess: {
        __typename: 'MessagingChannelVerifyCheck'
        status: MessagingChannelVerifyCheckStatus
        detail: string | null
      }
      appSubscription: {
        __typename: 'MessagingChannelVerifyCheck'
        status: MessagingChannelVerifyCheckStatus
        detail: string | null
      }
    }
    phoneNumber: {
      __typename: 'MessagingChannelVerifyPhoneNumber'
      id: string
      displayPhoneNumber: string | null
      verifiedName: string | null
      qualityRating: string | null
      messagingLimit: string | null
      platformType: string | null
      status: string | null
    } | null
    waba: { __typename: 'MessagingChannelVerifyWaba'; id: string; name: string | null } | null
    subscribedApps: Array<{
      __typename: 'MessagingChannelSubscribedApp'
      id: string
      name: string | null
      link: string | null
    }> | null
  }
}

export type ChannelsAdminQueryVariables = Exact<{ [key: string]: never }>

export type ChannelsAdminQuery = {
  __typename: 'Query'
  channels: Array<{
    __typename: 'MessagingChannel'
    id: string
    displayNumber: string
    defaultPlaybookId: string | null
    defaultOwner: MessagingConversationOwnership | null
    defaultOversightScenarioId: string | null
    supportPlaybookId: string | null
    isDefaultTransactional: boolean
    phoneNumberId: string
    provider: string
    purpose: MessagingChannelPurpose
    status: MessagingChannelStatus
    wabaId: string | null
    metaAppId: string | null
    health: {
      __typename: 'MessagingChannelHealth'
      qualityRating: string | null
      qualityEvent: string | null
      qualityEventAt: DateTimeString | null
      messagingLimit: string | null
      healthSyncedAt: DateTimeString | null
    } | null
    caseActionDefaults: {
      __typename: 'MessagingChannelCaseActionDefault'
      id: string
      customerRepliedActionTypeId: string | null
      screeningCompletedActionTypeId: string | null
      updatedAt: DateTimeString
    } | null
    transactionalAutoReply: {
      __typename: 'MessagingChannelTransactionalAutoReply'
      id: string
      templateName: string
      templateLanguage: string
      updatedAt: DateTimeString
    } | null
    autoReply: {
      __typename: 'MessagingChannelAutoReply'
      id: string
      enabled: boolean
      text: string
      applyWhenBotOwned: boolean
      observeHolidays: boolean
      updatedAt: DateTimeString
      sendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          start: string
          end: string
        }>
      } | null
    } | null
    caseType: { __typename: 'CaseType'; id: string; name: string } | null
  }>
}

export type CustomerAddressFragment = {
  __typename: 'CustomerAddress'
  id: string
  city: string
  cityArea: string | null
  note: string | null
  postalCode: string
  streetAddress1: string
  streetAddress2: string | null
  isPermanentResidency: boolean
  isDefault: boolean
  country: { __typename: 'Country'; code: string; name: string } | null
  countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
}

export type CustomerConversationFragment = {
  __typename: 'MessagingConversation'
  id: string
  customerWaId: string
  lifecycle: MessagingConversationLifecycle
  lastActivityAt: DateTimeString
  ownership: MessagingConversationOwnership
  customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
  assignee: { __typename: 'User'; id: string; displayName: string } | null
  linkedCases: Array<{ __typename: 'CaseBrief'; id: string; code: string }>
  channel: { __typename: 'MessagingChannel'; id: string; displayNumber: string } | null
}

export type CustomerDetailFragment = {
  __typename: 'Customer'
  id: string
  code: string
  name: string
  gender: Gender | null
  accountNodeAccess: AccountNodeAccess | null
  marriageStatus: CustomerMaritalStatus | null
  educationLevel: CustomerEducationLevel | null
  governmentId: string | null
  birthDate: DateString | null
  birthPlace: string | null
  occupation: string | null
  residentialStatus: CustomerResidentialStatus | null
  jobTitle: string | null
  employmentStatus: CustomerEmploymentStatus | null
  companyType: CustomerCompanyType | null
  salaryRange: CustomerSalaryRange | null
  referralSource: CustomerReferralSource | null
  otherDebtTypes: Array<CustomerOtherDebtType>
  otherDebtTypeOther: string | null
  portalEnabledAt: DateTimeString | null
  domicile: string | null
  addresses: Array<{
    __typename: 'CustomerAddress'
    id: string
    city: string
    cityArea: string | null
    note: string | null
    postalCode: string
    streetAddress1: string
    streetAddress2: string | null
    isPermanentResidency: boolean
    isDefault: boolean
    cases: { __typename: 'CaseConnection'; totalCount: number }
    country: { __typename: 'Country'; code: string; name: string } | null
    countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
  }>
  contactPhones: Array<{
    __typename: 'CustomerContactPhone'
    id: string
    note: string | null
    isDefault: boolean
    whatsappCapability: MessagingNumberCapability
    cases: { __typename: 'CaseConnection'; totalCount: number }
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    }
  }>
  contactEmails: Array<{
    __typename: 'CustomerContactEmail'
    id: string
    emailAddress: string
    note: string | null
    isDefault: boolean
    cases: { __typename: 'CaseConnection'; totalCount: number }
  }>
  documents: {
    __typename: 'CustomerDocumentConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CustomerDocumentEdge'
      node: {
        __typename: 'CustomerDocument'
        id: string
        type: CustomerDocumentType
        otherType: string | null
        note: string | null
        createdAt: DateTimeString
        file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  events: {
    __typename: 'CustomerEventConnection'
    edges: Array<{
      __typename: 'CustomerEventEdge'
      node: {
        __typename: 'CustomerEvent'
        id: string
        type: CustomerEventType
        date: DateTimeString
        parameters: any
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
  domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
}

export type CustomerDetailFinancialAccountFragment = {
  __typename: 'FinancialAccount'
  id: string
  code: string
  status: FinancialAccountStatus
  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
  provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
  case: {
    __typename: 'Case'
    id: string
    status: CaseStatus
    stage: CaseStage
    assignedToUser: { __typename: 'User'; displayName: string } | null
    closeReason: { __typename: 'CaseCloseReason'; name: string } | null
  }
}

export type CustomerEmailFragment = {
  __typename: 'CustomerContactEmail'
  id: string
  emailAddress: string
  note: string | null
  isDefault: boolean
}

export type CustomerEventFragment = {
  __typename: 'CustomerEvent'
  id: string
  type: CustomerEventType
  date: DateTimeString
  parameters: any
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type CustomerListFragment = {
  __typename: 'Customer'
  id: string
  name: string
  createdAt: DateTimeString
  cases: { __typename: 'CaseConnection'; totalCount: number }
}

export type CustomerPhoneFragment = {
  __typename: 'CustomerContactPhone'
  id: string
  note: string | null
  isDefault: boolean
  whatsappCapability: MessagingNumberCapability
  phoneNumber: {
    __typename: 'PhoneNumber'
    countryCode: string | null
    displayNumber: string
    international: string
    national: string | null
  }
}

export type CustomerAddressCreateMutationVariables = Exact<{
  id: string | number
  input: CustomerAddressCreateInput
}>

export type CustomerAddressCreateMutation = {
  __typename: 'Mutation'
  customerAddressCreate: {
    __typename: 'CustomerAddressCreate'
    customerAddress: {
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
      }
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }
  }
}

export type CustomerAddressDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CustomerAddressDeleteMutation = {
  __typename: 'Mutation'
  customerAddressDelete: {
    __typename: 'CustomerAddressDelete'
    customerAddress: {
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
      }
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }
  }
}

export type CustomerDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CustomerDeleteMutation = {
  __typename: 'Mutation'
  customerDelete: {
    __typename: 'CustomerDelete'
    customer: { __typename: 'Customer'; id: string; code: string; name: string }
  }
}

export type CustomerDocumentCreateMutationVariables = Exact<{
  customerId: string | number
  input: CustomerDocumentCreateInput
}>

export type CustomerDocumentCreateMutation = {
  __typename: 'Mutation'
  customerDocumentCreate: {
    __typename: 'CustomerDocumentCreate'
    customerDocument: {
      __typename: 'CustomerDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      customer: {
        __typename: 'Customer'
        id: string
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type CustomerDocumentDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CustomerDocumentDeleteMutation = {
  __typename: 'Mutation'
  customerDocumentDelete: {
    __typename: 'CustomerDocumentDelete'
    customerDocument: {
      __typename: 'CustomerDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      customer: {
        __typename: 'Customer'
        id: string
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type CustomerDocumentIdCardScanMutationVariables = Exact<{
  documentId: string | number
}>

export type CustomerDocumentIdCardScanMutation = {
  __typename: 'Mutation'
  ocrScanCustomerIdCard: {
    __typename: 'OcrScanCustomerIdCard'
    metadata: any
    cardDataCustomer: {
      __typename: 'Customer'
      name: string
      gender: Gender | null
      birthDate: DateString | null
      birthPlace: string | null
      marriageStatus: CustomerMaritalStatus | null
      educationLevel: CustomerEducationLevel | null
      governmentId: string | null
      occupation: string | null
    }
    cardDataAddress: {
      __typename: 'CustomerAddress'
      streetAddress1: string
      streetAddress2: string | null
      city: string
      cityArea: string | null
      countryCode: string
      postalCode: string
      isDefault: boolean
      isPermanentResidency: boolean
      note: string | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }
  }
}

export type CustomerDocumentIdCardScanCommitMutationVariables = Exact<{
  customerId: string | number
  addressInput: CustomerAddressCreateInput
  customerUpdateInput: CustomerUpdateInput
}>

export type CustomerDocumentIdCardScanCommitMutation = {
  __typename: 'Mutation'
  customerAddressCreate: {
    __typename: 'CustomerAddressCreate'
    customerAddress: {
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
      }
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }
  }
  customerUpdate: {
    __typename: 'CustomerUpdate'
    customer: {
      __typename: 'Customer'
      id: string
      code: string
      name: string
      gender: Gender | null
      accountNodeAccess: AccountNodeAccess | null
      marriageStatus: CustomerMaritalStatus | null
      educationLevel: CustomerEducationLevel | null
      governmentId: string | null
      birthDate: DateString | null
      birthPlace: string | null
      occupation: string | null
      residentialStatus: CustomerResidentialStatus | null
      jobTitle: string | null
      employmentStatus: CustomerEmploymentStatus | null
      companyType: CustomerCompanyType | null
      salaryRange: CustomerSalaryRange | null
      referralSource: CustomerReferralSource | null
      otherDebtTypes: Array<CustomerOtherDebtType>
      otherDebtTypeOther: string | null
      portalEnabledAt: DateTimeString | null
      domicile: string | null
      cases: {
        __typename: 'CaseConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CaseEdge'
          node: {
            __typename: 'Case'
            pendingOrder: {
              __typename: 'Order'
              id: string
              currentProgress: {
                __typename: 'OrderCurrentProgress'
                nextState: OrderStatus
                isValid: boolean
                blockers: Array<
                  | {
                      __typename: 'OrderStateProgressBlockerCaseDocumentType'
                      caseDocumentType: CaseDocumentType
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerCaseIdentityCheck'
                      case: { __typename: 'Case'; id: string }
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerCustomerDocumentType'
                      customerDocumentType: CustomerDocumentType
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerFinancialAccountDocumentType'
                      financialAccountDocumentType: FinancialAccountDocumentType
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerGenerateDocumentType'
                      documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerOrderDocumentType'
                      documentType: { __typename: 'OrderDocumentType'; id: string; name: string }
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerOrderPaymentStatus'
                      orderPaymentStatus: Array<OrderPaymentStatus>
                    }
                  | {
                      __typename: 'OrderStateProgressBlockerValidationEntity'
                      orderValidationEntity: OrderValidationEntity
                      entityReferenceCodes: Array<string>
                    }
                >
              } | null
            } | null
          }
        }>
      }
      addresses: Array<{
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        cases: { __typename: 'CaseConnection'; totalCount: number }
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      }>
      contactPhones: Array<{
        __typename: 'CustomerContactPhone'
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        cases: { __typename: 'CaseConnection'; totalCount: number }
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      }>
      contactEmails: Array<{
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }>
      documents: {
        __typename: 'CustomerDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CustomerDocumentEdge'
          node: {
            __typename: 'CustomerDocument'
            id: string
            type: CustomerDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CustomerEventConnection'
        edges: Array<{
          __typename: 'CustomerEventEdge'
          node: {
            __typename: 'CustomerEvent'
            id: string
            type: CustomerEventType
            date: DateTimeString
            parameters: any
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
    }
  }
}

export type CustomerDocumentUpdateMutationVariables = Exact<{
  id: string | number
  input: CustomerDocumentUpdateInput
}>

export type CustomerDocumentUpdateMutation = {
  __typename: 'Mutation'
  customerDocumentUpdate: {
    __typename: 'CustomerDocumentUpdate'
    customerDocument: {
      __typename: 'CustomerDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      customer: {
        __typename: 'Customer'
        id: string
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
      }
    }
  }
}

export type CustomerEmailCreateMutationVariables = Exact<{
  id: string | number
  input: CustomerContactEmailCreateInput
}>

export type CustomerEmailCreateMutation = {
  __typename: 'Mutation'
  customerContactEmailCreate: {
    __typename: 'CustomerContactEmailCreate'
    customerContactEmail: {
      __typename: 'CustomerContactEmail'
      id: string
      emailAddress: string
      note: string | null
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }>
      }
    }
  }
}

export type CustomerEmailDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CustomerEmailDeleteMutation = {
  __typename: 'Mutation'
  customerContactEmailDelete: {
    __typename: 'CustomerContactEmailDelete'
    customerContactEmail: {
      __typename: 'CustomerContactEmail'
      id: string
      emailAddress: string
      note: string | null
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }>
      }
    }
  }
}

export type CustomerMergeMutationVariables = Exact<{
  targetId: string | number
  sourceId: string | number
}>

export type CustomerMergeMutation = {
  __typename: 'Mutation'
  customerMerge: {
    __typename: 'CustomerMerge'
    movedCaseCount: number
    mergedContactCount: number
    customer: { __typename: 'Customer'; id: string; code: string; name: string }
  }
}

export type CustomerPhoneCreateMutationVariables = Exact<{
  id: string | number
  input: CustomerContactPhoneCreateInput
}>

export type CustomerPhoneCreateMutation = {
  __typename: 'Mutation'
  customerContactPhoneCreate: {
    __typename: 'CustomerContactPhoneCreate'
    customerContactPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      note: string | null
      isDefault: boolean
      whatsappCapability: MessagingNumberCapability
      customer: {
        __typename: 'Customer'
        id: string
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          cases: { __typename: 'CaseConnection'; totalCount: number }
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
      }
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    }
  }
}

export type CustomerPhoneDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CustomerPhoneDeleteMutation = {
  __typename: 'Mutation'
  customerContactPhoneDelete: {
    __typename: 'CustomerContactPhoneDelete'
    customerContactPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      note: string | null
      isDefault: boolean
      whatsappCapability: MessagingNumberCapability
      customer: {
        __typename: 'Customer'
        id: string
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          cases: { __typename: 'CaseConnection'; totalCount: number }
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
      }
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    }
  }
}

export type CustomerPortalAccessDisableMutationVariables = Exact<{
  id: string | number
}>

export type CustomerPortalAccessDisableMutation = {
  __typename: 'Mutation'
  customerPortalAccessDisable: {
    __typename: 'CustomerPortalAccessDisable'
    customer: { __typename: 'Customer'; id: string; portalEnabledAt: DateTimeString | null }
  }
}

export type CustomerPortalAccessSendMutationVariables = Exact<{
  id: string | number
  delivery: CustomerPortalInviteDeliveryMethod
  targetPhoneId?: string | number | null | undefined
  acknowledgedContactId?: string | number | null | undefined
  acknowledgedStatus?: CustomerPortalInviteTargetStatus | null | undefined
}>

export type CustomerPortalAccessSendMutation = {
  __typename: 'Mutation'
  customerPortalAccessSend: {
    __typename: 'CustomerPortalAccessSend'
    delivery: CustomerPortalInviteDeliveryMethod
    inviteId: string | null
    inviteFailure: CustomerPortalInviteFailure | null
    readAloudToken: string | null
    customer: { __typename: 'Customer'; id: string; portalEnabledAt: DateTimeString | null }
  }
}

export type CustomerPortalInviteSendMutationVariables = Exact<{
  id: string | number
  delivery: CustomerPortalInviteDeliveryMethod
  targetPhoneId?: string | number | null | undefined
  acknowledgedContactId?: string | number | null | undefined
  acknowledgedStatus?: CustomerPortalInviteTargetStatus | null | undefined
}>

export type CustomerPortalInviteSendMutation = {
  __typename: 'Mutation'
  customerPortalInviteSend: {
    __typename: 'CustomerPortalAccessSend'
    delivery: CustomerPortalInviteDeliveryMethod
    inviteId: string | null
    readAloudToken: string | null
    customer: { __typename: 'Customer'; id: string; portalEnabledAt: DateTimeString | null }
  }
}

export type CustomerRequestErasureMutationVariables = Exact<{
  id: string | number
}>

export type CustomerRequestErasureMutation = {
  __typename: 'Mutation'
  customerRequestErasure: {
    __typename: 'CustomerErasure'
    casesRedacted: number
    documentsDeleted: number
    customer: { __typename: 'Customer'; id: string; code: string; name: string }
  }
}

export type CustomerSetDefaultAddressMutationVariables = Exact<{
  id: string | number
}>

export type CustomerSetDefaultAddressMutation = {
  __typename: 'Mutation'
  customerAddressSetDefault: {
    __typename: 'CustomerAddressSetDefault'
    customerAddress: {
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        gender: Gender | null
        accountNodeAccess: AccountNodeAccess | null
        marriageStatus: CustomerMaritalStatus | null
        educationLevel: CustomerEducationLevel | null
        governmentId: string | null
        birthDate: DateString | null
        birthPlace: string | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        portalEnabledAt: DateTimeString | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          cases: { __typename: 'CaseConnection'; totalCount: number }
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }>
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }
  }
}

export type CustomerSetDefaultEmailMutationVariables = Exact<{
  id: string | number
}>

export type CustomerSetDefaultEmailMutation = {
  __typename: 'Mutation'
  customerEmailSetDefault: {
    __typename: 'CustomerContactEmailSetDefault'
    customerContactEmail: {
      __typename: 'CustomerContactEmail'
      id: string
      emailAddress: string
      note: string | null
      isDefault: boolean
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        gender: Gender | null
        accountNodeAccess: AccountNodeAccess | null
        marriageStatus: CustomerMaritalStatus | null
        educationLevel: CustomerEducationLevel | null
        governmentId: string | null
        birthDate: DateString | null
        birthPlace: string | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        portalEnabledAt: DateTimeString | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          cases: { __typename: 'CaseConnection'; totalCount: number }
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }>
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
    }
  }
}

export type CustomerSetDefaultPhoneMutationVariables = Exact<{
  id: string | number
}>

export type CustomerSetDefaultPhoneMutation = {
  __typename: 'Mutation'
  customerPhoneSetDefault: {
    __typename: 'CustomerContactPhoneSetDefault'
    customerContactPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      note: string | null
      isDefault: boolean
      whatsappCapability: MessagingNumberCapability
      customer: {
        __typename: 'Customer'
        id: string
        code: string
        name: string
        gender: Gender | null
        accountNodeAccess: AccountNodeAccess | null
        marriageStatus: CustomerMaritalStatus | null
        educationLevel: CustomerEducationLevel | null
        governmentId: string | null
        birthDate: DateString | null
        birthPlace: string | null
        occupation: string | null
        residentialStatus: CustomerResidentialStatus | null
        jobTitle: string | null
        employmentStatus: CustomerEmploymentStatus | null
        companyType: CustomerCompanyType | null
        salaryRange: CustomerSalaryRange | null
        referralSource: CustomerReferralSource | null
        otherDebtTypes: Array<CustomerOtherDebtType>
        otherDebtTypeOther: string | null
        portalEnabledAt: DateTimeString | null
        domicile: string | null
        addresses: Array<{
          __typename: 'CustomerAddress'
          id: string
          city: string
          cityArea: string | null
          note: string | null
          postalCode: string
          streetAddress1: string
          streetAddress2: string | null
          isPermanentResidency: boolean
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
          country: { __typename: 'Country'; code: string; name: string } | null
          countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        }>
        contactPhones: Array<{
          __typename: 'CustomerContactPhone'
          id: string
          note: string | null
          isDefault: boolean
          whatsappCapability: MessagingNumberCapability
          cases: { __typename: 'CaseConnection'; totalCount: number }
          phoneNumber: {
            __typename: 'PhoneNumber'
            countryCode: string | null
            displayNumber: string
            international: string
            national: string | null
          }
        }>
        contactEmails: Array<{
          __typename: 'CustomerContactEmail'
          id: string
          emailAddress: string
          note: string | null
          isDefault: boolean
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }>
        documents: {
          __typename: 'CustomerDocumentConnection'
          totalCount: number
          edges: Array<{
            __typename: 'CustomerDocumentEdge'
            node: {
              __typename: 'CustomerDocument'
              id: string
              type: CustomerDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        events: {
          __typename: 'CustomerEventConnection'
          edges: Array<{
            __typename: 'CustomerEventEdge'
            node: {
              __typename: 'CustomerEvent'
              id: string
              type: CustomerEventType
              date: DateTimeString
              parameters: any
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }
          }>
        }
        domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
        domicileRegency: {
          __typename: 'Regency'
          id: string
          name: string
          kind: RegencyKind
        } | null
      }
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    }
  }
}

export type CustomerUpdateMutationVariables = Exact<{
  id: string | number
  input: CustomerUpdateInput
}>

export type CustomerUpdateMutation = {
  __typename: 'Mutation'
  customerUpdate: {
    __typename: 'CustomerUpdate'
    customer: {
      __typename: 'Customer'
      id: string
      code: string
      name: string
      gender: Gender | null
      accountNodeAccess: AccountNodeAccess | null
      marriageStatus: CustomerMaritalStatus | null
      educationLevel: CustomerEducationLevel | null
      governmentId: string | null
      birthDate: DateString | null
      birthPlace: string | null
      occupation: string | null
      residentialStatus: CustomerResidentialStatus | null
      jobTitle: string | null
      employmentStatus: CustomerEmploymentStatus | null
      companyType: CustomerCompanyType | null
      salaryRange: CustomerSalaryRange | null
      referralSource: CustomerReferralSource | null
      otherDebtTypes: Array<CustomerOtherDebtType>
      otherDebtTypeOther: string | null
      portalEnabledAt: DateTimeString | null
      domicile: string | null
      addresses: Array<{
        __typename: 'CustomerAddress'
        id: string
        city: string
        cityArea: string | null
        note: string | null
        postalCode: string
        streetAddress1: string
        streetAddress2: string | null
        isPermanentResidency: boolean
        isDefault: boolean
        cases: { __typename: 'CaseConnection'; totalCount: number }
        country: { __typename: 'Country'; code: string; name: string } | null
        countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      }>
      contactPhones: Array<{
        __typename: 'CustomerContactPhone'
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        cases: { __typename: 'CaseConnection'; totalCount: number }
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      }>
      contactEmails: Array<{
        __typename: 'CustomerContactEmail'
        id: string
        emailAddress: string
        note: string | null
        isDefault: boolean
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }>
      documents: {
        __typename: 'CustomerDocumentConnection'
        totalCount: number
        edges: Array<{
          __typename: 'CustomerDocumentEdge'
          node: {
            __typename: 'CustomerDocument'
            id: string
            type: CustomerDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      events: {
        __typename: 'CustomerEventConnection'
        edges: Array<{
          __typename: 'CustomerEventEdge'
          node: {
            __typename: 'CustomerEvent'
            id: string
            type: CustomerEventType
            date: DateTimeString
            parameters: any
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }
        }>
      }
      domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
      domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
    }
  }
}

export type CustomerConversationsQueryVariables = Exact<{
  id: string | number
}>

export type CustomerConversationsQuery = {
  __typename: 'Query'
  customer: {
    __typename: 'Customer'
    id: string
    conversations: Array<{
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      ownership: MessagingConversationOwnership
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{ __typename: 'CaseBrief'; id: string; code: string }>
      channel: { __typename: 'MessagingChannel'; id: string; displayNumber: string } | null
    }>
  } | null
}

export type CustomerDetailQueryVariables = Exact<{
  id: string | number
  casesFirst?: number | null | undefined
  casesAfter?: string | null | undefined
  casesLast?: number | null | undefined
  casesBefore?: string | null | undefined
  financialAccountsFirst?: number | null | undefined
  financialAccountsAfter?: string | null | undefined
  financialAccountsLast?: number | null | undefined
  financialAccountsBefore?: string | null | undefined
}>

export type CustomerDetailQuery = {
  __typename: 'Query'
  customer: {
    __typename: 'Customer'
    id: string
    code: string
    name: string
    gender: Gender | null
    accountNodeAccess: AccountNodeAccess | null
    marriageStatus: CustomerMaritalStatus | null
    educationLevel: CustomerEducationLevel | null
    governmentId: string | null
    birthDate: DateString | null
    birthPlace: string | null
    occupation: string | null
    residentialStatus: CustomerResidentialStatus | null
    jobTitle: string | null
    employmentStatus: CustomerEmploymentStatus | null
    companyType: CustomerCompanyType | null
    salaryRange: CustomerSalaryRange | null
    referralSource: CustomerReferralSource | null
    otherDebtTypes: Array<CustomerOtherDebtType>
    otherDebtTypeOther: string | null
    portalEnabledAt: DateTimeString | null
    domicile: string | null
    cases: {
      __typename: 'CaseConnection'
      totalCount: number
      edges: Array<{
        __typename: 'CaseEdge'
        node: {
          __typename: 'Case'
          id: string
          code: string
          stage: CaseStage
          status: CaseStatus
          flow: CaseFlow | null
          updatedAt: DateTimeString
          type: { __typename: 'CaseType'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        }
      }>
      pageInfo: {
        __typename: 'PageInfo'
        endCursor: string | null
        hasNextPage: boolean
        hasPreviousPage: boolean
        startCursor: string | null
      }
    }
    financialAccounts: {
      __typename: 'FinancialAccountConnection'
      totalCount: number
      edges: Array<{
        __typename: 'FinancialAccountEdge'
        node: {
          __typename: 'FinancialAccount'
          id: string
          code: string
          status: FinancialAccountStatus
          outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
          provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
          case: {
            __typename: 'Case'
            id: string
            status: CaseStatus
            stage: CaseStage
            assignedToUser: { __typename: 'User'; displayName: string } | null
            closeReason: { __typename: 'CaseCloseReason'; name: string } | null
          }
        }
      }>
      pageInfo: {
        __typename: 'PageInfo'
        endCursor: string | null
        hasNextPage: boolean
        hasPreviousPage: boolean
        startCursor: string | null
      }
    }
    addresses: Array<{
      __typename: 'CustomerAddress'
      id: string
      city: string
      cityArea: string | null
      note: string | null
      postalCode: string
      streetAddress1: string
      streetAddress2: string | null
      isPermanentResidency: boolean
      isDefault: boolean
      cases: { __typename: 'CaseConnection'; totalCount: number }
      country: { __typename: 'Country'; code: string; name: string } | null
      countryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    }>
    contactPhones: Array<{
      __typename: 'CustomerContactPhone'
      id: string
      note: string | null
      isDefault: boolean
      whatsappCapability: MessagingNumberCapability
      cases: { __typename: 'CaseConnection'; totalCount: number }
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    }>
    contactEmails: Array<{
      __typename: 'CustomerContactEmail'
      id: string
      emailAddress: string
      note: string | null
      isDefault: boolean
      cases: { __typename: 'CaseConnection'; totalCount: number }
    }>
    documents: {
      __typename: 'CustomerDocumentConnection'
      totalCount: number
      edges: Array<{
        __typename: 'CustomerDocumentEdge'
        node: {
          __typename: 'CustomerDocument'
          id: string
          type: CustomerDocumentType
          otherType: string | null
          note: string | null
          createdAt: DateTimeString
          file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    events: {
      __typename: 'CustomerEventConnection'
      edges: Array<{
        __typename: 'CustomerEventEdge'
        node: {
          __typename: 'CustomerEvent'
          id: string
          type: CustomerEventType
          date: DateTimeString
          parameters: any
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
        }
      }>
    }
    domicileCountryArea: { __typename: 'CountryArea'; id: string; name: string } | null
    domicileRegency: { __typename: 'Regency'; id: string; name: string; kind: RegencyKind } | null
  } | null
}

export type CustomerListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: CustomersFilterInput | null | undefined
  sort?: CustomersSortInput | null | undefined
}>

export type CustomerListQuery = {
  __typename: 'Query'
  customers: {
    __typename: 'CustomerConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CustomerEdge'
      node: {
        __typename: 'Customer'
        id: string
        name: string
        createdAt: DateTimeString
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      endCursor: string | null
      hasNextPage: boolean
      hasPreviousPage: boolean
      startCursor: string | null
    }
  }
}

export type CustomerPortalInviteTargetsQueryVariables = Exact<{
  id: string | number
  PERMISSION_MANAGE_CUSTOMERS?: boolean
  PERMISSION_MANAGE_SETTINGS?: boolean
}>

export type CustomerPortalInviteTargetsQuery = { __typename: 'Query' } & {
  __typename: 'Query'
  customerPortalInviteTargets?: {
    __typename: 'CustomerPortalInviteTargets'
    phones: Array<{
      __typename: 'CustomerPortalInvitePhoneTarget'
      invitable: boolean
      status: CustomerPortalInviteTargetStatus
      phone: {
        __typename: 'CustomerContactPhone'
        id: string
        note: string | null
        isDefault: boolean
        whatsappCapability: MessagingNumberCapability
        phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string }
      }
    }>
    email: {
      __typename: 'CustomerPortalInviteEmailTarget'
      status: CustomerPortalInviteTargetStatus
      email: { __typename: 'CustomerContactEmail'; id: string; emailAddress: string }
    } | null
  }
  portalDeliveryOptions?: { __typename: 'PortalDeliveryOptions'; whatsapp: boolean }
}

export type FinancialProviderAvailableAccountTypeFragment = {
  __typename: 'FinancialAccountType'
  id: string
  value: string
  name: string
}

export type FinancialProviderDetailFragment = {
  __typename: 'FinancialProvider'
  businessName: string
  id: string
  name: string
  createdAt: DateTimeString
  accountTypes: Array<{ __typename: 'FinancialAccountType'; id: string; name: string }>
}

export type FinancialProviderDetailContactFragment = {
  __typename: 'FinancialProviderContact'
  id: string
  name: string
  role: string
  emailAddress: string | null
  createdAt: DateTimeString
  phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string } | null
}

export type FinancialProviderListFragment = {
  __typename: 'FinancialProvider'
  id: string
  name: string
  createdAt: DateTimeString
}

export type FinancialProviderContactCreateMutationVariables = Exact<{
  id: string | number
  input: FinancialProviderContactCreateInput
}>

export type FinancialProviderContactCreateMutation = {
  __typename: 'Mutation'
  financialProviderContactCreate: {
    __typename: 'FinancialProviderContactCreate'
    financialProviderContact: {
      __typename: 'FinancialProviderContact'
      id: string
      name: string
      role: string
      emailAddress: string | null
      createdAt: DateTimeString
      financialProvider: {
        __typename: 'FinancialProvider'
        businessName: string
        id: string
        name: string
        createdAt: DateTimeString
        accountTypes: Array<{ __typename: 'FinancialAccountType'; id: string; name: string }>
      }
      phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string } | null
    }
  }
}

export type FinancialProviderCreateMutationVariables = Exact<{
  input: FinancialProviderCreateInput
}>

export type FinancialProviderCreateMutation = {
  __typename: 'Mutation'
  financialProviderCreate: {
    __typename: 'FinancialProviderCreate'
    financialProvider: {
      __typename: 'FinancialProvider'
      id: string
      name: string
      createdAt: DateTimeString
    }
  }
}

export type FinancialProviderDeleteMutationVariables = Exact<{
  id: string | number
}>

export type FinancialProviderDeleteMutation = {
  __typename: 'Mutation'
  financialProviderDelete: {
    __typename: 'FinancialProviderDelete'
    financialProvider: {
      __typename: 'FinancialProvider'
      businessName: string
      id: string
      name: string
      createdAt: DateTimeString
      accountTypes: Array<{ __typename: 'FinancialAccountType'; id: string; name: string }>
    }
  }
}

export type FinancialProviderUpdateMutationVariables = Exact<{
  id: string | number
  input: FinancialProviderUpdateInput
}>

export type FinancialProviderUpdateMutation = {
  __typename: 'Mutation'
  financialProviderUpdate: {
    __typename: 'FinancialProviderUpdate'
    financialProvider: {
      __typename: 'FinancialProvider'
      businessName: string
      id: string
      name: string
      createdAt: DateTimeString
      accountTypes: Array<{ __typename: 'FinancialAccountType'; id: string; name: string }>
    }
  }
}

export type FinancialProviderDetailQueryVariables = Exact<{
  id: string | number
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: FinancialProviderContactSortInput | null | undefined
}>

export type FinancialProviderDetailQuery = {
  __typename: 'Query'
  financialProvider: {
    __typename: 'FinancialProvider'
    businessName: string
    id: string
    name: string
    createdAt: DateTimeString
    contacts: {
      __typename: 'FinancialProviderContactConnection'
      totalCount: number
      edges: Array<{
        __typename: 'FinancialProviderContactEdge'
        node: {
          __typename: 'FinancialProviderContact'
          id: string
          name: string
          role: string
          emailAddress: string | null
          createdAt: DateTimeString
          phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string } | null
        }
      }>
      pageInfo: {
        __typename: 'PageInfo'
        endCursor: string | null
        hasNextPage: boolean
        hasPreviousPage: boolean
        startCursor: string | null
      }
    }
    availableAccountTypes: Array<{
      __typename: 'FinancialAccountType'
      id: string
      value: string
      name: string
    }>
    accountTypes: Array<{ __typename: 'FinancialAccountType'; id: string; name: string }>
  } | null
}

export type FinancialProviderListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: FinancialProvidersFilterInput | null | undefined
  sort?: FinancialProvidersSortInput | null | undefined
}>

export type FinancialProviderListQuery = {
  __typename: 'Query'
  financialProviders: {
    __typename: 'FinancialProviderConnection'
    totalCount: number
    edges: Array<{
      __typename: 'FinancialProviderEdge'
      cursor: string
      node: { __typename: 'FinancialProvider'; id: string; name: string; createdAt: DateTimeString }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type HomeQueueCaseFragment = {
  __typename: 'Case'
  id: string
  code: string
  createdAt: DateTimeString
  stage: CaseStage
  customer: { __typename: 'Customer'; id: string; name: string }
}

export type HomeCasesQueryVariables = Exact<{
  weekStart: DateTimeString
}>

export type HomeCasesQuery = {
  __typename: 'Query'
  myOpen: { __typename: 'CaseConnection'; totalCount: number }
  myUncontacted: { __typename: 'CaseConnection'; totalCount: number }
  myStale: { __typename: 'CaseConnection'; totalCount: number }
  myUnpaid: { __typename: 'CaseConnection'; totalCount: number }
  myNewThisWeek: { __typename: 'CaseConnection'; totalCount: number }
  myUncontactedRows: {
    __typename: 'CaseConnection'
    edges: Array<{
      __typename: 'CaseEdge'
      node: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        stage: CaseStage
        customer: { __typename: 'Customer'; id: string; name: string }
      }
    }>
  }
  myStaleRows: {
    __typename: 'CaseConnection'
    edges: Array<{
      __typename: 'CaseEdge'
      node: {
        __typename: 'Case'
        id: string
        code: string
        createdAt: DateTimeString
        stage: CaseStage
        customer: { __typename: 'Customer'; id: string; name: string }
      }
    }>
  }
}

export type HomeFeeWindowFragment = {
  __typename: 'ReportFeeCollection'
  adjustedOrders: number
  timezone: string
  unpricedOrders: number
  currencies: Array<{
    __typename: 'ReportFeeCollectionByCurrency'
    currency: string
    totals: {
      __typename: 'ReportFeeCollectionTotals'
      collectionRate: number | null
      paid: { __typename: 'Money'; amount: number; currency: string }
      prescribed: { __typename: 'Money'; amount: number; currency: string }
    }
  }>
}

export type HomeFeesQueryVariables = Exact<{
  todayStart: DateTimeString
  weekAgo: DateTimeString
  monthStart: DateTimeString
  now: DateTimeString
}>

export type HomeFeesQuery = {
  __typename: 'Query'
  feesToday: {
    __typename: 'ReportFeeCollection'
    adjustedOrders: number
    timezone: string
    unpricedOrders: number
    currencies: Array<{
      __typename: 'ReportFeeCollectionByCurrency'
      currency: string
      totals: {
        __typename: 'ReportFeeCollectionTotals'
        collectionRate: number | null
        paid: { __typename: 'Money'; amount: number; currency: string }
        prescribed: { __typename: 'Money'; amount: number; currency: string }
      }
    }>
  }
  feesWeek: {
    __typename: 'ReportFeeCollection'
    adjustedOrders: number
    timezone: string
    unpricedOrders: number
    currencies: Array<{
      __typename: 'ReportFeeCollectionByCurrency'
      currency: string
      buckets: Array<{
        __typename: 'ReportFeeCollectionBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        paid: { __typename: 'Money'; amount: number; currency: string }
      }>
      totals: {
        __typename: 'ReportFeeCollectionTotals'
        collectionRate: number | null
        paid: { __typename: 'Money'; amount: number; currency: string }
        prescribed: { __typename: 'Money'; amount: number; currency: string }
      }
    }>
  }
  feesMonth: {
    __typename: 'ReportFeeCollection'
    adjustedOrders: number
    timezone: string
    unpricedOrders: number
    currencies: Array<{
      __typename: 'ReportFeeCollectionByCurrency'
      currency: string
      totals: {
        __typename: 'ReportFeeCollectionTotals'
        collectionRate: number | null
        paid: { __typename: 'Money'; amount: number; currency: string }
        prescribed: { __typename: 'Money'; amount: number; currency: string }
      }
    }>
  }
}

export type HomeManagerQueryVariables = Exact<{
  weekStart: DateTimeString
  prevWeekStart: DateTimeString
  weekAgo: DateTimeString
  now: DateTimeString
  PERMISSION_MANAGE_CASES?: boolean
  PERMISSION_MANAGE_USERS?: boolean
}>

export type HomeManagerQuery = {
  __typename: 'Query'
  summary?: {
    __typename: 'CaseDashboardSummary'
    pendingCases: number
    pendingAging: number
    openCases: number
    createdThisWeek: number
    createdLastWeek: number
    closedThisWeek: number
    closedLastWeek: number
    stuckCases: number
    stageUncontacted: number
    stageOnboarding: number
    stageServicing: number
    stageComplete: number
  }
  reportNewLeads?: {
    __typename: 'ReportNewLeads'
    leadsTotal: Array<{
      __typename: 'ReportNewLeadsInInterval'
      count: number
      interval: { __typename: 'Interval'; since: DateTimeString }
    }>
  }
  workload?: Array<{
    __typename: 'CaseWorkloadEntry'
    openCases: number
    uncontactedCases: number
    user: { __typename: 'User'; id: string; displayName: string }
  }>
  caseEvents: {
    __typename: 'CaseEventConnection'
    edges: Array<{
      __typename: 'CaseEventEdge'
      node: {
        __typename: 'CaseEvent'
        id: string
        type: CaseEventType
        date: DateTimeString
        parameters: any
        case: { __typename: 'Case'; id: string; code: string }
        actionResult: { __typename: 'CaseActionResult'; name: string; success: boolean } | null
        actionType: { __typename: 'CaseActionType'; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
        user: { __typename: 'User'; id: string; displayName: string; email: string } | null
        financialAccount: { __typename: 'FinancialAccount'; id: string; name: string } | null
        fromCustomer: {
          __typename: 'CaseEventCustomer'
          id: string
          code: string
          name: string
        } | null
        toCustomer: {
          __typename: 'CaseEventCustomer'
          id: string
          code: string
          name: string
        } | null
      }
    }>
  }
}

export type HomeRemindersQueryVariables = Exact<{
  todayEnd: DateTimeString
}>

export type HomeRemindersQuery = {
  __typename: 'Query'
  overdue: {
    __typename: 'CaseReminderConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseReminderEdge'
      node: {
        __typename: 'CaseReminder'
        id: string
        dueAt: DateTimeString
        note: string | null
        status: CaseReminderStatus
        source: CaseReminderSource
        isOverdue: boolean
        outsideBusinessHours: boolean | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          customer: { __typename: 'Customer'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
        } | null
        type: { __typename: 'CaseActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  today: {
    __typename: 'CaseReminderConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseReminderEdge'
      node: {
        __typename: 'CaseReminder'
        id: string
        dueAt: DateTimeString
        note: string | null
        status: CaseReminderStatus
        source: CaseReminderSource
        isOverdue: boolean
        outsideBusinessHours: boolean | null
        case: {
          __typename: 'Case'
          id: string
          code: string
          customer: { __typename: 'Customer'; id: string; name: string }
          assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
        } | null
        type: { __typename: 'CaseActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
      }
    }>
  }
  summary: { __typename: 'CaseReminderSummary'; overdue: number; dueToday: number }
}

export type HomeSettlementQueryVariables = Exact<{ [key: string]: never }>

export type HomeSettlementQuery = {
  __typename: 'Query'
  settlementTickets: {
    __typename: 'TicketConnection'
    pageInfo: { __typename: 'PageInfo'; hasNextPage: boolean }
    edges: Array<{
      __typename: 'TicketEdge'
      node:
        | { __typename: 'MediationTicket'; id: string }
        | {
            __typename: 'NegotiationTicket'
            id: string
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
            adjustedDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }
    }>
  }
}

export type HomeTeamQueryVariables = Exact<{
  todayStart: DateTimeString
  weekStart: DateTimeString
  now: DateTimeString
}>

export type HomeTeamQuery = {
  __typename: 'Query'
  teamOpen: { __typename: 'CaseConnection'; totalCount: number }
  teamUncontacted: { __typename: 'CaseConnection'; totalCount: number }
  teamTickets: { __typename: 'TicketConnection'; totalCount: number }
  reportAssignedLeads: {
    __typename: 'ReportAssignedLeads'
    data: Array<{
      __typename: 'ReportAssignedLeadsByUser'
      user: { __typename: 'User'; id: string; displayName: string }
      intervalCounts: Array<{
        __typename: 'ReportAssignedLeadsByUserInInterval'
        countAutoAssigned: number
        countManuallyAssigned: number
        countTotal: number
        interval: { __typename: 'Interval'; since: DateTimeString }
      }>
    }>
  }
  reportUserActivity: Array<{
    __typename: 'ReportUserActivityByUser'
    user: { __typename: 'User'; id: string; displayName: string }
    lastEvent: { __typename: 'CaseEvent'; id: string; date: DateTimeString }
    currentIdlePeriod: { __typename: 'ReportUserActivityIdleTime'; duration: number } | null
  }>
}

export type HomeTeamRosterQueryVariables = Exact<{
  teamId: string | number
}>

export type HomeTeamRosterQuery = {
  __typename: 'Query'
  teamRoster: {
    __typename: 'UserConnection'
    edges: Array<{
      __typename: 'UserEdge'
      node: {
        __typename: 'User'
        id: string
        displayName: string
        open: { __typename: 'CaseConnection'; totalCount: number }
        uncontacted: { __typename: 'CaseConnection'; totalCount: number }
      }
    }>
  }
}

export type HomeTicketsQueryVariables = Exact<{
  weekStart: DateTimeString
}>

export type HomeTicketsQuery = {
  __typename: 'Query'
  myOpenTickets: { __typename: 'TicketConnection'; totalCount: number }
  myUncontactedTickets: { __typename: 'TicketConnection'; totalCount: number }
  myNewTickets: { __typename: 'TicketConnection'; totalCount: number }
  myOldestTickets: {
    __typename: 'TicketConnection'
    edges: Array<{
      __typename: 'TicketEdge'
      node:
        | {
            __typename: 'MediationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              case: { __typename: 'Case'; id: string; code: string }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            }
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              case: { __typename: 'Case'; id: string; code: string }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
            adjustedDebt: { __typename: 'Money'; amount: number; currency: string } | null
          }
    }>
  }
}

export type CaseImportQuarantineLeadFragment = {
  __typename: 'CaseImportQuarantine'
  id: string
  appId: string | null
  createdAt: DateTimeString
  customerEmail: string | null
  customerName: string | null
  customerPhone: string | null
  error: string | null
  externalIdentifier: string | null
  issues: any
  schemaVersion: string | null
  sourceCampaign: string | null
  stage: CaseImportQuarantineStage
  content: any
}

export type CaseImportQuarantineResolveMutationVariables = Exact<{
  id: string | number
  input: CaseImportQuarantineResolveInput
}>

export type CaseImportQuarantineResolveMutation = {
  __typename: 'Mutation'
  caseImportQuarantineResolve: {
    __typename: 'CaseImportQuarantineResolve'
    case: { __typename: 'Case'; id: string; code: string }
  }
}

export type CaseImportQuarantineRetryMutationVariables = Exact<{
  id: string | number
}>

export type CaseImportQuarantineRetryMutation = {
  __typename: 'Mutation'
  caseImportQuarantineRetry: {
    __typename: 'CaseImportQuarantineRetry'
    case: { __typename: 'Case'; id: string; code: string }
  }
}

export type CaseImportQuarantineRetryManyMutationVariables = Exact<{
  ids: Array<string | number> | string | number
}>

export type CaseImportQuarantineRetryManyMutation = {
  __typename: 'Mutation'
  caseImportQuarantineRetryMany: {
    __typename: 'CaseImportQuarantineRetryMany'
    imported: number
    failed: number
    skipped: number
  }
}

export type CaseImportQuarantineCountQueryVariables = Exact<{ [key: string]: never }>

export type CaseImportQuarantineCountQuery = {
  __typename: 'Query'
  caseImportQuarantineCount: number
}

export type CaseImportQuarantinesQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  filter?: CaseImportQuarantinesFilterInput | null | undefined
  sort?: CaseImportQuarantinesSortInput | null | undefined
}>

export type CaseImportQuarantinesQuery = {
  __typename: 'Query'
  caseImportQuarantines: {
    __typename: 'CaseImportQuarantineConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseImportQuarantineEdge'
      cursor: string
      node: {
        __typename: 'CaseImportQuarantine'
        id: string
        appId: string | null
        createdAt: DateTimeString
        customerEmail: string | null
        customerName: string | null
        customerPhone: string | null
        error: string | null
        externalIdentifier: string | null
        issues: any
        schemaVersion: string | null
        sourceCampaign: string | null
        stage: CaseImportQuarantineStage
        content: any
      }
    }>
    pageInfo: { __typename: 'PageInfo'; hasNextPage: boolean; endCursor: string | null }
  }
}

export type ChannelInfoFragment = {
  __typename: 'MessagingChannel'
  id: string
  displayNumber: string
  defaultPlaybookId: string | null
  defaultOwner: MessagingConversationOwnership | null
  defaultOversightScenarioId: string | null
  supportPlaybookId: string | null
  isDefaultTransactional: boolean
  phoneNumberId: string
  provider: string
  purpose: MessagingChannelPurpose
  status: MessagingChannelStatus
  wabaId: string | null
  metaAppId: string | null
  caseType: { __typename: 'CaseType'; id: string; name: string } | null
}

export type ConversationListItemFragment = {
  __typename: 'MessagingConversation'
  id: string
  customerWaId: string
  customerName: string | null
  lifecycle: MessagingConversationLifecycle
  lastActivityAt: DateTimeString
  lastInboundSeq: number | null
  needsAttention: boolean
  owesReply: boolean
  dismissed: boolean
  safetyFlagged: boolean
  ownership: MessagingConversationOwnership
  windowExpiresAt: DateTimeString | null
  customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
  assignee: { __typename: 'User'; id: string; displayName: string } | null
  linkedCases: Array<{
    __typename: 'CaseBrief'
    id: string
    code: string
    stage: CaseStage
    type: { __typename: 'CaseType'; id: string; name: string }
  }>
  channel: {
    __typename: 'MessagingChannel'
    id: string
    displayNumber: string
    defaultPlaybookId: string | null
    defaultOwner: MessagingConversationOwnership | null
    defaultOversightScenarioId: string | null
    supportPlaybookId: string | null
    isDefaultTransactional: boolean
    phoneNumberId: string
    provider: string
    purpose: MessagingChannelPurpose
    status: MessagingChannelStatus
    wabaId: string | null
    metaAppId: string | null
    caseType: { __typename: 'CaseType'; id: string; name: string } | null
  } | null
  lastMessage: {
    __typename: 'MessagingMessagePreview'
    createdAt: DateTimeString
    direction: MessagingMessageDirection
    seq: number
    source: MessagingMessageSource | null
    state: MessagingMessageState
    textPreview: string | null
    type: MessagingMessageType
    sender: { __typename: 'User'; id: string; displayName: string } | null
  } | null
}

export type MessageItemFragment = {
  __typename: 'MessagingMessage'
  id: string
  wamid: string | null
  seq: number
  clientMsgId: string | null
  clientSeq: number | null
  createdAt: DateTimeString
  direction: MessagingMessageDirection
  source: MessagingMessageSource
  state: MessagingMessageState
  type: MessagingMessageType
  text: string | null
  errorCode: string | null
  parked: boolean | null
  nextAttemptAt: DateTimeString | null
  sender: { __typename: 'User'; id: string; displayName: string } | null
  context: { __typename: 'MessagingMessageContext'; quotedWamid: string } | null
  reaction: { __typename: 'MessagingMessageReaction'; emoji: string; targetWamid: string } | null
  media: {
    __typename: 'MessagingMessageMediaRef'
    mediaId: string
    mimeType: string
    filename: string | null
    caption: string | null
    sizeBytes: number | null
  } | null
  interactive: {
    __typename: 'MessagingMessageInteractive'
    kind: MessagingMessageInteractiveKind
    body: string
    header: string | null
    footer: string | null
    listButton: string | null
    buttons: Array<{
      __typename: 'MessagingMessageInteractiveButton'
      id: string
      title: string
    }> | null
    sections: Array<{
      __typename: 'MessagingMessageInteractiveListSection'
      title: string | null
      rows: Array<{
        __typename: 'MessagingMessageInteractiveListRow'
        id: string
        title: string
        description: string | null
      }>
    }> | null
  } | null
  interactiveReply: {
    __typename: 'MessagingMessageInteractiveReply'
    id: string
    title: string
    kind: MessagingMessageInteractiveReplyKind
    description: string | null
  } | null
  template: {
    __typename: 'MessagingMessageTemplate'
    name: string
    language: string
    body: string | null
    components: any
    footer: string | null
    header: {
      __typename: 'MessagingMessageTemplateHeader'
      format: string
      imageUrl: string | null
      text: string | null
    } | null
    buttons: Array<{
      __typename: 'MessagingMessageTemplateButton'
      type: string
      text: string
      url: string | null
      phoneNumber: string | null
    }> | null
  } | null
  unknown: {
    __typename: 'MessagingMessageUnknownDetail'
    metaType: string
    unsupportedType: string | null
  } | null
}

export type MessagePreviewFragment = {
  __typename: 'MessagingMessagePreview'
  createdAt: DateTimeString
  direction: MessagingMessageDirection
  seq: number
  source: MessagingMessageSource | null
  state: MessagingMessageState
  textPreview: string | null
  type: MessagingMessageType
  sender: { __typename: 'User'; id: string; displayName: string } | null
}

export type QuickReplyItemFragment = {
  __typename: 'MessagingQuickReply'
  id: string
  shortcut: string
  body: string
  sequence: number
}

export type CaseCreateFromConversationMutationVariables = Exact<{
  input: CaseCreateFromConversationInput
}>

export type CaseCreateFromConversationMutation = {
  __typename: 'Mutation'
  caseCreateFromConversation: {
    __typename: 'MessagingCaseCreateFromConversation'
    ladderRung: MessagingCaseAssignmentReason | null
    handedOver: boolean
    conversationStale: boolean
    case: { __typename: 'Case'; id: string; code: string }
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type CaseStartConversationMutationVariables = Exact<{
  id: string | number
  input: CaseStartConversationInput
}>

export type CaseStartConversationMutation = {
  __typename: 'Mutation'
  caseStartConversation: {
    __typename: 'MessagingCaseStartConversation'
    reusedExisting: boolean
    case: {
      __typename: 'Case'
      id: string
      conversations: Array<{
        __typename: 'MessagingConversation'
        id: string
        customerWaId: string
        customerName: string | null
        lifecycle: MessagingConversationLifecycle
        lastActivityAt: DateTimeString
        lastInboundSeq: number | null
        needsAttention: boolean
        owesReply: boolean
        dismissed: boolean
        safetyFlagged: boolean
        ownership: MessagingConversationOwnership
        windowExpiresAt: DateTimeString | null
        customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
        assignee: { __typename: 'User'; id: string; displayName: string } | null
        linkedCases: Array<{
          __typename: 'CaseBrief'
          id: string
          code: string
          stage: CaseStage
          type: { __typename: 'CaseType'; id: string; name: string }
        }>
        channel: {
          __typename: 'MessagingChannel'
          id: string
          displayNumber: string
          defaultPlaybookId: string | null
          defaultOwner: MessagingConversationOwnership | null
          defaultOversightScenarioId: string | null
          supportPlaybookId: string | null
          isDefaultTransactional: boolean
          phoneNumberId: string
          provider: string
          purpose: MessagingChannelPurpose
          status: MessagingChannelStatus
          wabaId: string | null
          metaAppId: string | null
          caseType: { __typename: 'CaseType'; id: string; name: string } | null
        } | null
        lastMessage: {
          __typename: 'MessagingMessagePreview'
          createdAt: DateTimeString
          direction: MessagingMessageDirection
          seq: number
          source: MessagingMessageSource | null
          state: MessagingMessageState
          textPreview: string | null
          type: MessagingMessageType
          sender: { __typename: 'User'; id: string; displayName: string } | null
        } | null
      }>
    }
  }
}

export type ChannelTemplateCreateMutationVariables = Exact<{
  channelId: string | number
  input: MessagingChannelTemplateCreateInput
}>

export type ChannelTemplateCreateMutation = {
  __typename: 'Mutation'
  channelTemplateCreate: {
    __typename: 'MessagingChannelTemplateCreate'
    metaTemplateId: string
    status: string
    category: string
  }
}

export type ChannelTemplateDeleteMutationVariables = Exact<{
  channelId: string | number
  metaTemplateId: string
  allLanguages: boolean
}>

export type ChannelTemplateDeleteMutation = {
  __typename: 'Mutation'
  channelTemplateDelete: { __typename: 'MessagingChannelTemplateMutation'; success: boolean }
}

export type ChannelTemplateHeaderImageUploadMutationVariables = Exact<{
  channelId: string | number
  input: MessagingChannelTemplateHeaderImageUploadInput
}>

export type ChannelTemplateHeaderImageUploadMutation = {
  __typename: 'Mutation'
  channelTemplateHeaderImageUpload: {
    __typename: 'MessagingChannelTemplateHeaderImage'
    assetId: string
    previewUrl: string | null
    contentType: string
    sizeBytes: number
  }
}

export type ChannelTemplateProbeWriteMutationVariables = Exact<{
  channelId: string | number
}>

export type ChannelTemplateProbeWriteMutation = {
  __typename: 'Mutation'
  channelTemplateProbeWrite: {
    __typename: 'MessagingChannelTemplateWriteProbe'
    status: MessagingChannelVerifyCheckStatus
    detail: string | null
    probeName: string
    cleanedUp: boolean
  }
}

export type ChannelTemplateSyncMutationVariables = Exact<{
  channelId: string | number
}>

export type ChannelTemplateSyncMutation = {
  __typename: 'Mutation'
  channelTemplateSync: {
    __typename: 'MessagingChannelTemplateSync'
    upserted: number
    removed: number
  }
}

export type ChannelTemplateUpdateMutationVariables = Exact<{
  channelId: string | number
  metaTemplateId: string
  input: MessagingChannelTemplateUpdateInput
}>

export type ChannelTemplateUpdateMutation = {
  __typename: 'Mutation'
  channelTemplateUpdate: { __typename: 'MessagingChannelTemplateMutation'; success: boolean }
}

export type ConversationArchiveMutationVariables = Exact<{
  conversationId: string | number
  reason?: string | null | undefined
}>

export type ConversationArchiveMutation = {
  __typename: 'Mutation'
  conversationArchive: {
    __typename: 'MessagingConversationArchive'
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type ConversationHandbackMutationVariables = Exact<{
  conversationId: string | number
}>

export type ConversationHandbackMutation = {
  __typename: 'Mutation'
  conversationHandback: {
    __typename: 'MessagingConversationHandback'
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type ConversationPickUpMutationVariables = Exact<{
  conversationId: string | number
}>

export type ConversationPickUpMutation = {
  __typename: 'Mutation'
  conversationPickUp: {
    __typename: 'MessagingConversationPickUp'
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type ConversationSetCustomerMutationVariables = Exact<{
  conversationId: string | number
  customerId: string | number
}>

export type ConversationSetCustomerMutation = {
  __typename: 'Mutation'
  conversationSetCustomer: {
    __typename: 'MessagingConversationSetCustomer'
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type ConversationSetDismissedMutationVariables = Exact<{
  conversationId: string | number
  dismissed: boolean
}>

export type ConversationSetDismissedMutation = {
  __typename: 'Mutation'
  conversationSetDismissed: {
    __typename: 'MessagingConversationDismiss'
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type ConversationTakeOverMutationVariables = Exact<{
  conversationId: string | number
}>

export type ConversationTakeOverMutation = {
  __typename: 'Mutation'
  conversationTakeOver: {
    __typename: 'MessagingConversationTakeOver'
    conversation: {
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }
  }
}

export type ConversationTypingMutationVariables = Exact<{
  conversationId: string | number
}>

export type ConversationTypingMutation = {
  __typename: 'Mutation'
  conversationTyping: { __typename: 'MessagingConversationTypingAck'; accepted: boolean }
}

export type MessageSendMutationVariables = Exact<{
  input: MessagingMessageSendInput
}>

export type MessageSendMutation = {
  __typename: 'Mutation'
  messageSend: { __typename: 'MessagingMessageSendAck'; accepted: boolean; clientMsgId: string }
}

export type MessageSkipMutationVariables = Exact<{
  conversationId: string | number
  messageId: string | number
}>

export type MessageSkipMutation = {
  __typename: 'Mutation'
  messagingMessageSkip: { __typename: 'MessagingMessageSkip'; messageId: string }
}

export type MessagingInsightFileDocumentMutationVariables = Exact<{
  conversationId: string | number
  insightId: string
}>

export type MessagingInsightFileDocumentMutation = {
  __typename: 'Mutation'
  messagingInsightFileDocument: {
    __typename: 'MessagingInsightFileDocument'
    insightAction: {
      __typename: 'MessagingInsightAction'
      id: string
      targetEntity: string
      documentId: string | null
      createdAt: DateTimeString
    }
  }
}

export type MessagingQuickReplyCreateMutationVariables = Exact<{
  input: MessagingQuickReplyCreateInput
}>

export type MessagingQuickReplyCreateMutation = {
  __typename: 'Mutation'
  messagingQuickReplyCreate: {
    __typename: 'MessagingQuickReply'
    id: string
    shortcut: string
    body: string
    sequence: number
  }
}

export type MessagingQuickReplyDeleteMutationVariables = Exact<{
  id: string | number
}>

export type MessagingQuickReplyDeleteMutation = {
  __typename: 'Mutation'
  messagingQuickReplyDelete: {
    __typename: 'MessagingQuickReplyDelete'
    quickReply: {
      __typename: 'MessagingQuickReply'
      id: string
      shortcut: string
      body: string
      sequence: number
    }
  }
}

export type MessagingQuickReplyUpdateMutationVariables = Exact<{
  id: string | number
  input: MessagingQuickReplyUpdateInput
}>

export type MessagingQuickReplyUpdateMutation = {
  __typename: 'Mutation'
  messagingQuickReplyUpdate: {
    __typename: 'MessagingQuickReply'
    id: string
    shortcut: string
    body: string
    sequence: number
  }
}

export type ChannelSendableTemplatesQueryVariables = Exact<{
  channelId: string | number
}>

export type ChannelSendableTemplatesQuery = {
  __typename: 'Query'
  channelSendableTemplates: Array<{
    __typename: 'MessagingConversationTemplate'
    id: string
    metaTemplateId: string
    name: string
    language: string
    category: string
    components: any
  }>
}

export type ChannelTemplateLibraryQueryVariables = Exact<{
  channelId: string | number
}>

export type ChannelTemplateLibraryQuery = {
  __typename: 'Query'
  channelTemplates: Array<{
    __typename: 'MessagingChannelTemplate'
    id: string
    metaTemplateId: string
    name: string
    language: string
    category: string
    status: string
    quality: string | null
    components: any
    rejectedReason: string | null
    rejectionRecommendation: string | null
    statusNotifiedAt: DateTimeString | null
    headerImageUrl: string | null
    editBlockedReason: string | null
    syncedAt: DateTimeString
  }>
}

export type ConversationQueryVariables = Exact<{
  id: string | number
  operatorId?: string | number | null | undefined
  scope?: MessagingConversationScope | null | undefined
}>

export type ConversationQuery = {
  __typename: 'Query'
  conversation: {
    __typename: 'MessagingConversation'
    whatsappCapability: MessagingNumberCapability
    threadContactPhoneId: string | null
    id: string
    customerWaId: string
    customerName: string | null
    lifecycle: MessagingConversationLifecycle
    lastActivityAt: DateTimeString
    lastInboundSeq: number | null
    needsAttention: boolean
    owesReply: boolean
    dismissed: boolean
    safetyFlagged: boolean
    ownership: MessagingConversationOwnership
    windowExpiresAt: DateTimeString | null
    linkedCases: Array<{
      __typename: 'CaseBrief'
      id: string
      code: string
      stage: CaseStage
      status: CaseStatus
      flow: CaseFlow | null
      updatedAt: DateTimeString
      type: { __typename: 'CaseType'; id: string; name: string }
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
    }>
    playbook: {
      __typename: 'MessagingPlaybookOption'
      id: string
      name: string
      type: MessagingPlaybookType | null
    } | null
    customer: { __typename: 'Customer'; id: string; name: string } | null
    source: {
      __typename: 'MessagingConversationSource'
      code: string
      channel: string
      resolved: boolean
      resolvedAt: DateTimeString | null
      campaign: string | null
      medium: string | null
      type: string | null
      tool: string | null
    } | null
    customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
    assignee: { __typename: 'User'; id: string; displayName: string } | null
    channel: {
      __typename: 'MessagingChannel'
      id: string
      displayNumber: string
      defaultPlaybookId: string | null
      defaultOwner: MessagingConversationOwnership | null
      defaultOversightScenarioId: string | null
      supportPlaybookId: string | null
      isDefaultTransactional: boolean
      phoneNumberId: string
      provider: string
      purpose: MessagingChannelPurpose
      status: MessagingChannelStatus
      wabaId: string | null
      metaAppId: string | null
      caseType: { __typename: 'CaseType'; id: string; name: string } | null
    } | null
    lastMessage: {
      __typename: 'MessagingMessagePreview'
      createdAt: DateTimeString
      direction: MessagingMessageDirection
      seq: number
      source: MessagingMessageSource | null
      state: MessagingMessageState
      textPreview: string | null
      type: MessagingMessageType
      sender: { __typename: 'User'; id: string; displayName: string } | null
    } | null
  } | null
}

export type ConversationCaseQueryVariables = Exact<{
  id: string | number
}>

export type ConversationCaseQuery = {
  __typename: 'Query'
  case: {
    __typename: 'Case'
    id: string
    code: string
    status: CaseStatus
    stage: CaseStage
    flow: CaseFlow | null
    type: { __typename: 'CaseType'; id: string; name: string }
    customer: { __typename: 'Customer'; id: string; name: string }
  }
}

export type ConversationCaseCallbacksQueryVariables = Exact<{
  id: string | number
  operatorId?: string | number | null | undefined
  scope?: MessagingConversationScope | null | undefined
}>

export type ConversationCaseCallbacksQuery = {
  __typename: 'Query'
  conversation: {
    __typename: 'MessagingConversation'
    id: string
    linkedCases: Array<{
      __typename: 'CaseBrief'
      id: string
      callback: {
        __typename: 'CaseBriefCallback'
        dueAt: DateTimeString
        source: CaseReminderSource
        bookedByName: string | null
      } | null
    }>
  } | null
}

export type ConversationCountsQueryVariables = Exact<{
  scope: MessagingConversationScope
  includeDismissed?: boolean | null | undefined
}>

export type ConversationCountsQuery = {
  __typename: 'Query'
  conversationCounts: {
    __typename: 'MessagingConversationCounts'
    needsAttention: number
    owesReply: number
    total: number
  }
}

export type ConversationEventsQueryVariables = Exact<{
  conversationId: string | number
  scope?: MessagingConversationScope | null | undefined
}>

export type ConversationEventsQuery = {
  __typename: 'Query'
  conversationEvents: Array<{
    __typename: 'MessagingConversationEvent'
    id: string
    seq: number
    kind: MessagingConversationEventKind
    payload: any
    createdAt: DateTimeString
    insightAction: {
      __typename: 'MessagingInsightAction'
      id: string
      targetEntity: string
      documentId: string | null
      createdAt: DateTimeString
    } | null
  }>
}

export type ConversationMessagesQueryVariables = Exact<{
  conversationId: string | number
  cursor?: string | null | undefined
  direction?: MessagingMessageHistoryDirection | null | undefined
  limit?: number | null | undefined
  operatorId?: string | number | null | undefined
  scope?: MessagingConversationScope | null | undefined
}>

export type ConversationMessagesQuery = {
  __typename: 'Query'
  conversationMessages: {
    __typename: 'MessagingMessageList'
    conversationId: string
    hasMore: boolean
    nextCursor: string | null
    items: Array<{
      __typename: 'MessagingMessage'
      id: string
      wamid: string | null
      seq: number
      clientMsgId: string | null
      clientSeq: number | null
      createdAt: DateTimeString
      direction: MessagingMessageDirection
      source: MessagingMessageSource
      state: MessagingMessageState
      type: MessagingMessageType
      text: string | null
      errorCode: string | null
      parked: boolean | null
      nextAttemptAt: DateTimeString | null
      sender: { __typename: 'User'; id: string; displayName: string } | null
      context: { __typename: 'MessagingMessageContext'; quotedWamid: string } | null
      reaction: {
        __typename: 'MessagingMessageReaction'
        emoji: string
        targetWamid: string
      } | null
      media: {
        __typename: 'MessagingMessageMediaRef'
        mediaId: string
        mimeType: string
        filename: string | null
        caption: string | null
        sizeBytes: number | null
      } | null
      interactive: {
        __typename: 'MessagingMessageInteractive'
        kind: MessagingMessageInteractiveKind
        body: string
        header: string | null
        footer: string | null
        listButton: string | null
        buttons: Array<{
          __typename: 'MessagingMessageInteractiveButton'
          id: string
          title: string
        }> | null
        sections: Array<{
          __typename: 'MessagingMessageInteractiveListSection'
          title: string | null
          rows: Array<{
            __typename: 'MessagingMessageInteractiveListRow'
            id: string
            title: string
            description: string | null
          }>
        }> | null
      } | null
      interactiveReply: {
        __typename: 'MessagingMessageInteractiveReply'
        id: string
        title: string
        kind: MessagingMessageInteractiveReplyKind
        description: string | null
      } | null
      template: {
        __typename: 'MessagingMessageTemplate'
        name: string
        language: string
        body: string | null
        components: any
        footer: string | null
        header: {
          __typename: 'MessagingMessageTemplateHeader'
          format: string
          imageUrl: string | null
          text: string | null
        } | null
        buttons: Array<{
          __typename: 'MessagingMessageTemplateButton'
          type: string
          text: string
          url: string | null
          phoneNumber: string | null
        }> | null
      } | null
      unknown: {
        __typename: 'MessagingMessageUnknownDetail'
        metaType: string
        unsupportedType: string | null
      } | null
    }>
  }
}

export type ConversationTemplatesQueryVariables = Exact<{
  conversationId: string | number
  operatorId?: string | number | null | undefined
}>

export type ConversationTemplatesQuery = {
  __typename: 'Query'
  conversationTemplates: Array<{
    __typename: 'MessagingConversationTemplate'
    id: string
    metaTemplateId: string
    name: string
    language: string
    category: string
    components: any
  }>
}

export type ConversationsQueryVariables = Exact<{
  cursor?: string | null | undefined
  includeDismissed?: boolean | null | undefined
  limit?: number | null | undefined
  operatorId?: string | number | null | undefined
  scope?: MessagingConversationScope | null | undefined
}>

export type ConversationsQuery = {
  __typename: 'Query'
  conversations: {
    __typename: 'MessagingConversationList'
    hasMore: boolean
    nextCursor: string | null
    items: Array<{
      __typename: 'MessagingConversation'
      id: string
      customerWaId: string
      customerName: string | null
      lifecycle: MessagingConversationLifecycle
      lastActivityAt: DateTimeString
      lastInboundSeq: number | null
      needsAttention: boolean
      owesReply: boolean
      dismissed: boolean
      safetyFlagged: boolean
      ownership: MessagingConversationOwnership
      windowExpiresAt: DateTimeString | null
      customerWaPhone: { __typename: 'PhoneNumber'; displayNumber: string }
      assignee: { __typename: 'User'; id: string; displayName: string } | null
      linkedCases: Array<{
        __typename: 'CaseBrief'
        id: string
        code: string
        stage: CaseStage
        type: { __typename: 'CaseType'; id: string; name: string }
      }>
      channel: {
        __typename: 'MessagingChannel'
        id: string
        displayNumber: string
        defaultPlaybookId: string | null
        defaultOwner: MessagingConversationOwnership | null
        defaultOversightScenarioId: string | null
        supportPlaybookId: string | null
        isDefaultTransactional: boolean
        phoneNumberId: string
        provider: string
        purpose: MessagingChannelPurpose
        status: MessagingChannelStatus
        wabaId: string | null
        metaAppId: string | null
        caseType: { __typename: 'CaseType'; id: string; name: string } | null
      } | null
      lastMessage: {
        __typename: 'MessagingMessagePreview'
        createdAt: DateTimeString
        direction: MessagingMessageDirection
        seq: number
        source: MessagingMessageSource | null
        state: MessagingMessageState
        textPreview: string | null
        type: MessagingMessageType
        sender: { __typename: 'User'; id: string; displayName: string } | null
      } | null
    }>
  }
}

export type MessageMediaQueryVariables = Exact<{
  conversationId: string | number
  mediaId: string
  operatorId?: string | number | null | undefined
  scope?: MessagingConversationScope | null | undefined
}>

export type MessageMediaQuery = {
  __typename: 'Query'
  messageMedia: {
    __typename: 'MessagingMessageMedia'
    mediaId: string
    url: string
    mimeType: string
    filename: string | null
    sizeBytes: number | null
    expiresAt: DateTimeString
  }
}

export type MessagingQuickRepliesQueryVariables = Exact<{ [key: string]: never }>

export type MessagingQuickRepliesQuery = {
  __typename: 'Query'
  messagingQuickReplies: Array<{
    __typename: 'MessagingQuickReply'
    id: string
    shortcut: string
    body: string
    sequence: number
  }>
}

export type StartConversationChannelsQueryVariables = Exact<{ [key: string]: never }>

export type StartConversationChannelsQuery = {
  __typename: 'Query'
  channels: Array<{
    __typename: 'MessagingChannel'
    id: string
    displayNumber: string
    status: MessagingChannelStatus
    caseType: { __typename: 'CaseType'; id: string } | null
  }>
}

export type ConversationChangedSubscriptionVariables = Exact<{
  conversationId: string | number
  operatorId?: string | number | null | undefined
  scope?: MessagingConversationScope | null | undefined
}>

export type ConversationChangedSubscription = {
  __typename: 'Subscription'
  conversationChanged: {
    __typename: 'MessagingConversationChangedEvent'
    conversationId: string
    accessLost: boolean | null
    message: {
      __typename: 'MessagingMessage'
      id: string
      wamid: string | null
      seq: number
      clientMsgId: string | null
      clientSeq: number | null
      createdAt: DateTimeString
      direction: MessagingMessageDirection
      source: MessagingMessageSource
      state: MessagingMessageState
      type: MessagingMessageType
      text: string | null
      errorCode: string | null
      parked: boolean | null
      nextAttemptAt: DateTimeString | null
      sender: { __typename: 'User'; id: string; displayName: string } | null
      context: { __typename: 'MessagingMessageContext'; quotedWamid: string } | null
      reaction: {
        __typename: 'MessagingMessageReaction'
        emoji: string
        targetWamid: string
      } | null
      media: {
        __typename: 'MessagingMessageMediaRef'
        mediaId: string
        mimeType: string
        filename: string | null
        caption: string | null
        sizeBytes: number | null
      } | null
      interactive: {
        __typename: 'MessagingMessageInteractive'
        kind: MessagingMessageInteractiveKind
        body: string
        header: string | null
        footer: string | null
        listButton: string | null
        buttons: Array<{
          __typename: 'MessagingMessageInteractiveButton'
          id: string
          title: string
        }> | null
        sections: Array<{
          __typename: 'MessagingMessageInteractiveListSection'
          title: string | null
          rows: Array<{
            __typename: 'MessagingMessageInteractiveListRow'
            id: string
            title: string
            description: string | null
          }>
        }> | null
      } | null
      interactiveReply: {
        __typename: 'MessagingMessageInteractiveReply'
        id: string
        title: string
        kind: MessagingMessageInteractiveReplyKind
        description: string | null
      } | null
      template: {
        __typename: 'MessagingMessageTemplate'
        name: string
        language: string
        body: string | null
        components: any
        footer: string | null
        header: {
          __typename: 'MessagingMessageTemplateHeader'
          format: string
          imageUrl: string | null
          text: string | null
        } | null
        buttons: Array<{
          __typename: 'MessagingMessageTemplateButton'
          type: string
          text: string
          url: string | null
          phoneNumber: string | null
        }> | null
      } | null
      unknown: {
        __typename: 'MessagingMessageUnknownDetail'
        metaType: string
        unsupportedType: string | null
      } | null
    } | null
    stateChange: {
      __typename: 'MessagingMessageStateChange'
      messageId: string
      state: MessagingMessageState
      revision: number
      clientMsgId: string | null
      errorCode: string | null
      parked: boolean | null
      nextAttemptAt: DateTimeString | null
    } | null
    assignment: {
      __typename: 'MessagingConversationAssignmentChange'
      assigneeId: string | null
      assigneeName: string | null
    } | null
  }
}

export type InboxChangedSubscriptionVariables = Exact<{
  scope?: MessagingConversationScope | null | undefined
  operatorId?: string | number | null | undefined
}>

export type InboxChangedSubscription = {
  __typename: 'Subscription'
  inboxChanged: { __typename: 'MessagingInboxChangedEvent'; conversationId: string }
}

export type OversightScenarioInfoFragment = {
  __typename: 'MessagingOversightScenario'
  id: string
  name: string
  enabled: boolean
  sampleRate: number
  model: string
  analyzers: Array<OversightAnalyzerKind>
  healthEveryN: number
  healthMinIntervalMs: number
  maxCallsPerConvDay: number
  createdAt: DateTimeString
  updatedAt: DateTimeString
}

export type OversightScenarioCreateMutationVariables = Exact<{
  input: MessagingOversightScenarioCreateInput
}>

export type OversightScenarioCreateMutation = {
  __typename: 'Mutation'
  oversightScenarioCreate: {
    __typename: 'MessagingOversightScenario'
    id: string
    name: string
    enabled: boolean
    sampleRate: number
    model: string
    analyzers: Array<OversightAnalyzerKind>
    healthEveryN: number
    healthMinIntervalMs: number
    maxCallsPerConvDay: number
    createdAt: DateTimeString
    updatedAt: DateTimeString
  }
}

export type OversightScenarioUpdateMutationVariables = Exact<{
  id: string | number
  input: MessagingOversightScenarioUpdateInput
}>

export type OversightScenarioUpdateMutation = {
  __typename: 'Mutation'
  oversightScenarioUpdate: {
    __typename: 'MessagingOversightScenario'
    id: string
    name: string
    enabled: boolean
    sampleRate: number
    model: string
    analyzers: Array<OversightAnalyzerKind>
    healthEveryN: number
    healthMinIntervalMs: number
    maxCallsPerConvDay: number
    createdAt: DateTimeString
    updatedAt: DateTimeString
  }
}

export type OversightScenarioDetailQueryVariables = Exact<{
  id: string | number
}>

export type OversightScenarioDetailQuery = {
  __typename: 'Query'
  oversightScenario: {
    __typename: 'MessagingOversightScenario'
    id: string
    name: string
    enabled: boolean
    sampleRate: number
    model: string
    analyzers: Array<OversightAnalyzerKind>
    healthEveryN: number
    healthMinIntervalMs: number
    maxCallsPerConvDay: number
    createdAt: DateTimeString
    updatedAt: DateTimeString
  } | null
}

export type OversightScenariosQueryVariables = Exact<{ [key: string]: never }>

export type OversightScenariosQuery = {
  __typename: 'Query'
  oversightScenarios: Array<{
    __typename: 'MessagingOversightScenario'
    id: string
    name: string
    enabled: boolean
    sampleRate: number
    model: string
    analyzers: Array<OversightAnalyzerKind>
    healthEveryN: number
    healthMinIntervalMs: number
    maxCallsPerConvDay: number
    createdAt: DateTimeString
    updatedAt: DateTimeString
  }>
}

export type PlaybookVersionInfoFragment = {
  __typename: 'MessagingPlaybookVersion'
  aggregationDelayMs: number
  createdAt: DateTimeString
  enabledTools: Array<string>
  followUpDelayMs: number
  followUpEnabled: boolean
  followUpMaxCount: number
  followUpPostNudgeQuietMs: number
  followUpPrompt: string
  followUpTemplate: string
  handoffTurnLimit: number
  historyLimit: number
  leadCaptureEnabled: boolean
  mediaVisionEnabled: boolean
  modelId: string | null
  outcomes: Array<string>
  summaryEnabled: boolean
  systemPrompt: string
  temperature: number | null
  turnLimit: number
  version: number
  followUpSendWindow: {
    __typename: 'MessagingSendWindow'
    tz: string
    rules: Array<{
      __typename: 'MessagingSendWindowRule'
      days: Array<MessagingSendWindowDay>
      end: string
      start: string
    }>
  } | null
}

export type PlaybookInfoFragment = {
  __typename: 'MessagingPlaybook'
  id: string
  name: string
  type: MessagingPlaybookType | null
  description: string | null
  appId: string | null
  crmAppName: string | null
  crmAppPermissions: Array<Permission> | null
  crmTokenSet: boolean
  activeVersion: number | null
  createdAt: DateTimeString
  updatedAt: DateTimeString
  versions: Array<{
    __typename: 'MessagingPlaybookVersion'
    aggregationDelayMs: number
    createdAt: DateTimeString
    enabledTools: Array<string>
    followUpDelayMs: number
    followUpEnabled: boolean
    followUpMaxCount: number
    followUpPostNudgeQuietMs: number
    followUpPrompt: string
    followUpTemplate: string
    handoffTurnLimit: number
    historyLimit: number
    leadCaptureEnabled: boolean
    mediaVisionEnabled: boolean
    modelId: string | null
    outcomes: Array<string>
    summaryEnabled: boolean
    systemPrompt: string
    temperature: number | null
    turnLimit: number
    version: number
    followUpSendWindow: {
      __typename: 'MessagingSendWindow'
      tz: string
      rules: Array<{
        __typename: 'MessagingSendWindowRule'
        days: Array<MessagingSendWindowDay>
        end: string
        start: string
      }>
    } | null
  }>
}

export type PlaybookCreateMutationVariables = Exact<{
  input: MessagingPlaybookCreateInput
}>

export type PlaybookCreateMutation = {
  __typename: 'Mutation'
  playbookCreate: {
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  }
}

export type PlaybookCrmTokenRotateMutationVariables = Exact<{
  id: string | number
}>

export type PlaybookCrmTokenRotateMutation = {
  __typename: 'Mutation'
  playbookCrmTokenRotate: {
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  }
}

export type PlaybookUpdateMutationVariables = Exact<{
  id: string | number
  input: MessagingPlaybookUpdateInput
}>

export type PlaybookUpdateMutation = {
  __typename: 'Mutation'
  playbookUpdate: {
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  }
}

export type PlaybookVersionActivateMutationVariables = Exact<{
  id: string | number
  version: number
}>

export type PlaybookVersionActivateMutation = {
  __typename: 'Mutation'
  playbookVersionActivate: {
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  }
}

export type PlaybookVersionCreateMutationVariables = Exact<{
  id: string | number
  input: MessagingPlaybookVersionInput
}>

export type PlaybookVersionCreateMutation = {
  __typename: 'Mutation'
  playbookVersionCreate: {
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  }
}

export type MessagingModelsQueryVariables = Exact<{ [key: string]: never }>

export type MessagingModelsQuery = {
  __typename: 'Query'
  messagingModels: Array<{
    __typename: 'MessagingModelInfo'
    description: string | null
    displayName: string
    id: string
    maturity: MessagingModelMaturity
  }>
}

export type MessagingToolsQueryVariables = Exact<{ [key: string]: never }>

export type MessagingToolsQuery = {
  __typename: 'Query'
  messagingTools: Array<{
    __typename: 'MessagingToolInfo'
    description: string
    name: string
    requiredPermissions: Array<string>
  }>
}

export type PlaybookAppOptionsQueryVariables = Exact<{
  first?: number | null | undefined
}>

export type PlaybookAppOptionsQuery = {
  __typename: 'Query'
  apps: {
    __typename: 'AppConnection'
    edges: Array<{ __typename: 'AppEdge'; node: { __typename: 'App'; id: string; name: string } }>
  }
}

export type PlaybookDetailQueryVariables = Exact<{
  id: string | number
}>

export type PlaybookDetailQuery = {
  __typename: 'Query'
  playbook: {
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  } | null
}

export type PlaybooksQueryVariables = Exact<{ [key: string]: never }>

export type PlaybooksQuery = {
  __typename: 'Query'
  playbooks: Array<{
    __typename: 'MessagingPlaybook'
    id: string
    name: string
    type: MessagingPlaybookType | null
    description: string | null
    appId: string | null
    crmAppName: string | null
    crmAppPermissions: Array<Permission> | null
    crmTokenSet: boolean
    activeVersion: number | null
    createdAt: DateTimeString
    updatedAt: DateTimeString
    versions: Array<{
      __typename: 'MessagingPlaybookVersion'
      aggregationDelayMs: number
      createdAt: DateTimeString
      enabledTools: Array<string>
      followUpDelayMs: number
      followUpEnabled: boolean
      followUpMaxCount: number
      followUpPostNudgeQuietMs: number
      followUpPrompt: string
      followUpTemplate: string
      handoffTurnLimit: number
      historyLimit: number
      leadCaptureEnabled: boolean
      mediaVisionEnabled: boolean
      modelId: string | null
      outcomes: Array<string>
      summaryEnabled: boolean
      systemPrompt: string
      temperature: number | null
      turnLimit: number
      version: number
      followUpSendWindow: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          end: string
          start: string
        }>
      } | null
    }>
  }>
}

export type ReportAttributionCoverageCountsFragment = {
  __typename: 'ReportAttributionCoverageCounts'
  cases: number
  noSource: number
  surfaceOnly: number
  mediumOnly: number
  campaignKnown: number
  adKnown: number
  referralUnresolved: number
  referralResolved: number
}

export type ReportAcquisitionFunnelQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: CaseSourceDimension | null | undefined
  attributedOnly?: boolean | null | undefined
}>

export type ReportAcquisitionFunnelQuery = {
  __typename: 'Query'
  reportAcquisitionFunnel: {
    __typename: 'ReportAcquisitionFunnel'
    timezone: string
    dimension: CaseSourceDimension | null
    excludedOtherCurrencyOrders: number
    groups: Array<{
      __typename: 'ReportAcquisitionFunnelGroup'
      key: string | null
      label: string | null
      buckets: Array<{
        __typename: 'ReportAcquisitionFunnelBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportAcquisitionFunnelCounts'
          submitted: number
          contacted: number
          eligible: number
          orderCreated: number
          paid: number
          closedUncontactable: number
          closedDuplicate: number
          closedOther: number
        }
        stageExits: Array<{ __typename: 'ReportAcquisitionFunnelStageExits'; open: number }>
        timeToStage: {
          __typename: 'ReportAcquisitionFunnelTimeToStage'
          contactedP50Hours: number | null
          contactedP90Hours: number | null
          paidP50Days: number | null
          paidP90Days: number | null
        }
      }>
      totals: {
        __typename: 'ReportAcquisitionFunnelBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportAcquisitionFunnelCounts'
          submitted: number
          contacted: number
          eligible: number
          orderCreated: number
          paid: number
          closedUncontactable: number
          closedDuplicate: number
          closedOther: number
        }
        stageExits: Array<{ __typename: 'ReportAcquisitionFunnelStageExits'; open: number }>
        timeToStage: {
          __typename: 'ReportAcquisitionFunnelTimeToStage'
          contactedP50Hours: number | null
          contactedP90Hours: number | null
          paidP50Days: number | null
          paidP90Days: number | null
        }
        workState: {
          __typename: 'ReportAcquisitionFunnelWorkState'
          neverAssigned: number
          noActionLogged: number
          unsuccessfulActionsOnly: number
          everConversation: number
          customerReplied: number
        }
        unattributed: {
          __typename: 'ReportAcquisitionFunnelUnattributed'
          noSource: number
          blank: number
          placeholder: number
          intake: {
            __typename: 'ReportAcquisitionFunnelIntakePath'
            none: number
            webForm: number
            fbForm: number
            messagingLead: number
            messagingEscalation: number
            other: number
          }
        }
        prescribed: { __typename: 'Money'; amount: number; currency: string }
        collected: { __typename: 'Money'; amount: number; currency: string }
        expectedPipeline: { __typename: 'Money'; amount: number; currency: string }
      }
    }>
  }
}

export type ReportAcquisitionFunnelWhyQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: CaseSourceDimension | null | undefined
  attributedOnly?: boolean | null | undefined
}>

export type ReportAcquisitionFunnelWhyQuery = {
  __typename: 'Query'
  reportNeverReached: {
    __typename: 'ReportNeverReached'
    groups: Array<{
      __typename: 'ReportNeverReachedGroup'
      key: string | null
      neverReached: number
      reasons: Array<{
        __typename: 'ReportNeverReachedReason'
        reason: AcquisitionNeverReachedReason
        category: AcquisitionNeverReachedCategory
        totals: { __typename: 'ReportNeverReachedBucket'; cases: number }
      }>
    }>
  }
  reportCloseReasons: {
    __typename: 'ReportCloseReasons'
    groups: Array<{
      __typename: 'ReportCloseReasonsGroup'
      key: string | null
      closedUnpaid: number
      reasons: Array<{
        __typename: 'ReportCloseReasonsReason'
        closeReasonId: string | null
        value: string | null
        name: string | null
        totals: {
          __typename: 'ReportCloseReasonsBucket'
          cases: number
          medianAgeHours: number | null
        }
      }>
    }>
  }
}

export type ReportAcquisitionFunnelWindowMoneyQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: CaseSourceDimension | null | undefined
  attributedOnly?: boolean | null | undefined
  PERMISSION_READ_FINANCE?: boolean
}>

export type ReportAcquisitionFunnelWindowMoneyQuery = {
  __typename: 'Query'
  reportFeeEvolution?: {
    __typename: 'ReportFeeEvolution'
    currencies: Array<{
      __typename: 'ReportFeeEvolutionByCurrency'
      currency: string
      totalPrescribed: { __typename: 'Money'; amount: number; currency: string }
      totalCollected: { __typename: 'Money'; amount: number; currency: string }
      groups: Array<{
        __typename: 'ReportFeeEvolutionGroup'
        key: string | null
        totalPrescribed: { __typename: 'Money'; amount: number; currency: string }
        totalCollected: { __typename: 'Money'; amount: number; currency: string }
      }>
    }>
  }
}

export type ReportAcquisitionSankeyFunnelQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
}>

export type ReportAcquisitionSankeyFunnelQuery = {
  __typename: 'Query'
  reportAcquisitionFunnel: {
    __typename: 'ReportAcquisitionFunnel'
    timezone: string
    groups: Array<{
      __typename: 'ReportAcquisitionFunnelGroup'
      key: string | null
      buckets: Array<{
        __typename: 'ReportAcquisitionFunnelBucket'
        interval: { __typename: 'Interval'; since: DateTimeString }
        counts: { __typename: 'ReportAcquisitionFunnelCounts'; submitted: number }
      }>
      totals: {
        __typename: 'ReportAcquisitionFunnelBucket'
        counts: {
          __typename: 'ReportAcquisitionFunnelCounts'
          submitted: number
          contacted: number
          eligible: number
          orderCreated: number
          paid: number
          closedUncontactable: number
          closedDuplicate: number
          closedOther: number
        }
        stageExits: Array<{
          __typename: 'ReportAcquisitionFunnelStageExits'
          stage: AcquisitionFunnelStage
          closedUncontactable: number
          closedDuplicate: number
          closedOther: number
          open: number
        }>
        expectedPipeline: { __typename: 'Money'; amount: number; currency: string }
        collected: { __typename: 'Money'; amount: number; currency: string }
      }
    }>
  }
}

export type ReportAcquisitionSpendQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
}>

export type ReportAcquisitionSpendQuery = {
  __typename: 'Query'
  reportAcquisitionSpend: {
    __typename: 'ReportAcquisitionSpend'
    configured: boolean
    degraded: boolean
    accountCurrency: string | null
    accountTimezone: string | null
    adAccountId: string | null
    partial: boolean
    snapshotAsOf: DateTimeString | null
    timezone: string
    unsettledDays: number
    campaigns: Array<{
      __typename: 'ReportAcquisitionSpendCampaign'
      campaignId: string
      campaignName: string
      totalImpressions: number
      totalClicks: number
      totalOutboundClicks: number
      totalLeadsOnsite: number
      totalSpend: { __typename: 'Money'; amount: number; currency: string }
    }> | null
  }
}

export type ReportAcquisitionWebFunnelQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
}>

export type ReportAcquisitionWebFunnelQuery = {
  __typename: 'Query'
  reportAcquisitionWebFunnel: {
    __typename: 'ReportAcquisitionWebFunnel'
    configured: boolean
    degraded: boolean
    degradedParts: Array<string>
    site: Array<{
      __typename: 'ReportAcquisitionWebFunnelBucket'
      pageViews: number | null
      formViews: number | null
      formStarts: number | null
      formSubmissions: number | null
      formViewSessions: number | null
      formStartSessions: number | null
      formSubmitSessions: number | null
      submitErrors: number | null
      submitErrorsValidation: number
      submitErrorsServer: number
      errorSessions: number | null
      recoveredSubmissions: number | null
      sessions: number | null
      sessionsMeta: number | null
      sessionsGoogleAds: number | null
      sessionsTiktok: number | null
      sessionsOther: number | null
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
    }> | null
  }
}

export type ReportAssignedLeadsQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: Period
}>

export type ReportAssignedLeadsQuery = {
  __typename: 'Query'
  reportAssignedLeads: {
    __typename: 'ReportAssignedLeads'
    interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
    data: Array<{
      __typename: 'ReportAssignedLeadsByUser'
      user: {
        __typename: 'User'
        displayName: string
        userTeam: { __typename: 'UserTeam'; name: string } | null
      }
      intervalCounts: Array<{
        __typename: 'ReportAssignedLeadsByUserInInterval'
        countManuallyAssigned: number
        countAutoAssigned: number
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      }>
    }>
  }
}

export type ReportAttributionCoverageQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
}>

export type ReportAttributionCoverageQuery = {
  __typename: 'Query'
  reportAttributionCoverage: {
    __typename: 'ReportAttributionCoverage'
    timezone: string
    buckets: Array<{
      __typename: 'ReportAttributionCoverageBucket'
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportAttributionCoverageCounts'
        cases: number
        noSource: number
        surfaceOnly: number
        mediumOnly: number
        campaignKnown: number
        adKnown: number
        referralUnresolved: number
        referralResolved: number
      }
    }>
    totals: {
      __typename: 'ReportAttributionCoverageCounts'
      cases: number
      noSource: number
      surfaceOnly: number
      mediumOnly: number
      campaignKnown: number
      adKnown: number
      referralUnresolved: number
      referralResolved: number
    }
    channels: Array<{
      __typename: 'ReportAttributionCoverageChannel'
      intake: AcquisitionIntakePath
      kind: string | null
      buckets: Array<{
        __typename: 'ReportAttributionCoverageBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportAttributionCoverageCounts'
          cases: number
          noSource: number
          surfaceOnly: number
          mediumOnly: number
          campaignKnown: number
          adKnown: number
          referralUnresolved: number
          referralResolved: number
        }
      }>
      totals: {
        __typename: 'ReportAttributionCoverageCounts'
        cases: number
        noSource: number
        surfaceOnly: number
        mediumOnly: number
        campaignKnown: number
        adKnown: number
        referralUnresolved: number
        referralResolved: number
      }
    }>
  }
}

export type ReportFeeCohortCollectionQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
}>

export type ReportFeeCohortCollectionQuery = {
  __typename: 'Query'
  reportFeeCohortCollection: {
    __typename: 'ReportFeeCohortCollection'
    timezone: string
    asOf: DateTimeString
    days: Array<number>
    unpricedOrders: number
    adjustedOrders: number
    mismatchedCurrencyPayments: number
    interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
    currencies: Array<{
      __typename: 'ReportFeeCohortCollectionByCurrency'
      currency: string
      totals: {
        __typename: 'ReportFeeCohortTotals'
        orders: number
        collectedShare: number | null
        outstandingOrders: number
        prescribed: { __typename: 'Money'; amount: number; currency: string }
        collected: { __typename: 'Money'; amount: number; currency: string }
        outstanding: { __typename: 'Money'; amount: number; currency: string }
        points: Array<{
          __typename: 'ReportFeeCohortPoint'
          day: number
          share: number | null
          collected: { __typename: 'Money'; amount: number; currency: string } | null
        }>
      }
      ageing: Array<{
        __typename: 'ReportFeeAgeingBand'
        band: string
        minDays: number
        maxDays: number | null
        orders: number
        outstanding: { __typename: 'Money'; amount: number; currency: string }
      }>
      buckets: Array<{
        __typename: 'ReportFeeCohortBucket'
        orders: number
        collectedShare: number | null
        mature: boolean
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        prescribed: { __typename: 'Money'; amount: number; currency: string }
        collected: { __typename: 'Money'; amount: number; currency: string }
        points: Array<{
          __typename: 'ReportFeeCohortPoint'
          day: number
          share: number | null
          collected: { __typename: 'Money'; amount: number; currency: string } | null
        }>
      }>
    }>
  }
}

export type ReportFeeEvolutionQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: CaseSourceDimension | null | undefined
}>

export type ReportFeeEvolutionQuery = {
  __typename: 'Query'
  reportFeeEvolution: {
    __typename: 'ReportFeeEvolution'
    timezone: string
    unpricedOrders: number
    dimension: CaseSourceDimension | null
    currencies: Array<{
      __typename: 'ReportFeeEvolutionByCurrency'
      currency: string
      totalPrescribed: { __typename: 'Money'; amount: number; currency: string }
      totalCollected: { __typename: 'Money'; amount: number; currency: string }
      buckets: Array<{
        __typename: 'ReportFeeEvolutionBucket'
        orderCount: number
        paymentCount: number
        prescribedRolling5BusinessDay: number | null
        prescribedRolling30Day: number | null
        collectedRolling5BusinessDay: number | null
        collectedRolling30Day: number | null
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        prescribed: { __typename: 'Money'; amount: number; currency: string }
        collected: { __typename: 'Money'; amount: number; currency: string }
      }>
      groups: Array<{
        __typename: 'ReportFeeEvolutionGroup'
        key: string | null
        totalPrescribed: { __typename: 'Money'; amount: number; currency: string }
        totalCollected: { __typename: 'Money'; amount: number; currency: string }
        buckets: Array<{
          __typename: 'ReportFeeEvolutionBucket'
          orderCount: number
          paymentCount: number
          prescribedRolling5BusinessDay: number | null
          prescribedRolling30Day: number | null
          collectedRolling5BusinessDay: number | null
          collectedRolling30Day: number | null
          interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
          prescribed: { __typename: 'Money'; amount: number; currency: string }
          collected: { __typename: 'Money'; amount: number; currency: string }
        }>
      }>
    }>
  }
}

export type ReportFeeMonthQueryVariables = Exact<{
  month: string
}>

export type ReportFeeMonthQuery = {
  __typename: 'Query'
  reportFeeMonth: {
    __typename: 'ReportFeeMonth'
    month: string
    timezone: string
    elapsedCalendarDays: number
    totalCalendarDays: number
    elapsedBusinessDays: number
    totalBusinessDays: number
    interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
    currencies: Array<{
      __typename: 'ReportFeeMonthByCurrency'
      currency: string
      days: Array<{
        __typename: 'ReportFeeMonthDay'
        date: string
        prescribed: { __typename: 'Money'; amount: number; currency: string }
        collected: { __typename: 'Money'; amount: number; currency: string }
        cumulativePrescribed: { __typename: 'Money'; amount: number; currency: string }
        cumulativeCollected: { __typename: 'Money'; amount: number; currency: string }
      }>
      previousMonthDays: Array<{
        __typename: 'ReportFeeMonthDay'
        date: string
        cumulativePrescribed: { __typename: 'Money'; amount: number; currency: string }
        cumulativeCollected: { __typename: 'Money'; amount: number; currency: string }
      }>
      mtdPrescribed: { __typename: 'Money'; amount: number; currency: string }
      mtdCollected: { __typename: 'Money'; amount: number; currency: string }
      prescribedProjection: {
        __typename: 'ReportFeeProjection'
        calendarRunRate: { __typename: 'Money'; amount: number; currency: string } | null
        businessDayRunRate: { __typename: 'Money'; amount: number; currency: string } | null
      }
      collectedProjection: {
        __typename: 'ReportFeeProjection'
        calendarRunRate: { __typename: 'Money'; amount: number; currency: string } | null
        businessDayRunRate: { __typename: 'Money'; amount: number; currency: string } | null
      }
      previousMonth: {
        __typename: 'ReportFeeMonthComparison'
        prescribedTotal: { __typename: 'Money'; amount: number; currency: string }
        collectedTotal: { __typename: 'Money'; amount: number; currency: string }
        prescribedToSameDay: { __typename: 'Money'; amount: number; currency: string }
        collectedToSameDay: { __typename: 'Money'; amount: number; currency: string }
      }
    }>
  }
}

export type ReportLeadDuplicatesQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: CaseSourceDimension | null | undefined
}>

export type ReportLeadDuplicatesQuery = {
  __typename: 'Query'
  reportLeadDuplicates: {
    __typename: 'ReportLeadDuplicates'
    timezone: string
    dimension: CaseSourceDimension | null
    groups: Array<{
      __typename: 'ReportLeadDuplicatesGroup'
      key: string | null
      rates: {
        __typename: 'ReportLeadDuplicatesRates'
        repeatRate: number | null
        paidRepeatRate: number | null
      }
      totals: {
        __typename: 'ReportLeadDuplicatesBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportLeadDuplicatesCounts'
          leads: number
          duplicates: number
          duplicatesSameType: number
          duplicatesPhoneOnly: number
          firstTime: number
          autoClosed: number
          classificationNew: number
          classificationReturning: number
          classificationDuplicate: number
          classificationUnclassified: number
          paidLeads: number
          paidRepeats: number
        }
      }
      buckets: Array<{
        __typename: 'ReportLeadDuplicatesBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportLeadDuplicatesCounts'
          leads: number
          duplicates: number
          duplicatesSameType: number
          duplicatesPhoneOnly: number
          firstTime: number
          autoClosed: number
          classificationNew: number
          classificationReturning: number
          classificationDuplicate: number
          classificationUnclassified: number
          paidLeads: number
          paidRepeats: number
        }
      }>
    }>
  }
}

export type ReportMessagingActivityQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
}>

export type ReportMessagingActivityQuery = {
  __typename: 'Query'
  reportMessagingActivity: {
    __typename: 'ReportMessagingActivity'
    configured: boolean
    degraded: boolean
    timezone: string
    totals: {
      __typename: 'ReportMessagingActivityBucket'
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportMessagingActivityCounts'
        conversationsStarted: number
        startedByCustomer: number
        startedByBusiness: number
        inboundMessages: number
        outboundOperator: number
        outboundBrain: number
        outboundSystem: number
        outboundExternal: number
        activeConversations: number | null
        customersMessaging: number | null
        handoffs: number
        archived: number
      }
    }
    buckets: Array<{
      __typename: 'ReportMessagingActivityBucket'
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportMessagingActivityCounts'
        conversationsStarted: number
        startedByCustomer: number
        startedByBusiness: number
        inboundMessages: number
        outboundOperator: number
        outboundBrain: number
        outboundSystem: number
        outboundExternal: number
        activeConversations: number | null
        customersMessaging: number | null
        handoffs: number
        archived: number
      }
    }>
    responseTimes: Array<{
      __typename: 'ReportMessagingResponseTimeBucket'
      turns: number
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      responders: Array<{
        __typename: 'ReportMessagingResponderStats'
        responder: ReportMessagingResponder
        turns: number
        p50Minutes: number | null
        p90Minutes: number | null
      }>
    }>
  }
}

export type ReportMessagingDeliveryQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: ReportMessagingDimension | null | undefined
}>

export type ReportMessagingDeliveryQuery = {
  __typename: 'Query'
  reportMessagingDelivery: {
    __typename: 'ReportMessagingDelivery'
    configured: boolean
    degraded: boolean
    timezone: string
    dimension: ReportMessagingDimension
    totals: {
      __typename: 'ReportMessagingDeliveryBucket'
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportMessagingDeliveryCounts'
        attempted: number
        pending: number
        retrying: number
        failed: number
        sent: number
        delivered: number
        read: number
      }
    }
    rates: {
      __typename: 'ReportMessagingDeliveryRates'
      acceptRate: number | null
      deliveryRate: number | null
      readRate: number | null
      failureRate: number | null
    }
    buckets: Array<{
      __typename: 'ReportMessagingDeliveryBucket'
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportMessagingDeliveryCounts'
        attempted: number
        pending: number
        retrying: number
        failed: number
        sent: number
        delivered: number
        read: number
      }
    }>
    groups: Array<{
      __typename: 'ReportMessagingDeliveryGroup'
      key: string | null
      templateLanguage: string | null
      templateCategory: string | null
      totals: {
        __typename: 'ReportMessagingDeliveryBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportMessagingDeliveryCounts'
          attempted: number
          pending: number
          retrying: number
          failed: number
          sent: number
          delivered: number
          read: number
        }
      }
      rates: {
        __typename: 'ReportMessagingDeliveryRates'
        acceptRate: number | null
        deliveryRate: number | null
        readRate: number | null
        failureRate: number | null
      }
      buckets: Array<{
        __typename: 'ReportMessagingDeliveryBucket'
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportMessagingDeliveryCounts'
          attempted: number
          pending: number
          retrying: number
          failed: number
          sent: number
          delivered: number
          read: number
        }
      }>
    }>
    failures: Array<{
      __typename: 'ReportMessagingFailure'
      errorCode: string
      errorClass: string | null
      sampleTitle: string | null
      kind: ReportMessagingOutboundKind
      templateName: string | null
      total: number
      buckets: Array<{
        __typename: 'ReportMessagingCountBucket'
        count: number
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      }>
    }>
    latency: Array<{
      __typename: 'ReportMessagingLatencyBucket'
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      template: {
        __typename: 'ReportMessagingLatencyStats'
        sentCount: number
        sentP50Ms: number | null
        sentP95Ms: number | null
        deliveredCount: number
        deliveredP50Ms: number | null
        deliveredP95Ms: number | null
        readCount: number
        readP50Ms: number | null
      } | null
      session: {
        __typename: 'ReportMessagingLatencyStats'
        sentCount: number
        sentP50Ms: number | null
        sentP95Ms: number | null
        deliveredCount: number
        deliveredP50Ms: number | null
        deliveredP95Ms: number | null
        readCount: number
        readP50Ms: number | null
      } | null
    }>
    billing: Array<{
      __typename: 'ReportMessagingBilling'
      pricingCategory: string | null
      pricingType: string | null
      billable: boolean | null
      total: number
      buckets: Array<{
        __typename: 'ReportMessagingCountBucket'
        count: number
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      }>
    }>
  }
}

export type ReportMessagingRepliesQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: ReportCalendarPeriod
  dimension?: ReportMessagingDimension | null | undefined
}>

export type ReportMessagingRepliesQuery = {
  __typename: 'Query'
  reportMessagingReplies: {
    __typename: 'ReportMessagingReplies'
    configured: boolean
    degraded: boolean
    timezone: string
    dimension: ReportMessagingDimension
    totals: {
      __typename: 'ReportMessagingReplyBucket'
      replyMedianMinutes: number | null
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportMessagingReplyCounts'
        sent: number
        delivered: number
        replied24h: number
        replied72h: number
        repliedAny: number
      }
    }
    rates: {
      __typename: 'ReportMessagingReplyRates'
      replyRate24h: number | null
      replyRate72h: number | null
      replyRateAny: number | null
      replyRateOfDelivered: number | null
    }
    buckets: Array<{
      __typename: 'ReportMessagingReplyBucket'
      replyMedianMinutes: number | null
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      counts: {
        __typename: 'ReportMessagingReplyCounts'
        sent: number
        delivered: number
        replied24h: number
        replied72h: number
        repliedAny: number
      }
    }>
    groups: Array<{
      __typename: 'ReportMessagingReplyGroup'
      key: string | null
      templateLanguage: string | null
      totals: {
        __typename: 'ReportMessagingReplyBucket'
        replyMedianMinutes: number | null
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportMessagingReplyCounts'
          sent: number
          delivered: number
          replied24h: number
          replied72h: number
          repliedAny: number
        }
      }
      rates: {
        __typename: 'ReportMessagingReplyRates'
        replyRate24h: number | null
        replyRate72h: number | null
        replyRateAny: number | null
        replyRateOfDelivered: number | null
      }
      buckets: Array<{
        __typename: 'ReportMessagingReplyBucket'
        replyMedianMinutes: number | null
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
        counts: {
          __typename: 'ReportMessagingReplyCounts'
          sent: number
          delivered: number
          replied24h: number
          replied72h: number
          repliedAny: number
        }
      }>
    }>
    byHour: Array<{
      __typename: 'ReportMessagingReplyHourSlot'
      isoWeekday: number
      hourOfDay: number
      sent: number
      replied24h: number
      replyRate24h: number | null
    }>
  }
}

export type ReportNewLeadsQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
  period: Period
}>

export type ReportNewLeadsQuery = {
  __typename: 'Query'
  reportNewLeads: {
    __typename: 'ReportNewLeads'
    interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
    leadsByAuthor: Array<{
      __typename: 'ReportNewLeadsByAuthor'
      totalCount: number
      createdBy:
        | { __typename: 'App'; displayName: string }
        | { __typename: 'User'; displayName: string }
        | null
      intervalCounts: Array<{
        __typename: 'ReportNewLeadsInInterval'
        count: number
        interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
      }>
    }>
    leadsTotal: Array<{
      __typename: 'ReportNewLeadsInInterval'
      count: number
      interval: { __typename: 'Interval'; since: DateTimeString; until: DateTimeString }
    }>
  }
}

export type ReportUserActivityQueryVariables = Exact<{
  since: DateTimeString
  until: DateTimeString
}>

export type ReportUserActivityQuery = {
  __typename: 'Query'
  reportUserActivity: Array<{
    __typename: 'ReportUserActivityByUser'
    user: {
      __typename: 'User'
      id: string
      displayName: string
      userTeam: { __typename: 'UserTeam'; name: string } | null
    }
    currentIdlePeriod: {
      __typename: 'ReportUserActivityIdleTime'
      duration: number
      since: DateTimeString
      until: DateTimeString
    } | null
    longestIdlePeriod: {
      __typename: 'ReportUserActivityIdleTime'
      duration: number
      since: DateTimeString
      until: DateTimeString
    } | null
    firstEvent: { __typename: 'CaseEvent'; id: string; date: DateTimeString; type: CaseEventType }
    lastEvent: { __typename: 'CaseEvent'; id: string; date: DateTimeString; type: CaseEventType }
  }>
}

export type SafetyTermCreateMutationVariables = Exact<{
  input: MessagingSafetyTermCreateInput
}>

export type SafetyTermCreateMutation = {
  __typename: 'Mutation'
  safetyTermCreate: {
    __typename: 'MessagingSafetyTerm'
    id: string
    term: string
    matchMode: MessagingSafetyMatchMode
    enabled: boolean
    note: string | null
  }
}

export type SafetyTermDeleteMutationVariables = Exact<{
  termId: string | number
}>

export type SafetyTermDeleteMutation = {
  __typename: 'Mutation'
  safetyTermDelete: { __typename: 'MessagingSafetyTermDelete'; success: boolean }
}

export type SafetyTermUpdateMutationVariables = Exact<{
  termId: string | number
  input: MessagingSafetyTermUpdateInput
}>

export type SafetyTermUpdateMutation = {
  __typename: 'Mutation'
  safetyTermUpdate: {
    __typename: 'MessagingSafetyTerm'
    id: string
    term: string
    matchMode: MessagingSafetyMatchMode
    enabled: boolean
    note: string | null
  }
}

export type SafetyCheckQueryVariables = Exact<{
  texts: Array<string> | string
}>

export type SafetyCheckQuery = {
  __typename: 'Query'
  safetyCheck: {
    __typename: 'MessagingSafetyCheck'
    checked: boolean
    flagged: boolean
    matches: Array<{
      __typename: 'MessagingSafetyMatch'
      start: number
      end: number
      term: string
      termId: string
      textIndex: number
    }>
  }
}

export type SafetyTermPreviewQueryVariables = Exact<{
  input: MessagingSafetyTermPreviewInput
}>

export type SafetyTermPreviewQuery = {
  __typename: 'Query'
  safetyTermPreview: {
    __typename: 'MessagingSafetyTermPreview'
    matchedCount: number
    sampleSize: number
    examples: Array<{
      __typename: 'MessagingSafetyTermPreviewExample'
      text: string
      start: number
      end: number
    }>
  }
}

export type SafetyTermsQueryVariables = Exact<{ [key: string]: never }>

export type SafetyTermsQuery = {
  __typename: 'Query'
  safetyTerms: Array<{
    __typename: 'MessagingSafetyTerm'
    id: string
    term: string
    matchMode: MessagingSafetyMatchMode
    enabled: boolean
    hitCount: number | null
    note: string | null
  }>
}

export type CampaignResolverScheduleInfoFragment = {
  __typename: 'CampaignResolverSchedule'
  mode: CampaignResolverScheduleMode
  observeHolidays: boolean
  window: {
    __typename: 'MessagingSendWindow'
    tz: string
    rules: Array<{
      __typename: 'MessagingSendWindowRule'
      days: Array<MessagingSendWindowDay>
      start: string
      end: string
    }>
  }
}

export type OrderDocumentTypeFragment = {
  __typename: 'OrderDocumentType'
  id: string
  name: string
  isGeneratable: boolean
  requiresSignature: boolean
  appliesMeterai: boolean
  documentFilenameTemplate: string | null
  hasTemplate: boolean
  updatedAt: DateTimeString
}

export type ProductPackageFragment = {
  __typename: 'ProductPackage'
  id: string
  availability: Array<ProductPackageAvailability>
  name: string
  publicName: string
  isActive: boolean
  createdAt: DateTimeString
  defaultDurationMonths: number
  defaultFeeRate: number
  minimumFeeRate: number
  serviceLineLabel: string
  products: Array<{ __typename: 'Product'; id: string; name: string }>
  caseType: {
    __typename: 'CaseType'
    id: string
    name: string
    products: Array<{ __typename: 'Product'; id: string; name: string; productType: ProductType }>
  }
  minimumFee: { __typename: 'Money'; amount: number; currency: string }
  orderLines: Array<{
    __typename: 'ProductPackageOrderLine'
    id: string
    label: string
    amount: { __typename: 'Money'; amount: number; currency: string }
  }>
  stateDependencies: Array<{
    __typename: 'ProductPackageStateDependencies'
    id: string
    state: OrderStatus
    supportAutomaticStateChange: boolean
    caseDocumentTypes: Array<CaseDocumentType>
    customerDocumentTypes: Array<CustomerDocumentType>
    financialAccountDocumentTypes: Array<FinancialAccountDocumentType>
    orderPaymentStatus: Array<OrderPaymentStatus>
    validEntities: Array<OrderValidationEntity>
    orderDocumentTypes: Array<{
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }>
  }>
  stateEffects: Array<{
    __typename: 'ProductPackageStateEffects'
    id: string
    state: OrderStatus
    generateDocumentTypes: Array<{
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }>
  }>
  documentTypes: Array<{
    __typename: 'OrderDocumentType'
    id: string
    name: string
    isGeneratable: boolean
    requiresSignature: boolean
    appliesMeterai: boolean
    documentFilenameTemplate: string | null
    hasTemplate: boolean
    updatedAt: DateTimeString
  }>
}

export type ProductPackageStateDependenciesFragment = {
  __typename: 'ProductPackageStateDependencies'
  id: string
  state: OrderStatus
  supportAutomaticStateChange: boolean
  caseDocumentTypes: Array<CaseDocumentType>
  customerDocumentTypes: Array<CustomerDocumentType>
  financialAccountDocumentTypes: Array<FinancialAccountDocumentType>
  orderPaymentStatus: Array<OrderPaymentStatus>
  validEntities: Array<OrderValidationEntity>
  orderDocumentTypes: Array<{
    __typename: 'OrderDocumentType'
    id: string
    name: string
    isGeneratable: boolean
    requiresSignature: boolean
    appliesMeterai: boolean
    documentFilenameTemplate: string | null
    hasTemplate: boolean
    updatedAt: DateTimeString
  }>
}

export type ProductPackageStateEffectsFragment = {
  __typename: 'ProductPackageStateEffects'
  id: string
  state: OrderStatus
  generateDocumentTypes: Array<{
    __typename: 'OrderDocumentType'
    id: string
    name: string
    isGeneratable: boolean
    requiresSignature: boolean
    appliesMeterai: boolean
    documentFilenameTemplate: string | null
    hasTemplate: boolean
    updatedAt: DateTimeString
  }>
}

export type CampaignCreateMutationVariables = Exact<{
  input: CampaignCreateInput
}>

export type CampaignCreateMutation = {
  __typename: 'Mutation'
  create: { __typename: 'CampaignCreate'; newSettingsItem: { __typename: 'Campaign'; id: string } }
}

export type CampaignDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CampaignDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CampaignDelete'
    deletedSettingsItem: { __typename: 'Campaign'; id: string }
  }
}

export type CampaignResolverCreateMutationVariables = Exact<{
  input: CampaignResolverCreateInput
}>

export type CampaignResolverCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'CampaignResolverCreate'
    newSettingsItem: { __typename: 'CampaignResolver'; id: string }
  }
}

export type CampaignResolverDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CampaignResolverDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CampaignResolverDelete'
    deletedSettingsItem: { __typename: 'CampaignResolver'; id: string }
  }
}

export type CampaignResolverUpdateMutationVariables = Exact<{
  id: string | number
  input: CampaignResolverUpdateInput
}>

export type CampaignResolverUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CampaignResolverUpdate'
    updatedSettingsItem: { __typename: 'CampaignResolver'; id: string }
  }
}

export type CampaignUpdateMutationVariables = Exact<{
  id: string | number
  input: CampaignUpdateInput
}>

export type CampaignUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CampaignUpdate'
    updatedSettingsItem: { __typename: 'Campaign'; id: string }
  }
}

export type CaseActionResultCreateMutationVariables = Exact<{
  input: CaseActionResultCreateInput
}>

export type CaseActionResultCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'CaseActionResultCreate'
    newSettingsItem: { __typename: 'CaseActionResult'; id: string }
  }
}

export type CaseActionResultDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CaseActionResultDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CaseActionResultDelete'
    deletedSettingsItem: { __typename: 'CaseActionResult'; id: string }
  }
}

export type CaseActionResultUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseActionResultUpdateInput
}>

export type CaseActionResultUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CaseActionResultUpdate'
    updatedSettingsItem: { __typename: 'CaseActionResult'; id: string }
  }
}

export type CaseActionTypeCreateMutationVariables = Exact<{
  input: CaseActionTypeCreateInput
}>

export type CaseActionTypeCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'CaseActionTypeCreate'
    newSettingsItem: { __typename: 'CaseActionType'; id: string }
  }
}

export type CaseActionTypeDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CaseActionTypeDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CaseActionTypeDelete'
    deletedSettingsItem: { __typename: 'CaseActionType'; id: string }
  }
}

export type CaseActionTypeUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseActionTypeUpdateInput
}>

export type CaseActionTypeUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CaseActionTypeUpdate'
    updatedSettingsItem: { __typename: 'CaseActionType'; id: string }
  }
}

export type CaseCloseReasonCreateMutationVariables = Exact<{
  input: CaseCloseReasonCreateInput
}>

export type CaseCloseReasonCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'CaseCloseReasonCreate'
    newSettingsItem: { __typename: 'CaseCloseReason'; id: string }
  }
}

export type CaseCloseReasonDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CaseCloseReasonDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CaseCloseReasonDelete'
    deletedSettingsItem: { __typename: 'CaseCloseReason'; id: string }
  }
}

export type CaseCloseReasonUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseCloseReasonUpdateInput
}>

export type CaseCloseReasonUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CaseCloseReasonUpdate'
    updatedSettingsItem: { __typename: 'CaseCloseReason'; id: string }
  }
}

export type CaseTagCreateMutationVariables = Exact<{
  input: CaseTagCreateInput
}>

export type CaseTagCreateMutation = {
  __typename: 'Mutation'
  create: { __typename: 'CaseTagCreate'; newSettingsItem: { __typename: 'CaseTag'; id: string } }
}

export type CaseTagDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CaseTagDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CaseTagDelete'
    deletedSettingsItem: { __typename: 'CaseTag'; id: string }
  }
}

export type CaseTagUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseTagUpdateInput
}>

export type CaseTagUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CaseTagUpdate'
    updatedSettingsItem: { __typename: 'CaseTag'; id: string }
  }
}

export type CaseTypeCreateMutationVariables = Exact<{
  input: CaseTypeCreateInput
}>

export type CaseTypeCreateMutation = {
  __typename: 'Mutation'
  create: { __typename: 'CaseTypeCreate'; newSettingsItem: { __typename: 'CaseType'; id: string } }
}

export type CaseTypeDeleteMutationVariables = Exact<{
  id: string | number
}>

export type CaseTypeDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'CaseTypeDelete'
    deletedSettingsItem: { __typename: 'CaseType'; id: string }
  }
}

export type CaseTypeUpdateMutationVariables = Exact<{
  id: string | number
  input: CaseTypeUpdateInput
}>

export type CaseTypeUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'CaseTypeUpdate'
    updatedSettingsItem: { __typename: 'CaseType'; id: string }
  }
}

export type FinancialAccountTypeCreateMutationVariables = Exact<{
  input: FinancialAccountTypeCreateInput
}>

export type FinancialAccountTypeCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'FinancialAccountTypeCreate'
    newSettingsItem: { __typename: 'FinancialAccountType'; id: string }
  }
}

export type FinancialAccountTypeDeleteMutationVariables = Exact<{
  id: string | number
}>

export type FinancialAccountTypeDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'FinancialAccountTypeDelete'
    deletedSettingsItem: { __typename: 'FinancialAccountType'; id: string }
  }
}

export type FinancialAccountTypeUpdateMutationVariables = Exact<{
  id: string | number
  input: FinancialAccountTypeUpdateInput
}>

export type FinancialAccountTypeUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'FinancialAccountTypeUpdate'
    updatedSettingsItem: { __typename: 'FinancialAccountType'; id: string }
  }
}

export type OrderDocumentTypeCreateMutationVariables = Exact<{
  productPackageId: string | number
  input: OrderDocumentTypeCreateInput
}>

export type OrderDocumentTypeCreateMutation = {
  __typename: 'Mutation'
  orderDocumentTypeCreate: {
    __typename: 'OrderDocumentTypeCreate'
    documentType: {
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
    }
  }
}

export type OrderDocumentTypeDeleteMutationVariables = Exact<{
  id: string | number
}>

export type OrderDocumentTypeDeleteMutation = {
  __typename: 'Mutation'
  orderDocumentTypeDelete: {
    __typename: 'OrderDocumentTypeDelete'
    documentType: {
      __typename: 'OrderDocumentType'
      id: string
      productPackage: {
        __typename: 'ProductPackage'
        id: string
        documentTypes: Array<{
          __typename: 'OrderDocumentType'
          id: string
          name: string
          isGeneratable: boolean
          requiresSignature: boolean
          appliesMeterai: boolean
          documentFilenameTemplate: string | null
          hasTemplate: boolean
          updatedAt: DateTimeString
        }>
      }
    }
  }
}

export type OrderDocumentTypeTemplateTestRenderMutationVariables = Exact<{
  id: string | number
  input: OrderDocumentTypeTemplateTestRenderInput
}>

export type OrderDocumentTypeTemplateTestRenderMutation = {
  __typename: 'Mutation'
  orderDocumentTypeTemplateTestRender: {
    __typename: 'OrderDocumentTypeTemplateTestRender'
    url: string
  }
}

export type OrderDocumentTypeTemplateUploadMutationVariables = Exact<{
  id: string | number
  input: OrderDocumentTypeTemplateUploadInput
  requiresSignature: boolean
}>

export type OrderDocumentTypeTemplateUploadMutation = {
  __typename: 'Mutation'
  orderDocumentTypeTemplateUpload: {
    __typename: 'OrderDocumentTypeTemplateUpload'
    documentType: {
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }
  }
  orderDocumentTypeUpdate: {
    __typename: 'OrderDocumentTypeUpdate'
    documentType: {
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }
  }
}

export type OrderDocumentTypeTemplateVerifyMutationVariables = Exact<{
  id: string | number
  input: OrderDocumentTypeTemplateVerifyInput
}>

export type OrderDocumentTypeTemplateVerifyMutation = {
  __typename: 'Mutation'
  orderDocumentTypeTemplateVerify: {
    __typename: 'OrderDocumentTypeTemplateVerify'
    usedKeys: Array<string>
    unusedKeys: Array<string>
    signatures: Array<string>
  }
}

export type OrderDocumentTypeUpdateMutationVariables = Exact<{
  id: string | number
  input: OrderDocumentTypeUpdateInput
}>

export type OrderDocumentTypeUpdateMutation = {
  __typename: 'Mutation'
  orderDocumentTypeUpdate: {
    __typename: 'OrderDocumentTypeUpdate'
    documentType: {
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }
  }
}

export type ProductCreateMutationVariables = Exact<{
  input: ProductCreateInput
}>

export type ProductCreateMutation = {
  __typename: 'Mutation'
  create: { __typename: 'ProductCreate'; newSettingsItem: { __typename: 'Product'; id: string } }
}

export type ProductDeleteMutationVariables = Exact<{
  id: string | number
}>

export type ProductDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'ProductDelete'
    deletedSettingsItem: { __typename: 'Product'; id: string }
  }
}

export type ProductPackageCreateMutationVariables = Exact<{
  input: ProductPackageCreateInput
}>

export type ProductPackageCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'ProductPackageCreate'
    newSettingsItem: { __typename: 'ProductPackage'; id: string }
  }
}

export type ProductPackageDeleteMutationVariables = Exact<{
  id: string | number
}>

export type ProductPackageDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'ProductPackageDelete'
    deletedSettingsItem: { __typename: 'ProductPackage'; id: string }
  }
}

export type ProductPackageUpdateMutationVariables = Exact<{
  id: string | number
  input: ProductPackageUpdateInput
}>

export type ProductPackageUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'ProductPackageUpdate'
    updatedSettingsItem: { __typename: 'ProductPackage'; id: string }
  }
}

export type ProductUpdateMutationVariables = Exact<{
  id: string | number
  input: ProductUpdateInput
}>

export type ProductUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'ProductUpdate'
    updatedSettingsItem: { __typename: 'Product'; id: string }
  }
}

export type TicketActionResultCreateMutationVariables = Exact<{
  input: TicketActionResultCreateInput
}>

export type TicketActionResultCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'TicketActionResultCreate'
    newSettingsItem: { __typename: 'TicketActionResult'; id: string }
  }
}

export type TicketActionResultDeleteMutationVariables = Exact<{
  id: string | number
}>

export type TicketActionResultDeleteMutation = {
  __typename: 'Mutation'
  ticketActionResultDelete: {
    __typename: 'TicketActionResultDelete'
    ticketActionResult: { __typename: 'TicketActionResult'; id: string }
  }
}

export type TicketActionResultUpdateMutationVariables = Exact<{
  id: string | number
  input: TicketActionResultUpdateInput
}>

export type TicketActionResultUpdateMutation = {
  __typename: 'Mutation'
  ticketActionResultUpdate: {
    __typename: 'TicketActionResultUpdate'
    ticketActionResult: { __typename: 'TicketActionResult'; id: string }
  }
}

export type TicketActionTypeCreateMutationVariables = Exact<{
  input: TicketActionTypeCreateInput
}>

export type TicketActionTypeCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'TicketActionTypeCreate'
    newSettingsItem: { __typename: 'TicketActionType'; id: string }
  }
}

export type TicketActionTypeDeleteMutationVariables = Exact<{
  id: string | number
}>

export type TicketActionTypeDeleteMutation = {
  __typename: 'Mutation'
  ticketActionTypeDelete: {
    __typename: 'TicketActionTypeDelete'
    ticketActionType: { __typename: 'TicketActionType'; id: string }
  }
}

export type TicketActionTypeUpdateMutationVariables = Exact<{
  id: string | number
  input: TicketActionTypeUpdateInput
}>

export type TicketActionTypeUpdateMutation = {
  __typename: 'Mutation'
  ticketActionTypeUpdate: {
    __typename: 'TicketActionTypeUpdate'
    ticketActionType: { __typename: 'TicketActionType'; id: string }
  }
}

export type TicketCloseReasonCreateMutationVariables = Exact<{
  input: TicketCloseReasonCreateInput
}>

export type TicketCloseReasonCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'TicketCloseReasonCreate'
    newSettingsItem: { __typename: 'TicketCloseReason'; id: string }
  }
}

export type TicketCloseReasonDeleteMutationVariables = Exact<{
  id: string | number
}>

export type TicketCloseReasonDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'TicketCloseReasonDelete'
    deletedSettingsItem: { __typename: 'TicketCloseReason'; id: string }
  }
}

export type TicketCloseReasonUpdateMutationVariables = Exact<{
  id: string | number
  input: TicketCloseReasonUpdateInput
}>

export type TicketCloseReasonUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'TicketCloseReasonUpdate'
    updatedSettingsItem: { __typename: 'TicketCloseReason'; id: string }
  }
}

export type TicketTagCreateMutationVariables = Exact<{
  input: TicketTagCreateInput
}>

export type TicketTagCreateMutation = {
  __typename: 'Mutation'
  create: {
    __typename: 'TicketTagCreate'
    newSettingsItem: { __typename: 'TicketTag'; id: string }
  }
}

export type TicketTagDeleteMutationVariables = Exact<{
  id: string | number
}>

export type TicketTagDeleteMutation = {
  __typename: 'Mutation'
  delete: {
    __typename: 'TicketTagDelete'
    deletedSettingsItem: { __typename: 'TicketTag'; id: string }
  }
}

export type TicketTagUpdateMutationVariables = Exact<{
  id: string | number
  input: TicketTagUpdateInput
}>

export type TicketTagUpdateMutation = {
  __typename: 'Mutation'
  update: {
    __typename: 'TicketTagUpdate'
    updatedSettingsItem: { __typename: 'TicketTag'; id: string }
  }
}

export type CampaignChannelTemplatesQueryVariables = Exact<{
  channelId: string | number
}>

export type CampaignChannelTemplatesQuery = {
  __typename: 'Query'
  channelTemplates: Array<{
    __typename: 'MessagingChannelTemplate'
    id: string
    name: string
    language: string
    status: string
    components: any
  }>
}

export type CampaignDetailQueryVariables = Exact<{
  id: string | number
}>

export type CampaignDetailQuery = {
  __typename: 'Query'
  campaign: {
    __typename: 'Campaign'
    id: string
    name: string
    status: CampaignStatus
    flow: CaseFlow
    sendChannelId: string | null
    screeningPlaybookId: string | null
    conciergePlaybookId: string | null
    oversightScenarioId: string | null
    assignTagId: string | null
    customerRepliedActionTypeId: string | null
    screeningCompletedActionTypeId: string | null
    createdAt: DateTimeString
    openers: Array<{
      __typename: 'CampaignOpener'
      sequence: number
      delayMinutes: number
      templateName: string
      templateLanguage: string
      parameters: Array<{
        __typename: 'CampaignOpenerParameter'
        source: CampaignOpenerParameterSource
        text: string | null
        fallback: string | null
      }>
    }>
    fallbacks: Array<{
      __typename: 'CampaignFallback'
      trigger: CampaignFallbackTrigger
      action: CampaignFallbackAction
      closeReasonId: string | null
      tagId: string | null
      waitMinutes: number | null
    }>
    caseType: { __typename: 'CaseType'; id: string; name: string }
  } | null
}

export type CampaignDetailOptionsQueryVariables = Exact<{ [key: string]: never }>

export type CampaignDetailOptionsQuery = {
  __typename: 'Query'
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
  channels: Array<{ __typename: 'MessagingChannel'; id: string; displayNumber: string }>
  playbookOptions: Array<{
    __typename: 'MessagingPlaybookOption'
    id: string
    name: string
    type: MessagingPlaybookType | null
  }>
  oversightScenarioOptions: Array<{
    __typename: 'MessagingOversightScenarioOption'
    id: string
    name: string
    enabled: boolean
  }>
  caseCloseReasons: {
    __typename: 'CaseCloseReasonConnection'
    edges: Array<{
      __typename: 'CaseCloseReasonEdge'
      node: {
        __typename: 'CaseCloseReason'
        id: string
        name: string
        value: string
        isActive: boolean
      }
    }>
  }
  caseTags: {
    __typename: 'CaseTagConnection'
    edges: Array<{
      __typename: 'CaseTagEdge'
      node: { __typename: 'CaseTag'; id: string; name: string; value: string; isActive: boolean }
    }>
  }
}

export type CampaignListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: CampaignsSortInput | null | undefined
}>

export type CampaignListQuery = {
  __typename: 'Query'
  campaigns: {
    __typename: 'CampaignConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CampaignEdge'
      node: {
        __typename: 'Campaign'
        id: string
        name: string
        status: CampaignStatus
        flow: CaseFlow
        createdAt: DateTimeString
        caseType: { __typename: 'CaseType'; id: string; name: string }
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
}

export type CampaignResolverDetailQueryVariables = Exact<{
  id: string | number
}>

export type CampaignResolverDetailQuery = {
  __typename: 'Query'
  campaignResolver: {
    __typename: 'CampaignResolver'
    id: string
    name: string
    priority: number
    isActive: boolean
    matchKind: string | null
    matchType: string | null
    matchMedium: string | null
    matchCampaign: string | null
    matchTool: string | null
    dataFilter: any
    scheduleOpenNow: boolean
    createdAt: DateTimeString
    schedule: {
      __typename: 'CampaignResolverSchedule'
      mode: CampaignResolverScheduleMode
      observeHolidays: boolean
      window: {
        __typename: 'MessagingSendWindow'
        tz: string
        rules: Array<{
          __typename: 'MessagingSendWindowRule'
          days: Array<MessagingSendWindowDay>
          start: string
          end: string
        }>
      }
    } | null
    campaign: { __typename: 'Campaign'; id: string; name: string }
  } | null
}

export type CampaignResolverDetailOptionsQueryVariables = Exact<{ [key: string]: never }>

export type CampaignResolverDetailOptionsQuery = {
  __typename: 'Query'
  campaigns: {
    __typename: 'CampaignConnection'
    edges: Array<{
      __typename: 'CampaignEdge'
      node: { __typename: 'Campaign'; id: string; name: string }
    }>
  }
}

export type CampaignResolverSimulateQueryVariables = Exact<{
  input: CampaignResolverSimulateInput
  lastDays: number
}>

export type CampaignResolverSimulateQuery = {
  __typename: 'Query'
  campaignResolverSimulate: {
    __typename: 'CampaignResolverSimulation'
    totalCases: number
    casesWithSource: number
    matchedCount: number
    outsideScheduleCount: number
    wouldCaptureCount: number
    wouldCapturePercent: number
    shadowedCount: number
    shadowedBy: Array<{
      __typename: 'CampaignResolverSimulationShadow'
      resolverId: string
      name: string
      count: number
    }>
    sample: Array<{
      __typename: 'CampaignResolverSimulationSample'
      caseId: string
      caseCode: string
      createdAt: DateTimeString
      kind: string | null
      type: string | null
      medium: string | null
      campaign: string | null
      tool: string | null
    }>
  }
}

export type CampaignRoutingOverviewQueryVariables = Exact<{
  lastDays: number
}>

export type CampaignRoutingOverviewQuery = {
  __typename: 'Query'
  campaignRoutingOverview: {
    __typename: 'CampaignRoutingOverview'
    totalCases: number
    casesWithSource: number
    rules: Array<{
      __typename: 'CampaignRoutingOverviewRule'
      resolverId: string
      name: string
      priority: number
      isActive: boolean
      campaignId: string
      campaignName: string
      campaignActive: boolean
      capturedCount: number
      capturedPercent: number
      scheduleOpenNow: boolean
      schedule: {
        __typename: 'CampaignResolverSchedule'
        mode: CampaignResolverScheduleMode
        observeHolidays: boolean
        window: {
          __typename: 'MessagingSendWindow'
          tz: string
          rules: Array<{
            __typename: 'MessagingSendWindowRule'
            days: Array<MessagingSendWindowDay>
            start: string
            end: string
          }>
        }
      } | null
    }>
    fallthrough: Array<{
      __typename: 'CampaignRoutingOverviewFallthrough'
      campaignId: string | null
      campaignName: string | null
      count: number
    }>
  }
}

export type CampaignRulesForCampaignQueryVariables = Exact<{
  campaignId: string | number
}>

export type CampaignRulesForCampaignQuery = {
  __typename: 'Query'
  campaignResolvers: {
    __typename: 'CampaignResolverConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CampaignResolverEdge'
      node: {
        __typename: 'CampaignResolver'
        id: string
        name: string
        priority: number
        isActive: boolean
        scheduleOpenNow: boolean
        schedule: {
          __typename: 'CampaignResolverSchedule'
          mode: CampaignResolverScheduleMode
          observeHolidays: boolean
          window: {
            __typename: 'MessagingSendWindow'
            tz: string
            rules: Array<{
              __typename: 'MessagingSendWindowRule'
              days: Array<MessagingSendWindowDay>
              start: string
              end: string
            }>
          }
        } | null
      }
    }>
  }
}

export type CaseActionResultDetailQueryVariables = Exact<{
  id: string | number
}>

export type CaseActionResultDetailQuery = {
  __typename: 'Query'
  caseActionResult: {
    __typename: 'CaseActionResult'
    id: string
    isActive: boolean
    value: string
    success: boolean
    name: string
    createdAt: DateTimeString
  } | null
}

export type CaseActionResultListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: CaseActionResultsSortInput | null | undefined
}>

export type CaseActionResultListQuery = {
  __typename: 'Query'
  caseActionResults: {
    __typename: 'CaseActionResultConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseActionResultEdge'
      node: {
        __typename: 'CaseActionResult'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseActionTypeDetailQueryVariables = Exact<{
  id: string | number
}>

export type CaseActionTypeDetailQuery = {
  __typename: 'Query'
  caseActionType: {
    __typename: 'CaseActionType'
    id: string
    isActive: boolean
    value: string
    name: string
    createdAt: DateTimeString
  } | null
}

export type CaseActionTypeListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: CaseActionTypesSortInput | null | undefined
}>

export type CaseActionTypeListQuery = {
  __typename: 'Query'
  caseActionTypes: {
    __typename: 'CaseActionTypeConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseActionTypeEdge'
      node: {
        __typename: 'CaseActionType'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseActionTypeOptionsQueryVariables = Exact<{
  PERMISSION_MANAGE_SETTINGS?: boolean
}>

export type CaseActionTypeOptionsQuery = {
  __typename: 'Query'
  caseActionTypes?: {
    __typename: 'CaseActionTypeConnection'
    edges: Array<{
      __typename: 'CaseActionTypeEdge'
      node: {
        __typename: 'CaseActionType'
        id: string
        name: string
        value: string
        isActive: boolean
      }
    }>
  }
}

export type CaseCloseReasonDetailQueryVariables = Exact<{
  id: string | number
}>

export type CaseCloseReasonDetailQuery = {
  __typename: 'Query'
  caseCloseReason: {
    __typename: 'CaseCloseReason'
    id: string
    isActive: boolean
    value: string
    name: string
    reportedMetaEvent: MetaEvent | null
    createdAt: DateTimeString
  } | null
}

export type CaseCloseReasonListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: CaseCloseReasonsSortInput | null | undefined
}>

export type CaseCloseReasonListQuery = {
  __typename: 'Query'
  caseCloseReasons: {
    __typename: 'CaseCloseReasonConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseCloseReasonEdge'
      node: {
        __typename: 'CaseCloseReason'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseSourceObservedValuesQueryVariables = Exact<{
  lastDays: number
}>

export type CaseSourceObservedValuesQuery = {
  __typename: 'Query'
  caseSourceObservedValues: {
    __typename: 'CaseSourceObservedValues'
    kind: Array<{ __typename: 'CaseSourceObservedValue'; value: string; count: number }>
    type: Array<{ __typename: 'CaseSourceObservedValue'; value: string; count: number }>
    medium: Array<{ __typename: 'CaseSourceObservedValue'; value: string; count: number }>
    campaign: Array<{ __typename: 'CaseSourceObservedValue'; value: string; count: number }>
    tool: Array<{ __typename: 'CaseSourceObservedValue'; value: string; count: number }>
    dataKeys: Array<{ __typename: 'CaseSourceObservedDataKey'; key: string; count: number }>
  }
}

export type CaseTagDetailQueryVariables = Exact<{
  id: string | number
}>

export type CaseTagDetailQuery = {
  __typename: 'Query'
  caseTag: {
    __typename: 'CaseTag'
    id: string
    isActive: boolean
    color: string | null
    name: string
    value: string
    createdAt: DateTimeString
  } | null
}

export type CaseTagListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: CaseTagsSortInput | null | undefined
}>

export type CaseTagListQuery = {
  __typename: 'Query'
  caseTags: {
    __typename: 'CaseTagConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseTagEdge'
      node: {
        __typename: 'CaseTag'
        id: string
        isActive: boolean
        value: string
        name: string
        color: string | null
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type CaseTypeDetailQueryVariables = Exact<{
  id: string | number
}>

export type CaseTypeDetailQuery = {
  __typename: 'Query'
  caseType: {
    __typename: 'CaseType'
    id: string
    isActive: boolean
    value: string
    name: string
    codePrefix: string
    implicitCampaignId: string | null
    createdAt: DateTimeString
    nameUsedInDocuments: string | null
    financialAccountTypes: Array<{ __typename: 'FinancialAccountType'; id: string }>
    campaigns: Array<{ __typename: 'Campaign'; id: string; name: string; status: CampaignStatus }>
  } | null
}

export type CaseTypeDetailOptionsQueryVariables = Exact<{ [key: string]: never }>

export type CaseTypeDetailOptionsQuery = {
  __typename: 'Query'
  financialAccountTypes: {
    __typename: 'FinancialAccountTypeConnection'
    edges: Array<{
      __typename: 'FinancialAccountTypeEdge'
      node: { __typename: 'FinancialAccountType'; id: string; name: string }
    }>
  }
}

export type CaseTypeListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: CaseTypesSortInput | null | undefined
}>

export type CaseTypeListQuery = {
  __typename: 'Query'
  caseTypes: {
    __typename: 'CaseTypeConnection'
    totalCount: number
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: {
        __typename: 'CaseType'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type FinancialAccountTypeDetailQueryVariables = Exact<{
  id: string | number
}>

export type FinancialAccountTypeDetailQuery = {
  __typename: 'Query'
  financialAccountType: {
    __typename: 'FinancialAccountType'
    id: string
    isActive: boolean
    value: string
    name: string
    createdAt: DateTimeString
    nameUsedInDocuments: string | null
    codePrefix: string
  } | null
}

export type FinancialAccountTypeListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: FinancialAccountTypesSortInput | null | undefined
}>

export type FinancialAccountTypeListQuery = {
  __typename: 'Query'
  financialAccountTypes: {
    __typename: 'FinancialAccountTypeConnection'
    totalCount: number
    edges: Array<{
      __typename: 'FinancialAccountTypeEdge'
      node: {
        __typename: 'FinancialAccountType'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type OrderDocumentTypeTemplateOptionsQueryVariables = Exact<{
  documentTypeId: string | number
}>

export type OrderDocumentTypeTemplateOptionsQuery = {
  __typename: 'Query'
  orderDocumentTypeTemplateOptions: {
    __typename: 'OrderDocumentTypeTemplateOptions'
    availableVariables: Array<{
      __typename: 'OrderDocumentTypeTemplateKey'
      key: string
      sampleValue: string
    }>
    availableImages: Array<{
      __typename: 'OrderDocumentTypeTemplateKey'
      key: string
      sampleValue: string
    }>
    availableSignatures: Array<{
      __typename: 'OrderDocumentTypeTemplateKey'
      key: string
      sampleValue: string
    }>
  }
}

export type ProductDetailQueryVariables = Exact<{
  id: string | number
}>

export type ProductDetailQuery = {
  __typename: 'Query'
  product: {
    __typename: 'Product'
    id: string
    name: string
    isActive: boolean
    productType: ProductType
    createdAt: DateTimeString
    caseType: { __typename: 'CaseType'; id: string; name: string }
  } | null
}

export type ProductListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: ProductsSortInput | null | undefined
}>

export type ProductListQuery = {
  __typename: 'Query'
  products: {
    __typename: 'ProductConnection'
    totalCount: number
    edges: Array<{
      __typename: 'ProductEdge'
      node: {
        __typename: 'Product'
        id: string
        name: string
        isActive: boolean
        productType: ProductType
        createdAt: DateTimeString
        caseType: { __typename: 'CaseType'; id: string; name: string }
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
}

export type ProductPackageDetailQueryVariables = Exact<{
  id: string | number
}>

export type ProductPackageDetailQuery = {
  __typename: 'Query'
  productPackage: {
    __typename: 'ProductPackage'
    id: string
    availability: Array<ProductPackageAvailability>
    name: string
    publicName: string
    isActive: boolean
    createdAt: DateTimeString
    defaultDurationMonths: number
    defaultFeeRate: number
    minimumFeeRate: number
    serviceLineLabel: string
    products: Array<{ __typename: 'Product'; id: string; name: string }>
    caseType: {
      __typename: 'CaseType'
      id: string
      name: string
      products: Array<{ __typename: 'Product'; id: string; name: string; productType: ProductType }>
    }
    minimumFee: { __typename: 'Money'; amount: number; currency: string }
    orderLines: Array<{
      __typename: 'ProductPackageOrderLine'
      id: string
      label: string
      amount: { __typename: 'Money'; amount: number; currency: string }
    }>
    stateDependencies: Array<{
      __typename: 'ProductPackageStateDependencies'
      id: string
      state: OrderStatus
      supportAutomaticStateChange: boolean
      caseDocumentTypes: Array<CaseDocumentType>
      customerDocumentTypes: Array<CustomerDocumentType>
      financialAccountDocumentTypes: Array<FinancialAccountDocumentType>
      orderPaymentStatus: Array<OrderPaymentStatus>
      validEntities: Array<OrderValidationEntity>
      orderDocumentTypes: Array<{
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
        appliesMeterai: boolean
        documentFilenameTemplate: string | null
        hasTemplate: boolean
        updatedAt: DateTimeString
      }>
    }>
    stateEffects: Array<{
      __typename: 'ProductPackageStateEffects'
      id: string
      state: OrderStatus
      generateDocumentTypes: Array<{
        __typename: 'OrderDocumentType'
        id: string
        name: string
        isGeneratable: boolean
        requiresSignature: boolean
        appliesMeterai: boolean
        documentFilenameTemplate: string | null
        hasTemplate: boolean
        updatedAt: DateTimeString
      }>
    }>
    documentTypes: Array<{
      __typename: 'OrderDocumentType'
      id: string
      name: string
      isGeneratable: boolean
      requiresSignature: boolean
      appliesMeterai: boolean
      documentFilenameTemplate: string | null
      hasTemplate: boolean
      updatedAt: DateTimeString
    }>
  }
}

export type ProductPackageListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: ProductPackagesSortInput | null | undefined
}>

export type ProductPackageListQuery = {
  __typename: 'Query'
  productPackages: {
    __typename: 'ProductPackageConnection'
    totalCount: number
    edges: Array<{
      __typename: 'ProductPackageEdge'
      node: {
        __typename: 'ProductPackage'
        id: string
        name: string
        isActive: boolean
        createdAt: DateTimeString
        caseType: { __typename: 'CaseType'; id: string; name: string }
        products: Array<{ __typename: 'Product'; id: string; name: string; isActive: boolean }>
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
}

export type TicketActionResultDetailQueryVariables = Exact<{
  id: string | number
}>

export type TicketActionResultDetailQuery = {
  __typename: 'Query'
  ticketActionResult: {
    __typename: 'TicketActionResult'
    id: string
    isActive: boolean
    value: string
    success: boolean
    name: string
    createdAt: DateTimeString
  } | null
}

export type TicketActionResultListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: TicketActionResultsSortInput | null | undefined
}>

export type TicketActionResultListQuery = {
  __typename: 'Query'
  ticketActionResults: {
    __typename: 'TicketActionResultConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketActionResultEdge'
      node: {
        __typename: 'TicketActionResult'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
        success: boolean
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type TicketActionTypeDetailQueryVariables = Exact<{
  id: string | number
}>

export type TicketActionTypeDetailQuery = {
  __typename: 'Query'
  ticketActionType: {
    __typename: 'TicketActionType'
    id: string
    isActive: boolean
    value: string
    name: string
    createdAt: DateTimeString
  } | null
}

export type TicketActionTypeListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: TicketActionTypesSortInput | null | undefined
}>

export type TicketActionTypeListQuery = {
  __typename: 'Query'
  ticketActionTypes: {
    __typename: 'TicketActionTypeConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketActionTypeEdge'
      node: {
        __typename: 'TicketActionType'
        id: string
        isActive: boolean
        value: string
        name: string
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type TicketCloseReasonDetailQueryVariables = Exact<{
  id: string | number
}>

export type TicketCloseReasonDetailQuery = {
  __typename: 'Query'
  ticketCloseReason: {
    __typename: 'TicketCloseReason'
    id: string
    isActive: boolean
    value: string
    name: string
    success: boolean
    ticketTypes: Array<TicketType>
    createdAt: DateTimeString
  } | null
}

export type TicketCloseReasonListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: TicketCloseReasonsSortInput | null | undefined
}>

export type TicketCloseReasonListQuery = {
  __typename: 'Query'
  ticketCloseReasons: {
    __typename: 'TicketCloseReasonConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketCloseReasonEdge'
      node: {
        __typename: 'TicketCloseReason'
        id: string
        isActive: boolean
        value: string
        name: string
        success: boolean
        ticketTypes: Array<TicketType>
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type TicketTagDetailQueryVariables = Exact<{
  id: string | number
}>

export type TicketTagDetailQuery = {
  __typename: 'Query'
  ticketTag: {
    __typename: 'TicketTag'
    id: string
    isActive: boolean
    color: string | null
    name: string
    value: string
    ticketTypes: Array<TicketType>
    createdAt: DateTimeString
  } | null
}

export type TicketTagListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  sort?: TicketTagsSortInput | null | undefined
}>

export type TicketTagListQuery = {
  __typename: 'Query'
  ticketTags: {
    __typename: 'TicketTagConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketTagEdge'
      node: {
        __typename: 'TicketTag'
        id: string
        isActive: boolean
        value: string
        name: string
        color: string | null
        ticketTypes: Array<TicketType>
        createdAt: DateTimeString
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type MediationTicketDetailFragment = {
  __typename: 'MediationTicket'
  id: string
  accountNodeAccess: AccountNodeAccess | null
  createdAt: DateTimeString
  closedAt: DateTimeString | null
  code: string
  status: TicketStatus
  type: TicketType
  financialAccount: {
    __typename: 'FinancialAccount'
    id: string
    code: string
    createdAt: DateTimeString
    lastDueDate: DateTimeString | null
    dpd: number | null
    index: number | null
    status: FinancialAccountStatus
    providerReference: string | null
    ptpDate: DateTimeString | null
    tickets: {
      __typename: 'FinancialAccountTicketConnection'
      totalCount: number
      edges: Array<{
        __typename: 'FinancialAccountTicketEdge'
        node:
          | {
              __typename: 'MediationTicket'
              id: string
              code: string
              createdAt: DateTimeString
              status: TicketStatus
              type: TicketType
              providerContact: {
                __typename: 'FinancialProviderContact'
                id: string
                name: string
                phoneNumber: {
                  __typename: 'PhoneNumber'
                  countryCode: string | null
                  displayNumber: string
                  international: string
                } | null
              }
              assignedTo: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: {
                __typename: 'TicketCloseReason'
                name: string
                success: boolean
              } | null
              lastEventConnection: {
                __typename: 'TicketEventConnection'
                edges: Array<{
                  __typename: 'TicketEventEdge'
                  node: { __typename: 'TicketEvent'; date: DateTimeString }
                }>
              }
            }
          | {
              __typename: 'NegotiationTicket'
              id: string
              code: string
              createdAt: DateTimeString
              status: TicketStatus
              type: TicketType
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
              assignedTo: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: {
                __typename: 'TicketCloseReason'
                name: string
                success: boolean
              } | null
              lastEventConnection: {
                __typename: 'TicketEventConnection'
                edges: Array<{
                  __typename: 'TicketEventEdge'
                  node: { __typename: 'TicketEvent'; date: DateTimeString }
                }>
              }
            }
      }>
    }
    type: { __typename: 'FinancialAccountType'; id: string; name: string }
    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
    principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
    provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
    providerContact: {
      __typename: 'FinancialProviderContact'
      id: string
      name: string
      role: string
      phoneNumberNote: string | null
      emailAddress: string | null
      emailAddressNote: string | null
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      } | null
    } | null
    registeredContactPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    } | null
  }
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    role: string
    phoneNumberNote: string | null
    emailAddress: string | null
    emailAddressNote: string | null
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    } | null
  }
  caseProduct: {
    __typename: 'CaseProduct'
    id: string
    case: {
      __typename: 'Case'
      id: string
      accountNodeAccess: AccountNodeAccess | null
      code: string
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      createdAt: DateTimeString
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
      customer: {
        __typename: 'Customer'
        id: string
        name: string
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }
    }
    product: { __typename: 'Product'; id: string; name: string }
  }
  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
  actions: Array<{
    __typename: 'TicketAction'
    id: string
    comment: string | null
    date: DateTimeString
    createdAt: DateTimeString
    type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
    result: {
      __typename: 'TicketActionResult'
      id: string
      name: string
      value: string
      success: boolean
    }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
  }>
  events: {
    __typename: 'TicketEventConnection'
    edges: Array<{
      __typename: 'TicketEventEdge'
      node: {
        __typename: 'TicketEvent'
        id: string
        type: TicketEventType
        date: DateTimeString
        parameters: any
        actionResult: {
          __typename: 'TicketActionResult'
          id: string
          name: string
          success: boolean
        } | null
        actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
        user: { __typename: 'User'; id: string; displayName: string } | null
      }
    }>
  }
  documents: Array<{
    __typename: 'TicketDocument'
    id: string
    type: TicketDocumentType
    otherType: string | null
    note: string | null
    createdAt: DateTimeString
    file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
  }>
  tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
}

export type NegotiationTicketDetailFragment = {
  __typename: 'NegotiationTicket'
  id: string
  accountNodeAccess: AccountNodeAccess | null
  createdAt: DateTimeString
  closedAt: DateTimeString | null
  code: string
  status: TicketStatus
  type: TicketType
  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
  adjustedDebt: { __typename: 'Money'; amount: number; currency: string } | null
  adjustedDebtDiscount: { __typename: 'Money'; amount: number; currency: string } | null
  financialAccount: {
    __typename: 'FinancialAccount'
    id: string
    code: string
    createdAt: DateTimeString
    lastDueDate: DateTimeString | null
    dpd: number | null
    index: number | null
    status: FinancialAccountStatus
    providerReference: string | null
    ptpDate: DateTimeString | null
    tickets: {
      __typename: 'FinancialAccountTicketConnection'
      totalCount: number
      edges: Array<{
        __typename: 'FinancialAccountTicketEdge'
        node:
          | {
              __typename: 'MediationTicket'
              id: string
              code: string
              createdAt: DateTimeString
              status: TicketStatus
              type: TicketType
              providerContact: {
                __typename: 'FinancialProviderContact'
                id: string
                name: string
                phoneNumber: {
                  __typename: 'PhoneNumber'
                  countryCode: string | null
                  displayNumber: string
                  international: string
                } | null
              }
              assignedTo: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: {
                __typename: 'TicketCloseReason'
                name: string
                success: boolean
              } | null
              lastEventConnection: {
                __typename: 'TicketEventConnection'
                edges: Array<{
                  __typename: 'TicketEventEdge'
                  node: { __typename: 'TicketEvent'; date: DateTimeString }
                }>
              }
            }
          | {
              __typename: 'NegotiationTicket'
              id: string
              code: string
              createdAt: DateTimeString
              status: TicketStatus
              type: TicketType
              outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
              assignedTo: { __typename: 'User'; id: string; displayName: string } | null
              closeReason: {
                __typename: 'TicketCloseReason'
                name: string
                success: boolean
              } | null
              lastEventConnection: {
                __typename: 'TicketEventConnection'
                edges: Array<{
                  __typename: 'TicketEventEdge'
                  node: { __typename: 'TicketEvent'; date: DateTimeString }
                }>
              }
            }
      }>
    }
    type: { __typename: 'FinancialAccountType'; id: string; name: string }
    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
    principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
    provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
    providerContact: {
      __typename: 'FinancialProviderContact'
      id: string
      name: string
      role: string
      phoneNumberNote: string | null
      emailAddress: string | null
      emailAddressNote: string | null
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      } | null
    } | null
    registeredContactPhone: {
      __typename: 'CustomerContactPhone'
      id: string
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      }
    } | null
  }
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    role: string
    phoneNumberNote: string | null
    emailAddress: string | null
    emailAddressNote: string | null
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    } | null
  } | null
  caseProduct: {
    __typename: 'CaseProduct'
    id: string
    case: {
      __typename: 'Case'
      id: string
      accountNodeAccess: AccountNodeAccess | null
      code: string
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      createdAt: DateTimeString
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
      customer: {
        __typename: 'Customer'
        id: string
        name: string
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }
    }
    product: { __typename: 'Product'; id: string; name: string }
  }
  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
  actions: Array<{
    __typename: 'TicketAction'
    id: string
    comment: string | null
    date: DateTimeString
    createdAt: DateTimeString
    type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
    result: {
      __typename: 'TicketActionResult'
      id: string
      name: string
      value: string
      success: boolean
    }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
  }>
  events: {
    __typename: 'TicketEventConnection'
    edges: Array<{
      __typename: 'TicketEventEdge'
      node: {
        __typename: 'TicketEvent'
        id: string
        type: TicketEventType
        date: DateTimeString
        parameters: any
        actionResult: {
          __typename: 'TicketActionResult'
          id: string
          name: string
          success: boolean
        } | null
        actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
        user: { __typename: 'User'; id: string; displayName: string } | null
      }
    }>
  }
  documents: Array<{
    __typename: 'TicketDocument'
    id: string
    type: TicketDocumentType
    otherType: string | null
    note: string | null
    createdAt: DateTimeString
    file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
  }>
  tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
}

export type TicketActionFragment = {
  __typename: 'TicketAction'
  id: string
  comment: string | null
  date: DateTimeString
  createdAt: DateTimeString
  type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
  result: {
    __typename: 'TicketActionResult'
    id: string
    name: string
    value: string
    success: boolean
  }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
}

export type TicketCloseReasonOptionFragment = {
  __typename: 'TicketCloseReason'
  id: string
  name: string
  success: boolean
}

type TicketDetailBase_MediationTicket_Fragment = {
  __typename: 'MediationTicket'
  id: string
  accountNodeAccess: AccountNodeAccess | null
  createdAt: DateTimeString
  closedAt: DateTimeString | null
  code: string
  status: TicketStatus
  type: TicketType
  caseProduct: {
    __typename: 'CaseProduct'
    id: string
    case: {
      __typename: 'Case'
      id: string
      accountNodeAccess: AccountNodeAccess | null
      code: string
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      createdAt: DateTimeString
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
      customer: {
        __typename: 'Customer'
        id: string
        name: string
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }
    }
    product: { __typename: 'Product'; id: string; name: string }
  }
  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
  actions: Array<{
    __typename: 'TicketAction'
    id: string
    comment: string | null
    date: DateTimeString
    createdAt: DateTimeString
    type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
    result: {
      __typename: 'TicketActionResult'
      id: string
      name: string
      value: string
      success: boolean
    }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
  }>
  events: {
    __typename: 'TicketEventConnection'
    edges: Array<{
      __typename: 'TicketEventEdge'
      node: {
        __typename: 'TicketEvent'
        id: string
        type: TicketEventType
        date: DateTimeString
        parameters: any
        actionResult: {
          __typename: 'TicketActionResult'
          id: string
          name: string
          success: boolean
        } | null
        actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
        user: { __typename: 'User'; id: string; displayName: string } | null
      }
    }>
  }
  documents: Array<{
    __typename: 'TicketDocument'
    id: string
    type: TicketDocumentType
    otherType: string | null
    note: string | null
    createdAt: DateTimeString
    file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
  }>
  tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
}

type TicketDetailBase_NegotiationTicket_Fragment = {
  __typename: 'NegotiationTicket'
  id: string
  accountNodeAccess: AccountNodeAccess | null
  createdAt: DateTimeString
  closedAt: DateTimeString | null
  code: string
  status: TicketStatus
  type: TicketType
  caseProduct: {
    __typename: 'CaseProduct'
    id: string
    case: {
      __typename: 'Case'
      id: string
      accountNodeAccess: AccountNodeAccess | null
      code: string
      status: CaseStatus
      stage: CaseStage
      flow: CaseFlow | null
      createdAt: DateTimeString
      closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
      assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
      customer: {
        __typename: 'Customer'
        id: string
        name: string
        birthDate: DateString | null
        birthPlace: string | null
        gender: Gender | null
        governmentId: string | null
        educationLevel: CustomerEducationLevel | null
        marriageStatus: CustomerMaritalStatus | null
        cases: { __typename: 'CaseConnection'; totalCount: number }
      }
    }
    product: { __typename: 'Product'; id: string; name: string }
  }
  assignedTo: { __typename: 'User'; id: string; displayName: string } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
  actions: Array<{
    __typename: 'TicketAction'
    id: string
    comment: string | null
    date: DateTimeString
    createdAt: DateTimeString
    type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
    result: {
      __typename: 'TicketActionResult'
      id: string
      name: string
      value: string
      success: boolean
    }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
  }>
  events: {
    __typename: 'TicketEventConnection'
    edges: Array<{
      __typename: 'TicketEventEdge'
      node: {
        __typename: 'TicketEvent'
        id: string
        type: TicketEventType
        date: DateTimeString
        parameters: any
        actionResult: {
          __typename: 'TicketActionResult'
          id: string
          name: string
          success: boolean
        } | null
        actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
        createdBy:
          | { __typename: 'App'; id: string; displayName: string }
          | { __typename: 'User'; id: string; displayName: string }
          | null
        user: { __typename: 'User'; id: string; displayName: string } | null
      }
    }>
  }
  documents: Array<{
    __typename: 'TicketDocument'
    id: string
    type: TicketDocumentType
    otherType: string | null
    note: string | null
    createdAt: DateTimeString
    file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
      | null
  }>
  tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
}

export type TicketDetailBaseFragment =
  TicketDetailBase_MediationTicket_Fragment | TicketDetailBase_NegotiationTicket_Fragment

export type TicketDocumentDetailFragment = {
  __typename: 'TicketDocument'
  id: string
  type: TicketDocumentType
  otherType: string | null
  note: string | null
  createdAt: DateTimeString
  file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
}

export type TicketEventFragment = {
  __typename: 'TicketEvent'
  id: string
  type: TicketEventType
  date: DateTimeString
  parameters: any
  actionResult: {
    __typename: 'TicketActionResult'
    id: string
    name: string
    success: boolean
  } | null
  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
  createdBy:
    | { __typename: 'App'; id: string; displayName: string }
    | { __typename: 'User'; id: string; displayName: string }
    | null
  user: { __typename: 'User'; id: string; displayName: string } | null
}

export type TicketFinancialAccountFragment = {
  __typename: 'FinancialAccount'
  id: string
  code: string
  createdAt: DateTimeString
  lastDueDate: DateTimeString | null
  dpd: number | null
  index: number | null
  status: FinancialAccountStatus
  providerReference: string | null
  ptpDate: DateTimeString | null
  tickets: {
    __typename: 'FinancialAccountTicketConnection'
    totalCount: number
    edges: Array<{
      __typename: 'FinancialAccountTicketEdge'
      node:
        | {
            __typename: 'MediationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                countryCode: string | null
                displayNumber: string
                international: string
              } | null
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            lastEventConnection: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: { __typename: 'TicketEvent'; date: DateTimeString }
              }>
            }
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            lastEventConnection: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: { __typename: 'TicketEvent'; date: DateTimeString }
              }>
            }
          }
    }>
  }
  type: { __typename: 'FinancialAccountType'; id: string; name: string }
  outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
  principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
  provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    role: string
    phoneNumberNote: string | null
    emailAddress: string | null
    emailAddressNote: string | null
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    } | null
  } | null
  registeredContactPhone: {
    __typename: 'CustomerContactPhone'
    id: string
    phoneNumber: {
      __typename: 'PhoneNumber'
      countryCode: string | null
      displayNumber: string
      international: string
      national: string | null
    }
  } | null
}

type TicketList_MediationTicket_Fragment = {
  __typename: 'MediationTicket'
  id: string
  code: string
  createdAt: DateTimeString
  status: TicketStatus
  type: TicketType
  financialAccount: {
    __typename: 'FinancialAccount'
    id: string
    code: string
    case: {
      __typename: 'Case'
      code: string
      customer: { __typename: 'Customer'; id: string; name: string }
    }
    provider: { __typename: 'FinancialProvider'; name: string } | null
  }
  providerContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string } | null
  }
  assignedTo: {
    __typename: 'User'
    id: string
    displayName: string
    userTeam: { __typename: 'UserTeam'; name: string } | null
  } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
}

type TicketList_NegotiationTicket_Fragment = {
  __typename: 'NegotiationTicket'
  id: string
  code: string
  createdAt: DateTimeString
  status: TicketStatus
  type: TicketType
  financialAccount: {
    __typename: 'FinancialAccount'
    id: string
    code: string
    case: {
      __typename: 'Case'
      code: string
      customer: { __typename: 'Customer'; id: string; name: string }
    }
    provider: { __typename: 'FinancialProvider'; name: string } | null
  }
  nullableProviderContact: {
    __typename: 'FinancialProviderContact'
    id: string
    name: string
    phoneNumber: { __typename: 'PhoneNumber'; displayNumber: string; international: string } | null
  } | null
  assignedTo: {
    __typename: 'User'
    id: string
    displayName: string
    userTeam: { __typename: 'UserTeam'; name: string } | null
  } | null
  closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
  tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
}

export type TicketListFragment =
  TicketList_MediationTicket_Fragment | TicketList_NegotiationTicket_Fragment

export type NegotiationTicketOutstandingDebtUpdateMutationVariables = Exact<{
  id: string | number
  input: NegotiationTicketOutstandingDebtUpdateInput
}>

export type NegotiationTicketOutstandingDebtUpdateMutation = {
  __typename: 'Mutation'
  negotiationTicketOutstandingDebtUpdate: {
    __typename: 'NegotiationTicketOutstandingDebtUpdate'
    negotiationTicket: {
      __typename: 'NegotiationTicket'
      id: string
      outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
    }
  }
}

export type NegotiationTicketSettlementUpdateMutationVariables = Exact<{
  id: string | number
  input: NegotiationTicketSettlementUpdateInput
}>

export type NegotiationTicketSettlementUpdateMutation = {
  __typename: 'Mutation'
  negotiationTicketSettlementUpdate: {
    __typename: 'NegotiationTicketSettlementUpdate'
    negotiationTicket: {
      __typename: 'NegotiationTicket'
      id: string
      adjustedDebt: { __typename: 'Money'; amount: number; currency: string } | null
      adjustedDebtDiscount: { __typename: 'Money'; amount: number; currency: string } | null
    }
  }
}

export type TicketActionCreateMutationVariables = Exact<{
  id: string | number
  input: TicketActionCreateInput
}>

export type TicketActionCreateMutation = {
  __typename: 'Mutation'
  ticketActionCreate: {
    __typename: 'TicketActionCreate'
    ticketAction: {
      __typename: 'TicketAction'
      id: string
      comment: string | null
      date: DateTimeString
      createdAt: DateTimeString
      ticket:
        | {
            __typename: 'MediationTicket'
            id: string
            accountNodeAccess: AccountNodeAccess | null
            createdAt: DateTimeString
            closedAt: DateTimeString | null
            code: string
            status: TicketStatus
            type: TicketType
            caseProduct: {
              __typename: 'CaseProduct'
              id: string
              case: {
                __typename: 'Case'
                id: string
                accountNodeAccess: AccountNodeAccess | null
                code: string
                status: CaseStatus
                stage: CaseStage
                flow: CaseFlow | null
                createdAt: DateTimeString
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                customer: {
                  __typename: 'Customer'
                  id: string
                  name: string
                  birthDate: DateString | null
                  birthPlace: string | null
                  gender: Gender | null
                  governmentId: string | null
                  educationLevel: CustomerEducationLevel | null
                  marriageStatus: CustomerMaritalStatus | null
                  cases: { __typename: 'CaseConnection'; totalCount: number }
                }
              }
              product: { __typename: 'Product'; id: string; name: string }
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
            actions: Array<{
              __typename: 'TicketAction'
              id: string
              comment: string | null
              date: DateTimeString
              createdAt: DateTimeString
              type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
              result: {
                __typename: 'TicketActionResult'
                id: string
                name: string
                value: string
                success: boolean
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
            }>
            events: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: {
                  __typename: 'TicketEvent'
                  id: string
                  type: TicketEventType
                  date: DateTimeString
                  parameters: any
                  actionResult: {
                    __typename: 'TicketActionResult'
                    id: string
                    name: string
                    success: boolean
                  } | null
                  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                  user: { __typename: 'User'; id: string; displayName: string } | null
                }
              }>
            }
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            accountNodeAccess: AccountNodeAccess | null
            createdAt: DateTimeString
            closedAt: DateTimeString | null
            code: string
            status: TicketStatus
            type: TicketType
            caseProduct: {
              __typename: 'CaseProduct'
              id: string
              case: {
                __typename: 'Case'
                id: string
                accountNodeAccess: AccountNodeAccess | null
                code: string
                status: CaseStatus
                stage: CaseStage
                flow: CaseFlow | null
                createdAt: DateTimeString
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                customer: {
                  __typename: 'Customer'
                  id: string
                  name: string
                  birthDate: DateString | null
                  birthPlace: string | null
                  gender: Gender | null
                  governmentId: string | null
                  educationLevel: CustomerEducationLevel | null
                  marriageStatus: CustomerMaritalStatus | null
                  cases: { __typename: 'CaseConnection'; totalCount: number }
                }
              }
              product: { __typename: 'Product'; id: string; name: string }
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
            actions: Array<{
              __typename: 'TicketAction'
              id: string
              comment: string | null
              date: DateTimeString
              createdAt: DateTimeString
              type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
              result: {
                __typename: 'TicketActionResult'
                id: string
                name: string
                value: string
                success: boolean
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
            }>
            events: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: {
                  __typename: 'TicketEvent'
                  id: string
                  type: TicketEventType
                  date: DateTimeString
                  parameters: any
                  actionResult: {
                    __typename: 'TicketActionResult'
                    id: string
                    name: string
                    success: boolean
                  } | null
                  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                  user: { __typename: 'User'; id: string; displayName: string } | null
                }
              }>
            }
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
      type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
      result: {
        __typename: 'TicketActionResult'
        id: string
        name: string
        value: string
        success: boolean
      }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type TicketActionDeleteMutationVariables = Exact<{
  id: string | number
}>

export type TicketActionDeleteMutation = {
  __typename: 'Mutation'
  ticketActionDelete: {
    __typename: 'TicketActionDelete'
    ticketAction: {
      __typename: 'TicketAction'
      id: string
      comment: string | null
      date: DateTimeString
      createdAt: DateTimeString
      ticket:
        | {
            __typename: 'MediationTicket'
            id: string
            accountNodeAccess: AccountNodeAccess | null
            createdAt: DateTimeString
            closedAt: DateTimeString | null
            code: string
            status: TicketStatus
            type: TicketType
            caseProduct: {
              __typename: 'CaseProduct'
              id: string
              case: {
                __typename: 'Case'
                id: string
                accountNodeAccess: AccountNodeAccess | null
                code: string
                status: CaseStatus
                stage: CaseStage
                flow: CaseFlow | null
                createdAt: DateTimeString
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                customer: {
                  __typename: 'Customer'
                  id: string
                  name: string
                  birthDate: DateString | null
                  birthPlace: string | null
                  gender: Gender | null
                  governmentId: string | null
                  educationLevel: CustomerEducationLevel | null
                  marriageStatus: CustomerMaritalStatus | null
                  cases: { __typename: 'CaseConnection'; totalCount: number }
                }
              }
              product: { __typename: 'Product'; id: string; name: string }
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
            actions: Array<{
              __typename: 'TicketAction'
              id: string
              comment: string | null
              date: DateTimeString
              createdAt: DateTimeString
              type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
              result: {
                __typename: 'TicketActionResult'
                id: string
                name: string
                value: string
                success: boolean
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
            }>
            events: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: {
                  __typename: 'TicketEvent'
                  id: string
                  type: TicketEventType
                  date: DateTimeString
                  parameters: any
                  actionResult: {
                    __typename: 'TicketActionResult'
                    id: string
                    name: string
                    success: boolean
                  } | null
                  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                  user: { __typename: 'User'; id: string; displayName: string } | null
                }
              }>
            }
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            accountNodeAccess: AccountNodeAccess | null
            createdAt: DateTimeString
            closedAt: DateTimeString | null
            code: string
            status: TicketStatus
            type: TicketType
            caseProduct: {
              __typename: 'CaseProduct'
              id: string
              case: {
                __typename: 'Case'
                id: string
                accountNodeAccess: AccountNodeAccess | null
                code: string
                status: CaseStatus
                stage: CaseStage
                flow: CaseFlow | null
                createdAt: DateTimeString
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                customer: {
                  __typename: 'Customer'
                  id: string
                  name: string
                  birthDate: DateString | null
                  birthPlace: string | null
                  gender: Gender | null
                  governmentId: string | null
                  educationLevel: CustomerEducationLevel | null
                  marriageStatus: CustomerMaritalStatus | null
                  cases: { __typename: 'CaseConnection'; totalCount: number }
                }
              }
              product: { __typename: 'Product'; id: string; name: string }
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
            actions: Array<{
              __typename: 'TicketAction'
              id: string
              comment: string | null
              date: DateTimeString
              createdAt: DateTimeString
              type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
              result: {
                __typename: 'TicketActionResult'
                id: string
                name: string
                value: string
                success: boolean
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
            }>
            events: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: {
                  __typename: 'TicketEvent'
                  id: string
                  type: TicketEventType
                  date: DateTimeString
                  parameters: any
                  actionResult: {
                    __typename: 'TicketActionResult'
                    id: string
                    name: string
                    success: boolean
                  } | null
                  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                  user: { __typename: 'User'; id: string; displayName: string } | null
                }
              }>
            }
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
      type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
      result: {
        __typename: 'TicketActionResult'
        id: string
        name: string
        value: string
        success: boolean
      }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type TicketActionUpdateMutationVariables = Exact<{
  id: string | number
  input: TicketActionUpdateInput
}>

export type TicketActionUpdateMutation = {
  __typename: 'Mutation'
  ticketActionUpdate: {
    __typename: 'TicketActionCreate'
    ticketAction: {
      __typename: 'TicketAction'
      id: string
      comment: string | null
      date: DateTimeString
      createdAt: DateTimeString
      ticket:
        | {
            __typename: 'MediationTicket'
            id: string
            accountNodeAccess: AccountNodeAccess | null
            createdAt: DateTimeString
            closedAt: DateTimeString | null
            code: string
            status: TicketStatus
            type: TicketType
            caseProduct: {
              __typename: 'CaseProduct'
              id: string
              case: {
                __typename: 'Case'
                id: string
                accountNodeAccess: AccountNodeAccess | null
                code: string
                status: CaseStatus
                stage: CaseStage
                flow: CaseFlow | null
                createdAt: DateTimeString
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                customer: {
                  __typename: 'Customer'
                  id: string
                  name: string
                  birthDate: DateString | null
                  birthPlace: string | null
                  gender: Gender | null
                  governmentId: string | null
                  educationLevel: CustomerEducationLevel | null
                  marriageStatus: CustomerMaritalStatus | null
                  cases: { __typename: 'CaseConnection'; totalCount: number }
                }
              }
              product: { __typename: 'Product'; id: string; name: string }
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
            actions: Array<{
              __typename: 'TicketAction'
              id: string
              comment: string | null
              date: DateTimeString
              createdAt: DateTimeString
              type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
              result: {
                __typename: 'TicketActionResult'
                id: string
                name: string
                value: string
                success: boolean
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
            }>
            events: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: {
                  __typename: 'TicketEvent'
                  id: string
                  type: TicketEventType
                  date: DateTimeString
                  parameters: any
                  actionResult: {
                    __typename: 'TicketActionResult'
                    id: string
                    name: string
                    success: boolean
                  } | null
                  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                  user: { __typename: 'User'; id: string; displayName: string } | null
                }
              }>
            }
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            accountNodeAccess: AccountNodeAccess | null
            createdAt: DateTimeString
            closedAt: DateTimeString | null
            code: string
            status: TicketStatus
            type: TicketType
            caseProduct: {
              __typename: 'CaseProduct'
              id: string
              case: {
                __typename: 'Case'
                id: string
                accountNodeAccess: AccountNodeAccess | null
                code: string
                status: CaseStatus
                stage: CaseStage
                flow: CaseFlow | null
                createdAt: DateTimeString
                closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
                assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
                customer: {
                  __typename: 'Customer'
                  id: string
                  name: string
                  birthDate: DateString | null
                  birthPlace: string | null
                  gender: Gender | null
                  governmentId: string | null
                  educationLevel: CustomerEducationLevel | null
                  marriageStatus: CustomerMaritalStatus | null
                  cases: { __typename: 'CaseConnection'; totalCount: number }
                }
              }
              product: { __typename: 'Product'; id: string; name: string }
            }
            assignedTo: { __typename: 'User'; id: string; displayName: string } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
            actions: Array<{
              __typename: 'TicketAction'
              id: string
              comment: string | null
              date: DateTimeString
              createdAt: DateTimeString
              type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
              result: {
                __typename: 'TicketActionResult'
                id: string
                name: string
                value: string
                success: boolean
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
            }>
            events: {
              __typename: 'TicketEventConnection'
              edges: Array<{
                __typename: 'TicketEventEdge'
                node: {
                  __typename: 'TicketEvent'
                  id: string
                  type: TicketEventType
                  date: DateTimeString
                  parameters: any
                  actionResult: {
                    __typename: 'TicketActionResult'
                    id: string
                    name: string
                    success: boolean
                  } | null
                  actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                  createdBy:
                    | { __typename: 'App'; id: string; displayName: string }
                    | { __typename: 'User'; id: string; displayName: string }
                    | null
                  user: { __typename: 'User'; id: string; displayName: string } | null
                }
              }>
            }
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
      type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
      result: {
        __typename: 'TicketActionResult'
        id: string
        name: string
        value: string
        success: boolean
      }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }
  }
}

export type TicketAssignMutationVariables = Exact<{
  id: string | number
  userId: string | number
}>

export type TicketAssignMutation = {
  __typename: 'Mutation'
  ticketAssign: {
    __typename: 'TicketAssign'
    ticket:
      | {
          __typename: 'MediationTicket'
          id: string
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
        }
      | {
          __typename: 'NegotiationTicket'
          id: string
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
        }
  }
}

export type TicketBulkAssignMutationVariables = Exact<{
  input: TicketBulkAssignInput
}>

export type TicketBulkAssignMutation = {
  __typename: 'Mutation'
  ticketBulkAssign: {
    __typename: 'TicketBulkAssign'
    tickets: Array<
      | {
          __typename: 'MediationTicket'
          id: string
          status: TicketStatus
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
        }
      | {
          __typename: 'NegotiationTicket'
          id: string
          status: TicketStatus
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
        }
    >
  }
}

export type TicketBulkCloseMutationVariables = Exact<{
  input: TicketBulkCloseInput
}>

export type TicketBulkCloseMutation = {
  __typename: 'Mutation'
  ticketBulkClose: {
    __typename: 'TicketBulkClose'
    tickets: Array<
      | {
          __typename: 'MediationTicket'
          id: string
          status: TicketStatus
          closedAt: DateTimeString | null
          closeReason: { __typename: 'TicketCloseReason'; id: string; name: string } | null
        }
      | {
          __typename: 'NegotiationTicket'
          id: string
          status: TicketStatus
          closedAt: DateTimeString | null
          closeReason: { __typename: 'TicketCloseReason'; id: string; name: string } | null
        }
    >
  }
}

export type TicketCloseMutationVariables = Exact<{
  id: string | number
  input: TicketCloseInput
}>

export type TicketCloseMutation = {
  __typename: 'Mutation'
  ticketClose: {
    __typename: 'TicketClose'
    ticket:
      | { __typename: 'MediationTicket'; id: string; status: TicketStatus }
      | { __typename: 'NegotiationTicket'; id: string; status: TicketStatus }
  }
}

export type TicketDocumentCreateMutationVariables = Exact<{
  ticketId: string | number
  input: TicketDocumentCreateInput
}>

export type TicketDocumentCreateMutation = {
  __typename: 'Mutation'
  ticketDocumentCreate: {
    __typename: 'TicketDocumentCreate'
    ticketDocument: {
      __typename: 'TicketDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      ticket:
        | {
            __typename: 'MediationTicket'
            id: string
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
          }
    }
  }
}

export type TicketDocumentDeleteMutationVariables = Exact<{
  id: string | number
}>

export type TicketDocumentDeleteMutation = {
  __typename: 'Mutation'
  ticketDocumentDelete: {
    __typename: 'TicketDocumentDelete'
    ticketDocument: {
      __typename: 'TicketDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      ticket:
        | {
            __typename: 'MediationTicket'
            id: string
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
          }
    }
  }
}

export type TicketDocumentUpdateMutationVariables = Exact<{
  id: string | number
  input: TicketDocumentUpdateInput
}>

export type TicketDocumentUpdateMutation = {
  __typename: 'Mutation'
  ticketDocumentUpdate: {
    __typename: 'TicketDocumentUpdate'
    ticketDocument: {
      __typename: 'TicketDocument'
      id: string
      file: { __typename: 'RemoteFile'; fileName: string }
      ticket:
        | {
            __typename: 'MediationTicket'
            id: string
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            documents: Array<{
              __typename: 'TicketDocument'
              id: string
              type: TicketDocumentType
              otherType: string | null
              note: string | null
              createdAt: DateTimeString
              file: {
                __typename: 'RemoteFile'
                url: string | null
                mimeType: string
                fileName: string
              }
              createdBy:
                | { __typename: 'App'; id: string; displayName: string }
                | { __typename: 'User'; id: string; displayName: string }
                | null
            }>
          }
    }
  }
}

export type TicketNoteAddMutationVariables = Exact<{
  id: string | number
  input: TicketNoteAddInput
}>

export type TicketNoteAddMutation = {
  __typename: 'Mutation'
  ticketNoteAdd: {
    __typename: 'TicketNoteAdd'
    ticket:
      | {
          __typename: 'MediationTicket'
          id: string
          accountNodeAccess: AccountNodeAccess | null
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          code: string
          status: TicketStatus
          type: TicketType
          caseProduct: {
            __typename: 'CaseProduct'
            id: string
            case: {
              __typename: 'Case'
              id: string
              accountNodeAccess: AccountNodeAccess | null
              code: string
              status: CaseStatus
              stage: CaseStage
              flow: CaseFlow | null
              createdAt: DateTimeString
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              customer: {
                __typename: 'Customer'
                id: string
                name: string
                birthDate: DateString | null
                birthPlace: string | null
                gender: Gender | null
                governmentId: string | null
                educationLevel: CustomerEducationLevel | null
                marriageStatus: CustomerMaritalStatus | null
                cases: { __typename: 'CaseConnection'; totalCount: number }
              }
            }
            product: { __typename: 'Product'; id: string; name: string }
          }
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
          actions: Array<{
            __typename: 'TicketAction'
            id: string
            comment: string | null
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
            result: {
              __typename: 'TicketActionResult'
              id: string
              name: string
              value: string
              success: boolean
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          events: {
            __typename: 'TicketEventConnection'
            edges: Array<{
              __typename: 'TicketEventEdge'
              node: {
                __typename: 'TicketEvent'
                id: string
                type: TicketEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'TicketActionResult'
                  id: string
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string } | null
              }
            }>
          }
          documents: Array<{
            __typename: 'TicketDocument'
            id: string
            type: TicketDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }>
          tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
        }
      | {
          __typename: 'NegotiationTicket'
          id: string
          accountNodeAccess: AccountNodeAccess | null
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          code: string
          status: TicketStatus
          type: TicketType
          caseProduct: {
            __typename: 'CaseProduct'
            id: string
            case: {
              __typename: 'Case'
              id: string
              accountNodeAccess: AccountNodeAccess | null
              code: string
              status: CaseStatus
              stage: CaseStage
              flow: CaseFlow | null
              createdAt: DateTimeString
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              customer: {
                __typename: 'Customer'
                id: string
                name: string
                birthDate: DateString | null
                birthPlace: string | null
                gender: Gender | null
                governmentId: string | null
                educationLevel: CustomerEducationLevel | null
                marriageStatus: CustomerMaritalStatus | null
                cases: { __typename: 'CaseConnection'; totalCount: number }
              }
            }
            product: { __typename: 'Product'; id: string; name: string }
          }
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
          actions: Array<{
            __typename: 'TicketAction'
            id: string
            comment: string | null
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
            result: {
              __typename: 'TicketActionResult'
              id: string
              name: string
              value: string
              success: boolean
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          events: {
            __typename: 'TicketEventConnection'
            edges: Array<{
              __typename: 'TicketEventEdge'
              node: {
                __typename: 'TicketEvent'
                id: string
                type: TicketEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'TicketActionResult'
                  id: string
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string } | null
              }
            }>
          }
          documents: Array<{
            __typename: 'TicketDocument'
            id: string
            type: TicketDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }>
          tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
        }
  }
}

export type TicketTagAttachMutationVariables = Exact<{
  id: string | number
  input: TicketTagAttachInput
}>

export type TicketTagAttachMutation = {
  __typename: 'Mutation'
  ticketTagAttach: {
    __typename: 'TicketTagAttach'
    ticket:
      | {
          __typename: 'MediationTicket'
          id: string
          accountNodeAccess: AccountNodeAccess | null
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          code: string
          status: TicketStatus
          type: TicketType
          caseProduct: {
            __typename: 'CaseProduct'
            id: string
            case: {
              __typename: 'Case'
              id: string
              accountNodeAccess: AccountNodeAccess | null
              code: string
              status: CaseStatus
              stage: CaseStage
              flow: CaseFlow | null
              createdAt: DateTimeString
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              customer: {
                __typename: 'Customer'
                id: string
                name: string
                birthDate: DateString | null
                birthPlace: string | null
                gender: Gender | null
                governmentId: string | null
                educationLevel: CustomerEducationLevel | null
                marriageStatus: CustomerMaritalStatus | null
                cases: { __typename: 'CaseConnection'; totalCount: number }
              }
            }
            product: { __typename: 'Product'; id: string; name: string }
          }
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
          actions: Array<{
            __typename: 'TicketAction'
            id: string
            comment: string | null
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
            result: {
              __typename: 'TicketActionResult'
              id: string
              name: string
              value: string
              success: boolean
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          events: {
            __typename: 'TicketEventConnection'
            edges: Array<{
              __typename: 'TicketEventEdge'
              node: {
                __typename: 'TicketEvent'
                id: string
                type: TicketEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'TicketActionResult'
                  id: string
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string } | null
              }
            }>
          }
          documents: Array<{
            __typename: 'TicketDocument'
            id: string
            type: TicketDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }>
          tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
        }
      | {
          __typename: 'NegotiationTicket'
          id: string
          accountNodeAccess: AccountNodeAccess | null
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          code: string
          status: TicketStatus
          type: TicketType
          caseProduct: {
            __typename: 'CaseProduct'
            id: string
            case: {
              __typename: 'Case'
              id: string
              accountNodeAccess: AccountNodeAccess | null
              code: string
              status: CaseStatus
              stage: CaseStage
              flow: CaseFlow | null
              createdAt: DateTimeString
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              customer: {
                __typename: 'Customer'
                id: string
                name: string
                birthDate: DateString | null
                birthPlace: string | null
                gender: Gender | null
                governmentId: string | null
                educationLevel: CustomerEducationLevel | null
                marriageStatus: CustomerMaritalStatus | null
                cases: { __typename: 'CaseConnection'; totalCount: number }
              }
            }
            product: { __typename: 'Product'; id: string; name: string }
          }
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
          actions: Array<{
            __typename: 'TicketAction'
            id: string
            comment: string | null
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
            result: {
              __typename: 'TicketActionResult'
              id: string
              name: string
              value: string
              success: boolean
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          events: {
            __typename: 'TicketEventConnection'
            edges: Array<{
              __typename: 'TicketEventEdge'
              node: {
                __typename: 'TicketEvent'
                id: string
                type: TicketEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'TicketActionResult'
                  id: string
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string } | null
              }
            }>
          }
          documents: Array<{
            __typename: 'TicketDocument'
            id: string
            type: TicketDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }>
          tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
        }
  }
}

export type TicketTagDetachMutationVariables = Exact<{
  id: string | number
  input: TicketTagDetachInput
}>

export type TicketTagDetachMutation = {
  __typename: 'Mutation'
  ticketTagDetach: {
    __typename: 'TicketTagDetach'
    ticket:
      | {
          __typename: 'MediationTicket'
          id: string
          accountNodeAccess: AccountNodeAccess | null
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          code: string
          status: TicketStatus
          type: TicketType
          caseProduct: {
            __typename: 'CaseProduct'
            id: string
            case: {
              __typename: 'Case'
              id: string
              accountNodeAccess: AccountNodeAccess | null
              code: string
              status: CaseStatus
              stage: CaseStage
              flow: CaseFlow | null
              createdAt: DateTimeString
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              customer: {
                __typename: 'Customer'
                id: string
                name: string
                birthDate: DateString | null
                birthPlace: string | null
                gender: Gender | null
                governmentId: string | null
                educationLevel: CustomerEducationLevel | null
                marriageStatus: CustomerMaritalStatus | null
                cases: { __typename: 'CaseConnection'; totalCount: number }
              }
            }
            product: { __typename: 'Product'; id: string; name: string }
          }
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
          actions: Array<{
            __typename: 'TicketAction'
            id: string
            comment: string | null
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
            result: {
              __typename: 'TicketActionResult'
              id: string
              name: string
              value: string
              success: boolean
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          events: {
            __typename: 'TicketEventConnection'
            edges: Array<{
              __typename: 'TicketEventEdge'
              node: {
                __typename: 'TicketEvent'
                id: string
                type: TicketEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'TicketActionResult'
                  id: string
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string } | null
              }
            }>
          }
          documents: Array<{
            __typename: 'TicketDocument'
            id: string
            type: TicketDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }>
          tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
        }
      | {
          __typename: 'NegotiationTicket'
          id: string
          accountNodeAccess: AccountNodeAccess | null
          createdAt: DateTimeString
          closedAt: DateTimeString | null
          code: string
          status: TicketStatus
          type: TicketType
          caseProduct: {
            __typename: 'CaseProduct'
            id: string
            case: {
              __typename: 'Case'
              id: string
              accountNodeAccess: AccountNodeAccess | null
              code: string
              status: CaseStatus
              stage: CaseStage
              flow: CaseFlow | null
              createdAt: DateTimeString
              closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
              assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
              customer: {
                __typename: 'Customer'
                id: string
                name: string
                birthDate: DateString | null
                birthPlace: string | null
                gender: Gender | null
                governmentId: string | null
                educationLevel: CustomerEducationLevel | null
                marriageStatus: CustomerMaritalStatus | null
                cases: { __typename: 'CaseConnection'; totalCount: number }
              }
            }
            product: { __typename: 'Product'; id: string; name: string }
          }
          assignedTo: { __typename: 'User'; id: string; displayName: string } | null
          closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
          actions: Array<{
            __typename: 'TicketAction'
            id: string
            comment: string | null
            date: DateTimeString
            createdAt: DateTimeString
            type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
            result: {
              __typename: 'TicketActionResult'
              id: string
              name: string
              value: string
              success: boolean
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
          }>
          events: {
            __typename: 'TicketEventConnection'
            edges: Array<{
              __typename: 'TicketEventEdge'
              node: {
                __typename: 'TicketEvent'
                id: string
                type: TicketEventType
                date: DateTimeString
                parameters: any
                actionResult: {
                  __typename: 'TicketActionResult'
                  id: string
                  name: string
                  success: boolean
                } | null
                actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
                createdBy:
                  | { __typename: 'App'; id: string; displayName: string }
                  | { __typename: 'User'; id: string; displayName: string }
                  | null
                user: { __typename: 'User'; id: string; displayName: string } | null
              }
            }>
          }
          documents: Array<{
            __typename: 'TicketDocument'
            id: string
            type: TicketDocumentType
            otherType: string | null
            note: string | null
            createdAt: DateTimeString
            file: {
              __typename: 'RemoteFile'
              url: string | null
              mimeType: string
              fileName: string
            }
            createdBy:
              | { __typename: 'App'; id: string; displayName: string }
              | { __typename: 'User'; id: string; displayName: string }
              | null
          }>
          tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
        }
  }
}

export type InitialTicketFilterUsersQueryVariables = Exact<{
  userIds?: Array<string | number> | string | number | null | undefined
}>

export type InitialTicketFilterUsersQuery = {
  __typename: 'Query'
  users: {
    __typename: 'UserConnection'
    edges: Array<{
      __typename: 'UserEdge'
      node: { __typename: 'User'; id: string; displayName: string }
    }>
  }
}

export type MediationTicketDetailQueryVariables = Exact<{
  id: string | number
}>

export type MediationTicketDetailQuery = {
  __typename: 'Query'
  mediationTicket: {
    __typename: 'MediationTicket'
    id: string
    accountNodeAccess: AccountNodeAccess | null
    createdAt: DateTimeString
    closedAt: DateTimeString | null
    code: string
    status: TicketStatus
    type: TicketType
    financialAccount: {
      __typename: 'FinancialAccount'
      id: string
      code: string
      createdAt: DateTimeString
      lastDueDate: DateTimeString | null
      dpd: number | null
      index: number | null
      status: FinancialAccountStatus
      providerReference: string | null
      ptpDate: DateTimeString | null
      tickets: {
        __typename: 'FinancialAccountTicketConnection'
        totalCount: number
        edges: Array<{
          __typename: 'FinancialAccountTicketEdge'
          node:
            | {
                __typename: 'MediationTicket'
                id: string
                code: string
                createdAt: DateTimeString
                status: TicketStatus
                type: TicketType
                providerContact: {
                  __typename: 'FinancialProviderContact'
                  id: string
                  name: string
                  phoneNumber: {
                    __typename: 'PhoneNumber'
                    countryCode: string | null
                    displayNumber: string
                    international: string
                  } | null
                }
                assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: {
                  __typename: 'TicketCloseReason'
                  name: string
                  success: boolean
                } | null
                lastEventConnection: {
                  __typename: 'TicketEventConnection'
                  edges: Array<{
                    __typename: 'TicketEventEdge'
                    node: { __typename: 'TicketEvent'; date: DateTimeString }
                  }>
                }
              }
            | {
                __typename: 'NegotiationTicket'
                id: string
                code: string
                createdAt: DateTimeString
                status: TicketStatus
                type: TicketType
                outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: {
                  __typename: 'TicketCloseReason'
                  name: string
                  success: boolean
                } | null
                lastEventConnection: {
                  __typename: 'TicketEventConnection'
                  edges: Array<{
                    __typename: 'TicketEventEdge'
                    node: { __typename: 'TicketEvent'; date: DateTimeString }
                  }>
                }
              }
        }>
      }
      type: { __typename: 'FinancialAccountType'; id: string; name: string }
      outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
      principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
      provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
      providerContact: {
        __typename: 'FinancialProviderContact'
        id: string
        name: string
        role: string
        phoneNumberNote: string | null
        emailAddress: string | null
        emailAddressNote: string | null
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        } | null
      } | null
      registeredContactPhone: {
        __typename: 'CustomerContactPhone'
        id: string
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
    }
    providerContact: {
      __typename: 'FinancialProviderContact'
      id: string
      name: string
      role: string
      phoneNumberNote: string | null
      emailAddress: string | null
      emailAddressNote: string | null
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      } | null
    }
    caseProduct: {
      __typename: 'CaseProduct'
      id: string
      case: {
        __typename: 'Case'
        id: string
        accountNodeAccess: AccountNodeAccess | null
        code: string
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        createdAt: DateTimeString
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
        customer: {
          __typename: 'Customer'
          id: string
          name: string
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }
      }
      product: { __typename: 'Product'; id: string; name: string }
    }
    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
    closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
    actions: Array<{
      __typename: 'TicketAction'
      id: string
      comment: string | null
      date: DateTimeString
      createdAt: DateTimeString
      type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
      result: {
        __typename: 'TicketActionResult'
        id: string
        name: string
        value: string
        success: boolean
      }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }>
    events: {
      __typename: 'TicketEventConnection'
      edges: Array<{
        __typename: 'TicketEventEdge'
        node: {
          __typename: 'TicketEvent'
          id: string
          type: TicketEventType
          date: DateTimeString
          parameters: any
          actionResult: {
            __typename: 'TicketActionResult'
            id: string
            name: string
            success: boolean
          } | null
          actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
          user: { __typename: 'User'; id: string; displayName: string } | null
        }
      }>
    }
    documents: Array<{
      __typename: 'TicketDocument'
      id: string
      type: TicketDocumentType
      otherType: string | null
      note: string | null
      createdAt: DateTimeString
      file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }>
    tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
  }
}

export type NegotiationTicketDetailQueryVariables = Exact<{
  id: string | number
}>

export type NegotiationTicketDetailQuery = {
  __typename: 'Query'
  negotiationTicket: {
    __typename: 'NegotiationTicket'
    id: string
    accountNodeAccess: AccountNodeAccess | null
    createdAt: DateTimeString
    closedAt: DateTimeString | null
    code: string
    status: TicketStatus
    type: TicketType
    outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
    adjustedDebt: { __typename: 'Money'; amount: number; currency: string } | null
    adjustedDebtDiscount: { __typename: 'Money'; amount: number; currency: string } | null
    financialAccount: {
      __typename: 'FinancialAccount'
      id: string
      code: string
      createdAt: DateTimeString
      lastDueDate: DateTimeString | null
      dpd: number | null
      index: number | null
      status: FinancialAccountStatus
      providerReference: string | null
      ptpDate: DateTimeString | null
      tickets: {
        __typename: 'FinancialAccountTicketConnection'
        totalCount: number
        edges: Array<{
          __typename: 'FinancialAccountTicketEdge'
          node:
            | {
                __typename: 'MediationTicket'
                id: string
                code: string
                createdAt: DateTimeString
                status: TicketStatus
                type: TicketType
                providerContact: {
                  __typename: 'FinancialProviderContact'
                  id: string
                  name: string
                  phoneNumber: {
                    __typename: 'PhoneNumber'
                    countryCode: string | null
                    displayNumber: string
                    international: string
                  } | null
                }
                assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: {
                  __typename: 'TicketCloseReason'
                  name: string
                  success: boolean
                } | null
                lastEventConnection: {
                  __typename: 'TicketEventConnection'
                  edges: Array<{
                    __typename: 'TicketEventEdge'
                    node: { __typename: 'TicketEvent'; date: DateTimeString }
                  }>
                }
              }
            | {
                __typename: 'NegotiationTicket'
                id: string
                code: string
                createdAt: DateTimeString
                status: TicketStatus
                type: TicketType
                outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
                assignedTo: { __typename: 'User'; id: string; displayName: string } | null
                closeReason: {
                  __typename: 'TicketCloseReason'
                  name: string
                  success: boolean
                } | null
                lastEventConnection: {
                  __typename: 'TicketEventConnection'
                  edges: Array<{
                    __typename: 'TicketEventEdge'
                    node: { __typename: 'TicketEvent'; date: DateTimeString }
                  }>
                }
              }
        }>
      }
      type: { __typename: 'FinancialAccountType'; id: string; name: string }
      outstandingDebt: { __typename: 'Money'; amount: number; currency: string }
      principalAmount: { __typename: 'Money'; amount: number; currency: string } | null
      provider: { __typename: 'FinancialProvider'; id: string; name: string } | null
      providerContact: {
        __typename: 'FinancialProviderContact'
        id: string
        name: string
        role: string
        phoneNumberNote: string | null
        emailAddress: string | null
        emailAddressNote: string | null
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        } | null
      } | null
      registeredContactPhone: {
        __typename: 'CustomerContactPhone'
        id: string
        phoneNumber: {
          __typename: 'PhoneNumber'
          countryCode: string | null
          displayNumber: string
          international: string
          national: string | null
        }
      } | null
    }
    providerContact: {
      __typename: 'FinancialProviderContact'
      id: string
      name: string
      role: string
      phoneNumberNote: string | null
      emailAddress: string | null
      emailAddressNote: string | null
      phoneNumber: {
        __typename: 'PhoneNumber'
        countryCode: string | null
        displayNumber: string
        international: string
        national: string | null
      } | null
    } | null
    caseProduct: {
      __typename: 'CaseProduct'
      id: string
      case: {
        __typename: 'Case'
        id: string
        accountNodeAccess: AccountNodeAccess | null
        code: string
        status: CaseStatus
        stage: CaseStage
        flow: CaseFlow | null
        createdAt: DateTimeString
        closeReason: { __typename: 'CaseCloseReason'; id: string; name: string } | null
        assignedToUser: { __typename: 'User'; id: string; displayName: string } | null
        customer: {
          __typename: 'Customer'
          id: string
          name: string
          birthDate: DateString | null
          birthPlace: string | null
          gender: Gender | null
          governmentId: string | null
          educationLevel: CustomerEducationLevel | null
          marriageStatus: CustomerMaritalStatus | null
          cases: { __typename: 'CaseConnection'; totalCount: number }
        }
      }
      product: { __typename: 'Product'; id: string; name: string }
    }
    assignedTo: { __typename: 'User'; id: string; displayName: string } | null
    closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
    createdBy:
      | { __typename: 'App'; id: string; displayName: string }
      | { __typename: 'User'; id: string; displayName: string }
    actions: Array<{
      __typename: 'TicketAction'
      id: string
      comment: string | null
      date: DateTimeString
      createdAt: DateTimeString
      type: { __typename: 'TicketActionType'; id: string; name: string; value: string }
      result: {
        __typename: 'TicketActionResult'
        id: string
        name: string
        value: string
        success: boolean
      }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
    }>
    events: {
      __typename: 'TicketEventConnection'
      edges: Array<{
        __typename: 'TicketEventEdge'
        node: {
          __typename: 'TicketEvent'
          id: string
          type: TicketEventType
          date: DateTimeString
          parameters: any
          actionResult: {
            __typename: 'TicketActionResult'
            id: string
            name: string
            success: boolean
          } | null
          actionType: { __typename: 'TicketActionType'; id: string; name: string } | null
          createdBy:
            | { __typename: 'App'; id: string; displayName: string }
            | { __typename: 'User'; id: string; displayName: string }
            | null
          user: { __typename: 'User'; id: string; displayName: string } | null
        }
      }>
    }
    documents: Array<{
      __typename: 'TicketDocument'
      id: string
      type: TicketDocumentType
      otherType: string | null
      note: string | null
      createdAt: DateTimeString
      file: { __typename: 'RemoteFile'; url: string | null; mimeType: string; fileName: string }
      createdBy:
        | { __typename: 'App'; id: string; displayName: string }
        | { __typename: 'User'; id: string; displayName: string }
        | null
    }>
    tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
  }
}

export type TicketActionOptionsQueryVariables = Exact<{
  id: string | number
}>

export type TicketActionOptionsQuery = {
  __typename: 'Query'
  ticket:
    | {
        __typename: 'MediationTicket'
        id: string
        availableActionResults: Array<{
          __typename: 'TicketActionResult'
          id: string
          name: string
          value: string
          success: boolean
        }>
        availableActionTypes: Array<{
          __typename: 'TicketActionType'
          id: string
          name: string
          value: string
        }>
      }
    | {
        __typename: 'NegotiationTicket'
        id: string
        availableActionResults: Array<{
          __typename: 'TicketActionResult'
          id: string
          name: string
          value: string
          success: boolean
        }>
        availableActionTypes: Array<{
          __typename: 'TicketActionType'
          id: string
          name: string
          value: string
        }>
      }
}

export type TicketAvailableTagsQueryVariables = Exact<{
  id: string | number
}>

export type TicketAvailableTagsQuery = {
  __typename: 'Query'
  ticket:
    | {
        __typename: 'MediationTicket'
        id: string
        availableTags: Array<{
          __typename: 'TicketTag'
          id: string
          name: string
          color: string | null
        }>
      }
    | {
        __typename: 'NegotiationTicket'
        id: string
        availableTags: Array<{
          __typename: 'TicketTag'
          id: string
          name: string
          color: string | null
        }>
      }
}

export type TicketCloseOptionsQueryVariables = Exact<{
  id: string | number
}>

export type TicketCloseOptionsQuery = {
  __typename: 'Query'
  ticket:
    | {
        __typename: 'MediationTicket'
        id: string
        availableCloseReasons: Array<{
          __typename: 'TicketCloseReason'
          id: string
          name: string
          success: boolean
        }>
      }
    | {
        __typename: 'NegotiationTicket'
        id: string
        availableCloseReasons: Array<{
          __typename: 'TicketCloseReason'
          id: string
          name: string
          success: boolean
        }>
      }
}

export type TicketListAllQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: TicketsAllFilterInput | null | undefined
  sort?: TicketsAllSortInput | null | undefined
}>

export type TicketListAllQuery = {
  __typename: 'Query'
  ticketsAll: {
    __typename: 'TicketConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketEdge'
      node:
        | {
            __typename: 'MediationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              code: string
              case: {
                __typename: 'Case'
                code: string
                customer: { __typename: 'Customer'; id: string; name: string }
              }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            }
            assignedTo: {
              __typename: 'User'
              id: string
              displayName: string
              userTeam: { __typename: 'UserTeam'; name: string } | null
            } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              code: string
              case: {
                __typename: 'Case'
                code: string
                customer: { __typename: 'Customer'; id: string; name: string }
              }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            nullableProviderContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            } | null
            assignedTo: {
              __typename: 'User'
              id: string
              displayName: string
              userTeam: { __typename: 'UserTeam'; name: string } | null
            } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type TicketListFilterOptionsQueryVariables = Exact<{ [key: string]: never }>

export type TicketListFilterOptionsQuery = {
  __typename: 'Query'
  ticketCloseReasons: {
    __typename: 'TicketCloseReasonConnection'
    edges: Array<{
      __typename: 'TicketCloseReasonEdge'
      node: { __typename: 'TicketCloseReason'; id: string; name: string; success: boolean }
    }>
  }
  products: {
    __typename: 'ProductConnection'
    edges: Array<{
      __typename: 'ProductEdge'
      node: { __typename: 'Product'; id: string; name: string }
    }>
  }
  userTeams: {
    __typename: 'UserTeamConnection'
    edges: Array<{
      __typename: 'UserTeamEdge'
      node: { __typename: 'UserTeam'; id: string; name: string }
    }>
  }
  ticketTags: {
    __typename: 'TicketTagConnection'
    edges: Array<{
      __typename: 'TicketTagEdge'
      node: { __typename: 'TicketTag'; id: string; name: string }
    }>
  }
}

export type TicketListTeamAssignedQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: TicketsTeamAssignedFilterInput | null | undefined
  sort?: TicketsTeamAssignedSortInput | null | undefined
}>

export type TicketListTeamAssignedQuery = {
  __typename: 'Query'
  ticketsTeamAssigned: {
    __typename: 'TicketConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketEdge'
      node:
        | {
            __typename: 'MediationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              code: string
              case: {
                __typename: 'Case'
                code: string
                customer: { __typename: 'Customer'; id: string; name: string }
              }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            }
            assignedTo: {
              __typename: 'User'
              id: string
              displayName: string
              userTeam: { __typename: 'UserTeam'; name: string } | null
            } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              code: string
              case: {
                __typename: 'Case'
                code: string
                customer: { __typename: 'Customer'; id: string; name: string }
              }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            nullableProviderContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            } | null
            assignedTo: {
              __typename: 'User'
              id: string
              displayName: string
              userTeam: { __typename: 'UserTeam'; name: string } | null
            } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type TicketListUserAssignedQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: TicketsUserAssignedFilterInput | null | undefined
  sort?: TicketsUserAssignedSortInput | null | undefined
}>

export type TicketListUserAssignedQuery = {
  __typename: 'Query'
  ticketsUserAssigned: {
    __typename: 'TicketConnection'
    totalCount: number
    edges: Array<{
      __typename: 'TicketEdge'
      node:
        | {
            __typename: 'MediationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              code: string
              case: {
                __typename: 'Case'
                code: string
                customer: { __typename: 'Customer'; id: string; name: string }
              }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            providerContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            }
            assignedTo: {
              __typename: 'User'
              id: string
              displayName: string
              userTeam: { __typename: 'UserTeam'; name: string } | null
            } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
        | {
            __typename: 'NegotiationTicket'
            id: string
            code: string
            createdAt: DateTimeString
            status: TicketStatus
            type: TicketType
            financialAccount: {
              __typename: 'FinancialAccount'
              id: string
              code: string
              case: {
                __typename: 'Case'
                code: string
                customer: { __typename: 'Customer'; id: string; name: string }
              }
              provider: { __typename: 'FinancialProvider'; name: string } | null
            }
            nullableProviderContact: {
              __typename: 'FinancialProviderContact'
              id: string
              name: string
              phoneNumber: {
                __typename: 'PhoneNumber'
                displayNumber: string
                international: string
              } | null
            } | null
            assignedTo: {
              __typename: 'User'
              id: string
              displayName: string
              userTeam: { __typename: 'UserTeam'; name: string } | null
            } | null
            closeReason: { __typename: 'TicketCloseReason'; name: string; success: boolean } | null
            tags: Array<{ __typename: 'TicketTag'; id: string; name: string; color: string | null }>
          }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type UserTeamDetailFragment = {
  __typename: 'UserTeam'
  id: string
  name: string
  types: Array<UserTeamType>
  businessEntities: Array<{ __typename: 'BusinessEntity'; id: string; name: string }>
  caseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }>
  users: Array<{ __typename: 'User'; displayName: string }>
}

export type UserTeamListFragment = {
  __typename: 'UserTeam'
  id: string
  name: string
  types: Array<UserTeamType>
  businessEntities: Array<{ __typename: 'BusinessEntity'; name: string }>
  caseTypes: Array<{ __typename: 'CaseType'; name: string }>
}

export type UserTeamAddMutationVariables = Exact<{
  input: UserTeamCreateInput
}>

export type UserTeamAddMutation = {
  __typename: 'Mutation'
  userTeamCreate: {
    __typename: 'UserTeamCreate'
    userTeam: {
      __typename: 'UserTeam'
      id: string
      name: string
      types: Array<UserTeamType>
      businessEntities: Array<{ __typename: 'BusinessEntity'; id: string; name: string }>
      caseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }>
      users: Array<{ __typename: 'User'; displayName: string }>
    }
  }
}

export type UserTeamDeleteMutationVariables = Exact<{
  id: string | number
}>

export type UserTeamDeleteMutation = {
  __typename: 'Mutation'
  userTeamDelete: { __typename: 'UserTeamDelete' }
}

export type UserTeamUpdateMutationVariables = Exact<{
  id: string | number
  input: UserTeamUpdateInput
}>

export type UserTeamUpdateMutation = {
  __typename: 'Mutation'
  userTeamUpdate: {
    __typename: 'UserTeamUpdate'
    userTeam: {
      __typename: 'UserTeam'
      id: string
      name: string
      types: Array<UserTeamType>
      businessEntities: Array<{ __typename: 'BusinessEntity'; id: string; name: string }>
      caseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }>
      users: Array<{ __typename: 'User'; displayName: string }>
    }
  }
}

export type UserTeamAddOptionsQueryVariables = Exact<{ [key: string]: never }>

export type UserTeamAddOptionsQuery = {
  __typename: 'Query'
  businessEntities: {
    __typename: 'BusinessEntityConnection'
    edges: Array<{
      __typename: 'BusinessEntityEdge'
      node: { __typename: 'BusinessEntity'; id: string; name: string }
    }>
  }
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
}

export type UserTeamDetailQueryVariables = Exact<{
  id: string | number
}>

export type UserTeamDetailQuery = {
  __typename: 'Query'
  userTeam: {
    __typename: 'UserTeam'
    id: string
    name: string
    types: Array<UserTeamType>
    businessEntities: Array<{ __typename: 'BusinessEntity'; id: string; name: string }>
    caseTypes: Array<{ __typename: 'CaseType'; id: string; name: string }>
    users: Array<{ __typename: 'User'; displayName: string }>
  }
  businessEntities: {
    __typename: 'BusinessEntityConnection'
    edges: Array<{
      __typename: 'BusinessEntityEdge'
      node: { __typename: 'BusinessEntity'; id: string; name: string }
    }>
  }
  caseTypes: {
    __typename: 'CaseTypeConnection'
    edges: Array<{
      __typename: 'CaseTypeEdge'
      node: { __typename: 'CaseType'; id: string; name: string }
    }>
  }
}

export type UserTeamListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: UserTeamsFilterInput | null | undefined
  sort?: UserTeamsSortInput | null | undefined
}>

export type UserTeamListQuery = {
  __typename: 'Query'
  userTeams: {
    __typename: 'UserTeamConnection'
    totalCount: number
    edges: Array<{
      __typename: 'UserTeamEdge'
      cursor: string
      node: {
        __typename: 'UserTeam'
        id: string
        name: string
        types: Array<UserTeamType>
        businessEntities: Array<{ __typename: 'BusinessEntity'; name: string }>
        caseTypes: Array<{ __typename: 'CaseType'; name: string }>
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type UserDetailFragment = {
  __typename: 'User'
  displayName: string
  isSuperUser: boolean
  sampleRateMultiplier: number
  id: string
  email: string
  firstName: string
  isActive: boolean
  lastName: string
  roles: Array<UserRole>
  roleDescription: string | null
  accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
  avatar: { __typename: 'Image'; url: string } | null
  userTeam: { __typename: 'UserTeam'; id: string; name: string } | null
}

export type UserInfoFragment = {
  __typename: 'User'
  id: string
  email: string
  displayName: string
}

export type UserListFragment = {
  __typename: 'User'
  id: string
  email: string
  firstName: string
  isActive: boolean
  lastName: string
  roles: Array<UserRole>
  roleDescription: string | null
  userTeam: { __typename: 'UserTeam'; id: string; name: string } | null
}

export type CaseBookReassignMutationVariables = Exact<{
  input: CaseBookReassignInput
}>

export type CaseBookReassignMutation = {
  __typename: 'Mutation'
  caseBookReassign: {
    __typename: 'CaseBookReassign'
    movedCaseCount: number
    failedCaseCount: number
    remainingCaseCount: number
  }
}

export type ChangeUserPasswordMutationVariables = Exact<{
  newPassword: string
  oldPassword: string
}>

export type ChangeUserPasswordMutation = {
  __typename: 'Mutation'
  passwordChange: { __typename: 'PasswordChange'; accessToken: string; refreshToken: string }
}

export type UserAddMutationVariables = Exact<{
  input: UserCreateInput
  redirectUrl?: string | null | undefined
}>

export type UserAddMutation = {
  __typename: 'Mutation'
  userCreate: {
    __typename: 'UserCreate'
    user: {
      __typename: 'User'
      displayName: string
      isSuperUser: boolean
      sampleRateMultiplier: number
      id: string
      email: string
      firstName: string
      isActive: boolean
      lastName: string
      roles: Array<UserRole>
      roleDescription: string | null
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
      avatar: { __typename: 'Image'; url: string } | null
      userTeam: { __typename: 'UserTeam'; id: string; name: string } | null
    }
  }
}

export type UserDeleteMutationVariables = Exact<{
  id: string | number
}>

export type UserDeleteMutation = {
  __typename: 'Mutation'
  userDelete: { __typename: 'UserDelete' }
}

export type UserRegisterMekariAutosignMutationVariables = Exact<{
  id: string | number
}>

export type UserRegisterMekariAutosignMutation = {
  __typename: 'Mutation'
  userRegisterMekariAutosign: {
    __typename: 'UserRegisterMekariAutosign'
    status: EsignAutosignStatus
    user: { __typename: 'User'; id: string }
  }
}

export type UserUpdateMutationVariables = Exact<{
  id: string | number
  input: UserUpdateInput
}>

export type UserUpdateMutation = {
  __typename: 'Mutation'
  userUpdate: {
    __typename: 'UserUpdate'
    user: {
      __typename: 'User'
      displayName: string
      isSuperUser: boolean
      sampleRateMultiplier: number
      id: string
      email: string
      firstName: string
      isActive: boolean
      lastName: string
      roles: Array<UserRole>
      roleDescription: string | null
      accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
      avatar: { __typename: 'Image'; url: string } | null
      userTeam: { __typename: 'UserTeam'; id: string; name: string } | null
    }
  }
}

export type UserAddOptionsQueryVariables = Exact<{ [key: string]: never }>

export type UserAddOptionsQuery = {
  __typename: 'Query'
  userTeams: {
    __typename: 'UserTeamConnection'
    edges: Array<{
      __typename: 'UserTeamEdge'
      node: { __typename: 'UserTeam'; id: string; name: string }
    }>
  }
}

export type UserBookQueryVariables = Exact<{
  id: string | number
}>

export type UserBookQuery = {
  __typename: 'Query'
  userBook: {
    __typename: 'UserBookModel'
    openCaseCount: number
    assignedCaseCount: number
    sampleCaseCodes: Array<string>
    relationshipCount: number
  }
}

export type UserDetailQueryVariables = Exact<{
  id: string | number
}>

export type UserDetailQuery = {
  __typename: 'Query'
  user: {
    __typename: 'User'
    displayName: string
    isSuperUser: boolean
    sampleRateMultiplier: number
    id: string
    email: string
    firstName: string
    isActive: boolean
    lastName: string
    roles: Array<UserRole>
    roleDescription: string | null
    accountPermissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
    avatar: { __typename: 'Image'; url: string } | null
    userTeam: { __typename: 'UserTeam'; id: string; name: string } | null
  }
  permissions: Array<{ __typename: 'AccountPermission'; code: Permission; name: string }>
  userTeams: {
    __typename: 'UserTeamConnection'
    edges: Array<{
      __typename: 'UserTeamEdge'
      node: { __typename: 'UserTeam'; id: string; name: string }
    }>
  }
}

export type UserListQueryVariables = Exact<{
  first?: number | null | undefined
  after?: string | null | undefined
  last?: number | null | undefined
  before?: string | null | undefined
  filter?: UsersFilterInput | null | undefined
  sort?: UsersSortInput | null | undefined
}>

export type UserListQuery = {
  __typename: 'Query'
  users: {
    __typename: 'UserConnection'
    totalCount: number
    edges: Array<{
      __typename: 'UserEdge'
      cursor: string
      node: {
        __typename: 'User'
        id: string
        email: string
        firstName: string
        isActive: boolean
        lastName: string
        roles: Array<UserRole>
        roleDescription: string | null
        avatar: { __typename: 'Image'; url: string } | null
        userTeam: { __typename: 'UserTeam'; id: string; name: string } | null
      }
    }>
    pageInfo: {
      __typename: 'PageInfo'
      hasPreviousPage: boolean
      hasNextPage: boolean
      startCursor: string | null
      endCursor: string | null
    }
  }
}

export type UserMekariAutosignStatusQueryVariables = Exact<{
  id: string | number
}>

export type UserMekariAutosignStatusQuery = {
  __typename: 'Query'
  userMekariAutosignStatus: EsignAutosignStatus
}

export const PageInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PageInfoFragment, unknown>
export const SelectCountryFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountry' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Country' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SelectCountryFragment, unknown>
export const SelectCountryAreaFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SelectCountryAreaFragment, unknown>
export const SelectCountryWithAreasFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryWithAreas' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Country' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountry' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'areas' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountry' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Country' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SelectCountryWithAreasFragment, unknown>
export const AppFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'App' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokens' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'token' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'playbook' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppFragment, unknown>
export const AuthorFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Author' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Author' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AuthorFragment, unknown>
export const AccountPermissionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccountPermission' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccountPermission' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AccountPermissionFragment, unknown>
export const UserFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'User' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccountPermission' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'types' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'canHandleCaseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccountPermission' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccountPermission' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserFragment, unknown>
export const BusinessEntityListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'teams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityListFragment, unknown>
export const BusinessEntityDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BusinessEntityList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'aliasName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyFullAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyShortAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceIdentifier' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'vatRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteUrl' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'teams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityDetailFragment, unknown>
export const BusinessHoursFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessHours' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessHours' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessHoursFragment, unknown>
export const CaseActionResultOptionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseActionResultOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionResultOptionFragment, unknown>
export const CaseActionTypeOptionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseActionTypeOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeOptionFragment, unknown>
export const CaseCloseReasonOptionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCloseReasonOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseCloseReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseReasonOptionFragment, unknown>
export const ChannelInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelInfoFragment, unknown>
export const MessagePreviewFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessagePreviewFragment, unknown>
export const ConversationListItemFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationListItemFragment, unknown>
export const CaseCardFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCardFragment, unknown>
export const CaseAssignmentReasonFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseAssignmentReasonFragment, unknown>
export const CaseActionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionFragment, unknown>
export const PhoneNumberCopyableFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PhoneNumberCopyableFragment, unknown>
export const BaseFinancialAccountFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BaseFinancialAccountFragment, unknown>
export const RemoteFileInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RemoteFileInfoFragment, unknown>
export const CaseDetailDocumentFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailDocumentFragment, unknown>
export const CaseDetailFinancialAccountDocumentFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailFinancialAccountDocumentFragment, unknown>
export const TicketCardFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketCardFragment, unknown>
export const CaseFinancialAccountFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseFinancialAccountFragment, unknown>
export const CaseDetailCaseDocumentFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailCaseDocumentFragment, unknown>
export const CaseEventFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseEventFragment, unknown>
export const SelectRegencyFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SelectRegencyFragment, unknown>
export const CustomerDomicileFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerDomicileFragment, unknown>
export const CustomerAddressFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerAddressFragment, unknown>
export const CustomerPhoneFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerPhoneFragment, unknown>
export const CustomerEmailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerEmailFragment, unknown>
export const CaseDetailCustomerDocumentFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailCustomerDocumentFragment, unknown>
export const CustomerEventFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerEventFragment, unknown>
export const UserInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserInfoFragment, unknown>
export const OrderDocumentTypeFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderDocumentTypeFragment, unknown>
export const OrderStateBlockerFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderStateBlockerFragment, unknown>
export const CaseDetailOrderDocumentEsignRequestFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailOrderDocumentEsignRequestFragment, unknown>
export const CaseDetailOrderDocumentFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailOrderDocumentFragment, unknown>
export const OrderEventFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderEventFragment, unknown>
export const CaseDetailOrderFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailOrderFragment, unknown>
export const CaseDetailOrderHistoryFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailOrderHistoryFragment, unknown>
export const CaseDetailProductFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailProductFragment, unknown>
export const CaseDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailFragment, unknown>
export const CaseDetailProductPackageFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProductPackage' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'minimumFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultDurationMonths' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailProductPackageFragment, unknown>
export const CaseListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fee' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseListFragment, unknown>
export const CaseReminderBriefFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderBriefFragment, unknown>
export const CaseReminderRowFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderRow' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseReminderBrief' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'assignedToUser' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderRowFragment, unknown>
export const FinancialAccountTypeOptionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialAccountTypeOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccountType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialAccountTypeOptionFragment, unknown>
export const ChannelAdminInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelAdminInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'health' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEventAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                { kind: 'Field', name: { kind: 'Name', value: 'healthSyncedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDefaults' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transactionalAutoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'autoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'applyWhenBotOwned' } },
                { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sendWindow' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rules' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelAdminInfoFragment, unknown>
export const CustomerConversationFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerConversation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerConversationFragment, unknown>
export const CustomerDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerDetailFragment, unknown>
export const CustomerDetailFinancialAccountFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetailFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'assignedToUser' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'closeReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerDetailFinancialAccountFragment, unknown>
export const CustomerListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerListFragment, unknown>
export const FinancialProviderAvailableAccountTypeFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderAvailableAccountType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccountType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'value' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialProviderAvailableAccountTypeFragment, unknown>
export const FinancialProviderListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialProviderListFragment, unknown>
export const FinancialProviderDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'FinancialProviderList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'businessName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialProviderDetailFragment, unknown>
export const FinancialProviderDetailContactFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetailContact' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialProviderContact' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialProviderDetailContactFragment, unknown>
export const HomeQueueCaseFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'HomeQueueCase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeQueueCaseFragment, unknown>
export const HomeFeeWindowFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'HomeFeeWindow' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportFeeCollection' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedOrders' } },
          { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
          { kind: 'Field', name: { kind: 'Name', value: 'unpricedOrders' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currencies' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'collectionRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'paid' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'prescribed' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeFeeWindowFragment, unknown>
export const CaseImportQuarantineLeadFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseImportQuarantineLead' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseImportQuarantine' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerPhone' } },
          { kind: 'Field', name: { kind: 'Name', value: 'error' } },
          { kind: 'Field', name: { kind: 'Name', value: 'externalIdentifier' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issues' } },
          { kind: 'Field', name: { kind: 'Name', value: 'schemaVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceCampaign' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'content' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseImportQuarantineLeadFragment, unknown>
export const MessageItemFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessageItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingMessage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wamid' } },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'clientMsgId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'clientSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'text' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parked' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nextAttemptAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'context' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'quotedWamid' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reaction' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'emoji' } },
                { kind: 'Field', name: { kind: 'Name', value: 'targetWamid' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'media' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'mediaId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'filename' } },
                { kind: 'Field', name: { kind: 'Name', value: 'caption' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizeBytes' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'interactive' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                { kind: 'Field', name: { kind: 'Name', value: 'header' } },
                { kind: 'Field', name: { kind: 'Name', value: 'footer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buttons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'listButton' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sections' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rows' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'interactiveReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'template' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'header' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'format' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'footer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buttons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unknown' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'metaType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unsupportedType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessageItemFragment, unknown>
export const QuickReplyItemFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'QuickReplyItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingQuickReply' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shortcut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'body' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sequence' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<QuickReplyItemFragment, unknown>
export const OversightScenarioInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OversightScenarioInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingOversightScenario' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'model' } },
          { kind: 'Field', name: { kind: 'Name', value: 'analyzers' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthEveryN' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthMinIntervalMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'maxCallsPerConvDay' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OversightScenarioInfoFragment, unknown>
export const PlaybookVersionInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookVersionInfoFragment, unknown>
export const PlaybookInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookInfoFragment, unknown>
export const ReportAttributionCoverageCountsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'cases' } },
          { kind: 'Field', name: { kind: 'Name', value: 'noSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'surfaceOnly' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediumOnly' } },
          { kind: 'Field', name: { kind: 'Name', value: 'campaignKnown' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adKnown' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralUnresolved' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralResolved' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportAttributionCoverageCountsFragment, unknown>
export const CampaignResolverScheduleInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'CampaignResolverSchedule' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'mode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'window' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignResolverScheduleInfoFragment, unknown>
export const ProductPackageStateDependenciesFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportAutomaticStateChange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'caseDocumentTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentTypes' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'validEntities' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageStateDependenciesFragment, unknown>
export const ProductPackageStateEffectsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackageStateEffects' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ProductPackageStateEffects' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'generateDocumentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageStateEffectsFragment, unknown>
export const ProductPackageFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackage' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'availability' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'publicName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'products' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultDurationMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultFeeRate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'minimumFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceLineLabel' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLines' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'amount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'stateDependencies' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'stateEffects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'ProductPackageStateEffects' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportAutomaticStateChange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'caseDocumentTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentTypes' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'validEntities' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackageStateEffects' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ProductPackageStateEffects' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'generateDocumentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageFragment, unknown>
export const TicketActionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionFragment, unknown>
export const TicketEventFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketEventFragment, unknown>
export const TicketDocumentDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketDocumentDetailFragment, unknown>
export const CustomerInfoFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerInfoFragment, unknown>
export const TicketDetailBaseFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketDetailBaseFragment, unknown>
export const TicketFinancialAccountFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketFinancialAccountFragment, unknown>
export const MediationTicketDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MediationTicketDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MediationTicketDetailFragment, unknown>
export const NegotiationTicketDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NegotiationTicketDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NegotiationTicket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adjustedDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adjustedDebtDiscount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<NegotiationTicketDetailFragment, unknown>
export const TicketCloseReasonOptionFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCloseReasonOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketCloseReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketCloseReasonOptionFragment, unknown>
export const TicketListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'userTeam' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'provider' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'nullableProviderContact' },
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketListFragment, unknown>
export const UserTeamListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'types' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamListFragment, unknown>
export const UserTeamDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'types' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamDetailFragment, unknown>
export const UserListFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roleDescription' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserListFragment, unknown>
export const UserDetailFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'isSuperUser' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRateMultiplier' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'size' },
                value: { kind: 'IntValue', value: '120' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roleDescription' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserDetailFragment, unknown>
export const FileUploadManyRequestDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FileUploadManyRequest' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: {
                  kind: 'NamedType',
                  name: { kind: 'Name', value: 'FileUploadRequestInput' },
                },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fileUploadManyRequest' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'uploadUrl' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'headers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'storageFileRef' } },
                { kind: 'Field', name: { kind: 'Name', value: 'expiresAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FileUploadManyRequestMutation, FileUploadManyRequestMutationVariables>
export const CountriesForSelectWithAreasDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CountriesForSelectWithAreas' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countries' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryWithAreas' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountry' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Country' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryWithAreas' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Country' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountry' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'areas' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CountriesForSelectWithAreasQuery,
  CountriesForSelectWithAreasQueryVariables
>
export const DomicileOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'DomicileOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countries' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountry' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'areas' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'SelectCountryArea' },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'regencies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'SelectRegency' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountry' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Country' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DomicileOptionsQuery, DomicileOptionsQueryVariables>
export const NotReferencedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'NotReferenced' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesAuth' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesCommon' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesCase' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesCustomer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesFinancialProvider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCodesTickets' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scopes' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<NotReferencedQuery, NotReferencedQueryVariables>
export const SearchCustomersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SearchCustomers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'search' },
            name: { kind: 'Name', value: 'customers' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'search' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'NAME' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'defaultEmail' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'defaultPhone' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'phoneNumber' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'displayNumber' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SearchCustomersQuery, SearchCustomersQueryVariables>
export const SearchFinancialAccountTypeProvidersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SearchFinancialAccountTypeProviders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountTypeId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'search' },
            name: { kind: 'Name', value: 'financialAccountTypeAvailableProviders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'financialAccountTypeId' },
                value: {
                  kind: 'Variable',
                  name: { kind: 'Name', value: 'financialAccountTypeId' },
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'search' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SearchFinancialAccountTypeProvidersQuery,
  SearchFinancialAccountTypeProvidersQueryVariables
>
export const SearchFinancialProviderContactsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SearchFinancialProviderContacts' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialProviderId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'search' },
            name: { kind: 'Name', value: 'financialProviderContacts' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'financialProvider' },
                      value: {
                        kind: 'Variable',
                        name: { kind: 'Name', value: 'financialProviderId' },
                      },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'search' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'phoneNumber' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SearchFinancialProviderContactsQuery,
  SearchFinancialProviderContactsQueryVariables
>
export const SearchUsersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SearchUsers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'search' },
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'search' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'query' } },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'LAST_NAME' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SearchUsersQuery, SearchUsersQueryVariables>
export const SearchUsersByFilterDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SearchUsersByFilter' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UsersFilterInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'search' },
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'LAST_NAME' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SearchUsersByFilterQuery, SearchUsersByFilterQueryVariables>
export const AppCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AppCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AppCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'appCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'app' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'App' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'App' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokens' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'token' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'playbook' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppCreateMutation, AppCreateMutationVariables>
export const AppDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AppDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'appDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'app' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'App' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'App' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokens' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'token' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'playbook' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppDeleteMutation, AppDeleteMutationVariables>
export const AppTokenCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AppTokenCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AppTokenCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'appTokenCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'token' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'appToken' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppTokenCreateMutation, AppTokenCreateMutationVariables>
export const AppTokenDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AppTokenDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'appTokenDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'appToken' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppTokenDeleteMutation, AppTokenDeleteMutationVariables>
export const AppUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AppUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AppUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'appUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'app' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'App' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'App' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokens' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'token' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'playbook' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppUpdateMutation, AppUpdateMutationVariables>
export const AppCreatePermissionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AppCreatePermissions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'permissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppCreatePermissionsQuery, AppCreatePermissionsQueryVariables>
export const AppDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AppDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'app' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'App' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'permissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'App' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokens' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'token' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'playbook' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppDetailQuery, AppDetailQueryVariables>
export const AppListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AppList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'AppsSortInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'AppsFilterInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'apps' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AppListQuery, AppListQueryVariables>
export const ConfirmAccountDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConfirmAccount' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'password' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmAccount' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'email' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'password' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'password' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'token' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'refreshToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'User' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccountPermission' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccountPermission' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'User' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccountPermission' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'types' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'canHandleCaseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConfirmAccountMutation, ConfirmAccountMutationVariables>
export const RefreshTokenDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RefreshToken' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokenRefresh' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'refreshToken' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'token' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RefreshTokenMutation, RefreshTokenMutationVariables>
export const RequestPasswordResetDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RequestPasswordReset' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'redirectUrl' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'requestPasswordReset' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'email' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'redirectUrl' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'redirectUrl' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RequestPasswordResetMutation, RequestPasswordResetMutationVariables>
export const RequestedPasswordSetDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'RequestedPasswordSet' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'password' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'requestedPasswordSet' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'email' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'password' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'password' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'token' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'refreshToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'User' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccountPermission' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccountPermission' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'User' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccountPermission' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'types' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'canHandleCaseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RequestedPasswordSetMutation, RequestedPasswordSetMutationVariables>
export const TokenAuthDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TokenAuth' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'password' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokenCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'email' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'email' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'password' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'password' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refreshToken' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'User' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccountPermission' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccountPermission' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'User' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccountPermission' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'types' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'canHandleCaseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TokenAuthMutation, TokenAuthMutationVariables>
export const VerifyTokenDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'VerifyToken' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tokenVerifyUser' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'token' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'User' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccountPermission' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccountPermission' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'User' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccountPermission' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'types' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'canHandleCaseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<VerifyTokenMutation, VerifyTokenMutationVariables>
export const UserNotifiedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'UserNotified' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userNotified' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ignoreSameSession' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'author' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserNotifiedSubscription, UserNotifiedSubscriptionVariables>
export const BusinessEntityAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'BusinessEntityAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntityCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntityCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'businessEntity' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'BusinessEntityDetail' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'teams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BusinessEntityList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'aliasName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyFullAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyShortAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceIdentifier' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'vatRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteUrl' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityAddMutation, BusinessEntityAddMutationVariables>
export const BusinessEntityClearBusinessHoursDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'BusinessEntityClearBusinessHours' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntityClearBusinessHours' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'businessEntity' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'businessHours' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'BusinessHours' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessHours' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessHours' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  BusinessEntityClearBusinessHoursMutation,
  BusinessEntityClearBusinessHoursMutationVariables
>
export const BusinessEntityDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'BusinessEntityDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntityDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: '__typename' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityDeleteMutation, BusinessEntityDeleteMutationVariables>
export const BusinessEntitySetBusinessHoursDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'BusinessEntitySetBusinessHours' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessHoursSetInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntitySetBusinessHours' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'businessEntity' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'businessHours' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'BusinessHours' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessHours' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessHours' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  BusinessEntitySetBusinessHoursMutation,
  BusinessEntitySetBusinessHoursMutationVariables
>
export const BusinessEntityUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'BusinessEntityUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntityUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntityUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'businessEntity' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'BusinessEntityDetail' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'teams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BusinessEntityList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'aliasName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyFullAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyShortAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceIdentifier' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'vatRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteUrl' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityUpdateMutation, BusinessEntityUpdateMutationVariables>
export const BusinessEntityBusinessHoursDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'BusinessEntityBusinessHours' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'businessHours' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BusinessHours' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessHours' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessHours' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  BusinessEntityBusinessHoursQuery,
  BusinessEntityBusinessHoursQueryVariables
>
export const BusinessEntityDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'BusinessEntityDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BusinessEntityDetail' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'teams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BusinessEntityList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'aliasName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyFullAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyShortAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceIdentifier' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'bankAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'vatRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteUrl' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityDetailQuery, BusinessEntityDetailQueryVariables>
export const BusinessEntityListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntitiesFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntitiesSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'cursor' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'BusinessEntityList' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BusinessEntityList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'BusinessEntity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'directorUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'teams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BusinessEntityListQuery, BusinessEntityListQueryVariables>
export const CaseActionCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionCreateInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'result' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'type' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionCreateMutation, CaseActionCreateMutationVariables>
export const CaseActionDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'result' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'type' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionDeleteMutation, CaseActionDeleteMutationVariables>
export const CaseActionUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionUpdateInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'result' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'type' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionUpdateMutation, CaseActionUpdateMutationVariables>
export const CaseAddressAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseAddressAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAddressAssignInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseAddressAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'addresses' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerAddress' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseAddressAssignMutation, CaseAddressAssignMutationVariables>
export const CaseAddressAssignNewDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseAddressAssignNew' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAddressAssignNewInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseAddressAssignNew' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'addresses' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerAddress' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseAddressAssignNewMutation, CaseAddressAssignNewMutationVariables>
export const CaseAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseAssignMutation, CaseAssignMutationVariables>
export const CaseBulkAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseBulkAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseBulkAssignInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseBulkAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'assignedToUser' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseBulkAssignMutation, CaseBulkAssignMutationVariables>
export const CaseBulkReopenDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseBulkReopen' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseBulkReopenInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseBulkReopen' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseBulkReopenMutation, CaseBulkReopenMutationVariables>
export const CaseCloseDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseClose' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseCloseInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseClose' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseMutation, CaseCloseMutationVariables>
export const CaseCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCreateMutation, CaseCreateMutationVariables>
export const CaseDocumentCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseDocumentCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocumentCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseDocumentCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'CaseDetailDocument' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'events' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'last' },
                                  value: { kind: 'IntValue', value: '250' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'CaseEvent' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDocumentCreateMutation, CaseDocumentCreateMutationVariables>
export const CaseDocumentDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseDocumentDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseDocumentDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'CaseDetailDocument' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'events' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'last' },
                                  value: { kind: 'IntValue', value: '250' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'CaseEvent' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDocumentDeleteMutation, CaseDocumentDeleteMutationVariables>
export const CaseDocumentUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseDocumentUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocumentUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseDocumentUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'CaseDetailDocument' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'events' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'last' },
                                  value: { kind: 'IntValue', value: '250' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'CaseEvent' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDocumentUpdateMutation, CaseDocumentUpdateMutationVariables>
export const CaseEmailAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseEmailAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEmailAssignInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseEmailAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactEmails' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEmail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseEmailAssignMutation, CaseEmailAssignMutationVariables>
export const CaseEmailAssignNewDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseEmailAssignNew' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEmailAssignNewInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseEmailAssignNew' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactEmails' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEmail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseEmailAssignNewMutation, CaseEmailAssignNewMutationVariables>
export const CaseIdentityConfirmDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseIdentityConfirm' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseIdentityConfirmInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseIdentityConfirm' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseIdentityConfirmMutation, CaseIdentityConfirmMutationVariables>
export const CaseMoveToNewCustomerDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseMoveToNewCustomer' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseMoveToNewCustomerInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseMoveToNewCustomer' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'previousCustomer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseMoveToNewCustomerMutation, CaseMoveToNewCustomerMutationVariables>
export const CaseNoteAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseNoteAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseNoteAddInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseNoteAdd' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseNoteAddMutation, CaseNoteAddMutationVariables>
export const CasePhoneAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CasePhoneAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CasePhoneAssignInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'casePhoneAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactPhones' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerPhone' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CasePhoneAssignMutation, CasePhoneAssignMutationVariables>
export const CasePhoneAssignNewDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CasePhoneAssignNew' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CasePhoneAssignNewInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'casePhoneAssignNew' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactPhones' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerPhone' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CasePhoneAssignNewMutation, CasePhoneAssignNewMutationVariables>
export const CaseReminderCancelDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseReminderCancel' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderCancel' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseReminder' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseReminderBrief' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderCancelMutation, CaseReminderCancelMutationVariables>
export const CaseReminderCompleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseReminderComplete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'actionId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderComplete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'actionId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'actionId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseReminder' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseReminderBrief' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderCompleteMutation, CaseReminderCompleteMutationVariables>
export const CaseReminderCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseReminderCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminderCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseReminder' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseReminderBrief' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderCreateMutation, CaseReminderCreateMutationVariables>
export const CaseReminderUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseReminderUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminderUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseReminder' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseReminderBrief' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderUpdateMutation, CaseReminderUpdateMutationVariables>
export const CaseReopenDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseReopen' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReopen' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReopenMutation, CaseReopenMutationVariables>
export const CaseTagAttachDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTagAttach' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTagAttachInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTagAttach' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagAttachMutation, CaseTagAttachMutationVariables>
export const CaseTagDetachDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTagDetach' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTagDetachInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTagDetach' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagDetachMutation, CaseTagDetachMutationVariables>
export const FinancialAccountCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountCreateInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountCreateMutation,
  FinancialAccountCreateMutationVariables
>
export const FinancialAccountDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountDeleteMutation,
  FinancialAccountDeleteMutationVariables
>
export const FinancialAccountDocumentCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountDocumentCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountDocumentCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountDocumentCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccountDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'financialAccount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailFinancialAccountDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'case' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'events' },
                                    arguments: [
                                      {
                                        kind: 'Argument',
                                        name: { kind: 'Name', value: 'last' },
                                        value: { kind: 'IntValue', value: '250' },
                                      },
                                    ],
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'edges' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'node' },
                                                selectionSet: {
                                                  kind: 'SelectionSet',
                                                  selections: [
                                                    {
                                                      kind: 'FragmentSpread',
                                                      name: { kind: 'Name', value: 'CaseEvent' },
                                                    },
                                                  ],
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountDocumentCreateMutation,
  FinancialAccountDocumentCreateMutationVariables
>
export const FinancialAccountDocumentDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountDocumentDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountDocumentDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccountDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'financialAccount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailFinancialAccountDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'case' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'events' },
                                    arguments: [
                                      {
                                        kind: 'Argument',
                                        name: { kind: 'Name', value: 'last' },
                                        value: { kind: 'IntValue', value: '250' },
                                      },
                                    ],
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'edges' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'node' },
                                                selectionSet: {
                                                  kind: 'SelectionSet',
                                                  selections: [
                                                    {
                                                      kind: 'FragmentSpread',
                                                      name: { kind: 'Name', value: 'CaseEvent' },
                                                    },
                                                  ],
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountDocumentDeleteMutation,
  FinancialAccountDocumentDeleteMutationVariables
>
export const FinancialAccountDocumentUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountDocumentUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountDocumentUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountDocumentUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccountDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'financialAccount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailFinancialAccountDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'case' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'events' },
                                    arguments: [
                                      {
                                        kind: 'Argument',
                                        name: { kind: 'Name', value: 'last' },
                                        value: { kind: 'IntValue', value: '250' },
                                      },
                                    ],
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'edges' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'node' },
                                                selectionSet: {
                                                  kind: 'SelectionSet',
                                                  selections: [
                                                    {
                                                      kind: 'FragmentSpread',
                                                      name: { kind: 'Name', value: 'CaseEvent' },
                                                    },
                                                  ],
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountDocumentUpdateMutation,
  FinancialAccountDocumentUpdateMutationVariables
>
export const FinancialAccountSetProviderContactDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountSetProviderContact' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountSetProviderContactInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountSetProviderContact' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountSetProviderContactMutation,
  FinancialAccountSetProviderContactMutationVariables
>
export const FinancialAccountUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountUpdateInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountUpdateMutation,
  FinancialAccountUpdateMutationVariables
>
export const MediationTicketCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MediationTicketCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MediationTicketCreateInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mediationTicketCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'financialAccountId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'mediationTicket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'financialAccount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'case' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MediationTicketCreateMutation, MediationTicketCreateMutationVariables>
export const NegotiationTicketCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'NegotiationTicketCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicketCreateInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'negotiationTicketCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'financialAccountId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'negotiationTicket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'financialAccount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'case' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  NegotiationTicketCreateMutation,
  NegotiationTicketCreateMutationVariables
>
export const OrderAddonCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderAddonCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderAddonCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderAddonCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'order' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pendingOrder' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailOrder' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderAddonCreateMutation, OrderAddonCreateMutationVariables>
export const OrderAmountUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderAmountUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderAmountUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderAmountUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'order' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderAmountUpdateMutation, OrderAmountUpdateMutationVariables>
export const OrderCancelDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderCancel' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCancel' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'order' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'products' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailProduct' },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pendingOrder' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailOrder' },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'orders' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderCancelMutation, OrderCancelMutationVariables>
export const OrderDocumentCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'orderId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'orderId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'currentProgress' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'blockers' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'FragmentSpread',
                                          name: { kind: 'Name', value: 'OrderStateBlocker' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailOrderDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'events' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'last' },
                                  value: { kind: 'IntValue', value: '250' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'OrderEvent' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderDocumentCreateMutation, OrderDocumentCreateMutationVariables>
export const OrderDocumentDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailOrderDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'events' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'last' },
                                  value: { kind: 'IntValue', value: '250' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'OrderEvent' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderDocumentDeleteMutation, OrderDocumentDeleteMutationVariables>
export const OrderDocumentGenerateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentGenerate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderDocumentGenerateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentGenerate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'documentType' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailOrderDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderDocumentGenerateMutation, OrderDocumentGenerateMutationVariables>
export const OrderDocumentRequestSignatureDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentRequestSignature' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentRequestSignature' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentRequestSignatureMutation,
  OrderDocumentRequestSignatureMutationVariables
>
export const OrderDocumentSyncEsignStatusDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentSyncEsignStatus' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentSyncEsignStatus' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'esignRequest' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentSyncEsignStatusMutation,
  OrderDocumentSyncEsignStatusMutationVariables
>
export const OrderDocumentUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailOrderDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'events' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'last' },
                                  value: { kind: 'IntValue', value: '250' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: { kind: 'Name', value: 'OrderEvent' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderDocumentUpdateMutation, OrderDocumentUpdateMutationVariables>
export const OrderInitialCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderInitialCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderInitialCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderInitialCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'order' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pendingOrder' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailOrder' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderInitialCreateMutation, OrderInitialCreateMutationVariables>
export const OrderPaymentAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderPaymentAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderPaymentCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderPaymentCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderPayment' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrder' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderPaymentAddMutation, OrderPaymentAddMutationVariables>
export const OrderPaymentDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderPaymentDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderPaymentDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderPayment' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrder' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderPaymentDeleteMutation, OrderPaymentDeleteMutationVariables>
export const OrderPaymentUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderPaymentUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderPaymentUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderPaymentUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderPayment' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'order' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrder' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderPaymentUpdateMutation, OrderPaymentUpdateMutationVariables>
export const OrderStateProgressDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderStateProgress' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderStateProgressInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderStateProgress' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'order' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'products' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailProduct' },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'pendingOrder' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailOrder' },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'orders' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderStateProgressMutation, OrderStateProgressMutationVariables>
export const OrderUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'order' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderUpdateMutation, OrderUpdateMutationVariables>
export const CaseActionOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseActionOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableActionResults' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseActionResultOption' },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableActionTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseActionTypeOption' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseActionResultOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseActionTypeOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionOptionsQuery, CaseActionOptionsQueryVariables>
export const CaseAvailableTagsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseAvailableTags' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableTags' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseAvailableTagsQuery, CaseAvailableTagsQueryVariables>
export const CaseCloseOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseCloseOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableCloseReasons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseCloseReasonOption' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCloseReasonOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseCloseReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseOptionsQuery, CaseCloseOptionsQueryVariables>
export const CaseCreateNewForCustomerOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseCreateNewForCustomerOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseCreateNewForCustomerOptionsQuery,
  CaseCreateNewForCustomerOptionsQueryVariables
>
export const CaseCreateOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseCreateOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'me' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'canHandleCaseTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCreateOptionsQuery, CaseCreateOptionsQueryVariables>
export const CaseDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseDetailQuery, CaseDetailQueryVariables>
export const CaseFinancialAccountOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseFinancialAccountOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableFinancialAccountTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'FinancialAccountTypeOption' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialAccountTypeOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccountType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseFinancialAccountOptionsQuery,
  CaseFinancialAccountOptionsQueryVariables
>
export const CaseListAllDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseListAll' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CasesAllFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CasesAllSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'casesAll' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseList' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fee' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseListAllQuery, CaseListAllQueryVariables>
export const CaseListFilterOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseListFilterOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseCloseReasons' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTags' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseListFilterOptionsQuery, CaseListFilterOptionsQueryVariables>
export const CaseListTeamAssignedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseListTeamAssigned' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'CasesTeamAssignedFilterInput' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CasesTeamAssignedSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'casesTeamAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseList' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fee' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseListTeamAssignedQuery, CaseListTeamAssignedQueryVariables>
export const CaseListUserAssignedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseListUserAssigned' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'CasesUserAssignedFilterInput' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CasesUserAssignedSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseList' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fee' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseListUserAssignedQuery, CaseListUserAssignedQueryVariables>
export const CaseOpenRemindersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseOpenReminders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reminders' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseReminderBrief' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseOpenRemindersQuery, CaseOpenRemindersQueryVariables>
export const CaseOrderOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseOrderOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'businessEntities' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orderCreateOptions' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'availability' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'productPackages' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailProductPackage' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProductPackage' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'minimumFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultDurationMonths' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseOrderOptionsQuery, CaseOrderOptionsQueryVariables>
export const CaseReminderCountsByDayDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseReminderCountsByDay' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminderScope' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'from' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Date' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'to' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Date' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderCountsByDay' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'from' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'from' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'to' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'to' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'date' } },
                { kind: 'Field', name: { kind: 'Name', value: 'open' } },
                { kind: 'Field', name: { kind: 'Name', value: 'overdue' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderCountsByDayQuery, CaseReminderCountsByDayQueryVariables>
export const CaseReminderHoursCheckDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseReminderHoursCheck' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dueAt' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderHoursCheck' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dueAt' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dueAt' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nextOpening' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderHoursCheckQuery, CaseReminderHoursCheckQueryVariables>
export const CaseReminderSummaryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseReminderSummary' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminderScope' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderSummary' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'overdue' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dueToday' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderSummaryQuery, CaseReminderSummaryQueryVariables>
export const CaseRemindersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseReminders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminderScope' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseRemindersFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DUE_AT' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseReminderRow' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderRow' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseReminderBrief' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'assignedToUser' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseRemindersQuery, CaseRemindersQueryVariables>
export const InitialCaseFilterCustomersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'InitialCaseFilterCustomers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'customerIds' } },
          type: {
            kind: 'ListType',
            type: {
              kind: 'NonNullType',
              type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customers' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'ids' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'customerIds' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  InitialCaseFilterCustomersQuery,
  InitialCaseFilterCustomersQueryVariables
>
export const InitialCaseFilterUsersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'InitialCaseFilterUsers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'userIds' } },
          type: {
            kind: 'ListType',
            type: {
              kind: 'NonNullType',
              type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'ids' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'userIds' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<InitialCaseFilterUsersQuery, InitialCaseFilterUsersQueryVariables>
export const CaseReminderDueDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'CaseReminderDue' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseReminderDue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'reminderId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'caseId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'caseCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseReminderDueSubscription, CaseReminderDueSubscriptionVariables>
export const CaseUpdatedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'CaseUpdated' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseUpdated' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ignoreSameSession' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'author' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'node' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAssignmentReason' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'rung' } },
          { kind: 'Field', name: { kind: 'Name', value: 'at' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actor' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'similarCase' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'holderKind' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'excludedUsers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialAccountDocument' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fullName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailFinancialAccountDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '25' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderHistory' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'invoiceBookCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailProduct' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseProduct' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'moveToNewCustomerBlocker' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sharedNumberThread' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'productPackages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'minimumFee' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedValue' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'assignedVia' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importClassification' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importSubmittedName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'identityCheckMergedName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importClassificationReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matcherVersion' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clusterSize' } },
                { kind: 'Field', name: { kind: 'Name', value: 'guardrailMaxCluster' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cell' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameRelation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'evidenceScore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameCheck' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sharedNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originConversationCustomer' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submittedName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignmentReason' },
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAssignmentReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseAssignmentReason' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'locked' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'source' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'data' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'optInAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'behaviour' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'campaignResolverId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flowChangeReason' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseAction' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCaseDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
                { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
                { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
                { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'defaultEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'addresses' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactPhones' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contactEmails' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documents' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'IntValue', value: '100' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'events' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'IntValue', value: '250' },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEvent' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmail' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'verifiedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserInfo' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUserTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderCreateOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'availability' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pendingOrder' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrderHistory' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailProduct' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseUpdatedSubscription, CaseUpdatedSubscriptionVariables>
export const OrderDocumentUpdatedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'OrderDocumentUpdated' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fetchInitial' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentUpdated' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ignoreSameSession' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fetchInitial' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fetchInitial' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'author' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'node' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentUpdatedSubscription,
  OrderDocumentUpdatedSubscriptionVariables
>
export const OrderUpdatedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'OrderUpdated' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fetchInitial' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderUpdated' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ignoreSameSession' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ignoreSameSession' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fetchInitial' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fetchInitial' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'author' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'node' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailOrder' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderDocumentEsignRequest' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signedOrderDocument' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'file' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'generated' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rendered' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'canBeSigned' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'signers' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isCustomer' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeEsignRequest' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CaseDetailOrderDocumentEsignRequest' },
                },
              ],
            },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailOrder' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'paymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'feeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'durationInMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentBookCode' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'estimatedFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paidAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'remainingAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'additionalPaymentOrderAdjustmentRange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'min' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'max' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currentProgress' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'nextState' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isValid' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'blockers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderStateBlocker' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'startAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'payments' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'paidAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'paidAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailOrderDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderEvent' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccounts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseProducts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderUpdatedSubscription, OrderUpdatedSubscriptionVariables>
export const ChannelBindCaseTypeDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelBindCaseType' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'caseTypeId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelBindCaseType' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'caseTypeId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'caseTypeId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelBindCaseTypeMutation, ChannelBindCaseTypeMutationVariables>
export const ChannelClearTransactionalAutoReplyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelClearTransactionalAutoReply' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelClearTransactionalAutoReply' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelAdminInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelAdminInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'health' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEventAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                { kind: 'Field', name: { kind: 'Name', value: 'healthSyncedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDefaults' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transactionalAutoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'autoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'applyWhenBotOwned' } },
                { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sendWindow' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rules' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ChannelClearTransactionalAutoReplyMutation,
  ChannelClearTransactionalAutoReplyMutationVariables
>
export const ChannelCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelCreateMutation, ChannelCreateMutationVariables>
export const ChannelRegisterNumberDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelRegisterNumber' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pin' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelRegisterNumber' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pin' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pin' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'registration' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'detail' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayPhoneNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'platformType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelRegisterNumberMutation, ChannelRegisterNumberMutationVariables>
export const ChannelSetAutoReplyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelSetAutoReply' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelAutoReplyInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelSetAutoReply' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelAdminInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelAdminInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'health' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEventAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                { kind: 'Field', name: { kind: 'Name', value: 'healthSyncedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDefaults' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transactionalAutoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'autoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'applyWhenBotOwned' } },
                { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sendWindow' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rules' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelSetAutoReplyMutation, ChannelSetAutoReplyMutationVariables>
export const ChannelSetCaseActionDefaultsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelSetCaseActionDefaults' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelCaseActionDefaultInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelSetCaseActionDefaults' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelAdminInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelAdminInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'health' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEventAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                { kind: 'Field', name: { kind: 'Name', value: 'healthSyncedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDefaults' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transactionalAutoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'autoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'applyWhenBotOwned' } },
                { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sendWindow' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rules' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ChannelSetCaseActionDefaultsMutation,
  ChannelSetCaseActionDefaultsMutationVariables
>
export const ChannelSetTransactionalAutoReplyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelSetTransactionalAutoReply' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelTransactionalAutoReplyInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelSetTransactionalAutoReply' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelAdminInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelAdminInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'health' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEventAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                { kind: 'Field', name: { kind: 'Name', value: 'healthSyncedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDefaults' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transactionalAutoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'autoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'applyWhenBotOwned' } },
                { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sendWindow' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rules' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ChannelSetTransactionalAutoReplyMutation,
  ChannelSetTransactionalAutoReplyMutationVariables
>
export const ChannelSubscribeAppDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelSubscribeApp' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelSubscribeApp' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'subscribedApps' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'link' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelSubscribeAppMutation, ChannelSubscribeAppMutationVariables>
export const ChannelUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channel' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelUpdateMutation, ChannelUpdateMutationVariables>
export const ChannelVerifyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelVerify' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelVerify' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'checks' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumberAccess' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'detail' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'registration' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'detail' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'wabaAccess' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'detail' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'appSubscription' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'detail' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayPhoneNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'verifiedName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'platformType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'waba' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'subscribedApps' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'link' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelVerifyMutation, ChannelVerifyMutationVariables>
export const ChannelsAdminDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ChannelsAdmin' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channels' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelAdminInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelAdminInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'health' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'qualityRating' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'qualityEventAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'messagingLimit' } },
                { kind: 'Field', name: { kind: 'Name', value: 'healthSyncedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionDefaults' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transactionalAutoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'autoReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'applyWhenBotOwned' } },
                { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sendWindow' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rules' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelsAdminQuery, ChannelsAdminQueryVariables>
export const CustomerAddressCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerAddressCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerAddressCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerAddressCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'addresses' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerAddress' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerAddressCreateMutation, CustomerAddressCreateMutationVariables>
export const CustomerAddressDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerAddressDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerAddressDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'addresses' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerAddress' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerAddressDeleteMutation, CustomerAddressDeleteMutationVariables>
export const CustomerDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerDeleteMutation, CustomerDeleteMutationVariables>
export const CustomerDocumentCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerDocumentCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerDocumentCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerDocumentCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailCustomerDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerDocumentCreateMutation,
  CustomerDocumentCreateMutationVariables
>
export const CustomerDocumentDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerDocumentDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerDocumentDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailCustomerDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerDocumentDeleteMutation,
  CustomerDocumentDeleteMutationVariables
>
export const CustomerDocumentIdCardScanDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerDocumentIdCardScan' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'documentId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ocrScanCustomerIdCard' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'documentId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cardDataCustomer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cardDataAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'countryArea' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'note' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'metadata' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerDocumentIdCardScanMutation,
  CustomerDocumentIdCardScanMutationVariables
>
export const CustomerDocumentIdCardScanCommitDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerDocumentIdCardScanCommit' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'addressInput' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerAddressCreateInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'customerUpdateInput' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerAddressCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'addressInput' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'allowReuseExisting' },
                value: { kind: 'BooleanValue', value: true },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'addresses' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerAddress' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'customerUpdateInput' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDetail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cases' },
                        arguments: [
                          {
                            kind: 'Argument',
                            name: { kind: 'Name', value: 'first' },
                            value: { kind: 'IntValue', value: '100' },
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'edges' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'node' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'pendingOrder' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'id' },
                                              },
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'currentProgress' },
                                                selectionSet: {
                                                  kind: 'SelectionSet',
                                                  selections: [
                                                    {
                                                      kind: 'Field',
                                                      name: { kind: 'Name', value: 'nextState' },
                                                    },
                                                    {
                                                      kind: 'Field',
                                                      name: { kind: 'Name', value: 'isValid' },
                                                    },
                                                    {
                                                      kind: 'Field',
                                                      name: { kind: 'Name', value: 'blockers' },
                                                      selectionSet: {
                                                        kind: 'SelectionSet',
                                                        selections: [
                                                          {
                                                            kind: 'FragmentSpread',
                                                            name: {
                                                              kind: 'Name',
                                                              value: 'OrderStateBlocker',
                                                            },
                                                          },
                                                        ],
                                                      },
                                                    },
                                                  ],
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderStateBlocker' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'OrderStateProgressBlocker' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseDocumentType' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCaseIdentityCheck' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerCustomerDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: {
                kind: 'Name',
                value: 'OrderStateProgressBlockerFinancialAccountDocumentType',
              },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentType' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerOrderPaymentStatus' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } }],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerGenerateDocumentType' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderStateProgressBlockerValidationEntity' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'orderValidationEntity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'entityReferenceCodes' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerDocumentIdCardScanCommitMutation,
  CustomerDocumentIdCardScanCommitMutationVariables
>
export const CustomerDocumentUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerDocumentUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerDocumentUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerDocumentUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '100' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'edges' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'node' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'FragmentSpread',
                                                name: {
                                                  kind: 'Name',
                                                  value: 'CaseDetailCustomerDocument',
                                                },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerDocumentUpdateMutation,
  CustomerDocumentUpdateMutationVariables
>
export const CustomerEmailCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerEmailCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerContactEmailCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerContactEmailCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerContactEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactEmails' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEmail' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerEmailCreateMutation, CustomerEmailCreateMutationVariables>
export const CustomerEmailDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerEmailDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerContactEmailDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerContactEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactEmails' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerEmail' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerEmailDeleteMutation, CustomerEmailDeleteMutationVariables>
export const CustomerMergeDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerMerge' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'targetId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sourceId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerMerge' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'targetId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'targetId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sourceId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sourceId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'movedCaseCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mergedContactCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerMergeMutation, CustomerMergeMutationVariables>
export const CustomerPhoneCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerPhoneCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerContactPhoneCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerContactPhoneCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerContactPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactPhones' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerPhone' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerPhoneCreateMutation, CustomerPhoneCreateMutationVariables>
export const CustomerPhoneDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerPhoneDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerContactPhoneDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerContactPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'contactPhones' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerPhone' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'cases' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'totalCount' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerPhoneDeleteMutation, CustomerPhoneDeleteMutationVariables>
export const CustomerPortalAccessDisableDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerPortalAccessDisable' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerPortalAccessDisable' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerPortalAccessDisableMutation,
  CustomerPortalAccessDisableMutationVariables
>
export const CustomerPortalAccessSendDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerPortalAccessSend' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'delivery' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerPortalInviteDeliveryMethod' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'targetPhoneId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedContactId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedStatus' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'CustomerPortalInviteTargetStatus' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerPortalAccessSend' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'delivery' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'delivery' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'targetPhoneId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'targetPhoneId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'acknowledgedContactId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedContactId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'acknowledgedStatus' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedStatus' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'delivery' } },
                { kind: 'Field', name: { kind: 'Name', value: 'inviteId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'inviteFailure' } },
                { kind: 'Field', name: { kind: 'Name', value: 'readAloudToken' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerPortalAccessSendMutation,
  CustomerPortalAccessSendMutationVariables
>
export const CustomerPortalInviteSendDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerPortalInviteSend' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'delivery' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CustomerPortalInviteDeliveryMethod' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'targetPhoneId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedContactId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedStatus' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'CustomerPortalInviteTargetStatus' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerPortalInviteSend' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'delivery' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'delivery' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'targetPhoneId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'targetPhoneId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'acknowledgedContactId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedContactId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'acknowledgedStatus' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'acknowledgedStatus' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'delivery' } },
                { kind: 'Field', name: { kind: 'Name', value: 'inviteId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'readAloudToken' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerPortalInviteSendMutation,
  CustomerPortalInviteSendMutationVariables
>
export const CustomerRequestErasureDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerRequestErasure' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerRequestErasure' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'casesRedacted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'documentsDeleted' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerRequestErasureMutation,
  CustomerRequestErasureMutationVariables
>
export const CustomerSetDefaultAddressDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerSetDefaultAddress' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerAddressSetDefault' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerDetail' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerSetDefaultAddressMutation,
  CustomerSetDefaultAddressMutationVariables
>
export const CustomerSetDefaultEmailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerSetDefaultEmail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerEmailSetDefault' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerContactEmail' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerDetail' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerSetDefaultEmailMutation,
  CustomerSetDefaultEmailMutationVariables
>
export const CustomerSetDefaultPhoneDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerSetDefaultPhone' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerPhoneSetDefault' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerContactPhone' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'customer' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerDetail' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerSetDefaultPhoneMutation,
  CustomerSetDefaultPhoneMutationVariables
>
export const CustomerUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CustomerUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerUpdateMutation, CustomerUpdateMutationVariables>
export const CustomerConversationsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CustomerConversations' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversations' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CustomerConversation' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerConversation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerConversationsQuery, CustomerConversationsQueryVariables>
export const CustomerDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CustomerDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'casesFirst' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'casesAfter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'casesLast' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'casesBefore' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountsFirst' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountsAfter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountsLast' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'financialAccountsBefore' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDetail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'before' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'casesBefore' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'after' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'casesAfter' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'casesFirst' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'casesLast' } },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CaseCard' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'pageInfo' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccounts' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'before' },
                      value: {
                        kind: 'Variable',
                        name: { kind: 'Name', value: 'financialAccountsBefore' },
                      },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'after' },
                      value: {
                        kind: 'Variable',
                        name: { kind: 'Name', value: 'financialAccountsAfter' },
                      },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: {
                        kind: 'Variable',
                        name: { kind: 'Name', value: 'financialAccountsFirst' },
                      },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: {
                        kind: 'Variable',
                        name: { kind: 'Name', value: 'financialAccountsLast' },
                      },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerDetailFinancialAccount' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'pageInfo' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectCountryArea' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CountryArea' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SelectRegency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Regency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDomicile' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'domicile' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileCountryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectCountryArea' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'domicileRegency' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SelectRegency' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerAddress' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerAddress' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'city' } },
          { kind: 'Field', name: { kind: 'Name', value: 'cityArea' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'country' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'countryArea' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress1' } },
          { kind: 'Field', name: { kind: 'Name', value: 'streetAddress2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPermanentResidency' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerPhone' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactPhone' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhoneNumberCopyable' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
          { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEmail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerContactEmail' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Document' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseDetailDocument' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomerEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'occupation' } },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerDomicile' } },
          { kind: 'Field', name: { kind: 'Name', value: 'residentialStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'jobTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'employmentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'salaryRange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherDebtTypeOther' } },
          { kind: 'Field', name: { kind: 'Name', value: 'portalEnabledAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addresses' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerAddress' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactPhones' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerPhone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'contactEmails' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomerEmail' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'cases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseDetailCustomerDocument' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerDetailFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'assignedToUser' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'closeReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerDetailQuery, CustomerDetailQueryVariables>
export const CustomerListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CustomerList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomersFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomersSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customers' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CustomerList' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CustomerListQuery, CustomerListQueryVariables>
export const CustomerPortalInviteTargetsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CustomerPortalInviteTargets' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'PERMISSION_MANAGE_CUSTOMERS' },
          },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'PERMISSION_MANAGE_SETTINGS' },
          },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'InlineFragment',
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CUSTOMERS' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customerPortalInviteTargets' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'id' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
                    },
                  ],
                  directives: [
                    {
                      kind: 'Directive',
                      name: { kind: 'Name', value: 'include' },
                      arguments: [
                        {
                          kind: 'Argument',
                          name: { kind: 'Name', value: 'if' },
                          value: {
                            kind: 'Variable',
                            name: { kind: 'Name', value: 'PERMISSION_MANAGE_SETTINGS' },
                          },
                        },
                      ],
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phones' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'phone' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'phoneNumber' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'displayNumber' },
                                        },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'international' },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'note' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'whatsappCapability' },
                                  },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'invitable' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'email' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'email' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'portalDeliveryOptions' },
                  directives: [
                    {
                      kind: 'Directive',
                      name: { kind: 'Name', value: 'include' },
                      arguments: [
                        {
                          kind: 'Argument',
                          name: { kind: 'Name', value: 'if' },
                          value: {
                            kind: 'Variable',
                            name: { kind: 'Name', value: 'PERMISSION_MANAGE_SETTINGS' },
                          },
                        },
                      ],
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'whatsapp' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CustomerPortalInviteTargetsQuery,
  CustomerPortalInviteTargetsQueryVariables
>
export const FinancialProviderContactCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialProviderContactCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialProviderContactCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialProviderContactCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialProviderContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'FinancialProviderDetailContact' },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'financialProvider' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'FinancialProviderDetail' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetailContact' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialProviderContact' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'FinancialProviderList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'businessName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialProviderContactCreateMutation,
  FinancialProviderContactCreateMutationVariables
>
export const FinancialProviderCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialProviderCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialProviderCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialProviderCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialProvider' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'FinancialProviderList' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialProviderCreateMutation,
  FinancialProviderCreateMutationVariables
>
export const FinancialProviderDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialProviderDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialProviderDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialProvider' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'FinancialProviderDetail' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'FinancialProviderList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'businessName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialProviderDeleteMutation,
  FinancialProviderDeleteMutationVariables
>
export const FinancialProviderUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialProviderUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialProviderUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialProviderUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialProvider' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'FinancialProviderDetail' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'FinancialProviderList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'businessName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialProviderUpdateMutation,
  FinancialProviderUpdateMutationVariables
>
export const FinancialProviderDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'FinancialProviderDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'FinancialProviderContactSortInput' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialProvider' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'FinancialProviderDetail' },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'contacts' },
                  arguments: [
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'before' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'after' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'first' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'last' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
                    },
                    {
                      kind: 'Argument',
                      name: { kind: 'Name', value: 'sort' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
                    },
                  ],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'edges' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'node' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'FinancialProviderDetailContact' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'pageInfo' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PageInfo' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableAccountTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'FinancialProviderAvailableAccountType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'FinancialProviderList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'businessName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderDetailContact' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'FinancialProviderContact' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'phoneNumber' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PageInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PageInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderAvailableAccountType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccountType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'value' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialProviderDetailQuery, FinancialProviderDetailQueryVariables>
export const FinancialProviderListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'FinancialProvidersFilterInput' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvidersSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialProviders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'cursor' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'FinancialProviderList' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'FinancialProviderList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialProvider' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialProviderListQuery, FinancialProviderListQueryVariables>
export const HomeCasesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeCases' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myOpen' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myUncontacted' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'contactAction' },
                      value: { kind: 'EnumValue', value: 'NONE' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myStale' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'age' },
                      value: {
                        kind: 'ObjectValue',
                        fields: [
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'min' },
                            value: { kind: 'IntValue', value: '3' },
                          },
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'unit' },
                            value: { kind: 'EnumValue', value: 'BUSINESS_DAY' },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myUnpaid' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'orderPaymentStatus' },
                      value: {
                        kind: 'ListValue',
                        values: [
                          { kind: 'EnumValue', value: 'UNPAID' },
                          { kind: 'EnumValue', value: 'PARTIALLY_PAID' },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myNewThisWeek' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'created' },
                      value: {
                        kind: 'ObjectValue',
                        fields: [
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'gte' },
                            value: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myUncontactedRows' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '5' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'contactAction' },
                      value: { kind: 'EnumValue', value: 'NONE' },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'HomeQueueCase' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myStaleRows' },
            name: { kind: 'Name', value: 'casesUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '5' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'age' },
                      value: {
                        kind: 'ObjectValue',
                        fields: [
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'min' },
                            value: { kind: 'IntValue', value: '3' },
                          },
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'unit' },
                            value: { kind: 'EnumValue', value: 'BUSINESS_DAY' },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'HomeQueueCase' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'HomeQueueCase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Case' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeCasesQuery, HomeCasesQueryVariables>
export const HomeFeesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeFees' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'todayStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weekAgo' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'monthStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'feesToday' },
            name: { kind: 'Name', value: 'reportFeeCollection' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'EnumValue', value: 'DAY' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'todayStart' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'HomeFeeWindow' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'feesWeek' },
            name: { kind: 'Name', value: 'reportFeeCollection' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'EnumValue', value: 'DAY' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'weekAgo' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'HomeFeeWindow' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currencies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'paid' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'feesMonth' },
            name: { kind: 'Name', value: 'reportFeeCollection' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'EnumValue', value: 'DAY' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'monthStart' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'HomeFeeWindow' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'HomeFeeWindow' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportFeeCollection' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'adjustedOrders' } },
          { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
          { kind: 'Field', name: { kind: 'Name', value: 'unpricedOrders' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'currencies' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'collectionRate' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'paid' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'prescribed' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeFeesQuery, HomeFeesQueryVariables>
export const HomeManagerDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeManager' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'prevWeekStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weekAgo' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_MANAGE_USERS' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'summary' },
            name: { kind: 'Name', value: 'caseDashboardSummary' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'weekStart' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'prevWeekStart' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'prevWeekStart' } },
              },
            ],
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'pendingCases' } },
                { kind: 'Field', name: { kind: 'Name', value: 'pendingAging' } },
                { kind: 'Field', name: { kind: 'Name', value: 'openCases' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdThisWeek' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdLastWeek' } },
                { kind: 'Field', name: { kind: 'Name', value: 'closedThisWeek' } },
                { kind: 'Field', name: { kind: 'Name', value: 'closedLastWeek' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stuckCases' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stageUncontacted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stageOnboarding' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stageServicing' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stageComplete' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportNewLeads' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'EnumValue', value: 'DAY' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'weekAgo' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
              },
            ],
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_CASES' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'leadsTotal' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'since' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'workload' },
            name: { kind: 'Name', value: 'caseAssignmentWorkload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '8' },
              },
            ],
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_USERS' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'openCases' } },
                { kind: 'Field', name: { kind: 'Name', value: 'uncontactedCases' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseEvents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'eventType' },
                      value: {
                        kind: 'ListValue',
                        values: [
                          { kind: 'EnumValue', value: 'ASSIGNED' },
                          { kind: 'EnumValue', value: 'CLOSED' },
                          { kind: 'EnumValue', value: 'REOPENED' },
                          { kind: 'EnumValue', value: 'CREATED' },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseEvent' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'case' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fromCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'toCustomer' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeManagerQuery, HomeManagerQueryVariables>
export const HomeRemindersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeReminders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'todayEnd' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'overdue' },
            name: { kind: 'Name', value: 'caseReminders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'EnumValue', value: 'MINE' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'overdue' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DUE_AT' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseReminderRow' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'today' },
            name: { kind: 'Name', value: 'caseReminders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'EnumValue', value: 'MINE' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'overdue' },
                      value: { kind: 'BooleanValue', value: false },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'dueTo' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'todayEnd' } },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DUE_AT' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseReminderRow' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'summary' },
            name: { kind: 'Name', value: 'caseReminderSummary' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'EnumValue', value: 'MINE' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'overdue' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dueToday' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderBrief' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isOverdue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outsideBusinessHours' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseReminderRow' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseReminder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseReminderBrief' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'assignedToUser' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeRemindersQuery, HomeRemindersQueryVariables>
export const HomeSettlementDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeSettlement' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'settlementTickets' },
            name: { kind: 'Name', value: 'ticketsUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '50' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'InlineFragment',
                              typeCondition: {
                                kind: 'NamedType',
                                name: { kind: 'Name', value: 'NegotiationTicket' },
                              },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'outstandingDebt' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'adjustedDebt' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeSettlementQuery, HomeSettlementQueryVariables>
export const HomeTeamDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeTeam' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'todayStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'teamOpen' },
            name: { kind: 'Name', value: 'casesTeamAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'teamUncontacted' },
            name: { kind: 'Name', value: 'casesTeamAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'contactAction' },
                      value: { kind: 'EnumValue', value: 'NONE' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'teamTickets' },
            name: { kind: 'Name', value: 'ticketsTeamAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAssignedLeads' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'EnumValue', value: 'DAY' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'user' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'intervalCounts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countAutoAssigned' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'countManuallyAssigned' },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'countTotal' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportUserActivity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'todayStart' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'now' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'lastEvent' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'date' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currentIdlePeriod' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'duration' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeTeamQuery, HomeTeamQueryVariables>
export const HomeTeamRosterDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeTeamRoster' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'teamId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'teamRoster' },
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '30' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'role' },
                      value: {
                        kind: 'ListValue',
                        values: [{ kind: 'EnumValue', value: 'OPERATOR' }],
                      },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'team' },
                      value: {
                        kind: 'ListValue',
                        values: [{ kind: 'Variable', name: { kind: 'Name', value: 'teamId' } }],
                      },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'isActive' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                            {
                              kind: 'Field',
                              alias: { kind: 'Name', value: 'open' },
                              name: { kind: 'Name', value: 'assignedToCases' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '0' },
                                },
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'filter' },
                                  value: {
                                    kind: 'ObjectValue',
                                    fields: [
                                      {
                                        kind: 'ObjectField',
                                        name: { kind: 'Name', value: 'status' },
                                        value: {
                                          kind: 'ListValue',
                                          values: [{ kind: 'EnumValue', value: 'OPEN' }],
                                        },
                                      },
                                    ],
                                  },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              alias: { kind: 'Name', value: 'uncontacted' },
                              name: { kind: 'Name', value: 'assignedToCases' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'first' },
                                  value: { kind: 'IntValue', value: '0' },
                                },
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'filter' },
                                  value: {
                                    kind: 'ObjectValue',
                                    fields: [
                                      {
                                        kind: 'ObjectField',
                                        name: { kind: 'Name', value: 'status' },
                                        value: {
                                          kind: 'ListValue',
                                          values: [{ kind: 'EnumValue', value: 'OPEN' }],
                                        },
                                      },
                                      {
                                        kind: 'ObjectField',
                                        name: { kind: 'Name', value: 'contactAction' },
                                        value: { kind: 'EnumValue', value: 'NONE' },
                                      },
                                    ],
                                  },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeTeamRosterQuery, HomeTeamRosterQueryVariables>
export const HomeTicketsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'HomeTickets' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myOpenTickets' },
            name: { kind: 'Name', value: 'ticketsUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myUncontactedTickets' },
            name: { kind: 'Name', value: 'ticketsUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'contactAction' },
                      value: { kind: 'EnumValue', value: 'NONE' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myNewTickets' },
            name: { kind: 'Name', value: 'ticketsUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '0' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'created' },
                      value: {
                        kind: 'ObjectValue',
                        fields: [
                          {
                            kind: 'ObjectField',
                            name: { kind: 'Name', value: 'gte' },
                            value: { kind: 'Variable', name: { kind: 'Name', value: 'weekStart' } },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'myOldestTickets' },
            name: { kind: 'Name', value: 'ticketsUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '5' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'status' },
                      value: { kind: 'ListValue', values: [{ kind: 'EnumValue', value: 'OPEN' }] },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                            {
                              kind: 'InlineFragment',
                              typeCondition: {
                                kind: 'NamedType',
                                name: { kind: 'Name', value: 'FinancialAccountTicket' },
                              },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'financialAccount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'case' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'id' },
                                              },
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'code' },
                                              },
                                            ],
                                          },
                                        },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'provider' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'name' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'InlineFragment',
                              typeCondition: {
                                kind: 'NamedType',
                                name: { kind: 'Name', value: 'MediationTicket' },
                              },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'providerContact' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'phoneNumber' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'displayNumber' },
                                              },
                                              {
                                                kind: 'Field',
                                                name: { kind: 'Name', value: 'international' },
                                              },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'InlineFragment',
                              typeCondition: {
                                kind: 'NamedType',
                                name: { kind: 'Name', value: 'NegotiationTicket' },
                              },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'outstandingDebt' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'adjustedDebt' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<HomeTicketsQuery, HomeTicketsQueryVariables>
export const CaseImportQuarantineResolveDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseImportQuarantineResolve' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseImportQuarantineResolveInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseImportQuarantineResolve' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseImportQuarantineResolveMutation,
  CaseImportQuarantineResolveMutationVariables
>
export const CaseImportQuarantineRetryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseImportQuarantineRetry' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseImportQuarantineRetry' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseImportQuarantineRetryMutation,
  CaseImportQuarantineRetryMutationVariables
>
export const CaseImportQuarantineRetryManyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseImportQuarantineRetryMany' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseImportQuarantineRetryMany' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'ids' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'imported' } },
                { kind: 'Field', name: { kind: 'Name', value: 'failed' } },
                { kind: 'Field', name: { kind: 'Name', value: 'skipped' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseImportQuarantineRetryManyMutation,
  CaseImportQuarantineRetryManyMutationVariables
>
export const CaseImportQuarantineCountDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseImportQuarantineCount' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [{ kind: 'Field', name: { kind: 'Name', value: 'caseImportQuarantineCount' } }],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseImportQuarantineCountQuery,
  CaseImportQuarantineCountQueryVariables
>
export const CaseImportQuarantinesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseImportQuarantines' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'CaseImportQuarantinesFilterInput' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'CaseImportQuarantinesSortInput' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseImportQuarantines' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'cursor' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CaseImportQuarantineLead' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseImportQuarantineLead' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseImportQuarantine' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerEmail' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerPhone' } },
          { kind: 'Field', name: { kind: 'Name', value: 'error' } },
          { kind: 'Field', name: { kind: 'Name', value: 'externalIdentifier' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issues' } },
          { kind: 'Field', name: { kind: 'Name', value: 'schemaVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceCampaign' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'content' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseImportQuarantinesQuery, CaseImportQuarantinesQueryVariables>
export const CaseCreateFromConversationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseCreateFromConversation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseCreateFromConversationInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseCreateFromConversation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'ladderRung' } },
                { kind: 'Field', name: { kind: 'Name', value: 'handedOver' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'conversationStale' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseCreateFromConversationMutation,
  CaseCreateFromConversationMutationVariables
>
export const CaseStartConversationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseStartConversation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseStartConversationInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseStartConversation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'reusedExisting' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'case' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'conversations' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'ConversationListItem' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseStartConversationMutation, CaseStartConversationMutationVariables>
export const ChannelTemplateCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelTemplateCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelTemplateCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplateCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'metaTemplateId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelTemplateCreateMutation, ChannelTemplateCreateMutationVariables>
export const ChannelTemplateDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelTemplateDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'metaTemplateId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'allLanguages' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplateDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'metaTemplateId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'metaTemplateId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'allLanguages' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'allLanguages' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'success' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelTemplateDeleteMutation, ChannelTemplateDeleteMutationVariables>
export const ChannelTemplateHeaderImageUploadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelTemplateHeaderImageUpload' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelTemplateHeaderImageUploadInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplateHeaderImageUpload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'assetId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'previewUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contentType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizeBytes' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ChannelTemplateHeaderImageUploadMutation,
  ChannelTemplateHeaderImageUploadMutationVariables
>
export const ChannelTemplateProbeWriteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelTemplateProbeWrite' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplateProbeWrite' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'detail' } },
                { kind: 'Field', name: { kind: 'Name', value: 'probeName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cleanedUp' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ChannelTemplateProbeWriteMutation,
  ChannelTemplateProbeWriteMutationVariables
>
export const ChannelTemplateSyncDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelTemplateSync' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplateSync' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'upserted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'removed' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelTemplateSyncMutation, ChannelTemplateSyncMutationVariables>
export const ChannelTemplateUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChannelTemplateUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'metaTemplateId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingChannelTemplateUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplateUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'metaTemplateId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'metaTemplateId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'success' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelTemplateUpdateMutation, ChannelTemplateUpdateMutationVariables>
export const ConversationArchiveDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationArchive' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'reason' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationArchive' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reason' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'reason' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationArchiveMutation, ConversationArchiveMutationVariables>
export const ConversationHandbackDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationHandback' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationHandback' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationHandbackMutation, ConversationHandbackMutationVariables>
export const ConversationPickUpDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationPickUp' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationPickUp' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationPickUpMutation, ConversationPickUpMutationVariables>
export const ConversationSetCustomerDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationSetCustomer' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationSetCustomer' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'customerId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'customerId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ConversationSetCustomerMutation,
  ConversationSetCustomerMutationVariables
>
export const ConversationSetDismissedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationSetDismissed' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dismissed' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationSetDismissed' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dismissed' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dismissed' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ConversationSetDismissedMutation,
  ConversationSetDismissedMutationVariables
>
export const ConversationTakeOverDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationTakeOver' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationTakeOver' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conversation' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationTakeOverMutation, ConversationTakeOverMutationVariables>
export const ConversationTypingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ConversationTyping' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationTyping' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'accepted' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationTypingMutation, ConversationTypingMutationVariables>
export const MessageSendDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MessageSend' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingMessageSendInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messageSend' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'accepted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'clientMsgId' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessageSendMutation, MessageSendMutationVariables>
export const MessageSkipDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MessageSkip' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'messageId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingMessageSkip' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'messageId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'messageId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'messageId' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessageSkipMutation, MessageSkipMutationVariables>
export const MessagingInsightFileDocumentDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MessagingInsightFileDocument' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'insightId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingInsightFileDocument' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'insightId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'insightId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'insightAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'targetEntity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'documentId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  MessagingInsightFileDocumentMutation,
  MessagingInsightFileDocumentMutationVariables
>
export const MessagingQuickReplyCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MessagingQuickReplyCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingQuickReplyCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingQuickReplyCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'QuickReplyItem' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'QuickReplyItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingQuickReply' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shortcut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'body' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sequence' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  MessagingQuickReplyCreateMutation,
  MessagingQuickReplyCreateMutationVariables
>
export const MessagingQuickReplyDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MessagingQuickReplyDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingQuickReplyDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'quickReply' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'QuickReplyItem' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'QuickReplyItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingQuickReply' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shortcut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'body' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sequence' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  MessagingQuickReplyDeleteMutation,
  MessagingQuickReplyDeleteMutationVariables
>
export const MessagingQuickReplyUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'MessagingQuickReplyUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingQuickReplyUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingQuickReplyUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'QuickReplyItem' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'QuickReplyItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingQuickReply' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shortcut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'body' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sequence' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  MessagingQuickReplyUpdateMutation,
  MessagingQuickReplyUpdateMutationVariables
>
export const ChannelSendableTemplatesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ChannelSendableTemplates' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelSendableTemplates' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'metaTemplateId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelSendableTemplatesQuery, ChannelSendableTemplatesQueryVariables>
export const ChannelTemplateLibraryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ChannelTemplateLibrary' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplates' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'metaTemplateId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quality' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
                { kind: 'Field', name: { kind: 'Name', value: 'rejectedReason' } },
                { kind: 'Field', name: { kind: 'Name', value: 'rejectionRecommendation' } },
                { kind: 'Field', name: { kind: 'Name', value: 'statusNotifiedAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'headerImageUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'editBlockedReason' } },
                { kind: 'Field', name: { kind: 'Name', value: 'syncedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChannelTemplateLibraryQuery, ChannelTemplateLibraryQueryVariables>
export const ConversationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'Conversation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ConversationListItem' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'linkedCases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CaseCard' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'whatsappCapability' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'playbook' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'threadContactPhoneId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'source' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'channel' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resolved' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resolvedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CaseCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSummary' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedToUser' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationQuery, ConversationQueryVariables>
export const ConversationCaseDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ConversationCase' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'case' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customer' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationCaseQuery, ConversationCaseQueryVariables>
export const ConversationCaseCallbacksDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ConversationCaseCallbacks' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'linkedCases' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'callback' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'dueAt' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'source' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'bookedByName' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ConversationCaseCallbacksQuery,
  ConversationCaseCallbacksQueryVariables
>
export const ConversationCountsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ConversationCounts' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingConversationScope' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'includeDismissed' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationCounts' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'includeDismissed' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'includeDismissed' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
                { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
                { kind: 'Field', name: { kind: 'Name', value: 'total' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationCountsQuery, ConversationCountsQueryVariables>
export const ConversationEventsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ConversationEvents' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationEvents' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'limit' },
                value: { kind: 'IntValue', value: '200' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'payload' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'insightAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'targetEntity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'documentId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationEventsQuery, ConversationEventsQueryVariables>
export const ConversationMessagesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ConversationMessages' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'cursor' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'direction' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'MessagingMessageHistoryDirection' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationMessages' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'cursor' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'cursor' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'direction' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'direction' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'limit' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'hasMore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nextCursor' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessageItem' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessageItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingMessage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wamid' } },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'clientMsgId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'clientSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'text' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parked' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nextAttemptAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'context' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'quotedWamid' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reaction' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'emoji' } },
                { kind: 'Field', name: { kind: 'Name', value: 'targetWamid' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'media' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'mediaId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'filename' } },
                { kind: 'Field', name: { kind: 'Name', value: 'caption' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizeBytes' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'interactive' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                { kind: 'Field', name: { kind: 'Name', value: 'header' } },
                { kind: 'Field', name: { kind: 'Name', value: 'footer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buttons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'listButton' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sections' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rows' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'interactiveReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'template' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'header' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'format' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'footer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buttons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unknown' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'metaType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unsupportedType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationMessagesQuery, ConversationMessagesQueryVariables>
export const ConversationTemplatesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ConversationTemplates' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationTemplates' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'metaTemplateId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationTemplatesQuery, ConversationTemplatesQueryVariables>
export const ConversationsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'Conversations' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'cursor' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'includeDismissed' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversations' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'cursor' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'cursor' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'includeDismissed' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'includeDismissed' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'limit' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'hasMore' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nextCursor' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ConversationListItem' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ChannelInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingChannel' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOwner' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultOversightScenarioId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportPlaybookId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefaultTransactional' } },
          { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
          { kind: 'Field', name: { kind: 'Name', value: 'purpose' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wabaId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'metaAppId' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessagePreview' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingMessagePreview' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'textPreview' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ConversationListItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerWaId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customerWaPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lifecycle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastActivityAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInboundSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'needsAttention' } },
          { kind: 'Field', name: { kind: 'Name', value: 'owesReply' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dismissed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'safetyFlagged' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ownership' } },
          { kind: 'Field', name: { kind: 'Name', value: 'windowExpiresAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'linkedCases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channel' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ChannelInfo' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'lastMessage' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessagePreview' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConversationsQuery, ConversationsQueryVariables>
export const MessageMediaDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MessageMedia' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'mediaId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messageMedia' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'mediaId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'mediaId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'mediaId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'filename' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizeBytes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'expiresAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessageMediaQuery, MessageMediaQueryVariables>
export const MessagingQuickRepliesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MessagingQuickReplies' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingQuickReplies' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'QuickReplyItem' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'QuickReplyItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingQuickReply' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shortcut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'body' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sequence' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessagingQuickRepliesQuery, MessagingQuickRepliesQueryVariables>
export const StartConversationChannelsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'StartConversationChannels' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channels' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'purpose' },
                value: { kind: 'EnumValue', value: 'CONVERSATIONAL' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  StartConversationChannelsQuery,
  StartConversationChannelsQueryVariables
>
export const ConversationChangedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'ConversationChanged' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'conversationChanged' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'conversationId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'conversationId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'conversationId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'message' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MessageItem' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'stateChange' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'messageId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'revision' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'clientMsgId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'errorCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'parked' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'nextAttemptAt' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'assignment' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'assigneeId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'assigneeName' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'accessLost' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MessageItem' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingMessage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'wamid' } },
          { kind: 'Field', name: { kind: 'Name', value: 'seq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'clientMsgId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'clientSeq' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'direction' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'sender' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'text' } },
          { kind: 'Field', name: { kind: 'Name', value: 'errorCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parked' } },
          { kind: 'Field', name: { kind: 'Name', value: 'nextAttemptAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'context' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'quotedWamid' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reaction' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'emoji' } },
                { kind: 'Field', name: { kind: 'Name', value: 'targetWamid' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'media' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'mediaId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'filename' } },
                { kind: 'Field', name: { kind: 'Name', value: 'caption' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizeBytes' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'interactive' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                { kind: 'Field', name: { kind: 'Name', value: 'header' } },
                { kind: 'Field', name: { kind: 'Name', value: 'footer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buttons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'listButton' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sections' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rows' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'interactiveReply' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'template' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'body' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'header' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'format' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'footer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buttons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unknown' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'metaType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unsupportedType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ConversationChangedSubscription,
  ConversationChangedSubscriptionVariables
>
export const InboxChangedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'subscription',
      name: { kind: 'Name', value: 'InboxChanged' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingConversationScope' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'inboxChanged' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'scope' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'scope' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'operatorId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'operatorId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'conversationId' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<InboxChangedSubscription, InboxChangedSubscriptionVariables>
export const OversightScenarioCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OversightScenarioCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingOversightScenarioCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'oversightScenarioCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OversightScenarioInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OversightScenarioInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingOversightScenario' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'model' } },
          { kind: 'Field', name: { kind: 'Name', value: 'analyzers' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthEveryN' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthMinIntervalMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'maxCallsPerConvDay' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OversightScenarioCreateMutation,
  OversightScenarioCreateMutationVariables
>
export const OversightScenarioUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OversightScenarioUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingOversightScenarioUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'oversightScenarioUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OversightScenarioInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OversightScenarioInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingOversightScenario' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'model' } },
          { kind: 'Field', name: { kind: 'Name', value: 'analyzers' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthEveryN' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthMinIntervalMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'maxCallsPerConvDay' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OversightScenarioUpdateMutation,
  OversightScenarioUpdateMutationVariables
>
export const OversightScenarioDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'OversightScenarioDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'oversightScenario' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OversightScenarioInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OversightScenarioInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingOversightScenario' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'model' } },
          { kind: 'Field', name: { kind: 'Name', value: 'analyzers' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthEveryN' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthMinIntervalMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'maxCallsPerConvDay' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OversightScenarioDetailQuery, OversightScenarioDetailQueryVariables>
export const OversightScenariosDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'OversightScenarios' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'oversightScenarios' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OversightScenarioInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OversightScenarioInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingOversightScenario' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'model' } },
          { kind: 'Field', name: { kind: 'Name', value: 'analyzers' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthEveryN' } },
          { kind: 'Field', name: { kind: 'Name', value: 'healthMinIntervalMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'maxCallsPerConvDay' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OversightScenariosQuery, OversightScenariosQueryVariables>
export const PlaybookCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'PlaybookCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingPlaybookCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbookCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookCreateMutation, PlaybookCreateMutationVariables>
export const PlaybookCrmTokenRotateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'PlaybookCrmTokenRotate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbookCrmTokenRotate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  PlaybookCrmTokenRotateMutation,
  PlaybookCrmTokenRotateMutationVariables
>
export const PlaybookUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'PlaybookUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingPlaybookUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbookUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookUpdateMutation, PlaybookUpdateMutationVariables>
export const PlaybookVersionActivateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'PlaybookVersionActivate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'version' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbookVersionActivate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'version' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'version' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  PlaybookVersionActivateMutation,
  PlaybookVersionActivateMutationVariables
>
export const PlaybookVersionCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'PlaybookVersionCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingPlaybookVersionInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbookVersionCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookVersionCreateMutation, PlaybookVersionCreateMutationVariables>
export const MessagingModelsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MessagingModels' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingModels' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'maturity' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessagingModelsQuery, MessagingModelsQueryVariables>
export const MessagingToolsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MessagingTools' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'messagingTools' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'requiredPermissions' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MessagingToolsQuery, MessagingToolsQueryVariables>
export const PlaybookAppOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'PlaybookAppOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'apps' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'isActive' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'NAME' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookAppOptionsQuery, PlaybookAppOptionsQueryVariables>
export const PlaybookDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'PlaybookDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbook' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybookDetailQuery, PlaybookDetailQueryVariables>
export const PlaybooksDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'Playbooks' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbooks' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookInfo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookVersionInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'MessagingPlaybookVersion' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'aggregationDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'enabledTools' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpDelayMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpMaxCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPostNudgeQuietMs' } },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpPrompt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'followUpSendWindow' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'followUpTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'handoffTurnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'historyLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'leadCaptureEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediaVisionEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'modelId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outcomes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'summaryEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'systemPrompt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'temperature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'turnLimit' } },
          { kind: 'Field', name: { kind: 'Name', value: 'version' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlaybookInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MessagingPlaybook' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'description' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmAppPermissions' } },
          { kind: 'Field', name: { kind: 'Name', value: 'crmTokenSet' } },
          { kind: 'Field', name: { kind: 'Name', value: 'activeVersion' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'versions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlaybookVersionInfo' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlaybooksQuery, PlaybooksQueryVariables>
export const ReportAcquisitionFunnelDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAcquisitionFunnel' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSourceDimension' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAcquisitionFunnel' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'attributedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dimension' } },
                { kind: 'Field', name: { kind: 'Name', value: 'excludedOtherCurrencyOrders' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'submitted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'contacted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'eligible' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'orderCreated' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paid' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedUncontactable' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedDuplicate' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'closedOther' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'stageExits' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'open' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'timeToStage' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'contactedP50Hours' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'contactedP90Hours' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidP50Days' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidP90Days' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'submitted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'contacted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'eligible' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'orderCreated' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paid' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedUncontactable' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedDuplicate' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'closedOther' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'stageExits' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'open' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'timeToStage' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'contactedP50Hours' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'contactedP90Hours' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidP50Days' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidP90Days' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'workState' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'neverAssigned' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'noActionLogged' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'unsuccessfulActionsOnly' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'everConversation' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'customerReplied' },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'unattributed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'noSource' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'blank' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'placeholder' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'intake' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'none' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'webForm' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'fbForm' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'messagingLead' },
                                        },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'messagingEscalation' },
                                        },
                                        { kind: 'Field', name: { kind: 'Name', value: 'other' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'expectedPipeline' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportAcquisitionFunnelQuery, ReportAcquisitionFunnelQueryVariables>
export const ReportAcquisitionFunnelWhyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAcquisitionFunnelWhy' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSourceDimension' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportNeverReached' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'attributedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'neverReached' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'reasons' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'reason' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'totals' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'cases' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportCloseReasons' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'attributedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'closedUnpaid' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'reasons' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'closeReasonId' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'totals' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'cases' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'medianAgeHours' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReportAcquisitionFunnelWhyQuery,
  ReportAcquisitionFunnelWhyQueryVariables
>
export const ReportAcquisitionFunnelWindowMoneyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAcquisitionFunnelWindowMoney' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSourceDimension' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'PERMISSION_READ_FINANCE' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportFeeEvolution' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'attributedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'attributedOnly' } },
              },
            ],
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_READ_FINANCE' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currencies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totalPrescribed' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totalCollected' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'groups' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'totalPrescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'totalCollected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReportAcquisitionFunnelWindowMoneyQuery,
  ReportAcquisitionFunnelWindowMoneyQueryVariables
>
export const ReportAcquisitionSankeyFunnelDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAcquisitionSankeyFunnel' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAcquisitionFunnel' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'EnumValue', value: 'KIND' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'submitted' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'submitted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'contacted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'eligible' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'orderCreated' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paid' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedUncontactable' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedDuplicate' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'closedOther' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'stageExits' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedUncontactable' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'closedDuplicate' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'closedOther' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'open' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'expectedPipeline' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReportAcquisitionSankeyFunnelQuery,
  ReportAcquisitionSankeyFunnelQueryVariables
>
export const ReportAcquisitionSpendDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAcquisitionSpend' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAcquisitionSpend' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'configured' } },
                { kind: 'Field', name: { kind: 'Name', value: 'degraded' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'accountTimezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'adAccountId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'partial' } },
                { kind: 'Field', name: { kind: 'Name', value: 'snapshotAsOf' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unsettledDays' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaigns' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignName' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totalSpend' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalImpressions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalClicks' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalOutboundClicks' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalLeadsOnsite' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportAcquisitionSpendQuery, ReportAcquisitionSpendQueryVariables>
export const ReportAcquisitionWebFunnelDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAcquisitionWebFunnel' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAcquisitionWebFunnel' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'configured' } },
                { kind: 'Field', name: { kind: 'Name', value: 'degraded' } },
                { kind: 'Field', name: { kind: 'Name', value: 'degradedParts' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'site' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'pageViews' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'formViews' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'formStarts' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'formSubmissions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'formViewSessions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'formStartSessions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'formSubmitSessions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'submitErrors' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'submitErrorsValidation' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'submitErrorsServer' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'errorSessions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'recoveredSubmissions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sessions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sessionsMeta' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sessionsGoogleAds' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sessionsTiktok' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sessionsOther' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReportAcquisitionWebFunnelQuery,
  ReportAcquisitionWebFunnelQueryVariables
>
export const ReportAssignedLeadsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAssignedLeads' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Period' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAssignedLeads' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'interval' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'data' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'user' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'userTeam' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'intervalCounts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'countManuallyAssigned' },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'countAutoAssigned' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportAssignedLeadsQuery, ReportAssignedLeadsQueryVariables>
export const ReportAttributionCoverageDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportAttributionCoverage' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportAttributionCoverage' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buckets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'channels' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'intake' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: {
                                      kind: 'Name',
                                      value: 'ReportAttributionCoverageCounts',
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ReportAttributionCoverageCounts' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'cases' } },
          { kind: 'Field', name: { kind: 'Name', value: 'noSource' } },
          { kind: 'Field', name: { kind: 'Name', value: 'surfaceOnly' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mediumOnly' } },
          { kind: 'Field', name: { kind: 'Name', value: 'campaignKnown' } },
          { kind: 'Field', name: { kind: 'Name', value: 'adKnown' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralUnresolved' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referralResolved' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReportAttributionCoverageQuery,
  ReportAttributionCoverageQueryVariables
>
export const ReportFeeCohortCollectionDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportFeeCohortCollection' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportFeeCohortCollection' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'asOf' } },
                { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unpricedOrders' } },
                { kind: 'Field', name: { kind: 'Name', value: 'adjustedOrders' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mismatchedCurrencyPayments' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'interval' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currencies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'orders' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'collectedShare' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'outstanding' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'outstandingOrders' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'points' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'day' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'collected' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'share' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ageing' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'band' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'minDays' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'maxDays' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'orders' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'outstanding' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'orders' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'collectedShare' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'mature' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'points' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'day' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'collected' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'share' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReportFeeCohortCollectionQuery,
  ReportFeeCohortCollectionQueryVariables
>
export const ReportFeeEvolutionDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportFeeEvolution' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSourceDimension' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportFeeEvolution' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unpricedOrders' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dimension' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currencies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totalPrescribed' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totalCollected' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'orderCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'paymentCount' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribedRolling5BusinessDay' },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribedRolling30Day' },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collectedRolling5BusinessDay' },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collectedRolling30Day' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'groups' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'totalPrescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'totalCollected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'buckets' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'interval' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'prescribed' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'collected' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'currency' },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'orderCount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paymentCount' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'prescribedRolling5BusinessDay' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'prescribedRolling30Day' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'collectedRolling5BusinessDay' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'collectedRolling30Day' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportFeeEvolutionQuery, ReportFeeEvolutionQueryVariables>
export const ReportFeeMonthDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportFeeMonth' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'month' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportFeeMonth' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'month' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'month' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'month' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'elapsedCalendarDays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCalendarDays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'elapsedBusinessDays' } },
                { kind: 'Field', name: { kind: 'Name', value: 'totalBusinessDays' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'interval' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currencies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'days' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'date' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'cumulativePrescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'cumulativeCollected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'previousMonthDays' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'date' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'cumulativePrescribed' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'cumulativeCollected' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'mtdPrescribed' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'mtdCollected' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'prescribedProjection' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'calendarRunRate' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'businessDayRunRate' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'collectedProjection' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'calendarRunRate' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'businessDayRunRate' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'previousMonth' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribedTotal' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collectedTotal' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'prescribedToSameDay' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'collectedToSameDay' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportFeeMonthQuery, ReportFeeMonthQueryVariables>
export const ReportLeadDuplicatesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportLeadDuplicates' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseSourceDimension' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportLeadDuplicates' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dimension' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rates' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'repeatRate' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'paidRepeatRate' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'leads' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'duplicates' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'duplicatesSameType' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'duplicatesPhoneOnly' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'firstTime' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'autoClosed' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationNew' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationReturning' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationDuplicate' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationUnclassified' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidLeads' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidRepeats' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'leads' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'duplicates' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'duplicatesSameType' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'duplicatesPhoneOnly' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'firstTime' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'autoClosed' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationNew' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationReturning' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationDuplicate' },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'classificationUnclassified' },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidLeads' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'paidRepeats' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportLeadDuplicatesQuery, ReportLeadDuplicatesQueryVariables>
export const ReportMessagingActivityDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportMessagingActivity' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportMessagingActivity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'configured' } },
                { kind: 'Field', name: { kind: 'Name', value: 'degraded' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'conversationsStarted' },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'startedByCustomer' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'startedByBusiness' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'inboundMessages' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundOperator' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundBrain' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundSystem' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundExternal' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'activeConversations' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'customersMessaging' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'handoffs' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buckets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'conversationsStarted' },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'startedByCustomer' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'startedByBusiness' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'inboundMessages' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundOperator' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundBrain' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundSystem' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'outboundExternal' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'activeConversations' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'customersMessaging' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'handoffs' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'archived' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'responseTimes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'turns' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'responders' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'responder' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'turns' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'p50Minutes' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'p90Minutes' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportMessagingActivityQuery, ReportMessagingActivityQueryVariables>
export const ReportMessagingDeliveryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportMessagingDelivery' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportMessagingDimension' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportMessagingDelivery' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'configured' } },
                { kind: 'Field', name: { kind: 'Name', value: 'degraded' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dimension' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'attempted' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'pending' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'retrying' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'failed' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'read' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rates' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'acceptRate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'deliveryRate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'readRate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'failureRate' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buckets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'attempted' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'pending' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'retrying' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'failed' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'read' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'templateCategory' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'attempted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'pending' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'retrying' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'failed' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'read' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rates' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'acceptRate' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveryRate' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'readRate' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'failureRate' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'attempted' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'pending' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'retrying' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'failed' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'read' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'failures' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'errorCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'errorClass' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sampleTitle' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'latency' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'template' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'sentCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sentP50Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sentP95Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveredCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveredP50Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveredP95Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'readCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'readP50Ms' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'session' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'sentCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sentP50Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sentP95Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveredCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveredP50Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'deliveredP95Ms' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'readCount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'readP50Ms' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billing' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'pricingCategory' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'pricingType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'billable' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportMessagingDeliveryQuery, ReportMessagingDeliveryQueryVariables>
export const ReportMessagingRepliesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportMessagingReplies' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportCalendarPeriod' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReportMessagingDimension' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportMessagingReplies' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'dimension' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'dimension' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'configured' } },
                { kind: 'Field', name: { kind: 'Name', value: 'degraded' } },
                { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dimension' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'replied24h' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'replied72h' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'repliedAny' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'replyMedianMinutes' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rates' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'replyRate24h' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'replyRate72h' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'replyRateAny' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'replyRateOfDelivered' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'buckets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'counts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'replied24h' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'replied72h' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'repliedAny' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'replyMedianMinutes' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'groups' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totals' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'replied24h' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'replied72h' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'repliedAny' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'replyMedianMinutes' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rates' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'replyRate24h' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'replyRate72h' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'replyRateAny' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'replyRateOfDelivered' },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'buckets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'counts' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'delivered' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'replied24h' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'replied72h' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'repliedAny' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'replyMedianMinutes' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'byHour' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'isoWeekday' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hourOfDay' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sent' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'replied24h' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'replyRate24h' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportMessagingRepliesQuery, ReportMessagingRepliesQueryVariables>
export const ReportNewLeadsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportNewLeads' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Period' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportNewLeads' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'reportPeriod' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'period' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'interval' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'leadsByAuthor' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'createdBy' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'Author' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'intervalCounts' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'interval' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'leadsTotal' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'interval' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Author' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Author' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportNewLeadsQuery, ReportNewLeadsQueryVariables>
export const ReportUserActivityDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ReportUserActivity' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'reportUserActivity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'since' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'since' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'until' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'until' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'userTeam' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currentIdlePeriod' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'duration' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'longestIdlePeriod' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'duration' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'since' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'until' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'firstEvent' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'date' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'lastEvent' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'date' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ReportUserActivityQuery, ReportUserActivityQueryVariables>
export const SafetyTermCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SafetyTermCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingSafetyTermCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'safetyTermCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'term' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchMode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SafetyTermCreateMutation, SafetyTermCreateMutationVariables>
export const SafetyTermDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SafetyTermDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'termId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'safetyTermDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'termId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'termId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'success' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SafetyTermDeleteMutation, SafetyTermDeleteMutationVariables>
export const SafetyTermUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'SafetyTermUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'termId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingSafetyTermUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'safetyTermUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'termId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'termId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'term' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchMode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SafetyTermUpdateMutation, SafetyTermUpdateMutationVariables>
export const SafetyCheckDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SafetyCheck' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'texts' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'safetyCheck' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'texts' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'texts' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'checked' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flagged' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'matches' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'term' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'termId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'textIndex' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SafetyCheckQuery, SafetyCheckQueryVariables>
export const SafetyTermPreviewDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SafetyTermPreview' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'MessagingSafetyTermPreviewInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'safetyTermPreview' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'matchedCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sampleSize' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'examples' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SafetyTermPreviewQuery, SafetyTermPreviewQueryVariables>
export const SafetyTermsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SafetyTerms' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'safetyTerms' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'term' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchMode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
                { kind: 'Field', name: { kind: 'Name', value: 'hitCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'note' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SafetyTermsQuery, SafetyTermsQueryVariables>
export const CampaignCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CampaignCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CampaignCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'campaignCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignCreateMutation, CampaignCreateMutationVariables>
export const CampaignDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CampaignDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'campaignDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignDeleteMutation, CampaignDeleteMutationVariables>
export const CampaignResolverCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CampaignResolverCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CampaignResolverCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'campaignResolverCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CampaignResolverCreateMutation,
  CampaignResolverCreateMutationVariables
>
export const CampaignResolverDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CampaignResolverDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'campaignResolverDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CampaignResolverDeleteMutation,
  CampaignResolverDeleteMutationVariables
>
export const CampaignResolverUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CampaignResolverUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CampaignResolverUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'campaignResolverUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'campaignResolver' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CampaignResolverUpdateMutation,
  CampaignResolverUpdateMutationVariables
>
export const CampaignUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CampaignUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CampaignUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'campaignUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignUpdateMutation, CampaignUpdateMutationVariables>
export const CaseActionResultCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionResultCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseActionResultCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'caseActionResultCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'caseActionResult' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseActionResultCreateMutation,
  CaseActionResultCreateMutationVariables
>
export const CaseActionResultDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionResultDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'caseActionResultDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'caseActionResult' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseActionResultDeleteMutation,
  CaseActionResultDeleteMutationVariables
>
export const CaseActionResultUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionResultUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseActionResultUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'caseActionResultUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'caseActionResult' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CaseActionResultUpdateMutation,
  CaseActionResultUpdateMutationVariables
>
export const CaseActionTypeCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionTypeCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionTypeCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'caseActionTypeCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'caseActionType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeCreateMutation, CaseActionTypeCreateMutationVariables>
export const CaseActionTypeDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionTypeDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'caseActionTypeDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'caseActionType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeDeleteMutation, CaseActionTypeDeleteMutationVariables>
export const CaseActionTypeUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseActionTypeUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionTypeUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'caseActionTypeUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'caseActionType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeUpdateMutation, CaseActionTypeUpdateMutationVariables>
export const CaseCloseReasonCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseCloseReasonCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseCloseReasonCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'caseCloseReasonCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'caseCloseReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseReasonCreateMutation, CaseCloseReasonCreateMutationVariables>
export const CaseCloseReasonDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseCloseReasonDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'caseCloseReasonDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'caseCloseReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseReasonDeleteMutation, CaseCloseReasonDeleteMutationVariables>
export const CaseCloseReasonUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseCloseReasonUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CaseCloseReasonUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'caseCloseReasonUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'caseCloseReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseReasonUpdateMutation, CaseCloseReasonUpdateMutationVariables>
export const CaseTagCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTagCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTagCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'caseTagCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'caseTag' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagCreateMutation, CaseTagCreateMutationVariables>
export const CaseTagDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTagDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'caseTagDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'caseTag' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagDeleteMutation, CaseTagDeleteMutationVariables>
export const CaseTagUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTagUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTagUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'caseTagUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'caseTag' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagUpdateMutation, CaseTagUpdateMutationVariables>
export const CaseTypeCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTypeCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTypeCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'caseTypeCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'caseType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTypeCreateMutation, CaseTypeCreateMutationVariables>
export const CaseTypeDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTypeDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'caseTypeDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'caseType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTypeDeleteMutation, CaseTypeDeleteMutationVariables>
export const CaseTypeUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseTypeUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTypeUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'caseTypeUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'caseType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTypeUpdateMutation, CaseTypeUpdateMutationVariables>
export const FinancialAccountTypeCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountTypeCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTypeCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'financialAccountTypeCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'financialAccountType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountTypeCreateMutation,
  FinancialAccountTypeCreateMutationVariables
>
export const FinancialAccountTypeDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountTypeDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'financialAccountTypeDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'financialAccountType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountTypeDeleteMutation,
  FinancialAccountTypeDeleteMutationVariables
>
export const FinancialAccountTypeUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'FinancialAccountTypeUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTypeUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'financialAccountTypeUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'financialAccountType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountTypeUpdateMutation,
  FinancialAccountTypeUpdateMutationVariables
>
export const OrderDocumentTypeCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentTypeCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'productPackageId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderDocumentTypeCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'productPackageId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'productPackageId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'productPackage' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documentTypes' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'OrderDocumentType' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeCreateMutation,
  OrderDocumentTypeCreateMutationVariables
>
export const OrderDocumentTypeDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentTypeDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'productPackage' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documentTypes' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'OrderDocumentType' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeDeleteMutation,
  OrderDocumentTypeDeleteMutationVariables
>
export const OrderDocumentTypeTemplateTestRenderDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentTypeTemplateTestRender' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderDocumentTypeTemplateTestRenderInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeTemplateTestRender' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeTemplateTestRenderMutation,
  OrderDocumentTypeTemplateTestRenderMutationVariables
>
export const OrderDocumentTypeTemplateUploadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentTypeTemplateUpload' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderDocumentTypeTemplateUploadInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'requiresSignature' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeTemplateUpload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'requiresSignature' },
                      value: {
                        kind: 'Variable',
                        name: { kind: 'Name', value: 'requiresSignature' },
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeTemplateUploadMutation,
  OrderDocumentTypeTemplateUploadMutationVariables
>
export const OrderDocumentTypeTemplateVerifyDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentTypeTemplateVerify' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderDocumentTypeTemplateVerifyInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeTemplateVerify' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'usedKeys' } },
                { kind: 'Field', name: { kind: 'Name', value: 'unusedKeys' } },
                { kind: 'Field', name: { kind: 'Name', value: 'signatures' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeTemplateVerifyMutation,
  OrderDocumentTypeTemplateVerifyMutationVariables
>
export const OrderDocumentTypeUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'OrderDocumentTypeUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'OrderDocumentTypeUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'documentType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'OrderDocumentType' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeUpdateMutation,
  OrderDocumentTypeUpdateMutationVariables
>
export const ProductCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ProductCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'productCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductCreateMutation, ProductCreateMutationVariables>
export const ProductDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ProductDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'productDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductDeleteMutation, ProductDeleteMutationVariables>
export const ProductPackageCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ProductPackageCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackageCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'productPackageCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'productPackage' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageCreateMutation, ProductPackageCreateMutationVariables>
export const ProductPackageDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ProductPackageDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'productPackageDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'productPackage' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageDeleteMutation, ProductPackageDeleteMutationVariables>
export const ProductPackageUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ProductPackageUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackageUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'productPackageUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'productPackage' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageUpdateMutation, ProductPackageUpdateMutationVariables>
export const ProductUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ProductUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'productUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'product' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductUpdateMutation, ProductUpdateMutationVariables>
export const TicketActionResultCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionResultCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'TicketActionResultCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'ticketActionResultCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'ticketActionResult' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketActionResultCreateMutation,
  TicketActionResultCreateMutationVariables
>
export const TicketActionResultDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionResultDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionResultDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketActionResult' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketActionResultDeleteMutation,
  TicketActionResultDeleteMutationVariables
>
export const TicketActionResultUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionResultUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'TicketActionResultUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionResultUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketActionResult' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketActionResultUpdateMutation,
  TicketActionResultUpdateMutationVariables
>
export const TicketActionTypeCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionTypeCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'TicketActionTypeCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'ticketActionTypeCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'ticketActionType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketActionTypeCreateMutation,
  TicketActionTypeCreateMutationVariables
>
export const TicketActionTypeDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionTypeDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionTypeDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketActionType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketActionTypeDeleteMutation,
  TicketActionTypeDeleteMutationVariables
>
export const TicketActionTypeUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionTypeUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'TicketActionTypeUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionTypeUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketActionType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketActionTypeUpdateMutation,
  TicketActionTypeUpdateMutationVariables
>
export const TicketCloseReasonCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketCloseReasonCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'TicketCloseReasonCreateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'ticketCloseReasonCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'ticketCloseReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketCloseReasonCreateMutation,
  TicketCloseReasonCreateMutationVariables
>
export const TicketCloseReasonDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketCloseReasonDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'ticketCloseReasonDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'ticketCloseReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketCloseReasonDeleteMutation,
  TicketCloseReasonDeleteMutationVariables
>
export const TicketCloseReasonUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketCloseReasonUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'TicketCloseReasonUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'ticketCloseReasonUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'ticketCloseReason' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TicketCloseReasonUpdateMutation,
  TicketCloseReasonUpdateMutationVariables
>
export const TicketTagCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketTagCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketTagCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'create' },
            name: { kind: 'Name', value: 'ticketTagCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'newSettingsItem' },
                  name: { kind: 'Name', value: 'ticketTag' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagCreateMutation, TicketTagCreateMutationVariables>
export const TicketTagDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketTagDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'delete' },
            name: { kind: 'Name', value: 'ticketTagDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'deletedSettingsItem' },
                  name: { kind: 'Name', value: 'ticketTag' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagDeleteMutation, TicketTagDeleteMutationVariables>
export const TicketTagUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketTagUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketTagUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'update' },
            name: { kind: 'Name', value: 'ticketTagUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'updatedSettingsItem' },
                  name: { kind: 'Name', value: 'ticketTag' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagUpdateMutation, TicketTagUpdateMutationVariables>
export const CampaignChannelTemplatesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignChannelTemplates' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channelTemplates' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'channelId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'channelId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'language' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'components' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignChannelTemplatesQuery, CampaignChannelTemplatesQueryVariables>
export const CampaignDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sendChannelId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'openers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'sequence' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'delayMinutes' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'templateName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'templateLanguage' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'parameters' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'source' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'fallback' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fallbacks' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'trigger' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'action' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'closeReasonId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'tagId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'waitMinutes' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningPlaybookId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'conciergePlaybookId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'oversightScenarioId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assignTagId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customerRepliedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'screeningCompletedActionTypeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignDetailQuery, CampaignDetailQueryVariables>
export const CampaignDetailOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignDetailOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'channels' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'purpose' },
                value: { kind: 'EnumValue', value: 'CONVERSATIONAL' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'playbookOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'oversightScenarioOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'enabled' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseCloseReasons' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTags' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignDetailOptionsQuery, CampaignDetailOptionsQueryVariables>
export const CampaignListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CampaignsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaigns' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'caseType' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignListQuery, CampaignListQueryVariables>
export const CampaignResolverDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignResolverDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaignResolver' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchKind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchMedium' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchCampaign' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchTool' } },
                { kind: 'Field', name: { kind: 'Name', value: 'dataFilter' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'schedule' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'scheduleOpenNow' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'CampaignResolverSchedule' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'mode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'window' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignResolverDetailQuery, CampaignResolverDetailQueryVariables>
export const CampaignResolverDetailOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignResolverDetailOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaigns' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CampaignResolverDetailOptionsQuery,
  CampaignResolverDetailOptionsQueryVariables
>
export const CampaignResolverSimulateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignResolverSimulate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'CampaignResolverSimulateInput' },
            },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'lastDays' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaignResolverSimulate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'lastDays' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'lastDays' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCases' } },
                { kind: 'Field', name: { kind: 'Name', value: 'casesWithSource' } },
                { kind: 'Field', name: { kind: 'Name', value: 'matchedCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'outsideScheduleCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'wouldCaptureCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'wouldCapturePercent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'shadowedCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'shadowedBy' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'resolverId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'sample' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'caseId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'caseCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'medium' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaign' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'tool' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignResolverSimulateQuery, CampaignResolverSimulateQueryVariables>
export const CampaignRoutingOverviewDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignRoutingOverview' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'lastDays' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaignRoutingOverview' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'lastDays' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'lastDays' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCases' } },
                { kind: 'Field', name: { kind: 'Name', value: 'casesWithSource' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'resolverId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignActive' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'capturedCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'capturedPercent' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'schedule' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
                            },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'scheduleOpenNow' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fallthrough' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'campaignName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'CampaignResolverSchedule' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'mode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'window' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignRoutingOverviewQuery, CampaignRoutingOverviewQueryVariables>
export const CampaignRulesForCampaignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CampaignRulesForCampaign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'campaignId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'campaignResolvers' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'campaignId' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'campaignId' } },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'PRIORITY' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'priority' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'schedule' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
                                  },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'scheduleOpenNow' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CampaignResolverScheduleInfo' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'CampaignResolverSchedule' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'mode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'observeHolidays' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'window' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'tz' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rules' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'days' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CampaignRulesForCampaignQuery, CampaignRulesForCampaignQueryVariables>
export const CaseActionResultDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseActionResultDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionResult' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionResultDetailQuery, CaseActionResultDetailQueryVariables>
export const CaseActionResultListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseActionResultList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionResultsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionResults' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionResultListQuery, CaseActionResultListQueryVariables>
export const CaseActionTypeDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseActionTypeDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionType' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeDetailQuery, CaseActionTypeDetailQueryVariables>
export const CaseActionTypeListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseActionTypeList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseActionTypesSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeListQuery, CaseActionTypeListQueryVariables>
export const CaseActionTypeOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseActionTypeOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: {
            kind: 'Variable',
            name: { kind: 'Name', value: 'PERMISSION_MANAGE_SETTINGS' },
          },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
          },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseActionTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'NAME' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'ASC' },
                    },
                  ],
                },
              },
            ],
            directives: [
              {
                kind: 'Directive',
                name: { kind: 'Name', value: 'include' },
                arguments: [
                  {
                    kind: 'Argument',
                    name: { kind: 'Name', value: 'if' },
                    value: {
                      kind: 'Variable',
                      name: { kind: 'Name', value: 'PERMISSION_MANAGE_SETTINGS' },
                    },
                  },
                ],
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseActionTypeOptionsQuery, CaseActionTypeOptionsQueryVariables>
export const CaseCloseReasonDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseCloseReasonDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseCloseReason' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'reportedMetaEvent' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseReasonDetailQuery, CaseCloseReasonDetailQueryVariables>
export const CaseCloseReasonListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseCloseReasonList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseCloseReasonsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseCloseReasons' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseCloseReasonListQuery, CaseCloseReasonListQueryVariables>
export const CaseSourceObservedValuesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseSourceObservedValues' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'lastDays' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseSourceObservedValues' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'lastDays' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'lastDays' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'kind' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'type' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'medium' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaign' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tool' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'dataKeys' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseSourceObservedValuesQuery, CaseSourceObservedValuesQueryVariables>
export const CaseTagDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseTagDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTag' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagDetailQuery, CaseTagDetailQueryVariables>
export const CaseTagListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseTagList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTagsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTags' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTagListQuery, CaseTagListQueryVariables>
export const CaseTypeDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseTypeDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'codePrefix' } },
                { kind: 'Field', name: { kind: 'Name', value: 'implicitCampaignId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameUsedInDocuments' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccountTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'campaigns' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTypeDetailQuery, CaseTypeDetailQueryVariables>
export const CaseTypeDetailOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseTypeDetailOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTypeDetailOptionsQuery, CaseTypeDetailOptionsQueryVariables>
export const CaseTypeListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CaseTypeList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseTypesSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseTypeListQuery, CaseTypeListQueryVariables>
export const FinancialAccountTypeDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'FinancialAccountTypeDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountType' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'nameUsedInDocuments' } },
                { kind: 'Field', name: { kind: 'Name', value: 'codePrefix' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinancialAccountTypeDetailQuery,
  FinancialAccountTypeDetailQueryVariables
>
export const FinancialAccountTypeListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'FinancialAccountTypeList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'FinancialAccountTypesSortInput' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccountTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FinancialAccountTypeListQuery, FinancialAccountTypeListQueryVariables>
export const OrderDocumentTypeTemplateOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'OrderDocumentTypeTemplateOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'documentTypeId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypeTemplateOptions' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'documentTypeId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'documentTypeId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableVariables' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sampleValue' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableImages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sampleValue' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableSignatures' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sampleValue' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  OrderDocumentTypeTemplateOptionsQuery,
  OrderDocumentTypeTemplateOptionsQueryVariables
>
export const ProductDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ProductDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'product' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseType' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductDetailQuery, ProductDetailQueryVariables>
export const ProductListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ProductList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'caseType' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductListQuery, ProductListQueryVariables>
export const ProductPackageDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ProductPackageDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackage' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ProductPackage' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDocumentType' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderDocumentType' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isGeneratable' } },
          { kind: 'Field', name: { kind: 'Name', value: 'requiresSignature' } },
          { kind: 'Field', name: { kind: 'Name', value: 'appliesMeterai' } },
          { kind: 'Field', name: { kind: 'Name', value: 'documentFilenameTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasTemplate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportAutomaticStateChange' } },
          { kind: 'Field', name: { kind: 'Name', value: 'caseDocumentTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customerDocumentTypes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'financialAccountDocumentTypes' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderDocumentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderPaymentStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'validEntities' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackageStateEffects' },
      typeCondition: {
        kind: 'NamedType',
        name: { kind: 'Name', value: 'ProductPackageStateEffects' },
      },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'state' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'generateDocumentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ProductPackage' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'availability' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'publicName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'products' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'productType' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultDurationMonths' } },
          { kind: 'Field', name: { kind: 'Name', value: 'defaultFeeRate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'minimumFee' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'minimumFeeRate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceLineLabel' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLines' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'amount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'stateDependencies' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'ProductPackageStateDependencies' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'stateEffects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'ProductPackageStateEffects' },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documentTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDocumentType' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageDetailQuery, ProductPackageDetailQueryVariables>
export const ProductPackageListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ProductPackageList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ProductPackagesSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'productPackages' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'caseType' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'products' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductPackageListQuery, ProductPackageListQueryVariables>
export const TicketActionResultDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketActionResultDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionResult' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionResultDetailQuery, TicketActionResultDetailQueryVariables>
export const TicketActionResultListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketActionResultList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'TicketActionResultsSortInput' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionResults' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionResultListQuery, TicketActionResultListQueryVariables>
export const TicketActionTypeDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketActionTypeDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionType' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionTypeDetailQuery, TicketActionTypeDetailQueryVariables>
export const TicketActionTypeListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketActionTypeList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketActionTypesSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionTypeListQuery, TicketActionTypeListQueryVariables>
export const TicketCloseReasonDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketCloseReasonDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketCloseReason' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                { kind: 'Field', name: { kind: 'Name', value: 'ticketTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketCloseReasonDetailQuery, TicketCloseReasonDetailQueryVariables>
export const TicketCloseReasonListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketCloseReasonList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketCloseReasonsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketCloseReasons' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'ticketTypes' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketCloseReasonListQuery, TicketCloseReasonListQueryVariables>
export const TicketTagDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketTagDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketTag' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'ticketTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagDetailQuery, TicketTagDetailQueryVariables>
export const TicketTagListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketTagList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketTagsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketTags' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'deactivated' },
                      value: { kind: 'BooleanValue', value: true },
                    },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'ticketTypes' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagListQuery, TicketTagListQueryVariables>
export const NegotiationTicketOutstandingDebtUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'NegotiationTicketOutstandingDebtUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicketOutstandingDebtUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'negotiationTicketOutstandingDebtUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'negotiationTicket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'outstandingDebt' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  NegotiationTicketOutstandingDebtUpdateMutation,
  NegotiationTicketOutstandingDebtUpdateMutationVariables
>
export const NegotiationTicketSettlementUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'NegotiationTicketSettlementUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicketSettlementUpdateInput' },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'negotiationTicketSettlementUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'negotiationTicket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'adjustedDebt' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'adjustedDebtDiscount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  NegotiationTicketSettlementUpdateMutation,
  NegotiationTicketSettlementUpdateMutationVariables
>
export const TicketActionCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketActionCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ticket' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketDetailBase' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionCreateMutation, TicketActionCreateMutationVariables>
export const TicketActionDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ticket' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketDetailBase' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionDeleteMutation, TicketActionDeleteMutationVariables>
export const TicketActionUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketActionUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketActionUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketActionUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketAction' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ticket' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketDetailBase' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionUpdateMutation, TicketActionUpdateMutationVariables>
export const TicketAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'userId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'userId' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'userId' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'assignedTo' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketAssignMutation, TicketAssignMutationVariables>
export const TicketBulkAssignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketBulkAssign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketBulkAssignInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketBulkAssign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tickets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'assignedTo' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketBulkAssignMutation, TicketBulkAssignMutationVariables>
export const TicketBulkCloseDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketBulkClose' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketBulkCloseInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketBulkClose' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tickets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'closeReason' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketBulkCloseMutation, TicketBulkCloseMutationVariables>
export const TicketCloseDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketClose' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketCloseInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketClose' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketCloseMutation, TicketCloseMutationVariables>
export const TicketDocumentCreateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketDocumentCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ticketId' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocumentCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketDocumentCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ticketId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ticket' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'TicketDocumentDetail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketDocumentCreateMutation, TicketDocumentCreateMutationVariables>
export const TicketDocumentDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketDocumentDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketDocumentDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ticket' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'TicketDocumentDetail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketDocumentDeleteMutation, TicketDocumentDeleteMutationVariables>
export const TicketDocumentUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketDocumentUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocumentUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketDocumentUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticketDocument' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'file' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'ticket' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'documents' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'TicketDocumentDetail' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketDocumentUpdateMutation, TicketDocumentUpdateMutationVariables>
export const TicketNoteAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketNoteAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketNoteAddInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketNoteAdd' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketNoteAddMutation, TicketNoteAddMutationVariables>
export const TicketTagAttachDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketTagAttach' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketTagAttachInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketTagAttach' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagAttachMutation, TicketTagAttachMutationVariables>
export const TicketTagDetachDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TicketTagDetach' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketTagDetachInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketTagDetach' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'ticket' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketTagDetachMutation, TicketTagDetachMutationVariables>
export const InitialTicketFilterUsersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'InitialTicketFilterUsers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'userIds' } },
          type: {
            kind: 'ListType',
            type: {
              kind: 'NonNullType',
              type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'ids' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'userIds' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<InitialTicketFilterUsersQuery, InitialTicketFilterUsersQueryVariables>
export const MediationTicketDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MediationTicketDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mediationTicket' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MediationTicketDetail' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MediationTicketDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MediationTicketDetailQuery, MediationTicketDetailQueryVariables>
export const NegotiationTicketDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'NegotiationTicketDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'negotiationTicket' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'NegotiationTicketDetail' },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketAction' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketAction' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'result' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'comment' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketEvent' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketEvent' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'date' } },
          { kind: 'Field', name: { kind: 'Name', value: 'parameters' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionResult' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actionType' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RemoteFileInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RemoteFile' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mimeType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDocumentDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketDocument' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'otherType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'file' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RemoteFileInfo' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomerInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Customer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'birthPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'gender' } },
          { kind: 'Field', name: { kind: 'Name', value: 'governmentId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'educationLevel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'marriageStatus' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cases' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'totalCount' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketDetailBase' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'closedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createdBy' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'App' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'actions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketAction' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '250' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketEvent' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'documents' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDocumentDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'caseProduct' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'accountNodeAccess' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'stage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'flow' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'closeReason' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'assignedToUser' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'FragmentSpread',
                                    name: { kind: 'Name', value: 'CustomerInfo' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'product' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhoneNumberCopyable' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhoneNumber' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'international' } },
          { kind: 'Field', name: { kind: 'Name', value: 'national' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'BaseFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'type' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'lastDueDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dpd' } },
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'principalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'provider' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'providerReference' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ptpDate' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registeredContactPhone' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCard' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'lastEventConnection' },
            name: { kind: 'Name', value: 'events' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'IntValue', value: '1' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'date' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'outstandingDebt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketFinancialAccount' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'FinancialAccount' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'BaseFinancialAccount' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tickets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '10' },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'field' },
                      value: { kind: 'EnumValue', value: 'DATE' },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'direction' },
                      value: { kind: 'EnumValue', value: 'DESC' },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketCard' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NegotiationTicketDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NegotiationTicket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketDetailBase' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'outstandingDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adjustedDebt' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adjustedDebtDiscount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'amount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'financialAccount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketFinancialAccount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'providerContact' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'phoneNumber' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'PhoneNumberCopyable' },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'phoneNumberNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddress' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emailAddressNote' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<NegotiationTicketDetailQuery, NegotiationTicketDetailQueryVariables>
export const TicketActionOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketActionOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticket' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableActionResults' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'success' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableActionTypes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'value' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketActionOptionsQuery, TicketActionOptionsQueryVariables>
export const TicketAvailableTagsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketAvailableTags' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticket' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableTags' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'color' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketAvailableTagsQuery, TicketAvailableTagsQueryVariables>
export const TicketCloseOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketCloseOptions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticket' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'availableCloseReasons' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'FragmentSpread',
                        name: { kind: 'Name', value: 'TicketCloseReasonOption' },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCloseReasonOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketCloseReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketCloseOptionsQuery, TicketCloseOptionsQueryVariables>
export const TicketListAllDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketListAll' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketsAllFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketsAllSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketsAll' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketList' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'userTeam' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'provider' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'nullableProviderContact' },
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketListAllQuery, TicketListAllQueryVariables>
export const TicketListFilterOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketListFilterOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketCloseReasons' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'TicketCloseReasonOption' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'products' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeams' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketTags' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketCloseReasonOption' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TicketCloseReason' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketListFilterOptionsQuery, TicketListFilterOptionsQueryVariables>
export const TicketListTeamAssignedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketListTeamAssigned' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'TicketsTeamAssignedFilterInput' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'TicketsTeamAssignedSortInput' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketsTeamAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketList' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'userTeam' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'provider' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'nullableProviderContact' },
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketListTeamAssignedQuery, TicketListTeamAssignedQueryVariables>
export const TicketListUserAssignedDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TicketListUserAssigned' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'TicketsUserAssignedFilterInput' },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: {
            kind: 'NamedType',
            name: { kind: 'Name', value: 'TicketsUserAssignedSortInput' },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'ticketsUserAssigned' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TicketList' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TicketList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Ticket' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'assignedTo' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'userTeam' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'closeReason' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'success' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tags' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'color' } },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'FinancialAccountTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'financialAccount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'case' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'customer' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'provider' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediationTicket' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: {
              kind: 'NamedType',
              name: { kind: 'Name', value: 'NegotiationTicket' },
            },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  alias: { kind: 'Name', value: 'nullableProviderContact' },
                  name: { kind: 'Name', value: 'providerContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'phoneNumber' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'displayNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'international' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TicketListUserAssignedQuery, TicketListUserAssignedQueryVariables>
export const UserTeamAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserTeamAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeamCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeamCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'userTeam' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'types' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamAddMutation, UserTeamAddMutationVariables>
export const UserTeamDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserTeamDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeamDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: '__typename' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamDeleteMutation, UserTeamDeleteMutationVariables>
export const UserTeamUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserTeamUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeamUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeamUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'userTeam' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'types' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamUpdateMutation, UserTeamUpdateMutationVariables>
export const UserTeamAddOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserTeamAddOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamAddOptionsQuery, UserTeamAddOptionsQueryVariables>
export const UserTeamDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserTeamDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamDetail' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'types' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserTeamList' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'displayName' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamDetailQuery, UserTeamDetailQueryVariables>
export const UserTeamListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserTeamList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeamsFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeamsSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeams' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'cursor' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            {
                              kind: 'FragmentSpread',
                              name: { kind: 'Name', value: 'UserTeamList' },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserTeamList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserTeam' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'types' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'businessEntities' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseTypes' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'name' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserTeamListQuery, UserTeamListQueryVariables>
export const CaseBookReassignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CaseBookReassign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CaseBookReassignInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'caseBookReassign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'movedCaseCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'failedCaseCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'remainingCaseCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CaseBookReassignMutation, CaseBookReassignMutationVariables>
export const ChangeUserPasswordDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ChangeUserPassword' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'newPassword' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'oldPassword' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'passwordChange' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'newPassword' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'newPassword' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'oldPassword' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'oldPassword' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refreshToken' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ChangeUserPasswordMutation, ChangeUserPasswordMutationVariables>
export const UserAddDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserAdd' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserCreateInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'redirectUrl' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userCreate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'redirectUrl' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'redirectUrl' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roleDescription' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'isSuperUser' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRateMultiplier' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'size' },
                value: { kind: 'IntValue', value: '120' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserAddMutation, UserAddMutationVariables>
export const UserDeleteDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserDelete' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userDelete' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: '__typename' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserDeleteMutation, UserDeleteMutationVariables>
export const UserRegisterMekariAutosignDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserRegisterMekariAutosign' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userRegisterMekariAutosign' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UserRegisterMekariAutosignMutation,
  UserRegisterMekariAutosignMutationVariables
>
export const UserUpdateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'UserUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userUpdate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserDetail' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roleDescription' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'isSuperUser' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRateMultiplier' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'size' },
                value: { kind: 'IntValue', value: '120' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserUpdateMutation, UserUpdateMutationVariables>
export const UserAddOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserAddOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeams' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserAddOptionsQuery, UserAddOptionsQueryVariables>
export const UserBookDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserBook' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userBook' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'openCaseCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assignedCaseCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sampleCaseCodes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'relationshipCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserBookQuery, UserBookQueryVariables>
export const UserDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'user' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserDetail' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'permissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeams' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'IntValue', value: '100' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roleDescription' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserDetail' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserList' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accountPermissions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'isSuperUser' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sampleRateMultiplier' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'avatar' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'size' },
                value: { kind: 'IntValue', value: '120' },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserDetailQuery, UserDetailQueryVariables>
export const UserListDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserList' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'UsersFilterInput' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'UsersSortInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'before' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'before' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'after' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'after' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'first' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'first' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'last' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'last' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'filter' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'filter' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'sort' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'sort' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'edges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'cursor' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'node' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserList' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'avatar' },
                              arguments: [
                                {
                                  kind: 'Argument',
                                  name: { kind: 'Name', value: 'size' },
                                  value: { kind: 'IntValue', value: '48' },
                                },
                              ],
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'hasPreviousPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hasNextPage' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'startCursor' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endCursor' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserList' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isActive' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'roles' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userTeam' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'roleDescription' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserListQuery, UserListQueryVariables>
export const UserMekariAutosignStatusDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'UserMekariAutosignStatus' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userMekariAutosignStatus' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserMekariAutosignStatusQuery, UserMekariAutosignStatusQueryVariables>
